/* ============================================================================
   KREAZ STUDIO — 3D Duvar Rafı Tasarım Aracı
   ----------------------------------------------------------------------------
   • Bağımlılık: yalnızca three.js çekirdeği (tek dosya, importmap gerektirmez).
     GLB okuyucu, kamera kontrolü ve stüdyo HDRI'ı bu dosyada yazılıdır; böylece
     Shopify temasındaki başka bir importmap ile çakışma riski yoktur.
   • Ölçü sistemi: duvar 5 cm'lik kare matristir. 1 birim = 5 cm = 0.05 m.
       Small  30 cm →  6 birim
       Medium 45 cm →  9 birim
       Large  60 cm → 12 birim
     Rafların tümü 20 cm yüksekliğindedir → 4 birim.
   ========================================================================== */

const THREE_CDN = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.js';

/* KREAZ logosu (mağaza logosundan vektöre çevrildi; 985×281 tuvalin
   koordinatları). Açılışta içi boş çiziliyor, yükleme ilerledikçe alttan
   siyahla doluyor. Mağaza bölümü aynı işaretlemeyi sunucuda basıyor
   (sections/kreaz-studio.liquid) — logo JS daha inmeden ekranda. */
const KREAZ_LOGO_YOLU = 'M12 13L60 13L60 119L86 119L131 13L181 13L126 138L181 268L131 268L85 162L60 162L60 268L12 268ZM214 13L384 13L384 162L337 162L383 268L332 268L287 162L261 162L261 268L214 268ZM261 55L336 55L336 119L261 119ZM427 15L552 15L552 57L427 57ZM427 120L552 120L552 162L427 162ZM427 225L552 225L552 267L427 267ZM596 13L766 13L766 268L718 268L718 162L643 162L643 268L596 268ZM644 55L718 55L718 119L644 119ZM808 13L978 13L978 56L862 224L978 224L978 268L808 268L808 224L925 56L808 56Z';
function yukleyiciHTML() {
  const svg = (sinif) => `<svg class="${sinif}" viewBox="8 9 974 263" aria-hidden="true"><path fill-rule="evenodd" d="${KREAZ_LOGO_YOLU}"/></svg>`;
  return `<div class="kz-boot" role="progressbar" aria-label="Stüdyo açılıyor"><div class="kz-boot-logo">${svg('kz-boot-iz')}${svg('kz-boot-dolu')}</div></div>`;
}

/** 1 grid karesi (metre). 5 cm. */
const U = 0.05;
/** Rafların grid yüksekliği (20 cm = 4 kare). */
const SHELF_ROWS = 4;
/** Süpürgelik yüksekliği (metre). */
const SUPURGELIK_H = 0.085;
/* Rafın konabileceği en alt sıra. Yan panel tablanın ~5 cm altına iniyor;
   0. ve 1. sıradaki raf süpürgeliğe giriyordu. 2. sırada panel süpürgeliğin
   yalnız 1,6 cm üstünde kalıyor ve yukarıdan bakınca ona biniyor gibi
   görünüyordu; 3 cm pay ile en alt sıra 3 (panelin ucu yerden 15 cm). */
const ALT_SIRA = Math.ceil((SUPURGELIK_H + 0.03) / U);
/* Eski kayıt ya da paylaşılan linkte raf süpürgelik hizasındaysa bütün
   tasarım birlikte yukarı kaydırılıyor: düzen bozulmuyor, raf düşmüyor. */
const supurgelikPayi = (satirlar) => (satirlar.length ? Math.max(0, ALT_SIRA - Math.min(...satirlar)) : 0);

/**
 * Asistan, müşteri bu kadar süre raf eklemezse bir öneriyle devreye girer.
 * Sabit aralık: eskiden her turda uzuyordu ve asistan üç ipucundan sonra
 * tamamen susuyordu — duvar boşken stüdyo ıssız kalıyordu.
 */
const IDLE_MS = 12000;
/** Rehber el: müşteri bu kadar süre hiçbir şeye dokunmazsa sıradaki adımı gösteriyor. */
const REHBER_BOSTA_MS = 22000;
/** Asistan e-posta adresini ancak duvarda bu kadar raf varken istiyor. */
const MAIL_MIN_RAF = 2;

/** Açılıştaki karşılama mesajından sonra e-posta formuna dönmeden önceki pay. */
const EMAIL_PROMPT_MS = 6000;
/**
 * İpucu baloncuğu bu süre ekranda kalır; sonra baloncuk e-posta formuna
 * (asistanın dinlenme haline) geri döner.
 */
const TIP_HOLD_MS = 7000;
/** WhatsApp çekmecesi bu süre açık kalır. */
const WA_DRAWER_MS = 5200;
/** Oda penceresinin açılma/kapanma solma süresi (CSS ile aynı olmalı). */
const ROOM_FADE_MS = 130;
/**
 * Asistan baloncuğunun sönme süresi (CSS'teki .26s + küçük pay).
 * Çekmeceler baloncuğun hemen altından açıldığı için, baloncuk sönmeden
 * açılırlarsa dar ekranda bir an üst üste biniyorlar. Bu yüzden her çekmece
 * bu kadar bekliyor.
 */
const BUBBLE_FADE_MS = 280;
/** Çekmecenin sola açılıp kapanma süresi (CSS ile aynı olmalı) + pay. */
const DRAWER_SLIDE_MS = 460;
/**
 * Oda seçildikten sonra "?" çekmecesi bu süre konuşur; asistan ancak bu
 * bittikten sonra tavsiyesini verir. İkisi aynı anda konuşunca dar ekranda
 * baloncuklar üst üste biniyordu.
 */
const ROOM_TOAST_MS = 1000;
/**
 * Oda seçilmemişken eşya düğmesine basılırsa gelen eşya.
 * Koltuk her evde var ve ölçeği en iyi anlatan parça.
 */
const VARSAYILAN_ESYA = 'koltuk';
/**
 * Çekmecedeki tanıtım yazıları — kısa, satış dili yok.
 * Mağaza bunları bölüm ayarından değiştirebilir (`shareHints`).
 */
const SHARE_HINTS = [
  'Tasarımını sevdiklerine gönder',
  'Emin değilsen birine sor',
  'Bu düzeni biriyle paylaş',
  'Kararsızsan gönder, beraber bakın',
  'Açan kişi senin duvarını görür',
  'İkinci bir göz iyi gelir',
  'Paylaş, sonra değiştirmeye devam et',
  'Linki sakla, sonra devam et',
];
/** Tasarım değişiminden sonra profili güncellemeden önce beklenen süre. */
const PROFILE_SYNC_MS = 3000;
/**
 * Asistan iki mesaj arasında en az bu kadar susar.
 * Uyarılar (çakışma, yer yok) bu kuralın dışında.
 */
const SAY_COOLDOWN_MS = 7000;
/** Asistan kaç kez konuşunca WhatsApp bir kez konuşsun. */
const WA_SPEAK_EVERY = 2;

/**
 * E-posta isteme cümleleri — sırayla döner, aynı cümle üst üste çıkmaz.
 * Sohbet dilinde: "kaydol" değil, "bunu sana göndereyim".
 */
const MAIL_ASKS = [
  'Güzel bir tasarım oldu. Mail adresini bırak, bunu sana göndereyim.',
  'Tasarımını kaybetme — adresini yaz, linkini yollayayım.',
  'Sonra tekrar açmak istersen mail adresini bırakman yeter.',
  'Ölçü alıp geri döneceksen, tasarımın mailinde hazır bekler.',
  'Aklında kalsın diye sana gönderebilirim. Adresin?',
  'Bu düzeni bir kenara yazayım mı? Mail adresin yeter.',
];
/** Klaviyo API sürümü — uç nokta tarih damgalı sürümleme kullanıyor. */
const KLAVIYO_REVISION = '2026-07-15';
/** Liste kimliği yoksa akışın tetikleneceği Klaviyo metriği. */
/* Clarity'ye giden müşteri olayları (Clarity → Filters → Custom events /
   Dashboard → Smart events). Adlar Clarity'de böyle görünüyor. */
const CLARITY_OLAYLARI = new Set([
  'kz_studyo_acildi', 'kz_paylasilan_acildi', 'kz_raf_eklendi', 'kz_renk_secildi',
  'kz_room_pick', 'kz_room_panel', 'kz_sepete_eklendi', 'kz_mail_captured',
  'kz_mail_dismissed', 'kz_whatsapp_share', 'kz_duvar_olcusu', 'kz_gece_modu',
  'kz_altin_saat', 'kz_dekor', 'kz_gpu_yedek',
]);
const KLAVIYO_METRIC = 'Raf Stüdyosu Tasarımı';
/* Stüdyodan çıkarken (sekme gizlenince/kapanınca) yazılan son tasarım.
   Akışı tetiklemiyor; son linkin profilde kesin durması için. */
const KLAVIYO_METRIC_SON = 'Raf Stüdyosu Son Tasarım';

/**
 * ODA ÖN AYARLARI
 *
 * Kimse "45 cm raf lazım" diye düşünmez; "antreme bir şey lazım" diye düşünür.
 * Müşteri odasını seçer, duvar hazır bir düzenle kurulur; oradan devam eder.
 *
 * Renk önerileri mağazanın İlham sayfasındaki kendi yönlendirmelerinden
 * geliyor (Blush Clay → yatak/çocuk odası, Napoli Sun → mutfak/çalışma).
 *
 * `tavsiyeler`, oda seçildikten sonra asistanın kullandığı bağlam cümleleri.
 * Sonraki turda karta görsel ve sahneye eşya eklenecek; bunun için diziye
 * `gorsel` / `esyalar` alanları eklemek yeterli, başka yeri değişmiyor.
 */
const ROOMS = [
  {
    id: 'mutfak', ad: 'Mutfak', ipucu: 'Baharatlıklar, kavanozlar, günlük takım',
    simge: 'odaMutfak', duvar: [320, 270], renk: 'napoli', renk2: 'canvas',
    esya: 'tezgah', aciklik: 'balkon', desen: 'kuleSira', urun: 'medium',
    tavsiyeler: [
      'Baharatlıkları alt rafa al; günlük kullandıkların göz hizasında kalsın.',
      'Tezgahın hemen üstü en çok işe yarayan yer — oraya bir raf daha sığar.',
      'Kavanozları aynı sıraya dizersen mutfak bir anda toplu görünür.',
      'Az kullandığın takımlar en üste; her gün uzandıkların ortaya.',
    ],
  },
  {
    id: 'kahve', ad: 'Kahve Köşesi', ipucu: 'Fincan, öğütücü, filtre kağıdı',
    simge: 'odaKahve', duvar: [320, 270], renk: 'carbon', renk2: 'sage',
    esya: 'kahveBar', desen: 'merdiven', urun: 'small',
    tavsiyeler: [
      'Öğütücü alt rafta dursun; üstte fincanlara yer kalsın.',
      'Filtre kağıdı ve kaşık için bir raf daha eklesen her şey elinin altında olur.',
      'İki raf arası 20 cm — uzun bir demlik bile rahat giriyor.',
      'Fincanları tek sıraya dizmek bu köşeyi kafe gibi gösteriyor.',
    ],
  },
  {
    id: 'antre', ad: 'Antre', ipucu: 'Anahtar, posta, küçük sepet',
    simge: 'odaAntre', duvar: [320, 270], renk: 'carbon', renk2: 'canvas',
    esya: 'konsol', yuzey: 'antre', aciklik: 'kapi', desen: 'sira', urun: 'large',
    tavsiyeler: [
      'Anahtarlık için üst raf yeter; alt rafı çanta ve postaya bırak.',
      'Kapıya en yakın raf en çok kullanılan olur — onu boş tutma.',
      'Antrede tek uzun raf da iş görür, ama ikincisi düzeni gerçekten kuruyor.',
      'Küçük bir sepet koy; içine giren her şey görünmez olsun.',
    ],
  },
  {
    id: 'calisma', ad: 'Çalışma Odası', ipucu: 'Masanın üstü boşalsın',
    simge: 'odaCalisma', duvar: [320, 270], renk: 'nordic', renk2: 'canvas',
    esya: 'masa', desen: 'izgara', urun: 'large',
    tavsiyeler: [
      'Masadan 40 cm yukarısı ideal — uzanınca yetişir, kafana çarpmaz.',
      'Alt rafı her gün kullandıklarına ayır; üstü kitap ve kutulara kalsın.',
      'Monitörün üstünü boş bırak, raf yanlara doğru uzasın.',
      'İki uzun raf masanın üstünü tamamen boşaltmaya yeter.',
    ],
  },
  {
    id: 'yatak', ad: 'Yatak Odası', ipucu: 'Kitap, lamba, çerçeve',
    simge: 'odaYatak', duvar: [320, 270], renk: 'sage', renk2: 'blush',
    esya: 'yatak', desen: 'braket', urun: 'medium',
    tavsiyeler: [
      'Başucu rafı yatak hizasından 50 cm yukarıda en rahatı.',
      'Bir rafı tamamen boş bırak — göz dinlensin.',
      'Kitap ve lamba alt rafta, çerçeveler üstte iyi duruyor.',
      'Blush Clay yatak odasında en çok tercih edilen renk oldu.',
    ],
  },
  {
    id: 'cocuk', ad: 'Çocuk Odası', ipucu: 'Alçak, güvenli, renkli',
    simge: 'odaCocuk', duvar: [320, 270], renk: 'blush', renk2: 'napoli',
    esya: 'besik', desen: 'merdiven', urun: 'small',
    tavsiyeler: [
      'Rafları alçak tut — çocuk kendi uzanabilsin, kendi toplasın.',
      'Kitapları öne bakacak şekilde dizmek için 30\'luk raflar ideal.',
      'Ağır oyuncakları en alta koy; üstü hafif şeylere kalsın.',
      'İki farklı renk karıştırmak çocuk odasında çok iyi duruyor.',
    ],
  },
  {
    id: 'banyo', ad: 'Banyo', ipucu: 'Havlu, şişeler, yedekler',
    simge: 'odaBanyo', duvar: [320, 270], renk: 'nordic', renk2: 'canvas',
    esya: 'lavabo', yuzey: 'fayans', aciklik: 'kapi', desen: 'cevre', urun: 'small',
    tavsiyeler: [
      'Havluları alt rafa katlayıp koy; şişeler üstte dursun.',
      'Lavabonun üstü değil yanı daha kullanışlı — su sıçramıyor.',
      'Yedekleri en üste al, her gün kullandıklarını alta.',
      'Beyaz fayansın üstünde Nordic Stone raflar net seçiliyor.',
    ],
  },
  {
    id: 'salon', ad: 'Salon', ipucu: 'Kitap, bitki, çerçeve',
    simge: 'odaSalon', duvar: [320, 270], renk: 'sage', renk2: 'carbon',
    esya: 'koltuk', desen: 'izgara', urun: 'medium',
    tavsiyeler: [
      'Salonda rafları simetrik dizmek duvarı büyük gösteriyor.',
      'Bir rafa sarkan bir bitki koy — düzen yumuşuyor.',
      'Kitapları renk sırasına göre dizmek basit ama etkili.',
      'Dört raf tek duvarı komple değiştirir; arada boşluk bırakmaktan çekinme.',
    ],
  },
  {
    id: 'balkon', ad: 'Balkon', ipucu: 'Saksı, fener, dış mekân',
    simge: 'odaBalkon', duvar: [320, 270], renk: 'carbon', renk2: 'napoli',
    esya: 'balkonSet', yuzey: 'balkon', aciklik: 'kapi',
    desen: 'izgara', urun: 'medium',
    tavsiyeler: [
      'Saksıları alt rafa diz; sulama kolaylaşır, su damlayan yer belli olur.',
      'Dışarıda Carbon Matte hem güneşte hem gölgede net duruyor.',
      'Feneri ve mumları göz hizasına al — akşam balkonu bambaşka oluyor.',
      'Üst rafı hafif tut; rüzgârlı balkonda ağır şeyler alt rafa.',
    ],
  },
];

/**
 * Tasarım büyüklüğü — oda seçicideki ikinci segme.
 * Amaç sepet ortalamasını yükseltmek: müşteri "küçük bir şey" isterse bile
 * gördüğü öneri hiçbir zaman iki raf değil.
 */
const BOYLAR = [
  { id: 'sade', ad: 'Sade', not: '3 raf', adet: 3 },
  { id: 'dengeli', ad: 'Dengeli', not: '6 raf', adet: 6 },
  { id: 'dolu', ad: 'Duvar dolusu', not: '9 raf', adet: 9 },
];

/** Renk kurgusu — oda seçicideki üçüncü segme. */
const RENK_MODLARI = [
  { id: 'tek', ad: 'Tek renk' },
  { id: 'cok', ad: 'Çok renkli' },
];

/**
 * ODA EŞYALARI
 *
 * Amaç müşteriye mobilya satmak değil; rafı gerçek bir bağlamda göstermek.
 * "Yatağımın başucunda nasıl durur?" sorusunun cevabı dönüşümü artırıyor.
 *
 * Tasarım kararları:
 *  - Her eşya basit KUTULARDAN kuruluyor. Hazır model indirilmiyor, dosya
 *    büyümüyor, yükleme süresi değişmiyor; bir eşya 6–14 kutu, yani sahneye
 *    birkaç yüz üçgen ekliyor.
 *  - Renkler bilerek soluk: sahnenin yıldızı raflar olmalı.
 *  - Eşya duvara yaslı durur ve YALNIZCA yatayda, ızgara üzerinde kayar.
 *    Kapladığı hücreler raf çarpışma ızgarasına katıldığı için, üstüne raf
 *    koymaya çalışmak "üst üste raf" ile aynı uyarıyı veriyor — ayrı bir
 *    çarpışma koduna gerek kalmadı.
 *
 * `en` / `yukseklik` ızgara birimidir (1 birim = 5 cm).
 */
const PROPS = {
  yatak:      { ad: 'Yatak',            en: 32, yukseklik: 18, ciz: cizYatak },
  masa:       { ad: 'Çalışma masası',   en: 24, yukseklik: 15, ciz: cizMasa },
  koltuk:     { ad: 'Koltuk',           en: 36, yukseklik: 17, ciz: cizKoltuk },
  besik:      { ad: 'Beşik',            en: 26, yukseklik: 19, ciz: cizBesik },
  // Yükseklik aynanın üstünü de kapsıyor: raflar aynayla çakışmasın.
  lavabo:     { ad: 'Lavabo', en: 16, yukseklik: 37, ciz: cizLavabo },
  konsol:     { ad: 'Konsol',           en: 20, yukseklik: 16, ciz: cizKonsol },
  tezgah:     { ad: 'Tezgah',           en: 36, yukseklik: 18, ciz: cizTezgah },
  kahveBar:   { ad: 'Kahve tezgahı',    en: 18, yukseklik: 18, ciz: cizKahveBar },
  balkonSet:  { ad: 'Balkon takımı',    en: 28, yukseklik: 15, ciz: cizBalkonSet },
};

/**
 * Açılış kamera açısı.
 *
 * Tam karşıdan bakınca oda düz bir duvara dönüşüyor ve pencere görünmüyordu.
 * Hafif köşeden bakmak hem tasarım duvarını hem yan duvarı (pencere/kapı)
 * aynı karede veriyor — müşteri "oda" olduğunu ilk bakışta anlıyor.
 */
const ACILIS_AZIMUT = -0.44;
const ACILIS_POLAR = Math.PI / 2 - 0.17;

/** Kameranın yatayda sapabileceği en büyük açı (±30°). Oda genişliği de buna göre kurulur. */
const MAX_AZIMUTH = Math.PI / 6;
/** Dikeyde yukarı/aşağı sapma sınırları. */
const MAX_POLAR_UP = 0.31;
const MAX_POLAR_DOWN = 0.14;
/** En fazla uzaklaşma katsayısı. Oda hacmi de bu mesafeye göre kurulur. */
const ZOOM_OUT_MAX = 1.3;
/* En yakın bakışta ekranı dolduran duvar genişliği / yüksekliği (m).
   0,70 m denendi, fazla yakındı: raflar ekranı kaplıyor, geri çekilmek
   için parmakları koyacak boş duvar kalmıyordu; yakın görünüm de en pahalı
   kare. 1,2 m'de 45 cm'lik raf telefonda ~140 px — tutması rahat, çevresinde
   duvar görünüyor. Masaüstünde ekran yüksekliği en az 1 m duvar gösteriyor. */
const YAKIN_GENISLIK = 1.2;
const YAKIN_YUKSEKLIK = 1.0;

let T = null; // three.js ad alanı (dinamik yüklenir)

/* ==========================================================================
   1) VARSAYILAN AYARLAR
   ========================================================================== */

const DEFAULT_CONFIG = {
  threeUrl: THREE_CDN,
  modelsUrl: null, // null → kreaz-models.js dosyası modülün yanından yüklenir

  /** Harici .glb kullanmak isterseniz doldurun; boşsa gömülü modeller kullanılır. */
  models: { small: null, medium: null, large: null },

  currency: 'TRY',
  moneyFormat: null, // Shopify money_format (ör. "{{amount_with_comma_separator}} TL")
  locale: 'tr-TR',

  /**
   * Kargo bedeli (kuruş). 2+ raf alımında ücretsizleşir.
   * Mağazadaki "Standart Kargo" oranıyla aynı olmalıdır; Liquid bunu bölüm
   * ayarından geçirir, çünkü Shopify teslimat oranlarını temaya açmıyor.
   */
  shippingCents: 24900,

  /** Duvar başlangıç ölçüleri (cm) — 5'in katı olmalıdır. */
  wall: { width: 240, height: 270, min: 120, max: 480, minHeight: 150, maxHeight: 320 },

  /** Ürünler. priceCents Shopify'dan gelir; yoksa price (TL) kullanılır. */
  products: [
    { id: 'small',  name: 'Small',  cm: 30, units: 6,  price: 1249 },
    { id: 'medium', name: 'Medium', cm: 45, units: 9,  price: 1499 },
    { id: 'large',  name: 'Large',  cm: 60, units: 12, price: 1749 },
  ],

  /**
   * Ürün renk paleti — RAL kodlarıyla.
   * hex değerleri RAL Classic karşılıklarıdır; ekrandaki his gerçek numuneye
   * göre farklıysa yalnızca bu diziyi düzenleyin.
   */
  colors: [
    { id: 'napoli',  name: 'Napoli Sun',   ral: 'RAL 1018', hex: '#f5d033', roughness: 0.48, clearcoat: 0.30 },
    { id: 'sage',    name: 'Sage Green',   ral: 'RAL 7033', hex: '#7d8471', roughness: 0.52, clearcoat: 0.28 },
    { id: 'nordic',  name: 'Nordic Stone', ral: 'RAL 5014', hex: '#6c7c98', roughness: 0.50, clearcoat: 0.30 },
    { id: 'canvas',  name: 'Raw Canvas',   ral: 'RAL 9001', hex: '#fdf4e3', roughness: 0.54, clearcoat: 0.26 },
    { id: 'blush',   name: 'Blush Clay',   ral: 'RAL 3015', hex: '#ea899a', roughness: 0.50, clearcoat: 0.28 },
    { id: 'carbon',  name: 'Carbon Matte', ral: 'RAL 9011', hex: '#1c1c1c', roughness: 0.56, clearcoat: 0.24 },
  ],

  /** Müşterinin deneyebileceği duvar renkleri (ilk kayıt varsayılandır). */
  wallColors: [
    { id: 'plaster', name: 'Kırık Beyaz', hex: '#ffffff' },
    { id: 'linen',   name: 'Keten',       hex: '#f0e8dc' },
    { id: 'greige',  name: 'Greige',      hex: '#ddd6cb' },
    { id: 'sand',    name: 'Kum',         hex: '#e3d3ba' },
    { id: 'clay',    name: 'Kil',         hex: '#d9c3b4' },
    { id: 'olive',   name: 'Zeytin',      hex: '#c3c4ae' },
    { id: 'mist',    name: 'Sis Mavi',    hex: '#c4d0d6' },
    { id: 'slate',   name: 'Antrasit',    hex: '#8e8d89' },
  ],

  /**
   * Avantaj merdiveni. `min` = bu kademeyi açan raf adedi.
   * Shopify tarafında bunlara karşılık gelen otomatik indirimler tanımlanmalıdır
   * (bkz. shopify/KURULUM.md). `code` verilirse checkout öncesi indirim kodu uygulanır.
   */
  tiers: [
    { min: 1, discount: 0,    freeShipping: false, label: 'Liste fiyatı',   short: '1 raf' },
    { min: 2, discount: 0,    freeShipping: true,  label: 'Ücretsiz kargo', short: 'Kargo bedava', code: null },
    { min: 3, discount: 0.05, freeShipping: true,  label: '%5 avantaj',     short: '%5',  code: null },
    { min: 4, discount: 0.08, freeShipping: true,  label: '%8 avantaj',     short: '%8',  code: null },
    { min: 6, discount: 0.12, freeShipping: true,  label: '%12 avantaj',    short: '%12', code: null },
    { min: 8, discount: 0.15, freeShipping: true,  label: '%15 avantaj',    short: '%15', code: null },
  ],

  /**
   * Shopify entegrasyonu — iki biçim desteklenir:
   *
   * 1) variants: elle yazılan eşleme
   *      { small: { carbon: 41234567890, chalk: 41234567891 }, ... }
   *
   * 2) productVariants: Liquid'in ürettiği varyant listesi (önerilen).
   *      { small: [ { id: 41234567890, option: 'Karbon Mat', price: 124900 } ], ... }
   *    `option` değeri, renk tanımındaki `shopifyOption` (yoksa `name`) ile eşleşir.
   *    Bu biçimde fiyatlar da Shopify'dan gelir; katalogdaki tutarlar renge göre güncellenir.
   */
  variants: null,
  productVariants: null,
  cartUrl: '/cart',
  checkoutUrl: '/checkout',
  /** 'checkout' → sepete ekleyip ödemeye git, 'cart' → sepete ekleyip sepete git */
  afterAdd: 'cart',

  brandName: 'KREAZ',
  brandSub: 'Raf Stüdyosu',
  persistKey: 'kreaz-studio-design-v1',

  /** Tam ekran stüdyodan mağazaya dönüş. */
  backUrl: '/',
  backLabel: 'Mağazaya dön',
  /** Mağaza logosu (boşsa yalnızca ok + etiket gösterilir). */
  logoUrl: null,

  /**
   * Tam ekran modu. Stüdyo tarayıcı penceresinin tamamını kaplar; sayfa
   * kaydırma ile tasarım ekranını sürükleme birbirine karışmaz.
   * Mağaza sayfasının içine gömmek isterseniz false yapın.
   */
  fullscreen: true,

  /** Gece modu başlangıçta kapalıdır; müşteri araç çubuğundaki ay ile açar. */
  night: false,

  /**
   * Asistanın boşta kalma önerilerine eklenecek mağaza cümleleri.
   * Örn. ['Bu hafta en çok 5 raflı setler tasarlandı.'] — buraya yalnızca
   * gerçekten arkasında durabileceğiniz cümleleri yazın; stüdyo kendiliğinden
   * sayı uydurmaz.
   */
  socialProof: [],

  /**
   * E-posta toplama ve paylaşım.
   *
   * Klaviyo alanları boşsa hiçbir Klaviyo isteği yapılmaz — Shopify müşteri
   * kaydı ve mağaza bildirimi yine çalışır. Public key istemci tarafında
   * durmak üzere tasarlanmıştır, gizli anahtar değildir.
   */
  klaviyoPublicKey: null,
  klaviyoListId: null,
  /** WhatsApp'ta paylaşılan mesajın başı; tasarım linki sonuna eklenir. */
  shareMessage: 'Duvarıma böyle bir raf düzeni kurdum — sence nasıl olmuş?',
  /** WhatsApp çekmecesindeki tanıtım yazıları (boşsa yerleşik metinler). */
  shareHints: null,
  /** Gizlilik metni adresi (e-posta alanının altında görünür). */
  privacyUrl: null,
  /** Paylaşım linklerinin tabanı; boşsa bulunulan sayfa kullanılır. */
  studioUrl: null,
};

/* ==========================================================================
   2) KÜÇÜK YARDIMCILAR
   ========================================================================== */

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smoothstep = (t) => t * t * (3 - 2 * t);
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const uid = () => Math.random().toString(36).slice(2, 9);
const snap5 = (v) => Math.round(v / 5) * 5;

/** Deterministik rastgele (dokuların her yüklemede aynı olması için). */
function seeded(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5;  s >>>= 0;
    return s / 4294967296;
  };
}

function el(tag, cls, html) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (html != null) n.innerHTML = html;
  return n;
}

const ICONS = {
  front: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/></svg>',
  left:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4 7 12l8 8"/></svg>',
  right: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 4 8 8-8 8"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg>',
  copy:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>',
  undo:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h11a5 5 0 0 1 0 10h-5"/><path d="m8 4-4 4 4 4"/></svg>',
  clear: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="m4 20 7-7M20 4 9 15"/><path d="M14 4h6v6"/></svg>',
  camera:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13" r="3.5"/></svg>',
  expand:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9V4h5M20 15v5h-5M20 9V4h-5M4 15v5h5"/></svg>',
  bag:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>',
  spark: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5 13.9 9l6.6 1.9-6.6 1.9L12 19.5 10.1 12.8 3.5 10.9 10.1 9 12 2.5Z"/></svg>',
  chevron: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
  ruler: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="8" width="20" height="8" rx="1.5"/><path d="M7 8v3M12 8v4M17 8v3"/></svg>',
  grab: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="6" r="1.2"/><circle cx="15" cy="6" r="1.2"/><circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/><circle cx="9" cy="18" r="1.2"/><circle cx="15" cy="18" r="1.2"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4.5 4.5L19 7"/></svg>',
  bot:   '<svg viewBox="0 0 32 32" fill="none"><rect x="6" y="9" width="20" height="15" rx="5" fill="#fff"/><circle cx="12.4" cy="16" r="2.1" fill="#000"/><circle cx="19.6" cy="16" r="2.1" fill="#000"/><path d="M13.2 20.4c1.7 1.3 3.9 1.3 5.6 0" stroke="#000" stroke-width="1.5" stroke-linecap="round"/><path d="M16 4.5v4" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/><circle cx="16" cy="3.6" r="1.7" fill="#fff"/></svg>',
  back:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m11 18-6-6 6-6"/></svg>',
  plus:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  tools: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h9M17 17h3"/><circle cx="16" cy="7" r="1.9"/><circle cx="10" cy="12" r="1.9"/><circle cx="15" cy="17" r="1.9"/></svg>',
  /** Listede rengin rafını temsil eden küçük siluet. */
  shelfMark: '<svg viewBox="0 0 28 18" aria-hidden="true"><rect x="1.5" y="2" width="25" height="9.5" rx="1" fill="currentColor" opacity=".92"/><rect x="1.5" y="11.5" width="25" height="3.2" rx="1" fill="currentColor"/><rect x="1.5" y="2" width="2.6" height="13" rx="1" fill="currentColor"/><rect x="23.9" y="2" width="2.6" height="13" rx="1" fill="currentColor"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  /** Gece modu aç/kapa. */
  moon:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.2A8.4 8.4 0 0 1 9.8 4 8.4 8.4 0 1 0 20 14.2Z"/></svg>',
  sun:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2.6v2.1M12 19.3v2.1M4.4 4.4l1.5 1.5M18.1 18.1l1.5 1.5M2.6 12h2.1M19.3 12h2.1M4.4 19.6l1.5-1.5M18.1 5.9l1.5-1.5"/></svg>',
  // Ufukta yarım güneş: altın saat.
  gunbatimi: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6.6 16.6a5.4 5.4 0 0 1 10.8 0"/><path d="M2.8 16.6h18.4"/><path d="M12 6v2.2M5.2 9.3l1.5 1.5M18.8 9.3l-1.5 1.5M2.9 13.3h1.9M19.2 13.3h1.9"/><path d="M8 20h8"/></svg>',
  // Sade vazo: oval ağız, ince boyun, yuvarlak gövde, kaide.
  vazo:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="3.6" rx="3.3" ry="1.05"/><path d="M8.9 3.9c.3 1.6.9 2.6.1 3.9C7.6 9.6 6 11.3 6 14.1 6 17.4 8.6 19.6 12 19.6s6-2.2 6-5.5c0-2.8-1.6-4.5-3-6.3-.8-1.3-.2-2.3.1-3.9"/><path d="M9.3 19.6l-.3 1.5h6l-.3-1.5"/></svg>',
  /**
   * WhatsApp resmî işareti: dolu konuşma balonu + içinde beyaz ahize.
   * Yeşil zemin düğmenin kendisinden gelir, glif beyaz basılır.
   */
  /* Tek renkli WhatsApp işareti — mağazanın siyah-beyaz diliyle uyumlu.
     Tek parça `currentColor`: siyah yuvarlağın içinde beyaz duruyor, asistan
     avatarıyla aynı görsel dil. (Eski sürüm yeşil zeminli iki renkliydi.) */
  whatsapp: '<svg viewBox="0 0 448 512" fill="currentColor" aria-hidden="true"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 110.9L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>',
  /* Oda ön ayarları düğmesi. Yuvarlağı düğmenin kendisi çiziyor, burada
     yalnızca soru işareti var — asistan avatarındaki gibi. */
  /* Oda seçici: izometrik kesit oda — solda pencere, sağda kapı, ortada
     saksıda bitki, parke zemin. Marka görselinden vektöre çizildi (köşedeki
     leke yok). Kapı kulbu ve saksı ağzı ayrı renk değil, şekilde açılmış
     DELİK (evenodd): düğmenin zemini ne olursa olsun doğru görünüyor. */
  oda: '<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M24 3 44 14v22L24 47 4 36V14Z" stroke-width="2.8"/>'
    + '<path d="M24 3v20M4 36l20-11 20 11" stroke-width="1.9"/>'
    + '<path d="M19 27.75l20 11M14 30.5l20 11M9 33.25l20 11" stroke-width="1.6"/>'
    + '<path d="M8 15.8 13 13.05v7L8 22.8ZM10.5 14.43v7M8 19.3l5-2.75" stroke-width="1.6"/>'
    + '<path fill="currentColor" stroke="none" fill-rule="evenodd" d="M33 15.95 39 19.25v14L33 29.95ZM37.9 25.6a.95.95 0 1 0 .01 0Z"/>'
    + '<path fill="currentColor" stroke="none" fill-rule="evenodd" d="M21 23h8l-1.5 6h-5ZM21.7 24.4h6.6v.8h-6.6Z"/>'
    + '<path fill="currentColor" stroke="none" d="M24.3 23Q19.5 19 20 11.5Q23.4 16.5 24.3 23ZM25 23Q23.6 15.5 25.4 8.5Q27.4 15.5 25 23ZM25.7 23Q27 16.5 31 12.5Q30.6 19.5 25.7 23Z"/>'
    + '</svg>',

  /* Oda eşyalarını göster/gizle düğmesi (gece düğmesinin yanında). */
  // Çekmeceli çalışma masası: tabla, solda tek ayak, sağda üç çekmece.
  esya: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6.2h19v2.3h-19z"/><path d="M5 8.5v11.3"/><path d="M12.6 8.5h7.2v11.3h-7.2z"/><path d="M12.6 12.3h7.2M12.6 16h7.2"/><path d="M15.4 10.4h1.6M15.4 14.15h1.6M15.4 17.9h1.6"/></svg>',

  /* ---------- Oda simgeleri ----------
     İnce çizgi, kare kadraj (32×32), tek renk (`currentColor`). Kart oda
     adını zaten yazdığı için simgenin altında yazı yok.
     ROOMS içinde anahtar adıyla (`simge`) çağrılıyorlar. */
  odaMutfak: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 4.5h12l2 5.5H8z"/><path d="M3.5 14.5h25"/><path d="M5.5 14.5V28h21V14.5"/><rect x="11.5" y="18.5" width="9" height="7.5" rx="1"/><path d="M13.5 21.5h5"/><path d="M8 17.8h1.5M22.5 17.8H24"/></svg>',
  odaKahve: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4.5c0 1.6-1.4 1.6-1.4 3.2M16 3.8c0 1.6-1.4 1.6-1.4 3.2M21 4.5c0 1.6-1.4 1.6-1.4 3.2"/><path d="M6.5 12h15v4.5a7.5 7.5 0 0 1-15 0z"/><path d="M21.5 13.5h1.8a3 3 0 0 1 0 6h-1.3"/><path d="M4.5 27h20"/></svg>',
  odaAntre: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4.5h10v23H4z"/><circle cx="11.3" cy="16.5" r=".9"/><path d="M17.5 9h11"/><path d="M20.5 9v2.6M26 9v2.6"/><path d="M19 13.2h3.2l-.8 6h-1.6z"/><path d="M17.5 22h11v5.5h-11z"/><path d="M17.5 24.6h11"/></svg>',
  odaCalisma: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="8.5" y="5" width="15" height="10" rx="1"/><path d="M16 15v3.4"/><path d="M12.5 18.4h7"/><path d="M3 21.5h26"/><path d="M6 21.5v6.5"/><rect x="18" y="21.5" width="8" height="6.5" rx="1"/><path d="M19.8 24.4h4.4"/></svg>',
  odaYatak: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 27.5V13a1.5 1.5 0 0 1 1.5-1.5H7v5.5"/><path d="M7 17h19.5A1.5 1.5 0 0 1 28 18.5V24H7z"/><path d="M7 21.5h21"/><rect x="9.5" y="18.3" width="6" height="2.8" rx="1"/><path d="M28 24v3.5"/></svg>',
  odaCocuk: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 4.5v3.2"/><path d="M12.5 7.7h7"/><circle cx="13.8" cy="10.3" r="1.1"/><circle cx="18.2" cy="10.3" r="1.1"/><path d="M4.5 28V15M27.5 28V15"/><path d="M4.5 16h23"/><path d="M4.5 24.2h23"/><path d="M10 16v8.2M16 16v8.2M22 16v8.2"/></svg>',
  odaBanyo: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 17.5h25"/><path d="M5.5 17.5v3.4a5.5 5.5 0 0 0 5.5 5.5h10a5.5 5.5 0 0 0 5.5-5.5v-3.4"/><path d="M9 17.5V10a2.5 2.5 0 0 1 2.5-2.5h2"/><path d="M13.5 5.5h3.2v4h-3.2z"/><path d="M14 11.6v1.2M16.3 12.6v1.2"/><path d="M9 26.4V28M23 26.4V28"/></svg>',
  odaBalkon: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 27V13.5h23V27"/><path d="M3 13.5h26"/><path d="M4.5 20.5h23"/><path d="M10.5 13.5V27M16 13.5V27M21.5 13.5V27"/><path d="M8 9.5a4.2 4.2 0 0 1 8.4 0"/><path d="M12.2 5.3v4.2"/><path d="M19.5 6.5h6M22.5 3.5v6"/></svg>',
  odaSalon: '<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 16.5v-4A2.5 2.5 0 0 1 8.5 10h15a2.5 2.5 0 0 1 2.5 2.5v4"/><path d="M4 23v-5a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2v5z"/><path d="M6 16.5h20"/><path d="M16 16.5V23"/><path d="M6.5 23v2.8M25.5 23v2.8"/></svg>',
};

/* ==========================================================================
   3) MİNİMAL GLB / glTF 2.0 ÇÖZÜMLEYİCİ
   Sıkıştırılmamış (Draco'suz), gömülü doku içermeyen modeller içindir.
   ========================================================================== */

const GLTF_COMPONENT = {
  5120: Int8Array, 5121: Uint8Array, 5122: Int16Array,
  5123: Uint16Array, 5125: Uint32Array, 5126: Float32Array,
};
const GLTF_ITEMS = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };

function parseGLB(arrayBuffer) {
  const dv = new DataView(arrayBuffer);
  if (dv.getUint32(0, true) !== 0x46546c67) throw new Error('Geçerli bir GLB dosyası değil.');

  const total = dv.getUint32(8, true);
  let cursor = 12, json = null, bin = null;

  while (cursor + 8 <= total) {
    const len = dv.getUint32(cursor, true);
    const type = dv.getUint32(cursor + 4, true);
    const start = cursor + 8;
    if (type === 0x4e4f534a) {
      json = JSON.parse(new TextDecoder().decode(new Uint8Array(arrayBuffer, start, len)));
    } else if (type === 0x004e4942) {
      bin = new Uint8Array(arrayBuffer, start, len);
    }
    cursor = start + len + ((4 - (len % 4)) % 4);
  }
  if (!json) throw new Error('GLB içinde JSON parçası yok.');
  return { json, bin };
}

function readAccessor(json, bin, index) {
  const acc = json.accessors[index];
  const items = GLTF_ITEMS[acc.type];
  const Arr = GLTF_COMPONENT[acc.componentType];
  if (!Arr || !items) throw new Error('Desteklenmeyen accessor tipi.');

  if (acc.bufferView === undefined) return new Arr(acc.count * items); // sıfır dolgulu

  const bv = json.bufferViews[acc.bufferView];
  const base = bin.byteOffset + (bv.byteOffset || 0) + (acc.byteOffset || 0);
  const bpe = Arr.BYTES_PER_ELEMENT;
  const stride = bv.byteStride || 0;

  // Hızlı yol: sıkı paketlenmiş ve hizalı veri doğrudan görüntülenir.
  if ((!stride || stride === items * bpe) && base % bpe === 0) {
    return new Arr(bin.buffer, base, acc.count * items);
  }

  // Yavaş yol: araya geçmiş (interleaved) ya da hizasız veriyi kopyala.
  const out = new Arr(acc.count * items);
  const view = new DataView(bin.buffer);
  const step = stride || items * bpe;
  const get = {
    5120: (o) => view.getInt8(o),      5121: (o) => view.getUint8(o),
    5122: (o) => view.getInt16(o, true), 5123: (o) => view.getUint16(o, true),
    5125: (o) => view.getUint32(o, true), 5126: (o) => view.getFloat32(o, true),
  }[acc.componentType];
  for (let i = 0; i < acc.count; i++) {
    for (let c = 0; c < items; c++) out[i * items + c] = get(base + i * step + c * bpe);
  }
  return out;
}

/** GLB'yi THREE.Object3D ağacına çevirir (geometri + hiyerarşi; malzemeler sonradan atanır). */
function glbToObject3D(arrayBuffer) {
  const { json, bin } = parseGLB(arrayBuffer);
  const placeholder = new T.MeshStandardMaterial({ color: 0xcccccc });

  const buildPrimitive = (prim) => {
    const geo = new T.BufferGeometry();
    const a = prim.attributes;
    if (a.POSITION === undefined) return null;

    geo.setAttribute('position', new T.BufferAttribute(readAccessor(json, bin, a.POSITION), 3));
    if (a.NORMAL !== undefined) {
      geo.setAttribute('normal', new T.BufferAttribute(readAccessor(json, bin, a.NORMAL), 3));
    }
    if (a.TEXCOORD_0 !== undefined) {
      geo.setAttribute('uv', new T.BufferAttribute(readAccessor(json, bin, a.TEXCOORD_0), 2));
    }
    if (prim.indices !== undefined) {
      geo.setIndex(new T.BufferAttribute(readAccessor(json, bin, prim.indices), 1));
    }
    if (a.NORMAL === undefined) geo.computeVertexNormals();
    return new T.Mesh(geo, placeholder);
  };

  const buildNode = (idx) => {
    const n = json.nodes[idx];
    let obj;
    if (n.mesh !== undefined) {
      const prims = (json.meshes[n.mesh].primitives || []).map(buildPrimitive).filter(Boolean);
      if (prims.length === 1) obj = prims[0];
      else { obj = new T.Group(); prims.forEach((p) => obj.add(p)); }
    } else {
      obj = new T.Group();
    }
    obj.name = n.name || `node_${idx}`;

    if (n.matrix) {
      obj.applyMatrix4(new T.Matrix4().fromArray(n.matrix));
    } else {
      if (n.translation) obj.position.fromArray(n.translation);
      if (n.rotation) obj.quaternion.fromArray(n.rotation);
      if (n.scale) obj.scale.fromArray(n.scale);
    }
    (n.children || []).forEach((c) => obj.add(buildNode(c)));
    return obj;
  };

  const root = new T.Group();
  const scene = json.scenes[json.scene ?? 0];
  (scene.nodes || []).forEach((i) => root.add(buildNode(i)));
  return root;
}

/* ==========================================================================
   4) PROSEDÜREL DOKULAR
   Harici görsel dosyası olmadan gerçekçi sıva / parke / toz boya yüzeyleri.
   ========================================================================== */

/** Kenarları sarmalı (tileable) fBm gürültü alanı üretir. */
function fbmField(size, octaves, baseCells, seed) {
  const rnd = seeded(seed);
  const out = new Float32Array(size * size);
  let amp = 1, cells = baseCells, norm = 0;

  for (let o = 0; o < octaves; o++) {
    const n = Math.max(2, Math.round(cells));
    const grid = new Float32Array(n * n);
    for (let i = 0; i < grid.length; i++) grid[i] = rnd();

    for (let y = 0; y < size; y++) {
      const fy = (y / size) * n;
      const y0 = Math.floor(fy) % n, y1 = (y0 + 1) % n, ty = smoothstep(fy - Math.floor(fy));
      for (let x = 0; x < size; x++) {
        const fx = (x / size) * n;
        const x0 = Math.floor(fx) % n, x1 = (x0 + 1) % n, tx = smoothstep(fx - Math.floor(fx));
        const top = lerp(grid[y0 * n + x0], grid[y0 * n + x1], tx);
        const bot = lerp(grid[y1 * n + x0], grid[y1 * n + x1], tx);
        out[y * size + x] += lerp(top, bot, ty) * amp;
      }
    }
    norm += amp; amp *= 0.5; cells *= 2;
  }
  for (let i = 0; i < out.length; i++) out[i] /= norm;
  return out;
}

/** Yükseklik alanından (Sobel) normal haritası üretir. */
function normalTextureFromField(field, size, strength) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  const img = ctx.createImageData(size, size);
  const at = (x, y) => field[((y + size) % size) * size + ((x + size) % size)];

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      let nx = -dx, ny = -dy, nz = 1;
      const l = Math.hypot(nx, ny, nz);
      nx /= l; ny /= l; nz /= l;
      const i = (y * size + x) * 4;
      img.data[i]     = (nx * 0.5 + 0.5) * 255;
      img.data[i + 1] = (ny * 0.5 + 0.5) * 255;
      img.data[i + 2] = (nz * 0.5 + 0.5) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new T.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = T.RepeatWrapping;
  tex.colorSpace = T.NoColorSpace;
  return tex;
}

/** Gri tonlu alanı tek kanal doku olarak döndürür (roughness / AO için). */
function grayTextureFromField(field, size, lo, hi) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  const img = ctx.createImageData(size, size);
  for (let i = 0; i < field.length; i++) {
    const v = clamp(lerp(lo, hi, field[i]), 0, 1) * 255;
    img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const tex = new T.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = T.RepeatWrapping;
  tex.colorSpace = T.NoColorSpace;
  return tex;
}

/** Mat sıva duvar dokuları. */
function makeWallTextures() {
  const S = 512;
  /*
   * Sıva "kadife" okunmalı — dalgalı değil.
   * Eski alan 40 hücrelik bir taban oktavla başlıyordu; bu, duvar boyunca
   * 3–4 cm'lik yumuşak tepeler üretiyor ve yalayarak gelen ışık altında
   * duvarı bulutlu gösteriyordu. Rafların gölge kenarı da bu kabartının
   * üzerinden geçtiği için dalgalı çıkıyordu. Taban frekansı yükseltilip
   * oktav sayısı düşürülünce geriye yalnızca boya taneciği kalıyor.
   */
  const fine = fbmField(S, 3, 120, 1337);
  const map = (() => {
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    const img = ctx.createImageData(S, S);
    /* Taban neredeyse nötr. Eskiden belirgin sıcaktı (0.90/0.89/0.86):
       ACES ton eşlemesi o sıcaklığı bastırıyordu, nötr ton eşlemede ise
       duvar kreme dönüyordu. Duvar rengi zaten seçilebiliyor; doku rengi
       taşımamalı, yalnızca sıva taneciği. */
    for (let i = 0; i < fine.length; i++) {
      const t = (fine[i] - 0.5) * 0.008;
      img.data[i * 4]     = clamp(0.940 + t, 0, 1) * 255;
      img.data[i * 4 + 1] = clamp(0.934 + t, 0, 1) * 255;
      img.data[i * 4 + 2] = clamp(0.922 + t, 0, 1) * 255;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    const tex = new T.CanvasTexture(cv);
    tex.wrapS = tex.wrapT = T.RepeatWrapping;
    tex.colorSpace = T.SRGBColorSpace;
    return tex;
  })();

  return {
    map,
    normalMap: normalTextureFromField(fine, S, 2.0),
    roughnessMap: grayTextureFromField(fine, S, 0.95, 1.0),
  };
}

/** Meşe parke dokuları (renk + normal + pürüzlülük). */
function makeFloorTextures() {
  const S = 1024;
  const PLANKS = 7;                 // dikey olarak 7 sıra tahta
  const plankH = S / PLANKS;
  const rnd = seeded(20240718);

  const color = document.createElement('canvas');
  color.width = color.height = S;
  const cx = color.getContext('2d', { willReadFrequently: true });

  const height = new Float32Array(S * S).fill(0.5);
  const hCv = document.createElement('canvas');
  hCv.width = hCv.height = S;
  const hx = hCv.getContext('2d', { willReadFrequently: true });
  hx.fillStyle = '#808080';
  hx.fillRect(0, 0, S, S);

  /* Doğal, açık meşe. Palet nötr ton eşlemeye göre ayarlandı: ACES
     turuncuyu bastırıyordu, nötrde aynı değerler somon/turuncu parkeye
     dönüyordu. Doygunluk ~%45 düşük, açıklık %5 yüksek, ton 5° sarıya
     kaydı; tahta dizilimi (tohumlu rastgele sayılar) aynı. */
  cx.fillStyle = '#b39d84';
  cx.fillRect(0, 0, S, S);

  for (let p = 0; p < PLANKS; p++) {
    const y0 = p * plankH;
    // Sıra başına kaydırmalı derz: her satır 2–3 tahtaya bölünür
    const cuts = [0];
    let x = 0;
    const offset = rnd() * S;
    while (x < S) { x += S * (0.34 + rnd() * 0.3); cuts.push(Math.min(S, x)); }

    for (let c = 0; c < cuts.length - 1; c++) {
      const x0 = cuts[c], x1 = cuts[c + 1];
      // Tahta başına hafif ton farkı — gerçek parkenin karakteri
      const l = 0.86 + rnd() * 0.26;
      // 26°'de düşük doygunlukta bazı tahtalar somona, 34°'te zeytine kaçıyordu.
      const hue = 31 + rnd() * 8;
      const sat = 19 + rnd() * 6.6;
      cx.fillStyle = `hsl(${hue} ${sat}% ${clamp(58.8 * l, 36, 75)}%)`;
      cx.fillRect(x0, y0, x1 - x0, plankH);

      // Damar çizgileri
      const grains = 26 + Math.floor(rnd() * 18);
      for (let g = 0; g < grains; g++) {
        const gy = y0 + rnd() * plankH;
        const alpha = 0.03 + rnd() * 0.085;
        const dark = rnd() > 0.35;
        cx.strokeStyle = dark
          ? `rgba(73,56,41,${alpha})`
          : `rgba(247,237,222,${alpha * 0.8})`;
        cx.lineWidth = 0.6 + rnd() * 2.1;
        cx.beginPath();
        cx.moveTo(x0, gy);
        const seg = 5;
        for (let s = 1; s <= seg; s++) {
          const t = s / seg;
          cx.lineTo(x0 + (x1 - x0) * t, gy + Math.sin((t + offset) * 6.3 + p) * (1.5 + rnd() * 3.5));
        }
        cx.stroke();
      }

      // Budaklar
      if (rnd() > 0.62) {
        const kx = x0 + 40 + rnd() * Math.max(1, x1 - x0 - 80);
        const ky = y0 + plankH * (0.25 + rnd() * 0.5);
        const kr = 5 + rnd() * 9;
        const grd = cx.createRadialGradient(kx, ky, 0, kx, ky, kr * 2.4);
        grd.addColorStop(0, 'rgba(66,50,35,.55)');
        grd.addColorStop(0.5, 'rgba(104,82,60,.2)');
        grd.addColorStop(1, 'rgba(104,82,60,0)');
        cx.fillStyle = grd;
        cx.beginPath(); cx.arc(kx, ky, kr * 2.4, 0, 7); cx.fill();
      }

      // Kısa kenar derzi
      cx.fillStyle = 'rgba(50,40,29,.5)';
      cx.fillRect(x1 - 1.5, y0, 1.5, plankH);
      hx.fillStyle = '#3c3c3c';
      hx.fillRect(x1 - 2, y0, 2, plankH);
    }

    // Uzun kenar derzi
    cx.fillStyle = 'rgba(45,34,24,.55)';
    cx.fillRect(0, y0 + plankH - 2, S, 2);
    hx.fillStyle = '#343434';
    hx.fillRect(0, y0 + plankH - 2.5, S, 2.5);
  }

  // Yükseklik alanını derzlerden + ince damar gürültüsünden türet
  const hData = hx.getImageData(0, 0, S, S).data;
  const grain = fbmField(S, 4, 24, 777);
  for (let i = 0; i < height.length; i++) {
    height[i] = (hData[i * 4] / 255) * 0.82 + grain[i] * 0.18;
  }

  const colorTex = new T.CanvasTexture(color);
  colorTex.wrapS = colorTex.wrapT = T.RepeatWrapping;
  colorTex.colorSpace = T.SRGBColorSpace;

  return {
    map: colorTex,
    normalMap: normalTextureFromField(height, S, 26),
    roughnessMap: grayTextureFromField(height, S, 0.30, 0.62),
  };
}

/** Toz boya (powder coat) mikro dokusu — portakal kabuğu etkisi. */
function makePowderTextures() {
  const S = 256;
  const f = fbmField(S, 4, 26, 4242);
  return {
    normalMap: normalTextureFromField(f, S, 3.2),
    roughnessMap: grayTextureFromField(f, S, 0.80, 1.0),
  };
}

/** 5 cm'lik grid çizgisi — tek hücrelik doku, tekrarlanarak duvara serilir. */
function makeGridCellTexture() {
  const S = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d');
  ctx.clearRect(0, 0, S, S);
  ctx.strokeStyle = 'rgba(23,20,15,.55)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, S - 2, S - 2);
  // Hücre merkezinde ince nokta — hizalamayı okumayı kolaylaştırır
  ctx.fillStyle = 'rgba(23,20,15,.16)';
  ctx.fillRect(S / 2 - 1, S / 2 - 1, 2, 2);
  const tex = new T.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = T.RepeatWrapping;
  tex.colorSpace = T.NoColorSpace;
  return tex;
}

/**
 * Duvarın tamamını kaplayan tek parça gölge örtüsü.
 * Köşelerde ortam gölgesi (AO), zeminde temas gölgesi ve anahtar ışığın
 * karşı tarafında hafif düşüş üretir — odaya asıl derinliği veren katman budur.
 * Ayrı bantlar yerine tek doku kullanılır; böylece görünür kenar çizgisi oluşmaz.
 */
function makeWallShadeTexture(roomW, roomH) {
  // Doku duvara birebir serildiği için tüm ölçüler metre cinsinden verilir;
  // oda büyüdükçe gölgelerin oranı değil, yalnızca konumu değişir.
  const PX = 190; // metre başına piksel
  const W = Math.max(256, Math.min(2048, Math.round(roomW * PX)));
  const H = Math.max(256, Math.min(1400, Math.round(roomH * PX)));
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  const py = H / roomH;                 // dikey: metre → piksel
  const yOf = (m) => H - m * py;        // duvar yüksekliği (yerden) → canvas y

  /*
   * Bantlar metre cinsinden sabit DEĞİL, duvarın oranına da bakar.
   * Sabit 0.85 m'lik bir temas gölgesi 2.4 m'lik duvarda doğru görünürken
   * 1.5 m'ye inen bir duvarın yarısını kaplıyor; müşteri ölçüyü küçültünce
   * "gölgelenme kaldı" diyordu. Oranla sınırlayınca her ölçüde aynı his.
   */
  const bandY = (m, frac) => Math.min(m, roomH * frac) * py;
  const bandX = (m, frac) => Math.min(m, roomW * frac) * (W / roomW);

  // --- Zemin birleşimi: en güçlü temas gölgesi ---
  const fh = bandY(0.85, 0.30);
  const floorBand = ctx.createLinearGradient(0, H - fh, 0, H);
  floorBand.addColorStop(0, 'rgba(38,30,20,0)');
  floorBand.addColorStop(0.55, 'rgba(38,30,20,.09)');
  floorBand.addColorStop(1, 'rgba(38,30,20,.30)');
  ctx.fillStyle = floorBand;
  ctx.fillRect(0, H - fh, W, fh);

  // --- Tavan birleşimi ---
  const ch = bandY(0.5, 0.18);
  const ceilBand = ctx.createLinearGradient(0, 0, 0, ch);
  ceilBand.addColorStop(0, 'rgba(38,30,20,.14)');
  ceilBand.addColorStop(1, 'rgba(38,30,20,0)');
  ctx.fillStyle = ceilBand;
  ctx.fillRect(0, 0, W, ch);

  // --- Yan duvar köşeleri ---
  // Gerçek yan duvarlar köşeyi zaten tarif ediyor; bu bant yalnızca yumuşatma.
  // Simetrik ve hafif: eskiden sol kenar sağın iki katı koyuydu ve oda
  // çevrilip sol duvar saydamlaşınca havada duran bir gölge gibi kalıyordu.
  const edge = bandX(0.4, 0.13);
  const left = ctx.createLinearGradient(0, 0, edge, 0);
  left.addColorStop(0, 'rgba(38,30,20,.06)');
  left.addColorStop(1, 'rgba(38,30,20,0)');
  ctx.fillStyle = left;
  ctx.fillRect(0, 0, edge, H);

  const right = ctx.createLinearGradient(W - edge, 0, W, 0);
  right.addColorStop(0, 'rgba(38,30,20,0)');
  right.addColorStop(1, 'rgba(38,30,20,.06)');
  ctx.fillStyle = right;
  ctx.fillRect(W - edge, 0, edge, H);

  // --- Pencerenin karşı yönünde (sol-alt) genel düşüş ---
  const fall = ctx.createLinearGradient(W, 0, 0, H);
  fall.addColorStop(0, 'rgba(38,30,20,0)');
  fall.addColorStop(0.55, 'rgba(38,30,20,.035)');
  fall.addColorStop(1, 'rgba(38,30,20,.065)');
  ctx.fillStyle = fall;
  ctx.fillRect(0, 0, W, H);

  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.wrapS = tex.wrapT = T.ClampToEdgeWrapping;
  return tex;
}

/**
 * Pencereden zemine düşen ışık lekesi.
 * Yumuşak kenarlı bir dikdörtgen + kayıt gölgesi; toplayıcı (additive) karışımla
 * zemine eklenir. Hacimsel ışık maliyetinin yanında bedava sayılır.
 */
/**
 * Fayans dokusu (renk + normal + pürüzlülük).
 *
 * Banyo için. Tek bir kare karo deseni üretip tekrarlıyoruz: doku 512²,
 * parke dokusuyla aynı bütçede. Derzler yükseklik alanında çukur olduğu için
 * normal haritası onlardan türetiliyor — ayrı bir çizim gerekmiyor.
 */
function makeTileTextures({ adet = 5, taban = [0.93, 0.93, 0.91], derzTon = 0.80, seed = 4242 } = {}) {
  const S = 512;
  const rnd = seeded(seed);
  const kare = S / adet;
  const derzPx = Math.max(2, Math.round(S * 0.008));

  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d', { willReadFrequently: true });

  // Derz rengi zemin, üstüne karolar
  ctx.fillStyle = `rgb(${taban.map((c) => Math.round(c * derzTon * 255)).join(',')})`;
  ctx.fillRect(0, 0, S, S);

  const yukseklik = new Float32Array(S * S).fill(0.35);  // derz = çukur

  for (let y = 0; y < adet; y++) {
    for (let x = 0; x < adet; x++) {
      // Karo başına çok hafif ton farkı — seramik hiçbir zaman tek renk değil
      const t = 0.965 + rnd() * 0.07;
      ctx.fillStyle = `rgb(${taban.map((c) => Math.round(clamp(c * t, 0, 1) * 255)).join(',')})`;
      const x0 = x * kare + derzPx, y0 = y * kare + derzPx;
      const w = kare - derzPx * 2;
      ctx.fillRect(x0, y0, w, w);

      // Aynı dikdörtgeni yükseklik alanında da kabart
      const ix0 = Math.round(x0), iy0 = Math.round(y0), iw = Math.round(w);
      for (let j = iy0; j < iy0 + iw && j < S; j++) {
        for (let i = ix0; i < ix0 + iw && i < S; i++) yukseklik[j * S + i] = 0.72;
      }
    }
  }

  const map = new T.CanvasTexture(cv);
  map.wrapS = map.wrapT = T.RepeatWrapping;
  map.colorSpace = T.SRGBColorSpace;

  return {
    map,
    normalMap: normalTextureFromField(yukseklik, S, 1.5),
    roughnessMap: grayTextureFromField(yukseklik, S, 0.42, 0.82),
  };
}

/**
 * Ayna yüzeyi.
 *
 * Saf metalik bir yüzey, sahnenin ortam haritası tekdüze olduğu için düz bir
 * renk lekesine dönüşüyor — aynadan çok boyalı panel gibi duruyor. Aynayı
 * ayna yapan şey yansımadaki DEĞİŞİM: üstte tavanın aydınlığı, ortada odanın
 * tonu, üzerinden geçen yumuşak bir parlama. Pencere camında da aynı yöntem
 * kullanılıyor; gerçek yansıma için ikinci bir render geçişine gerek yok.
 */
function makeMirrorTexture() {
  const W = 64, H = 128;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');

  const dikey = ctx.createLinearGradient(0, 0, 0, H);
  dikey.addColorStop(0, '#f4f7f8');      // tavan aydınlığı
  dikey.addColorStop(0.42, '#dfe7ea');
  dikey.addColorStop(0.70, '#c4d0d5');   // karşı duvarın tonu
  dikey.addColorStop(1, '#d6dee1');
  ctx.fillStyle = dikey;
  ctx.fillRect(0, 0, W, H);

  // Camın üzerinden geçen yumuşak parlama şeridi
  const parlama = ctx.createLinearGradient(0, H * 0.10, W, H * 0.80);
  parlama.addColorStop(0, 'rgba(255,255,255,0)');
  parlama.addColorStop(0.42, 'rgba(255,255,255,.34)');
  parlama.addColorStop(0.60, 'rgba(255,255,255,0)');
  ctx.fillStyle = parlama;
  ctx.fillRect(0, 0, W, H);

  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  return tex;
}

/* ============================================================
   ODA EŞYALARI — kutu geometrisinden kurulan basit mobilyalar

   Ortak eksen düzeni: grubun orijini eşyanın TABANININ ORTASI, duvar
   düzleminde (z=0). Eşya odaya doğru +z yönünde uzar, yukarı +y.
   Ölçüler metre.
   ============================================================ */

/** Tek kutu ekler. Gölge alır ve verir; sahne zaten gölge haritası kullanıyor. */
/**
 * Kenarları yuvarlatılmış kutu (three.js RoundedBoxGeometry'nin sade hali;
 * eşyalar dokusuz olduğu için UV üretmiyor).
 *
 * Kutunun her ekseni tek sayıda dilime bölünüyor; köşeye yakın köşe
 * noktaları küçük bir kürenin yüzeyine taşınıyor, ortadaki dilim düz
 * yüzü oluşturuyor. Normaller aynı küreden geldiği için gölgeleme kenarda
 * yumuşakça dönüyor — keskin kutu kenarının aksine ışığı bir çizgi gibi
 * yakalıyor.
 */
function yuvarlakKutu(w, h, d, r, seg) {
  r = Math.min(r, w / 2, h / 2, d / 2);
  const s = seg * 2 + 1;
  const geo = new T.BoxGeometry(1, 1, 1, s, s, s);
  geo.deleteAttribute('uv');
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  const bx = w / 2 - r, by = h / 2 - r, bz = d / 2 - r;
  const yarim = 0.5 / s;
  const n = new T.Vector3();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const sx = Math.sign(x), sy = Math.sign(y), sz = Math.sign(z);
    n.set(x - sx * yarim, y - sy * yarim, z - sz * yarim).normalize();
    pos.setXYZ(i, bx * sx + n.x * r, by * sy + n.y * r, bz * sz + n.z * r);
    nor.setXYZ(i, n.x, n.y, n.z);
  }
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Eşya parçası. `r` kenar yarıçapı: verilmezse gerçek mobilyadaki gibi ince
 * bir pah (en küçük ölçünün %18'i, en çok 12 mm). Şilte, minder, yastık gibi
 * yumuşak parçalara büyük yarıçap veriliyor. Derz çizgisi gibi ince
 * parçalar ve dokulu yüzeyler (ayna camı) düz kutu kalıyor.
 */
function kutu(g, mat, x, y, z, w, h, d, r) {
  if (r === undefined) r = mat.map ? 0 : Math.min(Math.min(w, h, d) * 0.18, 0.012);
  const geo = r >= 0.003
    ? yuvarlakKutu(w, h, d, r, r > 0.02 ? 3 : 1)
    : new T.BoxGeometry(w, h, d);
  const m = new T.Mesh(geo, mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  g.add(m);
  return m;
}

/* Ortak ölçü notu: `kutu()` MERKEZ alır. Bir parçanın tabanı `taban` ise
   merkezi `taban + yukseklik/2` olmalı. Önceki sürümde mutfak derzlerinde
   bu hesap atlanmış, derzler tezgahın 39 cm üstüne taşmıştı. */

/* Yumuşak parçaların kenar yarıçapları (son parametre) elle verildi:
   döşemeli başlık, şilte, yorgan ve yastıklar kutu gibi değil, kumaş gibi
   dursun. Sert parçalar kutu()'nun varsayılan ince pahını alıyor. */
function cizYatak(g, m, koyu, tas) {
  kutu(g, koyu, 0, 0.48, 0.035, 1.62, 0.96, 0.07, 0.03);   // başlık (döşemeli)
  kutu(g, m, 0, 0.11, 1.00, 1.52, 0.22, 1.92, 0.02);       // karyola kaidesi
  kutu(g, tas, 0, 0.33, 1.00, 1.58, 0.22, 1.96, 0.06);     // şilte
  kutu(g, m, 0, 0.455, 1.40, 1.60, 0.06, 1.24, 0.03);      // yorgan
  kutu(g, tas, -0.36, 0.51, 0.34, 0.62, 0.14, 0.38, 0.06); // yastıklar
  kutu(g, tas, 0.36, 0.51, 0.34, 0.62, 0.14, 0.38, 0.06);
}

function cizMasa(g, m, koyu, tas) {
  kutu(g, tas, 0, 0.735, 0.31, 1.30, 0.04, 0.62);       // tabla
  kutu(g, m, -0.63, 0.355, 0.31, 0.04, 0.71, 0.58);     // yan panel
  kutu(g, m, 0.42, 0.34, 0.30, 0.42, 0.68, 0.56);       // çekmeceli keson
  kutu(g, koyu, 0.42, 0.50, 0.582, 0.38, 0.012, 0.012); // çekmece hatları
  kutu(g, koyu, 0.42, 0.28, 0.582, 0.38, 0.012, 0.012);
  // Sandalye
  kutu(g, koyu, 0, 0.45, 0.96, 0.46, 0.06, 0.44);       // oturak
  kutu(g, koyu, 0, 0.70, 1.155, 0.46, 0.44, 0.05);      // sırtlık
  kutu(g, m, 0, 0.21, 0.96, 0.07, 0.42, 0.07);          // ayak
  kutu(g, m, 0, 0.02, 0.96, 0.44, 0.04, 0.44);          // taban
}

function cizKoltuk(g, m, koyu, tas) {
  kutu(g, m, 0, 0.50, 0.09, 1.80, 0.72, 0.18, 0.04);          // sırt gövdesi
  kutu(g, m, 0, 0.15, 0.50, 1.80, 0.30, 0.80, 0.03);          // oturma kaidesi
  kutu(g, tas, -0.38, 0.37, 0.50, 0.70, 0.14, 0.66, 0.05);    // oturma minderleri
  kutu(g, tas, 0.38, 0.37, 0.50, 0.70, 0.14, 0.66, 0.05);
  kutu(g, tas, -0.38, 0.60, 0.22, 0.68, 0.38, 0.12, 0.05);    // sırt minderleri
  kutu(g, tas, 0.38, 0.60, 0.22, 0.68, 0.38, 0.12, 0.05);
  kutu(g, m, -0.82, 0.32, 0.48, 0.16, 0.64, 0.84, 0.06);      // kolçaklar
  kutu(g, m, 0.82, 0.32, 0.48, 0.16, 0.64, 0.84, 0.06);
}

function cizBesik(g, m, koyu, tas) {
  kutu(g, m, -0.625, 0.48, 0.35, 0.05, 0.96, 0.70);     // yan paneller
  kutu(g, m, 0.625, 0.48, 0.35, 0.05, 0.96, 0.70);
  kutu(g, m, 0, 0.925, 0.035, 1.20, 0.05, 0.05);        // üst korkuluklar
  kutu(g, m, 0, 0.925, 0.665, 1.20, 0.05, 0.05);
  kutu(g, m, 0, 0.455, 0.035, 1.20, 0.05, 0.05);        // alt korkuluklar
  kutu(g, m, 0, 0.455, 0.665, 1.20, 0.05, 0.05);
  for (let i = 0; i < 7; i++) {                          // parmaklıklar
    const x = -0.51 + i * 0.17;
    kutu(g, m, x, 0.69, 0.035, 0.03, 0.42, 0.03);
    kutu(g, m, x, 0.69, 0.665, 0.03, 0.42, 0.03);
  }
  kutu(g, tas, 0, 0.485, 0.35, 1.18, 0.10, 0.64, 0.04); // şilte
}

/* Lavabo ve ayna TEK parça: banyoda ikisi ayrı durunca duvar dağınık
   görünüyor. Ayna yüzeyi sahnenin ortam haritasını yansıtıyor (`ayna`
   malzemesi) — ek bir render geçişi yok, maliyeti sıfır. */
function cizLavabo(g, m, koyu, tas, ayna) {
  kutu(g, m, 0, 0.39, 0.225, 0.80, 0.66, 0.45);         // dolap gövdesi
  kutu(g, koyu, 0, 0.03, 0.205, 0.72, 0.06, 0.40);      // gizli süpürgelik
  kutu(g, tas, 0, 0.75, 0.245, 0.86, 0.06, 0.49);       // tezgah
  kutu(g, tas, 0, 0.795, 0.245, 0.46, 0.03, 0.32);      // lavabo teknesi
  kutu(g, koyu, 0, 0.51, 0.452, 0.74, 0.012, 0.012);    // çekmece hattı
  kutu(g, koyu, 0, 0.86, 0.075, 0.035, 0.16, 0.035);    // batarya gövdesi
  kutu(g, koyu, 0, 0.925, 0.125, 0.035, 0.03, 0.12);    // batarya ucu

  // ---- Ayna ----
  kutu(g, tas, 0, 1.42, 0.014, 0.72, 0.80, 0.028);      // çerçeve/sırt
  kutu(g, ayna, 0, 1.42, 0.031, 0.66, 0.74, 0.008);     // cam
  kutu(g, koyu, 0, 1.01, 0.030, 0.72, 0.022, 0.05);     // altta ince raf
}

function cizKonsol(g, m, koyu, tas) {
  kutu(g, tas, 0, 0.76, 0.18, 1.10, 0.04, 0.36);        // tabla
  kutu(g, m, 0, 0.52, 0.18, 1.00, 0.44, 0.32);          // gövde
  kutu(g, koyu, 0, 0.52, 0.341, 0.96, 0.012, 0.012);    // çekmece hattı
  kutu(g, m, -0.47, 0.15, 0.06, 0.05, 0.30, 0.05);      // ayaklar
  kutu(g, m, 0.47, 0.15, 0.06, 0.05, 0.30, 0.05);
  kutu(g, m, -0.47, 0.15, 0.30, 0.05, 0.30, 0.05);
  kutu(g, m, 0.47, 0.15, 0.30, 0.05, 0.30, 0.05);
}

/* Tezgahın ÜSTÜ tamamen düz. Önceki sürümde derz kutuları ve batarya
   tezgahın üstüne taşıyordu; artık derzler dolap yüzeyine gömülü, tezgahta
   yalnızca eviyenin hafif izi var. */
function cizTezgah(g, m, koyu, tas) {
  kutu(g, m, 0, 0.42, 0.30, 1.80, 0.76, 0.60);          // alt dolaplar
  kutu(g, koyu, 0, 0.04, 0.28, 1.72, 0.08, 0.56);       // gizli süpürgelik
  kutu(g, tas, 0, 0.825, 0.31, 1.86, 0.05, 0.64);       // tezgah tablası
  // Dolap kapağı derzleri — dolabın ön yüzünde, tezgahın ALTINDA kalıyor.
  for (const x of [-0.60, 0, 0.60]) {
    kutu(g, koyu, x, 0.44, 0.601, 0.015, 0.68, 0.012);
  }
  kutu(g, koyu, 0, 0.62, 0.601, 1.76, 0.012, 0.012);    // yatay derz
  kutu(g, koyu, -0.45, 0.846, 0.31, 0.50, 0.012, 0.38); // eviye izi
}

/* Balkon takımı: küçük bir masa ve iki tabure. Dış mekânda büyük mobilya
   rafın önüne geçiyor; ölçeği veren küçük bir set yeterli. */
function cizBalkonSet(g, m, koyu, tas) {
  kutu(g, tas, 0, 0.72, 0.36, 0.62, 0.04, 0.62);        // masa tablası
  kutu(g, koyu, 0, 0.36, 0.36, 0.07, 0.68, 0.07);       // orta ayak
  kutu(g, koyu, 0, 0.02, 0.36, 0.44, 0.04, 0.44);       // ayak tabanı
  for (const x of [-0.46, 0.46]) {                       // tabureler
    kutu(g, m, x, 0.44, 0.36, 0.34, 0.05, 0.34);        // oturak
    kutu(g, m, x, 0.60, 0.20, 0.34, 0.28, 0.04);        // sırtlık
    kutu(g, koyu, x - 0.13, 0.21, 0.23, 0.04, 0.42, 0.04);
    kutu(g, koyu, x + 0.13, 0.21, 0.23, 0.04, 0.42, 0.04);
    kutu(g, koyu, x - 0.13, 0.21, 0.49, 0.04, 0.42, 0.04);
    kutu(g, koyu, x + 0.13, 0.21, 0.49, 0.04, 0.42, 0.04);
  }
}

function cizKahveBar(g, m, koyu, tas) {
  kutu(g, m, 0, 0.42, 0.24, 0.90, 0.76, 0.48);          // dolap
  kutu(g, koyu, 0, 0.035, 0.22, 0.84, 0.07, 0.44);      // gizli süpürgelik
  kutu(g, tas, 0, 0.825, 0.26, 0.96, 0.05, 0.52);       // tabla
  kutu(g, koyu, 0, 0.44, 0.481, 0.015, 0.68, 0.012);    // kapak derzi
  kutu(g, koyu, 0, 0.62, 0.481, 0.84, 0.012, 0.012);
}
function makeLightPatchTexture() {
  const S = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d');
  ctx.clearRect(0, 0, S, S);

  /* Yumuşak dikdörtgen gövde.
     Doku zemine geniş bir düzlem olarak yayıldığı için buradaki 1 piksel
     sahnede birkaç santimetreye karşılık geliyor: az bulanıklık ekranda
     KESKİN bir güneş dörtgeni olarak okunuyordu. Bulanıklık ve sönümlenme
     bu yüzden yüksek — istenen şey pencerenin keskin izdüşümü değil, odaya
     dağılmış bir aydınlık. */
  const m = S * 0.17;
  ctx.filter = `blur(${S * 0.14}px)`;
  ctx.fillStyle = 'rgba(255,244,222,.82)';
  ctx.fillRect(m, m, S - m * 2, S - m * 2);

  /* Orta kayıt (mullion) izi — tamamen silmek yerine hafifçe azaltıyoruz.
     Tam kesme, lekenin ortasında bıçak gibi bir çizgi bırakıyordu. */
  ctx.filter = `blur(${S * 0.075}px)`;
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = 'rgba(0,0,0,.45)';
  ctx.fillRect(S / 2 - S * 0.02, m, S * 0.04, S - m * 2);
  ctx.globalCompositeOperation = 'source-over';

  // Kenarlara doğru sönümlenme — merkezden itibaren başlıyor, sert sınır yok.
  ctx.filter = 'none';
  ctx.globalCompositeOperation = 'destination-in';
  const fade = ctx.createRadialGradient(S / 2, S / 2, S * 0.05, S / 2, S / 2, S * 0.5);
  fade.addColorStop(0, 'rgba(0,0,0,1)');
  fade.addColorStop(0.55, 'rgba(0,0,0,.55)');
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, S, S);
  ctx.globalCompositeOperation = 'source-over';

  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.wrapS = tex.wrapT = T.ClampToEdgeWrapping;
  return tex;
}

/**
 * Altın saatte pencerenin duvara düşen ışığı: iki cam bölmesi, aralarında
 * kayıt çizgisi, kenarları yumuşak. Toplayıcı karışımda kullanıldığı için
 * zemin SİYAH (hiçbir şey eklemiyor). Bulanıklık shadowBlur ile: ctx.filter
 * Safari'nin her sürümünde yok.
 */
function makeGoldenPatchTexture() {
  const S = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const c = cv.getContext('2d');
  c.fillStyle = '#000';
  c.fillRect(0, 0, S, S);
  const UZAK = 4000;
  const bolme = (x0, x1) => {
    c.save();
    c.shadowColor = 'rgba(255,247,232,1)';
    c.shadowBlur = S * 0.055;
    c.shadowOffsetX = UZAK;
    c.fillStyle = '#fff';
    c.fillRect(x0 * S - UZAK, S * 0.07, (x1 - x0) * S, S * 0.86);
    c.restore();
  };
  bolme(0.06, 0.475);
  bolme(0.525, 0.94);
  // Işık pencerenin alt kenarına doğru biraz daha dolgun: güneş alçaktan geliyor.
  c.globalCompositeOperation = 'multiply';
  const g = c.createLinearGradient(0, 0, 0, S);
  g.addColorStop(0, '#c9c9c9');
  g.addColorStop(1, '#ffffff');
  c.fillStyle = g;
  c.fillRect(0, 0, S, S);
  c.globalCompositeOperation = 'source-over';
  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.wrapS = tex.wrapT = T.ClampToEdgeWrapping;
  return tex;
}

/**
 * Yumuşak radyal parlama — gece modunda tavan armatürünün duvara ve zemine
 * düşen havuzunu taklit eder. Hacimsel ışık yerine iki düzlem yeter.
 */
function makeGlowTexture() {
  const S = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d');
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,226,178,1)');
  g.addColorStop(0.42, 'rgba(255,214,158,.55)');
  g.addColorStop(0.74, 'rgba(255,206,150,.16)');
  g.addColorStop(1, 'rgba(255,200,140,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);

  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.wrapS = tex.wrapT = T.ClampToEdgeWrapping;
  return tex;
}

/** Zeminde duvar dibine düşen temas gölgesi. */
function makeFloorShadeTexture() {
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d');
  ctx.clearRect(0, 0, S, S);
  /*
   * Bu bir TEMAS gölgesi: duvarın dibine sokulmalı, zemine yayılmamalı.
   * Eski değerler zemini 54 seviye karartıp derinliğin %42'sini kaplıyordu;
   * perspektifte bu, ekranda kaynağı belirsiz diyagonal bir parlaklık
   * sınırı olarak görünüyor ve pencere ışığıyla çelişiyordu.
   */
  const g = ctx.createLinearGradient(0, S, 0, 0);
  g.addColorStop(0, 'rgba(34,26,17,.20)');
  g.addColorStop(0.35, 'rgba(34,26,17,.07)');
  g.addColorStop(1, 'rgba(34,26,17,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.wrapS = tex.wrapT = T.ClampToEdgeWrapping;
  return tex;
}

/**
 * Seçim çerçevesi: rafın çevresinde yuvarlak köşeli, DÜZ ve ince bir çizgi,
 * dışında açık renkli ince bir hale (koyu duvarda da okunuyor), içinde çok
 * hafif bir ton. Eskiden kesikli çizgi bir dokuydu ve rafın oranına
 * gerildiği için çizgiler uzayıp kalınlaşıyordu. Şimdi çizgi kalınlığı
 * EKRAN pikseliyle sabit (fwidth): yakında da uzakta da aynı keskinlikte.
 */
function makeSelectionMaterial() {
  return new T.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uBoyut: { value: new T.Vector2(1, 1) },
      uYaricap: { value: 0.016 },
      uRenk: { value: new T.Color(0x17 / 255, 0x14 / 255, 0x0f / 255) },
      uOpak: { value: 1 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      varying vec2 vUv;
      uniform vec2 uBoyut;
      uniform float uYaricap, uOpak;
      uniform vec3 uRenk;
      float yuvarlakKutu(vec2 p, vec2 b, float r) {
        vec2 q = abs(p) - b + r;
        return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
      }
      void main() {
        vec2 p = (vUv - 0.5) * uBoyut;
        float d = yuvarlakKutu(p, uBoyut * 0.5 - 0.006, uYaricap);
        float px = max(fwidth(d), 1e-5);
        // ~2 px koyu çizgi, dışında ~2 px açık hale, içinde %5 ton.
        float cizgi = 1.0 - smoothstep(1.0 * px, 2.0 * px, abs(d));
        float hale = (1.0 - smoothstep(2.0 * px, 4.0 * px, d)) * step(0.0, d) * 0.55;
        float ic = (1.0 - smoothstep(-px, px, d)) * 0.05;
        float a = cizgi + max(hale, ic) * (1.0 - cizgi);
        if (a < 0.004) discard;
        vec3 renk = mix(vec3(1.0), uRenk, clamp((cizgi + ic) / max(a, 1e-4), 0.0, 1.0));
        gl_FragColor = vec4(renk, a * uOpak);
      }`,
  });
}

/** Stüdyo ortam haritası (equirectangular) — PBR yansımaları için. */
function makeStudioEnvTexture() {
  const W = 1024, H = 512;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');

  // Tavan → ufuk → zemin geçişi
  const base = ctx.createLinearGradient(0, 0, 0, H);
  base.addColorStop(0.00, '#ffffff');
  base.addColorStop(0.30, '#f2efe9');
  base.addColorStop(0.52, '#d9d4cb');
  base.addColorStop(0.60, '#a89f92');
  base.addColorStop(1.00, '#6f675d');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, W, H);

  // Softbox'lar: metal yüzeyde okunaklı, yumuşak parlamalar
  const blob = (u, v, rx, ry, alpha) => {
    const g = ctx.createRadialGradient(u * W, v * H, 0, u * W, v * H, Math.max(rx, ry));
    g.addColorStop(0, `rgba(255,255,255,${alpha})`);
    g.addColorStop(0.45, `rgba(255,252,245,${alpha * 0.42})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.save();
    ctx.translate(u * W, v * H);
    ctx.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
    ctx.translate(-u * W, -v * H);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  };

  blob(0.24, 0.20, 210, 150, 1.0);   // ana ışık (sol üst)
  blob(0.72, 0.26, 175, 130, 0.75);  // dolgu (sağ üst)
  blob(0.50, 0.06, 320, 110, 0.55);  // tavan yayılımı
  blob(0.95, 0.44, 120, 220, 0.35);  // yan pencere

  // Ufuk çizgisinde hafif sıcaklık
  const warm = ctx.createLinearGradient(0, H * 0.42, 0, H * 0.62);
  warm.addColorStop(0, 'rgba(255,236,206,0)');
  warm.addColorStop(0.5, 'rgba(255,232,198,.22)');
  warm.addColorStop(1, 'rgba(255,236,206,0)');
  ctx.fillStyle = warm;
  ctx.fillRect(0, 0, W, H);

  const tex = new T.CanvasTexture(cv);
  tex.mapping = T.EquirectangularReflectionMapping;
  tex.colorSpace = T.SRGBColorSpace;
  return tex;
}

/*
 * Rafların kendi ortam haritası ondan bu kat parlak ışık taşıyabiliyor.
 * Tuvale ÖLÇEKLİ (/RAF_ENV_OLCEK) yazılıyor, malzemede envMapIntensity ile
 * geri çarpılıyor: kayan noktalı doku gerekmiyor, float dokuda doğrusal
 * süzgeci desteklemeyen telefonlarda da aynı sonucu veriyor.
 */
const RAF_ENV_OLCEK = 2.5;
/* Raf malzemesinin yolları, en zenginden en sadeye. Açılıştaki öz-test
   (rafMalzemesiSina) cihazda doğru rengi veren ilkini seçiyor. */
const RAF_KIPLERI = [
  { ad: 'tam', fizik: true, harita: true, lamba: true },
  { ad: 'lambasiz', fizik: true, harita: true, lamba: false },
  { ad: 'haritasiz', fizik: true, harita: false, lamba: false },
  { ad: 'standart', fizik: false, harita: true, lamba: false },
  { ad: 'yalin', fizik: false, harita: false, lamba: false },
];
/** Gece ortam ışığı — oda (scene.environmentIntensity) ve raflar aynı oranda söner. */
const GECE_ORTAM = 0.34;
/* Altın saat: oda hafifçe kararıyor ama gece kadar değil; ışığın asıl kaynağı
   pencereden yatay giren güneş. */
const ALTIN_ORTAM = 0.6;

/**
 * Yalnızca RAFLARIN yansıttığı stüdyo ortamı.
 *
 * Kamera raflarla neredeyse aynı hizada durduğu için her yüzün yansıttığı
 * yön ufuk çizgisinin birkaç derecelik bandına düşüyor. Ölçüldü (u yatay,
 * y tuvalde yukarıdan oran): tabla üstleri u≈0.17–0.33 / y≈0.44–0.49,
 * yan paneller u≈0.17–0.33 / y≈0.46–0.56, plakalar u≈0.67–0.83 / y≈0.46–0.56.
 *
 * Toz boya karşıdan bakınca ışığın yalnızca ~%4'ünü yansıtır; odanın 0–1
 * aralığındaki ortam haritası bu bantlarda iz bırakmıyordu, raflar düz
 * boyanmış kutu gibi duruyordu. Buradaki yumuşak ışık kutuları ufuktan
 * 2,5 kata kadar parlak — ama yalnızca SIYIRARAK bakılan yüzlerin (tabla
 * üstü, yan panel) yansıttığı yerlerde. Plakaların karşısı bilerek sade:
 * oraya konan parlak kutu plakayı tek düze açıp rengi soldurdu.
 */
function makeShelfEnvTexture() {
  const W = 512, H = 256;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  const img = ctx.createImageData(W, H);

  // Duraklar sRGB yazılıyor; T.Color doğrusal değere çeviriyor.
  const durak = [
    [0.00, '#ffffff'], [0.30, '#f4f1ec'], [0.47, '#e6e0d6'],
    [0.56, '#b8afa2'], [1.00, '#6e665c'],
  ].map(([t, h]) => { const c = new T.Color(h); return [t, [c.r, c.g, c.b]]; });
  const gradyan = (t) => {
    for (let i = 1; i < durak.length; i++) {
      if (t > durak[i][0]) continue;
      const [t0, c0] = durak[i - 1], [t1, c1] = durak[i];
      const k = (t - t0) / (t1 - t0);
      return c0.map((v, j) => v + (c1[j] - v) * k);
    }
    return durak[durak.length - 1][1];
  };
  // Doğrusal parlaklık (L), merkez (u, y), yarı ölçüler, kenar yumuşaklığı (f)
  const kutular = [
    { u: 0.25, y: 0.44, hw: 0.140, hh: 0.030, f: 0.015, L: 2.5, c: [1, 0.98, 0.95] }, // tabla üstleri
    { u: 0.19, y: 0.52, hw: 0.035, hh: 0.060, f: 0.020, L: 1.7, c: [1, 1, 1] },       // yan panel, -x yüzü
    { u: 0.30, y: 0.54, hw: 0.035, hh: 0.050, f: 0.020, L: 1.7, c: [1, 0.97, 0.92] }, // yan panel, +x yüzü
    { u: 0.72, y: 0.47, hw: 0.100, hh: 0.055, f: 0.020, L: 1.0, c: [1, 1, 1] },       // plakalar: yalnız nötr
    { u: 0.50, y: 0.10, hw: 0.300, hh: 0.070, f: 0.050, L: 1.6, c: [1, 1, 1] },       // tavan
  ];
  const yumusak = (e0, e1, x) => {
    const t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const srgb = (v) => {
    const x = clamp(v / RAF_ENV_OLCEK, 0, 1);
    return 255 * (x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055);
  };

  for (let y = 0; y < H; y++) {
    const ty = (y + 0.5) / H;
    const taban = gradyan(ty);
    for (let x = 0; x < W; x++) {
      const tu = (x + 0.5) / W;
      let r = taban[0], g = taban[1], b = taban[2];
      for (const q of kutular) {
        let du = Math.abs(tu - q.u);
        du = Math.min(du, 1 - du);                     // yatayda sarmalı
        const m = (1 - yumusak(q.hw - q.f, q.hw + q.f, du))
                * (1 - yumusak(q.hh - q.f, q.hh + q.f, Math.abs(ty - q.y)));
        if (m <= 0) continue;
        r += (q.L * q.c[0] - r) * m;
        g += (q.L * q.c[1] - g) * m;
        b += (q.L * q.c[2] - b) * m;
      }
      const i = (y * W + x) * 4;
      img.data[i] = srgb(r); img.data[i + 1] = srgb(g); img.data[i + 2] = srgb(b);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new T.CanvasTexture(cv);
  tex.mapping = T.EquirectangularReflectionMapping;
  tex.colorSpace = T.SRGBColorSpace;
  return tex;
}

/* Raf profili (raf merkezine göre metre; üç ölçüde aynı, yalnız genişlik
   değişiyor). Modelden ışınla ölçüldü. */
const RAF_PROFIL = {
  yariH: 0.099,      // yan panellerin yarı yüksekliği
  tabla: -0.051,     // tablanın alt yüzü (15 cm öne çıkıyor)
  ust: -0.0493,      // tablanın üst yüzü — dekor eşyaları buraya oturuyor
  on: 0.146,         // tablanın ön kenarının duvardan uzaklığı
  plakaUst: 0.050,   // arka plakanın üst kenarı
  panel: 0.003,      // yan panel kalınlığı
};
/** Temas gölgesinin duvardan uzaklığı: plakanın ön yüzünün (2 mm) hemen önü. */
const RAF_GOLGE_Z = 0.0028;

/**
 * Rafın duvara ve kendi plakasına düşürdüğü yumuşak temas gölgesi.
 *
 * Anahtar ışık pencereden, arka duvarı yalayarak geliyor; gerçek gölge
 * haritası bu yüzden duvarda neredeyse iz bırakmıyor ve raflar duvara
 * yapıştırılmış çıkartma gibi duruyordu. Bu doku ışığın yönünden bağımsız
 * ortam gölgesini taklit ediyor: 15 cm'lik tablanın altı (en belirgin
 * işaret), tabla ile plakanın iç köşesi, yan panellerin iki yanı ve bütün
 * ünitenin altında geniş bir yumuşaklık.
 *
 * Ölçü başına bir kez üretiliyor; her raf yalnızca paylaşılan dokuyu taşıyan
 * tek bir düzlem ekliyor (kare başına maliyeti küçük, saydam bir dörtgen).
 */
function makeShelfShadowTexture(w) {
  const P = RAF_PROFIL;
  const MX = 0.04, MB = 0.13, MT = 0.03;   // raf çevresinde pay: yan, alt, üst
  const PPM = 720;                          // metre başına piksel
  const gw = w + 2 * MX, gh = 2 * P.yariH + MB + MT;
  const cw = Math.round(gw * PPM), ch = Math.round(gh * PPM);
  const cv = document.createElement('canvas');
  cv.width = cw; cv.height = ch;
  const c = cv.getContext('2d');
  const X = (x) => (x + gw / 2) * PPM;        // raf merkezine göre metre → piksel
  const Y = (y) => (P.yariH + MT - y) * PPM;  // yukarı pozitif → tuvalde aşağı
  const RENK = '38,30,22';                    // sıcak koyu: siyah gölge griye çalıyor
  const UZAK = 6000;

  /* Bulanık dikdörtgen: şekli tuvalin dışına çizip yalnızca gölgesini içeri
     düşürüyoruz. ctx.filter Safari'de her sürümde yok; shadowBlur her yerde. */
  const bulanik = (x0, y0, x1, y1, alfa, blurM) => {
    c.save();
    c.shadowColor = `rgba(${RENK},${alfa})`;
    c.shadowBlur = blurM * PPM;
    c.shadowOffsetX = UZAK;
    c.fillStyle = '#000';
    c.fillRect(X(x0) - UZAK, Y(y1), (x1 - x0) * PPM, (y1 - y0) * PPM);
    c.restore();
  };
  const ic = w / 2 - P.panel;

  // 1) Bütün ünitenin altında geniş, yumuşak ortam gölgesi
  bulanik(-w / 2 + 0.01, -P.yariH - 0.035, w / 2 - 0.01, -P.yariH + 0.03, 0.16, 0.045);

  // 2) Tablanın altı: tabladan aşağı sönüyor. Tabla çizgisinin üstüne
  //    taşmasın diye kırpılıyor — üstteki iç köşe ayrı ve daha hafif.
  c.save();
  c.beginPath(); c.rect(0, Y(P.tabla), cw, ch); c.clip();
  bulanik(-ic, -P.yariH - 0.02, ic, P.tabla + 0.015, 0.42, 0.03);
  bulanik(-ic + 0.004, P.tabla - 0.03, ic - 0.004, P.tabla + 0.004, 0.26, 0.008);
  c.restore();

  // 3) Tabla ile plakanın iç köşesi
  const kose = c.createLinearGradient(0, Y(P.tabla), 0, Y(P.tabla + 0.028));
  kose.addColorStop(0, `rgba(${RENK},0.20)`);
  kose.addColorStop(1, `rgba(${RENK},0)`);
  c.fillStyle = kose;
  c.fillRect(X(-ic), Y(P.tabla + 0.028), 2 * ic * PPM, 0.028 * PPM);

  // 4) Yan panellerin iç yüzü boyunca köşe gölgesi
  for (const yon of [-1, 1]) {
    const x0 = yon * ic, x1 = yon * (ic - 0.024);
    const g = c.createLinearGradient(X(x0), 0, X(x1), 0);
    g.addColorStop(0, `rgba(${RENK},0.15)`);
    g.addColorStop(1, `rgba(${RENK},0)`);
    c.fillStyle = g;
    c.fillRect(Math.min(X(x0), X(x1)), Y(P.yariH), Math.abs(X(x1) - X(x0)), 2 * P.yariH * PPM);
  }

  // 5) Panellerin dışında duvara düşen yumuşak gölge
  for (const yon of [-1, 1]) {
    const xa = yon * (w / 2), xb = yon * (w / 2 + 0.010);
    bulanik(Math.min(xa, xb), -P.yariH + 0.004, Math.max(xa, xb), P.yariH - 0.006, 0.22, 0.014);
  }

  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  return {
    tex, gw, gh,
    yc: P.yariH + MT - gh / 2,   // düzlem merkezinin raf merkezine göre yüksekliği
  };
}

/**
 * Oda maketinin altına düşen gölge: döşeme kenarında belirgin bir temas,
 * dışarı doğru geniş bir yumuşaklık. `w`×`d` döşemenin ayak izi (m).
 */
function makeRoomGroundShadow(w, d) {
  const M = 0.7;                       // döşemenin dışına taşan pay
  const PPM = 100;
  const gw = w + 2 * M, gd = d + 2 * M;
  const cw = Math.round(gw * PPM), ch = Math.round(gd * PPM);
  const cv = document.createElement('canvas');
  cv.width = cw; cv.height = ch;
  const c = cv.getContext('2d');
  const UZAK = 8000;
  const dikdortgen = (alfa, blurM, buyut) => {
    c.save();
    c.shadowColor = `rgba(34,30,26,${alfa})`;
    c.shadowBlur = blurM * PPM;
    c.shadowOffsetX = UZAK;
    c.fillStyle = '#000';
    c.fillRect((M - buyut) * PPM - UZAK, (M - buyut) * PPM, (w + 2 * buyut) * PPM, (d + 2 * buyut) * PPM);
    c.restore();
  };
  /* Döşemenin altı zaten görünmüyor; görünen yalnızca kenardan dışarı
     taşan kısım. Değerler oradan ölçülerek seçildi: kenarda ~%30,
     20 cm dışarıda ~%10, 50 cm'de neredeyse sıfır. */
  dikdortgen(0.42, 0.34, 0.06);   // geniş, yumuşak
  dikdortgen(0.50, 0.06, 0.01);   // döşeme kenarında temas
  const tex = new T.CanvasTexture(cv);
  tex.colorSpace = T.SRGBColorSpace;
  tex.userData.perBuild = true;   // buildRoom her kurulumda atıyor
  return { tex, w: gw, h: gd };
}

/** Eşya ile duvar arasındaki pay (placeProp ile aynı). */
const ESYA_DUVAR_PAYI = 0.02;

/**
 * Eşyanın zemine ve yaslandığı duvara düşürdüğü temas gölgeleri.
 *
 * Eşyalar gerçek gölge haritasında da gölge düşürüyor, ama pencere ışığı
 * yan taraftan geldiği için zeminde ve duvarda "oturma" hissi vermiyordu:
 * koltuk ve yatak zeminin üstünde yüzüyor gibiydi. Bu dokular ışığın
 * yönünden bağımsız ortam gölgesini taklit ediyor.
 *
 * Tek bir kutu yerine PARÇALARDAN hesaplanıyor: masanın yalnızca ayakları
 * zemine değiyor (orada belirgin), tablanın altı ise geniş ve hafif
 * kararıyor. Banyo dolabıyla aynası ayrı ayrı duvara iz bırakıyor.
 *
 * `parcalar`: eşyanın kendi koordinatında parça kutuları (Box3). Eşyanın
 * yerel uzayı: x ortada, y zeminden yukarı, z duvardan odaya.
 */
function makePropShadowTextures(parcalar) {
  const RENK = '38,30,22';
  const PPM = 160;
  const UZAK = 8000;
  const sonuc = [];

  /* Dikdörtgenlerin birleşimini TEK seferde bulanıklaştırır: önce maskeye
     opak çiziliyor, sonra maskenin gölgesi düşürülüyor. Parçaları tek tek
     yarı saydam çizmek, üst üste gelen yerlerde gölgeyi koyulaştırıyordu. */
  const katman = (c, cw, ch, dikdortgenler, alfa, blurM) => {
    if (!dikdortgenler.length) return;
    const maske = document.createElement('canvas');
    maske.width = cw; maske.height = ch;
    const m = maske.getContext('2d');
    m.fillStyle = '#000';
    for (const [x, y, w, h] of dikdortgenler) m.fillRect(x, y, w, h);
    c.save();
    c.shadowColor = `rgba(${RENK},${alfa})`;
    c.shadowBlur = blurM * PPM;
    c.shadowOffsetX = UZAK;
    c.drawImage(maske, -UZAK, 0);
    c.restore();
  };
  const doku = (cv) => {
    const tex = new T.CanvasTexture(cv);
    tex.colorSpace = T.SRGBColorSpace;
    tex.userData.kzGolge = true;
    return tex;
  };

  // ---- Zemin ----
  const alcak = parcalar.filter((b) => b.min.y < 0.6);   // zemini gölgeleyecek kadar alçak
  if (alcak.length) {
    const M = 0.18;
    const x0 = Math.min(...alcak.map((b) => b.min.x)) - M;
    const x1 = Math.max(...alcak.map((b) => b.max.x)) + M;
    const z0 = Math.min(...alcak.map((b) => b.min.z)) - M;
    const z1 = Math.max(...alcak.map((b) => b.max.z)) + M;
    const cw = Math.max(8, Math.round((x1 - x0) * PPM)), ch = Math.max(8, Math.round((z1 - z0) * PPM));
    const cv = document.createElement('canvas');
    cv.width = cw; cv.height = ch;
    const c = cv.getContext('2d');
    const r = (b) => [(b.min.x - x0) * PPM, (b.min.z - z0) * PPM, (b.max.x - b.min.x) * PPM, (b.max.z - b.min.z) * PPM];
    // Geniş, hafif: eşyanın altı
    katman(c, cw, ch, alcak.map(r), 0.20, 0.10);
    // Belirgin, dar: zemine gerçekten değen parçalar
    katman(c, cw, ch, parcalar.filter((b) => b.min.y < 0.03).map(r), 0.38, 0.03);
    sonuc.push({
      tex: doku(cv), zemin: true, w: x1 - x0, h: z1 - z0,
      konum: new T.Vector3((x0 + x1) / 2, 0.003, (z0 + z1) / 2),
    });
  }

  // ---- Duvar ----
  const yaslanan = parcalar.filter((b) => b.min.z < 0.06);
  if (yaslanan.length) {
    const M = 0.15;
    const x0 = Math.min(...yaslanan.map((b) => b.min.x)) - M;
    const x1 = Math.max(...yaslanan.map((b) => b.max.x)) + M;
    const y0 = Math.max(0, Math.min(...yaslanan.map((b) => b.min.y)) - M);
    const y1 = Math.max(...yaslanan.map((b) => b.max.y)) + M;
    const cw = Math.max(8, Math.round((x1 - x0) * PPM)), ch = Math.max(8, Math.round((y1 - y0) * PPM));
    const cv = document.createElement('canvas');
    cv.width = cw; cv.height = ch;
    const c = cv.getContext('2d');
    const r = (b) => [(b.min.x - x0) * PPM, (y1 - b.max.y) * PPM, (b.max.x - b.min.x) * PPM, (b.max.y - b.min.y) * PPM];
    katman(c, cw, ch, yaslanan.map(r), 0.26, 0.035);
    sonuc.push({
      tex: doku(cv), zemin: false, w: x1 - x0, h: y1 - y0,
      konum: new T.Vector3((x0 + x1) / 2, (y0 + y1) / 2, -ESYA_DUVAR_PAYI + 0.003),
    });
  }
  return sonuc;
}

/* Montaj vidaları. Modeldeki delikler (6 mm) raf ortası yüksekliğinde,
   kenardan ~52 mm içeride — üç ölçüde aynı; ışınla ölçüldü. Baş 8,5 mm
   çapında kubbe; vidasız raf duvara yapıştırılmış gibi duruyordu, müşteri
   de duvarında bu siyah başları görecek. */
const VIDA_KENAR = 0.052;
const VIDA_CAP = 0.0085;

/**
 * Bir rafın iki vidası TEK geometride (raf başına tek çizim çağrısı).
 * Kubbeli baş + üstünde yıldız (Phillips) girintisini okutan iki ince
 * çubuk. Çubukların düz üst yüzü ışığı kubbeden farklı açıyla yakaladığı
 * için yakından bakınca haç işareti seçiliyor; uzaktan yalnızca siyah bir
 * nokta — referans görseldeki gibi.
 */
function makeScrewGeometry(w) {
  const R = VIDA_CAP / 2;
  const H = 0.0023;                          // kubbe yüksekliği
  const parca = [];
  for (const sx of [-1, 1]) {
    const x = sx * (w / 2 - VIDA_KENAR);
    const bas = new T.SphereGeometry(R, 20, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    bas.rotateX(Math.PI / 2);                // kubbe duvardan dışarı (+z) baksın
    bas.scale(1, 1, H / R);
    bas.translate(x, 0, 0);
    parca.push(bas);
    for (const [cw, ch] of [[R * 1.15, 0.0008], [0.0008, R * 1.15]]) {
      const cubuk = new T.BoxGeometry(cw, ch, 0.0005);
      cubuk.translate(x, 0, H - 0.0001);
      parca.push(cubuk);
    }
  }
  // Basit birleştirme: yalnızca konum + normal (malzeme dokusuz).
  const duz = parca.map((g) => (g.index ? g.toNonIndexed() : g));
  const toplam = duz.reduce((n, g) => n + g.attributes.position.count, 0);
  const pos = new Float32Array(toplam * 3), nor = new Float32Array(toplam * 3);
  let o = 0;
  for (const g of duz) {
    pos.set(g.attributes.position.array, o * 3);
    nor.set(g.attributes.normal.array, o * 3);
    o += g.attributes.position.count;
  }
  parca.forEach((g) => g.dispose());
  const geo = new T.BufferGeometry();
  geo.setAttribute('position', new T.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new T.BufferAttribute(nor, 3));
  geo.computeBoundingSphere();
  return geo;
}

/* ==========================================================================
   DEKOR EŞYALARI
   Raflara konan küçük eşyalar (vazo düğmesi). Hepsi kodla üretiliyor:
   dışarıdan model ya da görsel indirilmiyor, açılışa yük bindirmiyor.

   Ölçüler gerçek eşyalardan: kitap 17–23 cm, vazo 11–19 cm, kupa 8,5 cm…
   Raf tablası 15 cm derin, yan yana raflar arası en az 20 cm; eşyalar bu
   kutuya göre seçiliyor (bkz. dekorKur).

   Her eşyanın kökeni TABANININ ORTASI: y = 0 rafın üst yüzü. Aynı malzemeyi
   paylaşan parçalar tek geometride birleştiriliyor (dekorBirlestir) —
   eşya başına bir-üç çizim çağrısı.
   ========================================================================== */

/** Pastel, gerçek eşya tonları: seramik, cam, kumaş, kâğıt. */
const DEKOR_PASTEL = ['#e8c7c1', '#c9d6c3', '#c7d4e0', '#efdcae', '#d9cde4',
  '#f0e6d8', '#d8b4a0', '#bfd9d0', '#e3d0c2', '#b9c4cf'];

/* Kitap sırtları: atlastaki 16 tasarımın zemin rengi ve başlığı. Gerçek bir
   raftaki gibi birkaç koyu ve canlı sırt da var; hepsi pastel olunca raf
   oyuncak gibi duruyordu. */
const DEKOR_KITAP = ['#7d2f2c', '#f2ede4', '#e9e1cf', '#9db4c0', '#c8573f', '#e2b8b0',
  '#3f5560', '#e6c35c', '#8fa89a', '#d6c3b1', '#2f2b28', '#b7c9a8', '#cf8f78', '#a99bb8',
  '#5f7f8c', '#efe0b8'];
/* Başlıklar yalnız markadan: KREAZ, FOLD ölçüleri ve toz boya renkleri. Renk
   adları kendi tonundaki sırtta (Napoli Sun sarı, Carbon Matte koyu…).
   KREAZ/FOLD kalın düz yazıyla, renk adları italik serifle basılıyor. */
const DEKOR_KITAP_ADLARI = ['KREAZ', 'Raw Canvas', 'FOLD 30', 'Raf Stüdyosu', 'FOLD',
  'Blush Clay', 'Nordic Stone', 'Napoli Sun', 'Sage Green', 'FOLD 45', 'Carbon Matte',
  'kreaz.co', 'KREAZ Studio', 'FOLD 60', 'Renk Kartelası', 'FOLD Çelik'];

/**
 * Oda → eşya havuzu. Her rafta birkaç GRUP var: bir büyük eşya (kitap sırası,
 * vazo, lamba, çerçeve…) ve çoğu zaman önüne ya da yanına konmuş küçük bir
 * eşya (mum, fincan, telefon…). Gerçek raflar böyle diziliyor; eşit aralıklı
 * tek tek eşyalar vitrin gibi duruyordu.
 */
const DEKOR_HAVUZ = {
  genel:   { buyuk: ['kitapDik', 'vazoDesen', 'cerceve', 'bitki', 'lamba', 'vazoCam', 'kitapYatay'],
    kucuk: ['mum', 'figur', 'bardakSu', 'telefon', 'kase', 'kupaBeyaz'] },
  salon:   { buyuk: ['kitapDik', 'vazoDesen', 'cerceve', 'bitki', 'lamba', 'vazoCam', 'kitapYatay'],
    kucuk: ['mum', 'figur', 'bardakSu', 'kase', 'telefon'] },
  mutfak:  { buyuk: ['tabak', 'kavanozTakim', 'vazoCam', 'bitki', 'kitapYatay', 'lamba', 'vazoDesen'],
    kucuk: ['kupaBeyaz', 'fincan', 'emaye', 'bardakSu', 'kase'] },
  kahve:   { buyuk: ['kavanozKahve', 'ogutucu', 'bitki', 'lamba', 'tabak', 'kitapYatay', 'vazoCam'],
    kucuk: ['fincan', 'kupaBeyaz', 'emaye', 'bardakSu'] },
  cocuk:   { buyuk: ['kitapDik', 'halka', 'ayicik', 'lamba', 'cerceve', 'kitapYatay'],
    kucuk: ['top', 'bloklar', 'figur'] },
  calisma: { buyuk: ['kitapDik', 'kitapYatay', 'lamba', 'bitki', 'cerceve', 'vazoDesen'],
    kucuk: ['kalemlik', 'telefon', 'kupaBeyaz', 'mum', 'figur'] },
  banyo:   { buyuk: ['sise', 'havlu', 'bitki', 'vazoCam', 'lamba'],
    kucuk: ['mum', 'kavanozPamuk', 'figur'] },
  yatak:   { buyuk: ['kitapYatay', 'kitapDik', 'lamba', 'cerceve', 'vazoDesen', 'bitki'],
    kucuk: ['mum', 'telefon', 'bardakSu', 'figur'] },
  antre:   { buyuk: ['lamba', 'cerceve', 'vazoDesen', 'bitki', 'kitapYatay'],
    kucuk: ['kase', 'mum', 'telefon', 'figur'] },
  balkon:  { buyuk: ['bitki', 'kaktus', 'lamba', 'vazoDesen', 'vazoCam'],
    kucuk: ['mum', 'kase', 'bardakSu'] },
};

/** Tuval dikdörtgeni → doku koordinatı [u0, v0, u1, v1] (doku dikeyde ters). */
function dekorUV(x, y, w, h, W, H) {
  return [x / W, 1 - (y + h) / H, (x + w) / W, 1 - y / H];
}

/**
 * Bütün dekor dokuları. Bir kez, dekor ilk açıldığında üretiliyor; hepsi
 * küçük (en büyüğü 1024×576 kitap atlası) ve bütün eşyalarda paylaşılıyor.
 */
function makeDekorTextures() {
  const rnd = seeded(91);
  const tuval = (w, h) => {
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    return [cv, cv.getContext('2d')];
  };
  const doku = (cv, tekrar) => {
    const t = new T.CanvasTexture(cv);
    t.colorSpace = T.SRGBColorSpace;
    if (tekrar) { t.wrapS = t.wrapT = T.RepeatWrapping; }
    t.anisotropy = 4;
    return t;
  };
  const benek = (c, W, H, n, renk, rMin, rMax) => {
    for (let i = 0; i < n; i++) {
      c.fillStyle = renk(rnd());
      c.beginPath(); c.arc(rnd() * W, rnd() * H, rMin + rnd() * (rMax - rMin), 0, Math.PI * 2); c.fill();
    }
  };

  // Seramik: beyaz zemin üstünde ince benekler (malzeme rengiyle boyanıyor).
  const [sc, sx] = tuval(128, 128);
  sx.fillStyle = '#fff'; sx.fillRect(0, 0, 128, 128);
  benek(sx, 128, 128, 520, (q) => (q < 0.8 ? `rgba(60,45,35,${0.06 + q * 0.16})` : `rgba(255,255,255,${q * 0.4})`), 0.35, 1.25);
  const seramik = doku(sc, true);
  seramik.repeat.set(3, 2);

  // Emaye: beyaz zemin, kenarlarda koyu çatlak/kırık lekeleri.
  const [ec, ex] = tuval(128, 128);
  ex.fillStyle = '#fff'; ex.fillRect(0, 0, 128, 128);
  benek(ex, 128, 128, 60, (q) => `rgba(25,20,18,${0.25 + q * 0.5})`, 0.3, 1.6);
  ex.fillStyle = 'rgba(25,20,18,.55)'; ex.fillRect(0, 122, 128, 6);
  const emaye = doku(ec, true);

  // Ahşap: sıcak zemin, ince damarlar.
  const [ac, ax] = tuval(128, 128);
  ax.fillStyle = '#fff'; ax.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 46; i++) {
    ax.strokeStyle = `rgba(110,70,35,${0.05 + rnd() * 0.12})`;
    ax.lineWidth = 0.6 + rnd() * 1.6;
    const y = rnd() * 128;
    ax.beginPath(); ax.moveTo(0, y);
    for (let x = 0; x <= 128; x += 16) ax.lineTo(x, y + Math.sin(x * 0.05 + i) * 2.5);
    ax.stroke();
  }
  const ahsap = doku(ac, true);

  // Kumaş (oyuncak ayı, havlu): ince dokuma.
  const [kc, kx] = tuval(64, 64);
  kx.fillStyle = '#fff'; kx.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 64; i += 2) {
    kx.fillStyle = 'rgba(0,0,0,.05)'; kx.fillRect(i, 0, 1, 64);
    kx.fillStyle = 'rgba(0,0,0,.04)'; kx.fillRect(0, i, 64, 1);
  }
  const kumas = doku(kc, true);
  kumas.repeat.set(4, 4);

  // Temas gölgesi: eşyanın tablaya oturduğu yerdeki yumuşak koyuluk.
  const [gc, gx] = tuval(64, 64);
  const gr = gx.createRadialGradient(32, 32, 2, 32, 32, 32);
  gr.addColorStop(0, 'rgba(30,22,15,.55)');
  gr.addColorStop(0.5, 'rgba(30,22,15,.22)');
  gr.addColorStop(1, 'rgba(30,22,15,0)');
  gx.fillStyle = gr; gx.fillRect(0, 0, 64, 64);
  const golge = doku(gc, false);

  // Top: dilimli plaj topu.
  const [tc, tx] = tuval(256, 8);
  ['#e8c7c1', '#f0e6d8', '#c7d4e0', '#efdcae', '#bfd9d0', '#f0e6d8']
    .forEach((r, i) => { tx.fillStyle = r; tx.fillRect(i * 256 / 6, 0, 256 / 6 + 1, 8); });
  const top = doku(tc, false);

  /* ---------- Kitap atlası ----------
     Üstte 16 sırt (64×448), altta sayfa kenarı (dik ve yatık iki yön) ve
     kapak kâğıdı. Bütün kitaplar tek malzemeyi kullanıyor: kapak rengi köşe
     renginden, sırt ve sayfa bu atlastan geliyor. Bir kitap sırası = tek
     çizim çağrısı, kaç kitap olursa olsun. */
  const KW = 1024, KH = 576;
  const [bc, bx] = tuval(KW, KH);
  const isik2 = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    return ((n >> 16) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114) / 255;
  };
  const sirt = [];
  DEKOR_KITAP.forEach((renk, i) => {
    const x = i * 64;
    bx.fillStyle = renk; bx.fillRect(x, 0, 64, 448);
    const acik = isik2(renk) > 0.55;
    const yazi = acik ? 'rgba(40,34,30,.86)' : 'rgba(246,240,230,.9)';
    const ince = acik ? 'rgba(40,34,30,.35)' : 'rgba(246,240,230,.45)';
    // Üstte/altta şerit ya da çift çizgi — her sırtta biraz farklı.
    bx.fillStyle = ince;
    if (i % 3 === 0) { bx.fillRect(x, 26, 64, 5); bx.fillRect(x, 448 - 31, 64, 5); }
    if (i % 3 === 1) { bx.fillRect(x, 20, 64, 2); bx.fillRect(x, 26, 64, 2); bx.fillRect(x, 448 - 28, 64, 2); }
    if (i % 4 === 2) { bx.fillRect(x, 0, 64, 40); }
    // Başlık sırt boyunca (90° çevrik).
    bx.save();
    bx.translate(x + 32, 224);
    bx.rotate(-Math.PI / 2);
    bx.fillStyle = yazi;
    bx.textAlign = 'center';
    bx.textBaseline = 'middle';
    const baslik = DEKOR_KITAP_ADLARI[i];
    const serif = !/kreaz|fold/i.test(baslik);
    bx.font = `${serif ? 'italic 500' : '700'} ${serif ? 26 : 22}px ${serif ? 'Georgia, "Times New Roman", serif' : '"Arial", "Helvetica", sans-serif'}`;
    // Renk adları İngilizce: "Nordic" NORDİC olmasın; Türkçe başlıklarda ı/i doğru.
    const metin = baslik.toLocaleUpperCase(/[çğıöşü]/i.test(baslik) ? 'tr' : 'en');
    const gen = bx.measureText(metin).width;
    if (gen > 320) bx.scale(320 / gen, 1);
    bx.fillText(metin, 0, 0);
    bx.restore();
    // Yayınevi işareti: altta küçük bir kare ya da yuvarlak.
    bx.fillStyle = yazi;
    if (i % 2) bx.fillRect(x + 26, 448 - 58, 12, 12);
    else { bx.beginPath(); bx.arc(x + 32, 448 - 52, 6, 0, Math.PI * 2); bx.fill(); }
    sirt.push(dekorUV(x + 1, 1, 62, 446, KW, KH));
  });
  // Sayfa kenarları: krem zemin, sayfa sayfa çizgiler.
  bx.fillStyle = '#f2ebdc'; bx.fillRect(0, 448, 680, 128);
  for (let i = 0; i < 340; i += 1 + Math.floor(rnd() * 2)) {
    bx.fillStyle = `rgba(150,128,96,${0.08 + rnd() * 0.16})`;
    bx.fillRect(i, 448, 1, 128);
  }
  for (let i = 448; i < 576; i += 1 + Math.floor(rnd() * 2)) {
    bx.fillStyle = `rgba(150,128,96,${0.08 + rnd() * 0.16})`;
    bx.fillRect(340, i, 340, 1);
  }
  // Kapak kâğıdı: hafif keten dokulu beyaz (rengi köşe renginden).
  bx.fillStyle = '#ffffff'; bx.fillRect(680, 448, 344, 128);
  for (let i = 0; i < 900; i++) {
    bx.fillStyle = `rgba(0,0,0,${0.02 + rnd() * 0.04})`;
    bx.fillRect(680 + rnd() * 344, 448 + rnd() * 128, 1 + rnd() * 3, 1);
  }
  const kitap = {
    tex: doku(bc, false),
    sirt,
    sayfaDik: dekorUV(4, 452, 330, 120, KW, KH),
    sayfaYatay: dekorUV(344, 452, 330, 120, KW, KH),
    kapak: dekorUV(690, 456, 320, 112, KW, KH),
  };
  kitap.tex.anisotropy = 8;

  /* ---------- Vazo desenleri ----------
     256×128, yatayda sarmalı: gövdenin çevresine bir tur sarılıyor. */
  const desen = {};
  const yeniDesen = (ad, ciz) => {
    const [cv, c] = tuval(256, 128);
    ciz(c, 256, 128);
    desen[ad] = doku(cv, true);
  };
  yeniDesen('terrakota', (c, W, H) => {
    c.fillStyle = '#efe5d3'; c.fillRect(0, 0, W, H);
    const t = '#c4643c';
    c.fillStyle = t;
    [12, 16, 44, 84, 112, 116].forEach((y) => c.fillRect(0, y, W, 2));
    for (let x = 0; x < W; x += 16) {              // üçgen dizisi
      c.beginPath(); c.moveTo(x, 40); c.lineTo(x + 8, 22); c.lineTo(x + 16, 40); c.fill();
      c.beginPath(); c.moveTo(x, 88); c.lineTo(x + 8, 106); c.lineTo(x + 16, 88); c.fill();
    }
    for (let x = 4; x < W; x += 10) { c.beginPath(); c.arc(x, 50, 1.6, 0, Math.PI * 2); c.fill(); }
    for (let x = 4; x < W; x += 10) { c.beginPath(); c.arc(x, 78, 1.6, 0, Math.PI * 2); c.fill(); }
    c.strokeStyle = t; c.lineWidth = 2;
    for (let x = 0; x < W; x += 64) {               // eşkenar dörtgen motif
      for (const s of [12, 7]) {
        c.beginPath(); c.moveTo(x + 32, 64 - s); c.lineTo(x + 32 + s * 1.8, 64); c.lineTo(x + 32, 64 + s); c.lineTo(x + 32 - s * 1.8, 64); c.closePath(); c.stroke();
      }
      c.fillRect(x + 4, 56, 2, 16); c.fillRect(x + 60, 56, 2, 16);
    }
  });
  yeniDesen('iznik', (c, W, H) => {
    c.fillStyle = '#f7f5f0'; c.fillRect(0, 0, W, H);
    const lac = '#2e5aa6', tur = '#3b9ea2';
    c.fillStyle = lac; c.fillRect(0, 0, W, 8); c.fillRect(0, H - 8, W, 8);
    c.fillStyle = tur; c.fillRect(0, 10, W, 2); c.fillRect(0, H - 12, W, 2);
    for (let x = 32; x < W; x += 64) {
      for (let p = 0; p < 6; p++) {                 // altı yapraklı çiçek
        const a = (p / 6) * Math.PI * 2;
        c.fillStyle = p % 2 ? lac : tur;
        c.beginPath(); c.ellipse(x + Math.cos(a) * 11, 64 + Math.sin(a) * 11, 9, 4.5, a, 0, Math.PI * 2); c.fill();
      }
      c.fillStyle = '#c94a3a'; c.beginPath(); c.arc(x, 64, 4.5, 0, Math.PI * 2); c.fill();
      c.strokeStyle = lac; c.lineWidth = 2;        // dal kıvrımları
      c.beginPath(); c.moveTo(x + 20, 64); c.bezierCurveTo(x + 28, 36, x + 40, 92, x + 44, 64); c.stroke();
      c.fillStyle = tur;
      c.beginPath(); c.ellipse(x + 32, 30, 6, 2.5, -0.6, 0, Math.PI * 2); c.fill();
      c.beginPath(); c.ellipse(x + 32, 98, 6, 2.5, 0.6, 0, Math.PI * 2); c.fill();
    }
  });
  yeniDesen('terrazzo', (c, W, H) => {
    c.fillStyle = '#efe8dd'; c.fillRect(0, 0, W, H);
    const renk = ['#c96f5a', '#3f5560', '#e6c35c', '#9db4c0', '#b7c9a8', '#2f2b28', '#e2b8b0'];
    for (let i = 0; i < 150; i++) {
      c.fillStyle = renk[Math.floor(rnd() * renk.length)];
      const x = rnd() * W, y = rnd() * H, r = 1.2 + rnd() * 3.6;
      c.beginPath();
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2 + rnd();
        const rr = r * (0.6 + rnd() * 0.6);
        k ? c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr) : c.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
      }
      c.fill();
    }
  });
  yeniDesen('mozaik', (c, W, H) => {
    c.fillStyle = '#ece6d8'; c.fillRect(0, 0, W, H);
    const renk = ['#1f3b4d', '#ece6d8', '#d7b56d', '#6a9fa3', '#1f3b4d'];
    for (let y = 0; y < H; y += 8) {
      for (let x = (y / 8) % 2 ? 6 : 0; x < W; x += 12) {
        c.fillStyle = renk[Math.floor(rnd() * renk.length)];
        c.fillRect(x + 1, y + 1, 9, 5);
      }
    }
  });
  yeniDesen('motif', (c, W, H) => {
    c.fillStyle = '#f5f3ee'; c.fillRect(0, 0, W, H);
    const lac = '#2a56a8';
    c.fillStyle = lac; c.fillRect(0, 6, W, 3); c.fillRect(0, H - 9, W, 3);
    for (const x of [64, 192]) {
      for (let p = 0; p < 8; p++) {
        const a = (p / 8) * Math.PI * 2;
        c.beginPath(); c.ellipse(x + Math.cos(a) * 18, 64 + Math.sin(a) * 18, 14, 5, a, 0, Math.PI * 2); c.fill();
      }
      c.beginPath(); c.arc(x, 64, 7, 0, Math.PI * 2); c.fill();
    }
  });
  yeniDesen('cizgi', (c, W, H) => {
    c.fillStyle = '#f1e9dc'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#9fb3a2';
    for (let x = 0; x < W; x += 16) c.fillRect(x, 0, 6, H);
  });
  // Çiçekli porselen (fincan): pembe güller, yeşil yapraklar, altın kenar.
  yeniDesen('cicek', (c, W, H) => {
    c.fillStyle = '#fbf8f1'; c.fillRect(0, 0, W, H);
    for (let i = 0; i < 22; i++) {
      const x = rnd() * W, y = 18 + rnd() * (H - 36);
      c.fillStyle = '#7fa06a';
      for (let l = 0; l < 3; l++) {
        const a = rnd() * Math.PI * 2;
        c.beginPath(); c.ellipse(x + Math.cos(a) * 9, y + Math.sin(a) * 9, 7, 3, a, 0, Math.PI * 2); c.fill();
      }
      c.fillStyle = '#eaa391'; c.beginPath(); c.arc(x, y, 6.5, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#d97b6a'; c.beginPath(); c.arc(x + 1, y - 1, 3.4, 0, Math.PI * 2); c.fill();
    }
    c.fillStyle = '#c9a85a'; c.fillRect(0, 0, W, 4); c.fillRect(0, H - 4, W, 4);
  });

  /* ---------- Çerçeve baskıları ----------
     Dört baskı tek tuvalde: gün batımı manzarası, kemer deseni, botanik
     çizim, dağ gölü. (Sepya bir portre denendi; bu ölçekte profil simgesi
     gibi okunuyordu.) */
  const [pc, px] = tuval(512, 168);
  const slot = (i) => i * 128;
  // 0) manzara
  let g = px.createLinearGradient(0, 0, 0, 168);
  g.addColorStop(0, '#f2c5a5'); g.addColorStop(0.6, '#f4e1cc'); g.addColorStop(1, '#efe3d2');
  px.fillStyle = g; px.fillRect(slot(0), 0, 128, 168);
  px.fillStyle = '#e88c5c'; px.beginPath(); px.arc(slot(0) + 78, 74, 18, 0, Math.PI * 2); px.fill();
  px.fillStyle = '#a7bf9f'; px.beginPath(); px.moveTo(slot(0), 120); px.quadraticCurveTo(slot(0) + 50, 84, slot(0) + 128, 112); px.lineTo(slot(0) + 128, 168); px.lineTo(slot(0), 168); px.fill();
  px.fillStyle = '#7f9b85'; px.beginPath(); px.moveTo(slot(0), 140); px.quadraticCurveTo(slot(0) + 80, 110, slot(0) + 128, 138); px.lineTo(slot(0) + 128, 168); px.lineTo(slot(0), 168); px.fill();
  // 1) kemerler
  px.fillStyle = '#efe6da'; px.fillRect(slot(1), 0, 128, 168);
  ['#c8643c', '#e3b98c', '#a9bda2', '#efe6da'].forEach((r, i) => {
    px.fillStyle = r;
    const rr = 46 - i * 11;
    px.beginPath(); px.arc(slot(1) + 64, 120, rr, Math.PI, 0); px.lineTo(slot(1) + 64 + rr, 140); px.lineTo(slot(1) + 64 - rr, 140); px.fill();
  });
  px.fillStyle = '#e88c5c'; px.beginPath(); px.arc(slot(1) + 96, 36, 9, 0, Math.PI * 2); px.fill();
  // 2) botanik
  px.fillStyle = '#f3efe5'; px.fillRect(slot(2), 0, 128, 168);
  px.strokeStyle = '#4f6b4c'; px.lineWidth = 1.6;
  px.beginPath(); px.moveTo(slot(2) + 64, 150); px.quadraticCurveTo(slot(2) + 58, 90, slot(2) + 70, 22); px.stroke();
  for (let i = 0; i < 7; i++) {
    const y = 135 - i * 16, sag = i % 2 ? 1 : -1;
    px.beginPath(); px.ellipse(slot(2) + 64 + sag * 14, y, 13, 5, sag * -0.5, 0, Math.PI * 2); px.stroke();
  }
  // 3) dağ gölü: gökyüzü, katman katman dağlar, göle düşen yansıma
  g = px.createLinearGradient(0, 0, 0, 100);
  g.addColorStop(0, '#c9d9e3'); g.addColorStop(1, '#eef0ea');
  px.fillStyle = g; px.fillRect(slot(3), 0, 128, 100);
  const dag = (renk, taban, tepeler) => {
    px.fillStyle = renk;
    px.beginPath(); px.moveTo(slot(3), taban);
    tepeler.forEach(([x, y]) => px.lineTo(slot(3) + x, y));
    px.lineTo(slot(3) + 128, taban); px.fill();
  };
  dag('#a9b8c2', 100, [[0, 70], [28, 44], [52, 66], [84, 36], [128, 62]]);
  dag('#7f939e', 100, [[0, 86], [36, 62], [70, 84], [100, 64], [128, 80]]);
  g = px.createLinearGradient(0, 100, 0, 168);
  g.addColorStop(0, '#9fb3bd'); g.addColorStop(1, '#d6e0e3');
  px.fillStyle = g; px.fillRect(slot(3), 100, 128, 68);
  px.fillStyle = 'rgba(255,255,255,.35)';
  for (let y = 108; y < 168; y += 9) px.fillRect(slot(3) + 18 + (y % 27), y, 40 + (y % 19), 1);
  px.fillStyle = '#5f7568'; px.fillRect(slot(3), 98, 128, 3);
  const baski = { tex: doku(pc, false), yer: [0, 1, 2, 3].map((i) => dekorUV(slot(i) + 2, 2, 124, 164, 512, 168)) };

  return { seramik, emaye, ahsap, kumas, golge, top, kitap, desen, baski };
}

/** Aynı malzemeyi paylaşan parçaları tek geometride toplar (konum, normal,
    uv ve varsa köşe rengi). Köşe rengi olmayan parça beyaz sayılıyor. */
function dekorBirlestir(parcalar) {
  const duz = parcalar.map((g) => (g.index ? g.toNonIndexed() : g));
  const n = duz.reduce((t, g) => t + g.attributes.position.count, 0);
  const renkli = duz.some((g) => g.attributes.color);
  const pos = new Float32Array(n * 3), nor = new Float32Array(n * 3), uv = new Float32Array(n * 2);
  const col = renkli ? new Float32Array(n * 3).fill(1) : null;
  let o = 0;
  for (const g of duz) {
    if (!g.attributes.normal) g.computeVertexNormals();
    pos.set(g.attributes.position.array, o * 3);
    nor.set(g.attributes.normal.array, o * 3);
    if (g.attributes.uv) uv.set(g.attributes.uv.array, o * 2);
    if (col && g.attributes.color) col.set(g.attributes.color.array, o * 3);
    o += g.attributes.position.count;
  }
  parcalar.forEach((g) => g.dispose());
  duz.forEach((g) => g.dispose());
  const geo = new T.BufferGeometry();
  geo.setAttribute('position', new T.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new T.BufferAttribute(nor, 3));
  geo.setAttribute('uv', new T.BufferAttribute(uv, 2));
  if (col) geo.setAttribute('color', new T.BufferAttribute(col, 3));
  geo.computeBoundingSphere();
  return geo;
}

/** Dönel gövde: [yarıçap, yükseklik] çiftlerinden (dış yüz yukarı, iç yüz aşağı). */
function dekorTorna(profil, seg = 26) {
  return new T.LatheGeometry(profil.map(([r, y]) => new T.Vector2(Math.max(0.0001, r), y)), seg);
}

/** Kenarı yumuşatılmış kutu; doku koordinatlarıyla (BoxGeometry tabanlı). */
function dekorKutu(w, h, d) {
  return new T.BoxGeometry(w, h, d);
}

/**
 * Tek kitap: kutunun altı yüzü atlasın ilgili bölgesine eşleniyor. Kapak
 * rengi köşe renginden geliyor; sırt ve sayfa kenarları atlastan. Böylece
 * bütün kitaplar tek malzemeyi paylaşıyor ve bir sıra tek parçada
 * birleşiyor.
 *   dik   → boyut [kalınlık, boy, derinlik]   (sırt +z'ye, dışarı bakıyor)
 *   yatık → boyut [boy, kalınlık, derinlik]   (sırt yine +z'de, yazı yatay)
 */
function kitapGeo(boyut, renk, sirtNo, yatay, atlas) {
  const geo = new T.BoxGeometry(boyut[0], boyut[1], boyut[2]);
  const uv = geo.attributes.uv;
  const renkler = new Float32Array(uv.count * 3).fill(1);
  const c = new T.Color(renk);
  const yuz = (f, r, cevir, boya) => {
    for (let j = 0; j < 4; j++) {
      const i = f * 4 + j;
      let u = uv.getX(i), v = uv.getY(i);
      // Yatık kitapta sırt boyu x'e, eni y'ye düşüyor; harflerin tepesi yukarı baksın.
      if (cevir) [u, v] = [1 - v, u];
      uv.setXY(i, r[0] + u * (r[2] - r[0]), r[1] + v * (r[3] - r[1]));
      if (boya) renkler.set([c.r, c.g, c.b], i * 3);
    }
  };
  const sirt = atlas.sirt[sirtNo % atlas.sirt.length];
  if (!yatay) {
    yuz(0, atlas.kapak, false, true); yuz(1, atlas.kapak, false, true);
    yuz(2, atlas.sayfaDik, false, false); yuz(3, atlas.sayfaDik, false, false);
    yuz(4, sirt, false, false); yuz(5, atlas.sayfaDik, false, false);
  } else {
    yuz(0, atlas.sayfaYatay, false, false); yuz(1, atlas.sayfaYatay, false, false);
    yuz(2, atlas.kapak, false, true); yuz(3, atlas.kapak, false, true);
    yuz(4, sirt, true, false); yuz(5, atlas.sayfaYatay, false, false);
  }
  geo.setAttribute('color', new T.BufferAttribute(renkler, 3));
  return geo;
}

/* ---------- Masa lambalarının ışığı ----------
   Sahneye gerçek ışık kaynağı EKLENMİYOR: ışık sayısı değişince bütün
   malzemeler yeniden derleniyor, her pikselde (duvar ve zemin dahil) bedel
   ödeniyordu. Bunun yerine raf ve eşya malzemelerine küçük bir ek var. Her
   lamba için:
   - ampulden gelen, mesafeyle sönen sıcak ışık (yalnız ampule dönük yüzler),
   - şapkanın kestiği koni: ampulden şapka kenarına çizilen çizginin üstü
     karanlık, çünkü şapkanın üstü kapalı,
   - lambanın kendi rafının kutusu: ışık komşu rafa, alttaki rafa ya da
     rafın arkasındaki duvara sızmıyor (arka plaka duvarı örtüyor).
   Gündüz lamba sayısı 0: ek, tek bir karşılaştırmayla atlanıyor.

   Sınır 16 lamba ve dekorKur duvara bundan fazlasını koymuyor. Eskiden
   sınır 6 idi; büyük duvarlarda yedinci lambadan sonrakiler hiç yanmıyor,
   yalnız gölgeleri görünüyordu. */
const LAMBA_EN_COK = 16;
let LAMBA_U = null;
function lambaUniform() {
  if (!LAMBA_U) {
    const dizi = () => Array.from({ length: LAMBA_EN_COK }, () => new T.Vector4());
    LAMBA_U = {
      kzLambaN: { value: 0 },
      kzLambaPos: { value: dizi() },   // xyz ampul (dünya), w şapkanın kestiği eğim (sin)
      kzLambaMin: { value: dizi() },   // xyz rafın iç kutusunun alt köşesi, w şiddet
      kzLambaMax: { value: dizi() },   // xyz kutunun üst köşesi
      kzLambaRenk: { value: new T.Color(0xffb56c) },
    };
  }
  return LAMBA_U;
}
function lambaIsigiEkle(mat) {
  const u = lambaUniform();
  const N = LAMBA_EN_COK;
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    /* Hangi lamba: KÖŞE aşamasında, nesnenin orijini (raf ya da eşya)
       hangi lambanın raf kutusundaysa o — bir rafta en çok bir lamba var.
       Piksel aşaması böylece 16 lambayı tek tek denemiyor, en çok bir
       lamba hesaplıyor (yakın planda kare başına ~4 ms → ~0,5 ms). */
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
varying vec3 vKzDunya;
flat varying int vKzLamba;
uniform int kzLambaN;
uniform vec4 kzLambaMin[${N}];
uniform vec4 kzLambaMax[${N}];`)
      .replace('#include <project_vertex>', `#include <project_vertex>
vKzDunya = (modelMatrix * vec4(transformed, 1.0)).xyz;
vKzLamba = -1;
vec3 kzO = modelMatrix[3].xyz;
for (int i = 0; i < ${N}; i++) {
  if (i >= kzLambaN) break;
  // Raf ve eşyaların orijini tablanın üstünde (düşen eşya 5 cm, yığının
  // üstündeki figür ~10 cm yukarıda). Yükseklik buna göre dar: alttaki
  // rafın kutusu üstteki rafı (en az 20 cm yukarıda) yakalamasın.
  vec3 lo = kzLambaMin[i].xyz - vec3(0.006, 0.008, 0.01);
  vec3 hi = vec3(kzLambaMax[i].x + 0.006, kzLambaMin[i].y + 0.12, kzLambaMax[i].z + 0.01);
  if (all(greaterThan(kzO, lo)) && all(lessThan(kzO, hi))) { vKzLamba = i; break; }
}`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vKzDunya;
flat varying int vKzLamba;
uniform vec4 kzLambaPos[${N}];
uniform vec4 kzLambaMin[${N}];
uniform vec4 kzLambaMax[${N}];
uniform vec3 kzLambaRenk;`)
      .replace('#include <opaque_fragment>', `if (vKzLamba >= 0) {
  int i = vKzLamba;
  vec3 a = kzLambaMin[i].xyz, b = kzLambaMax[i].xyz;
  vec3 ic = smoothstep(a - 0.003, a + 0.003, vKzDunya) * (1.0 - smoothstep(b - 0.003, b + 0.003, vKzDunya));
  float kutu = ic.x * ic.y * ic.z;
  vec3 L = kzLambaPos[i].xyz - vKzDunya;
  float d = max(length(L), 1e-4);
  L /= d;
  // L yüzeyden ampule: yüzey ampulün altındaysa L.y > 0.
  float koni = smoothstep(kzLambaPos[i].w - 0.13, kzLambaPos[i].w + 0.09, L.y);
  float ndl = max(dot(inverseTransformDirection(normal, viewMatrix), L), 0.0);
  float zay = 0.0085 / (d * d + 0.0024) * (1.0 - smoothstep(0.15, 0.42, d));
  float kzI = kutu * koni * ndl * zay * kzLambaMin[i].w;
  if (kzI > 0.0) {
  /* Her renkte aynı güçte: yüzeyin rengi parlaklığına bölünüp eşitleniyor.
     Düz çarpınca sarı rafta parlak, Nordic Stone'da sönük, Carbon Matte'de
     hiç yanmıyordu. Ton korunuyor (mavi raf mavi kalıyor); küçük sabit pay
     toz boyanın parlak yüzeyinin yansıttığı ışık — siyah rafta da görünüyor. */
  const vec3 kzW = vec3(0.2126, 0.7152, 0.0722);
  float kzP = dot(diffuseColor.rgb, kzW);
  vec3 kzEk = (diffuseColor.rgb * (0.45 / max(kzP, 0.15)) + 0.12) * kzLambaRenk * kzI;
  #ifdef TONE_MAPPING
  /* Ton eşleme parlak yüzeyde eklenen ışığı eziyor: aynı ışık sarı ve
     Raw Canvas rafta ekranda +15, Sage/Nordic'te +95 parlaklık veriyordu.
     Işık, EKRANDA istenen artışı verecek kadar ölçekleniyor: her renkte
     lambanın dibi aynı parlaklıkta, aynı mesafede aynı sönüşle. */
  float kzY0 = sqrt(max(dot(toneMapping(outgoingLight), kzW), 0.0));
  float kzY1 = sqrt(max(dot(toneMapping(outgoingLight + kzEk), kzW), 0.0));
  float kzHedef = 0.26 * (1.0 - exp(-0.5 * kzI));
  kzEk *= clamp(kzHedef / max(kzY1 - kzY0, 1e-3), 0.25, 5.0);
  #endif
  outgoingLight += kzEk;
  }
}
#include <opaque_fragment>`);
  };
  mat.customProgramCacheKey = () => 'kzLamba';
  return mat;
}

/** Bir öncekinden farklı bir sırt seç (yan yana iki aynı kitap olmasın). */
function kitapSec(k, son) {
  let no;
  do { no = Math.floor(k.rnd() * DEKOR_KITAP.length); } while (no === son);
  return no;
}

/**
 * Eşya tarifleri. Her biri `k` (malzeme fabrikası + rastgele sayı), en çok
 * yükseklik ve genişliği alır; sığmazsa null döner. Dönen nesne:
 *   { grup, w, d, h }  — w genişlik (x), d derinlik (z), h yükseklik.
 * Ek bayraklar: kitap (sırtı öne hizalı), arkada (duvara yaslı),
 * lamba (ışığın çıktığı nokta ve şapkanın kestiği eğim).
 * Genişlik/derinlik/yükseklik dekorKur'da geometriden yeniden ölçülüyor;
 * tarifteki değerler yalnız ilk tahmin.
 * Geometriler tabanın ortası (0,0,0) olacak biçimde kuruluyor.
 */
const DEKOR_TARIF = {
  /** Dik kitap sırası: 4–10 kitap, sırtlarda başlıklar; sonuncusu çoğu
      zaman öncekine yaslanıyor. Hepsi tek parça. */
  kitapDik(k, maxH, _ic, maxW) {
    if (maxH < 0.13 || maxW < 0.07) return null;
    const atlas = k.atlas();
    const n = 4 + Math.floor(k.rnd() * 7);
    const boyUst = Math.min(0.235, maxH - 0.004);
    const taban = k.ara(0.112, 0.13);
    const parcalar = [];
    let x = 0, enUzun = 0, son = -1;
    for (let i = 0; i < n; i++) {
      const h = k.ara(Math.min(0.15, boyUst - 0.012), boyUst);
      const t = k.rnd() < 0.2 ? k.ara(0.028, 0.04) : k.ara(0.012, 0.026);
      const d = clamp(taban + k.ara(-0.014, 0.01), 0.095, 0.136);
      if (x + t > maxW) break;
      son = kitapSec(k, son);
      const geo = kitapGeo([t, h, d], DEKOR_KITAP[son], son, false, atlas);
      geo.translate(x + t / 2, h / 2, (0.136 - d) / 2);
      parcalar.push(geo);
      x += t + 0.0007;
      enUzun = Math.max(enUzun, h);
    }
    if (parcalar.length < 2) { parcalar.forEach((g) => g.dispose()); return null; }
    let genis = x;
    if (k.rnd() < 0.5) {
      const h = Math.min(boyUst, k.ara(0.16, 0.22)), t = k.ara(0.015, 0.026);
      const d = clamp(taban, 0.095, 0.136), a = k.ara(0.14, 0.3);
      const px = x + h * Math.sin(a) + 0.001;
      if (px + t * Math.cos(a) <= maxW && h * Math.cos(a) + t * Math.sin(a) <= maxH) {
        son = kitapSec(k, son);
        const geo = kitapGeo([t, h, d], DEKOR_KITAP[son], son, false, atlas);
        geo.translate(t / 2, h / 2, 0);   // sol alt köşe dönme noktası
        geo.rotateZ(a);                    // tepesi sola, önceki kitaba yaslı
        geo.translate(px, 0, (0.136 - d) / 2);
        parcalar.push(geo);
        genis = px + t * Math.cos(a);
      }
    }
    const geo = dekorBirlestir(parcalar);
    geo.translate(-genis / 2, 0, 0);
    const g = new T.Group();
    g.add(k.mesh(geo, k.kitapMat()));
    return { grup: g, w: genis, d: 0.136, h: enUzun, kitap: true };
  },

  /** Yatık kitap yığını; üstünde çoğu zaman küçük bir eşya. */
  kitapYatay(k, maxH, _ic, maxW) {
    if (maxH < 0.045 || maxW < 0.13) return null;
    const atlas = k.atlas();
    const n = 2 + Math.floor(k.rnd() * 4);
    const parcalar = [];
    let y = 0, genis = 0, son = -1;
    for (let i = 0; i < n; i++) {
      const w = Math.min(maxW - 0.012, k.ara(0.14, 0.22));
      const t = k.ara(0.015, 0.034), d = k.ara(0.1, 0.128);
      if (y + t > maxH - 0.004) break;
      son = kitapSec(k, son);
      const geo = kitapGeo([w, t, d], DEKOR_KITAP[son], son, true, atlas);
      geo.rotateY(k.ara(-0.05, 0.05));
      geo.translate(k.ara(-0.008, 0.008), y + t / 2, k.ara(-0.006, 0.006));
      parcalar.push(geo);
      y += t;
      genis = Math.max(genis, w);
    }
    if (!parcalar.length) return null;
    const g = new T.Group();
    g.add(k.mesh(dekorBirlestir(parcalar), k.kitapMat()));
    let ust = y;
    if (k.rnd() < 0.6 && y + 0.04 < maxH) {
      const e = DEKOR_TARIF[k.sec(['figur', 'mum', 'tasKucuk'])](k, maxH - y);
      if (e && e.w < genis * 0.7) {
        e.grup.position.set(k.ara(-0.025, 0.025), y, 0);
        g.add(e.grup);
        ust = y + e.h;
      } else if (e) {
        e.grup.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
      }
    }
    return { grup: g, w: genis + 0.016, d: 0.13, h: ust };
  },

  /** Desenli seramik vazo: altı gövde biçimi × yedi desen. */
  vazoDesen(k, maxH) {
    const sekiller = {
      yuvarlak: [[0.55, 0], [0.85, 0.08], [1, 0.38], [0.9, 0.68], [0.5, 0.86], [0.42, 0.94], [0.5, 1]],
      baluster: [[0.45, 0], [0.52, 0.05], [0.92, 0.24], [1, 0.4], [0.78, 0.6], [0.36, 0.76], [0.3, 0.9], [0.46, 1]],
      yumurta: [[0.45, 0], [0.8, 0.1], [1, 0.42], [0.92, 0.72], [0.56, 0.9], [0.5, 0.93], [0.58, 1]],
      silindir: [[0.74, 0], [0.76, 0.05], [0.82, 0.5], [0.92, 0.92], [0.98, 1]],
      kavanoz: [[0.62, 0], [0.86, 0.12], [1, 0.45], [0.92, 0.8], [0.82, 0.9], [0.9, 1]],
      sise: [[0.64, 0], [0.72, 0.05], [0.72, 0.58], [0.44, 0.78], [0.3, 0.9], [0.38, 1]],
    };
    const ad = k.sec(Object.keys(sekiller));
    const dar = ad === 'sise' || ad === 'silindir';
    const h = Math.min(maxH - 0.004, k.ara(0.11, dar ? 0.2 : 0.17));
    if (h < 0.09) return null;
    const R = h * (dar ? k.ara(0.24, 0.3) : k.ara(0.32, 0.4));
    const egri = new T.SplineCurve(sekiller[ad].map(([r, y]) => new T.Vector2(r * R, y * h)));
    const dis = egri.getPoints(22).map((p) => [p.x, p.y]);
    const agiz = dis[dis.length - 1][0], kalin = 0.003;
    const profil = [[0, 0], [dis[0][0] * 0.92, 0], ...dis,
      [agiz - kalin, h], [Math.max(0.004, agiz * 0.82 - kalin), h - 0.012], [0, h - 0.03]];
    const desen = k.sec(['terrakota', 'iznik', 'terrazzo', 'mozaik', 'motif', 'cizgi']);
    const g = new T.Group();
    g.add(k.mesh(dekorTorna(profil, 32), k.desen(desen, 0.3)));
    g.rotation.y = k.rnd() * Math.PI * 2;
    return { grup: g, w: R * 2, d: R * 2, h };
  },

  /** Suyla dolu cam vazo, içinde bir-üç dal çiçek. */
  vazoCam(k, maxH) {
    const tip = k.sec(['top', 'tomurcuk', 'silindir']);
    const h = k.ara(0.09, 0.13);
    if (h + 0.055 > maxH) return null;
    let profil, R;
    if (tip === 'top') {
      R = h * 0.42;
      profil = [[0, 0], [R * 0.6, 0], [R * 0.95, h * 0.18], [R, h * 0.4], [R * 0.82, h * 0.66], [R * 0.36, h * 0.84],
        [R * 0.32, h], [R * 0.29, h], [R * 0.3, h * 0.85], [R * 0.78, h * 0.65], [R * 0.95, h * 0.4], [R * 0.9, h * 0.2],
        [R * 0.55, 0.004], [0, 0.004]];
    } else if (tip === 'tomurcuk') {
      R = h * 0.27;
      profil = [[0, 0], [R * 0.9, 0], [R, h * 0.3], [R * 0.5, h * 0.62], [R * 0.32, h * 0.9], [R * 0.4, h],
        [R * 0.36, h], [R * 0.28, h * 0.9], [R * 0.45, h * 0.62], [R * 0.93, h * 0.3], [R * 0.82, 0.004], [0, 0.004]];
    } else {
      R = h * 0.28;
      profil = [[0, 0], [R, 0], [R, h], [R - 0.0025, h], [R - 0.0025, 0.005], [0, 0.005]];
    }
    const g = new T.Group();
    const suH = h * k.ara(0.28, (tip === 'tomurcuk' ? 0.3 : 0.48));
    const suR = tip === 'silindir' ? R - 0.003 : R * 0.84;
    const su = new T.CylinderGeometry(suR, suR * 0.88, suH, 24);
    su.translate(0, 0.005 + suH / 2, 0);
    g.add(k.mesh(su, k.su(), false));
    g.add(k.mesh(dekorTorna(profil, 30), k.cam('#eef4f3'), false));

    const yukari = new T.Vector3(0, 1, 0);
    const saplar = [], cicekler = [], merkezler = [];
    const n = 1 + Math.floor(k.rnd() * 3);
    const tur = k.sec(['basak', 'papatya', 'lale']);
    let tepe = h;
    for (let i = 0; i < n; i++) {
      const boy = Math.min(maxH - 0.035, h + k.ara(0.04, 0.11));
      const egim = k.ara(-0.24, 0.24), yon = k.rnd() * Math.PI * 2;
      const sap = new T.CylinderGeometry(0.0013, 0.0015, boy, 5);
      sap.translate(0, boy / 2, 0); sap.rotateZ(egim); sap.rotateY(yon); sap.translate(0, 0.01, 0);
      saplar.push(sap);
      const eksen = new T.Vector3(-Math.sin(egim), Math.cos(egim), 0).applyAxisAngle(yukari, yon);
      const uc = eksen.clone().multiplyScalar(boy).add(new T.Vector3(0, 0.01, 0));
      const yaprak = new T.SphereGeometry(0.013, 8, 6);
      yaprak.scale(1, 0.16, 0.38); yaprak.rotateZ(0.55); yaprak.rotateY(yon + 1.2);
      const yp = eksen.clone().multiplyScalar(boy * 0.62).add(new T.Vector3(0, 0.01, 0));
      yaprak.translate(yp.x, yp.y, yp.z);
      saplar.push(yaprak);
      if (tur === 'basak') {
        for (let j = 0; j < 8; j++) {
          const s = new T.SphereGeometry(0.0058 - j * 0.00045, 8, 6);
          const p = uc.clone().addScaledVector(eksen, -j * 0.0072);
          s.translate(p.x, p.y, p.z);
          cicekler.push(s);
        }
      } else if (tur === 'papatya') {
        for (let j = 0; j < 9; j++) {
          const s = new T.SphereGeometry(0.0085, 8, 4);
          s.scale(1, 0.18, 0.4); s.translate(0.0085, 0, 0); s.rotateY((j / 9) * Math.PI * 2);
          s.translate(uc.x, uc.y, uc.z);
          cicekler.push(s);
        }
        const m = new T.SphereGeometry(0.0045, 8, 6);
        m.translate(uc.x, uc.y + 0.0012, uc.z);
        merkezler.push(m);
      } else {
        for (let j = 0; j < 4; j++) {
          const s = new T.SphereGeometry(0.0095, 10, 8);
          s.scale(0.62, 1.25, 0.62); s.translate(0.0042, 0.009, 0); s.rotateY((j / 4) * Math.PI * 2);
          s.translate(uc.x, uc.y, uc.z);
          cicekler.push(s);
        }
      }
      tepe = Math.max(tepe, uc.y + 0.02);
    }
    g.add(k.mesh(dekorBirlestir(saplar), k.duz('#6f8f58', 0.6)));
    g.add(k.mesh(dekorBirlestir(cicekler), k.duz(k.sec(['#c86d93', '#e9c46a', '#8e7cc3', '#f2a07b', '#f6f2ea']), 0.55)));
    if (merkezler.length) g.add(k.mesh(dekorBirlestir(merkezler), k.duz('#e5b33a', 0.6)));
    return { grup: g, w: Math.max(R * 2, 0.06), d: R * 2, h: tepe };
  },

  /** Su bardağı: hafif konik cam, içinde su. */
  bardakSu(k, maxH) {
    const h = Math.min(maxH - 0.004, k.ara(0.085, 0.11));
    if (h < 0.07) return null;
    const rU = k.ara(0.03, 0.035), rA = rU * 0.8, kalin = 0.0022;
    const suH = h * k.ara(0.45, 0.7);
    const suR = rA + (rU - rA) * (suH / h) - kalin - 0.0006;
    const g = new T.Group();
    g.add(k.mesh(dekorTorna([[0, 0.008], [rA - kalin - 0.0006, 0.008], [suR, 0.008 + suH], [0, 0.008 + suH]], 24), k.su(), false));
    g.add(k.mesh(dekorTorna([[0, 0], [rA, 0], [rU, h], [rU - kalin, h], [rA - kalin, 0.008], [0, 0.008]], 28),
      k.cam(k.sec(['#eef4f3', '#eef4f3', '#e7d6a6'])), false));
    return { grup: g, w: rU * 2, d: rU * 2, h };
  },

  /** Kenarlı tabak yığını (parlak porselen). */
  tabak(k, maxH) {
    const n = 3 + Math.floor(k.rnd() * 4);
    const r = k.ara(0.06, 0.067), kat = 0.0105;
    if (n * kat + 0.01 > maxH) return null;
    const parcalar = [];
    for (let i = 0; i < n; i++) {
      const t = dekorTorna([[0, 0.002], [r * 0.5, 0.002], [r * 0.53, 0], [r * 0.57, 0], [r * 0.6, 0.004],
        [r * 0.66, 0.0045], [r * 0.9, 0.009], [r, 0.0125], [r * 0.985, 0.0135], [r * 0.88, 0.0105],
        [r * 0.64, 0.0068], [r * 0.6, 0.006], [0, 0.006]], 40);
      t.translate(k.ara(-0.0015, 0.0015), i * kat, k.ara(-0.0015, 0.0015));
      parcalar.push(t);
    }
    const g = new T.Group();
    g.add(k.mesh(dekorBirlestir(parcalar), k.duz(k.sec(['#f6f4ef', '#f6f4ef', '#efe7da', '#e5ebe8']), 0.16)));
    return { grup: g, w: r * 2, d: r * 2, h: (n - 1) * kat + 0.0135 };
  },

  /** Çiçekli porselen fincan ve tabağı. */
  fincan(k, maxH) {
    if (maxH < 0.075) return null;
    const r = 0.032, h = 0.054;
    const fin = dekorTorna([[0, 0.009], [r * 0.45, 0.009], [r * 0.5, 0.012], [r * 0.75, 0.016], [r * 0.92, 0.03],
      [r, h], [r - 0.0025, h], [r * 0.9 - 0.0025, 0.031], [r * 0.72 - 0.002, 0.018], [0, 0.015]], 30);
    const tabak = dekorTorna([[0, 0], [0.034, 0], [0.036, 0.002], [0.05, 0.006], [0.058, 0.0095],
      [0.0575, 0.0105], [0.049, 0.008], [0.036, 0.0045], [0, 0.004]], 34);
    const yol = new T.CatmullRomCurve3([new T.Vector3(r * 0.95, 0.046, 0), new T.Vector3(r + 0.016, 0.05, 0),
      new T.Vector3(r + 0.02, 0.034, 0), new T.Vector3(r + 0.008, 0.022, 0), new T.Vector3(r * 0.85, 0.02, 0)]);
    const g = new T.Group();
    g.add(k.mesh(dekorBirlestir([fin, tabak]), k.desen('cicek', 0.22)));
    g.add(k.mesh(new T.TubeGeometry(yol, 16, 0.0028, 6, false), k.duz(k.sec(['#b8c38e', '#c9a85a']), 0.3)));
    g.rotation.y = k.ara(-0.8, 0.8);
    return { grup: g, w: 0.118, d: 0.118, h };
  },

  /** Emaye kupa: koyu kenar, yer yer kırık boya. */
  emaye(k, maxH) {
    const h = 0.082;
    if (h > maxH) return null;
    const r = 0.04, rA = 0.037;
    const govde = dekorTorna([[0, 0], [rA, 0], [r, h], [r - 0.0018, h], [rA - 0.0018, 0.004], [0, 0.004]], 30);
    const agiz = new T.TorusGeometry(r - 0.0009, 0.0016, 6, 30);
    agiz.rotateX(Math.PI / 2); agiz.translate(0, h, 0);
    const yol = new T.CatmullRomCurve3([new T.Vector3(r - 0.002, 0.066, 0), new T.Vector3(r + 0.02, 0.065, 0),
      new T.Vector3(r + 0.024, 0.045, 0), new T.Vector3(r + 0.02, 0.026, 0), new T.Vector3(r - 0.002, 0.024, 0)]);
    const g = new T.Group();
    g.add(k.mesh(govde, k.emaye(k.sec(['#e8b84a', '#9fb7a6', '#8fa9c4', '#f3efe6']))));
    g.add(k.mesh(dekorBirlestir([agiz, new T.TubeGeometry(yol, 18, 0.0026, 6, false)]), k.duz('#2a2725', 0.35)));
    g.rotation.y = k.ara(-0.9, 0.9);
    return { grup: g, w: r * 2 + 0.026, d: r * 2 + 0.026, h };
  },

  /** Parlak porselen kupa. */
  kupaBeyaz(k, maxH) {
    const h = 0.092;
    if (h > maxH) return null;
    const r = 0.04;
    const govde = dekorTorna([[0, 0], [r * 0.92, 0], [r, 0.006], [r, h], [r - 0.003, h], [r - 0.003, 0.008], [0, 0.008]], 32);
    const yol = new T.CatmullRomCurve3([new T.Vector3(r - 0.002, 0.075, 0), new T.Vector3(r + 0.02, 0.074, 0),
      new T.Vector3(r + 0.026, 0.05, 0), new T.Vector3(r + 0.014, 0.024, 0), new T.Vector3(r - 0.002, 0.02, 0)]);
    const g = new T.Group();
    g.add(k.mesh(dekorBirlestir([govde, new T.TubeGeometry(yol, 20, 0.0048, 8, false)]),
      k.duz(k.sec(['#f7f6f2', '#f7f6f2', '#2f2d2b', '#e9d9c4']), 0.18)));
    g.rotation.y = k.ara(-0.9, 0.9);
    return { grup: g, w: r * 2 + 0.03, d: r * 2 + 0.03, h };
  },

  /** Ahşap çerçevede baskı; arkasındaki ayakla duvara hafif yaslı. */
  cerceve(k, maxH) {
    const dikey = k.rnd() < 0.65;
    let w = dikey ? k.ara(0.095, 0.125) : k.ara(0.14, 0.17);
    let h = dikey ? w * 1.32 : w * 0.76;
    if (h + 0.006 > maxH) { h = maxH - 0.008; w = dikey ? h / 1.32 : h / 0.76; }
    if (h < 0.075) return null;
    const kenar = Math.max(0.009, w * 0.1), kal = 0.013;
    const cerceve = [
      dekorKutu(w, kenar, kal).translate(0, kenar / 2, 0),
      dekorKutu(w, kenar, kal).translate(0, h - kenar / 2, 0),
      dekorKutu(kenar, h - 2 * kenar, kal).translate(-w / 2 + kenar / 2, h / 2, 0),
      dekorKutu(kenar, h - 2 * kenar, kal).translate(w / 2 - kenar / 2, h / 2, 0),
    ];
    const arka = dekorKutu(w - 0.004, h - 0.004, 0.003).translate(0, h / 2, -kal / 2 + 0.0015);
    // Baskı: atlastaki dört resimden biri; yatay çerçevede ortası kırpılıyor.
    const r = [...k.baskiYerleri()[Math.floor(k.rnd() * 4)]];
    if (!dikey) { const kes = (r[3] - r[1]) * 0.23; r[1] += kes; r[3] -= kes; }
    const foto = new T.PlaneGeometry(w - 2 * kenar + 0.002, h - 2 * kenar + 0.002);
    const uv = foto.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, r[0] + uv.getX(i) * (r[2] - r[0]), r[1] + uv.getY(i) * (r[3] - r[1]));
    foto.translate(0, h / 2, kal / 2 - 0.004);
    /* Çerçeve ~7,5° geriye yaslı. Alt arka köşesi tablaya gömülmesin diye
       eğim bir tık yukarıdan veriliyor; ayak çerçevenin sırtından tablaya
       iniyor (eskiden sırttan yukarı doğru havada duruyordu). */
    const EGIM = 0.13, kalk = Math.sin(EGIM) * kal / 2 + 0.0004;
    const yasla = (geo) => geo.rotateX(-EGIM).translate(0, kalk, 0);
    const A = new T.Vector3(0, h * 0.58, -kal / 2).applyAxisAngle(new T.Vector3(1, 0, 0), -EGIM);
    A.y += kalk;
    const F = new T.Vector3(0, 0.0025, A.z - h * 0.2);
    const ayak = dekorKutu(0.018, A.distanceTo(F), 0.004)
      .rotateX(Math.atan2(A.z - F.z, A.y - F.y))
      .translate(0, (A.y + F.y) / 2, (A.z + F.z) / 2);
    const g = new T.Group();
    g.add(k.mesh(yasla(dekorBirlestir(cerceve)), k.ahsap(k.sec(['#b98c62', '#d9b48a', '#2e2c2a', '#f2eee6', '#8a6a4f']))));
    g.add(k.mesh(dekorBirlestir([yasla(arka), ayak]), k.duz('#6e5a46', 0.8)));
    g.add(k.mesh(yasla(foto), k.baski(), false));
    return { grup: g, w, d: 0.05, h: h * Math.cos(EGIM), arkada: true };
  },

  /** Rafa bırakılmış cep telefonu (ekranı yukarıda). */
  telefon(k, maxH) {
    if (maxH < 0.02) return null;
    const w = 0.072, l = 0.149, t = 0.0085;
    const kasa = yuvarlakKutu(w, t, l, 0.0075, 2);
    kasa.translate(0, t / 2, 0);
    const ekran = new T.PlaneGeometry(w - 0.007, l - 0.008);
    ekran.rotateX(-Math.PI / 2); ekran.translate(0, t + 0.0003, 0);
    const g = new T.Group();
    g.add(k.mesh(kasa, k.duz(k.sec(['#b9d3e8', '#e8c7c1', '#2f2d2b', '#c9d6c3', '#f0e6d8']), 0.45)));
    g.add(k.mesh(ekran, k.ekran(), false));
    // Rafın derinliğine yatık sığmıyor; enine bırakılmış.
    g.rotation.y = Math.PI / 2 + k.ara(-0.3, 0.3);
    return { grup: g, w: 0.165, d: 0.12, h: t };
  },

  /**
   * Mantar masa lambası. Akşam (altın saat ve gece) ampul yanıyor, şapkanın
   * açık renkli iç astarı içten aydınlanıyor. Işığın kendisi raf ve eşya
   * malzemelerinde hesaplanıyor (bkz. lambaIsigiEkle): şapkanın üstü kapalı
   * olduğu için yalnız AŞAĞIYA, kenarından taşan bir koni halinde ve yalnız
   * kendi rafının içine düşüyor — arka plaka duvarı örtüyor.
   */
  lamba(k, maxH) {
    const h = Math.min(maxH - 0.006, k.ara(0.13, 0.17));
    if (h < 0.11) return null;
    const R = k.ara(0.052, 0.066), kubbe = R * 0.78;
    const renk = k.sec(['#f0e6d8', '#c9d6c3', '#e8c7c1', '#efdcae', '#2f2d2b', '#c7d4e0']);
    const taban = dekorTorna([[0, 0], [R * 0.62, 0], [R * 0.66, 0.004], [R * 0.6, 0.011], [0.008, 0.013], [0, 0.013]], 30);
    const ayakBoy = h - kubbe * 0.9 - 0.01;
    const ayak = new T.CylinderGeometry(0.0058, 0.0072, ayakBoy, 12);
    ayak.translate(0, ayakBoy / 2 + 0.01, 0);
    const sapka = dekorTorna([[0.002, h], [R * 0.36, h - kubbe * 0.08], [R * 0.7, h - kubbe * 0.32],
      [R * 0.93, h - kubbe * 0.7], [R, h - kubbe], [R * 0.97, h - kubbe - 0.002]], 36);
    const g = new T.Group();
    g.add(k.mesh(dekorBirlestir([taban, ayak]), k.duz(renk, 0.4)));
    g.add(k.mesh(sapka, k.lambaSapka(renk)));
    // İç astar: kabuğun 2-3 mm içinde; üstten kabuk örtüyor, alttan görünüyor.
    const astar = dekorTorna([[0.002, h - 0.003], [R * 0.34, h - kubbe * 0.08 - 0.003],
      [R * 0.67, h - kubbe * 0.32 - 0.0025], [R * 0.89, h - kubbe * 0.7 - 0.001],
      [R * 0.945, h - kubbe + 0.0006]], 36);
    g.add(k.mesh(astar, k.lambaIc(), false));
    const ampulY = h - kubbe + 0.006;
    const ampul = new T.SphereGeometry(0.013, 14, 10);
    ampul.translate(0, ampulY, 0);
    g.add(k.mesh(ampul, k.ampul(), false));
    // Işığın çıktığı nokta ve şapkanın kestiği eğim: ampulden kenara çizgi.
    const nokta = new T.Object3D();
    nokta.position.y = ampulY;
    g.add(nokta);
    const dus = ampulY - (h - kubbe);
    /* Her lamba aynı güçte görünsün: uzun lambanın ampulü tablaya uzak,
       ışığı mesafeyle sönüyordu (13 cm ile 17 cm arasında iki kat fark).
       Şiddet ampulün yüksekliğine göre dengeleniyor; lamba da hep arka
       plakadan aynı uzaklıkta duruyor (arkada). */
    const olcek = (ampulY / 0.11) ** 2;
    return { grup: g, w: R * 2, d: R * 2, h, arkada: true, lamba: { nokta, sin: dus / Math.hypot(dus, R), olcek } };
  },

  /** Kavanoz: cam, içinde mercimek/makarna/kahve…, ahşap kapak. */
  kavanoz(k, maxH, ic) {
    const h = k.ara(0.08, 0.13);
    if (h + 0.018 > maxH) return null;
    const r = k.ara(0.031, 0.037);
    const g = new T.Group();
    const dolu = k.ara(0.45, 0.82) * h;
    const icerik = new T.CylinderGeometry(r - 0.004, r - 0.004, dolu, 20);
    icerik.translate(0, 0.004 + dolu / 2, 0);
    g.add(k.mesh(icerik, k.duz(ic || k.sec(['#d98b4f', '#efd7a0', '#8a5a44', '#c9a35a']), 0.9)));
    g.add(k.mesh(dekorTorna([[0, 0], [r, 0], [r, h], [r - 0.002, h], [r - 0.002, 0.004], [0, 0.004]], 24), k.cam('#eef3f2'), false));
    const kapak = new T.CylinderGeometry(r + 0.002, r + 0.002, 0.018, 24);
    kapak.translate(0, h + 0.009, 0);
    g.add(k.mesh(kapak, k.ahsap('#c8a37a')));
    return { grup: g, w: r * 2 + 0.004, d: r * 2 + 0.004, h: h + 0.018 };
  },
  kavanozKahve(k, maxH) { return DEKOR_TARIF.kavanoz(k, maxH, '#4a3226'); },
  kavanozPamuk(k, maxH) { return DEKOR_TARIF.kavanoz(k, maxH, '#faf8f4'); },
  /** Farklı boylarda iki-üç kavanoz yan yana. */
  kavanozTakim(k, maxH, _ic, maxW) {
    const icler = ['#d98b4f', '#efd7a0', '#8a5a44', '#c9a35a', '#e9e2cf'];
    const n = 2 + Math.floor(k.rnd() * 2);
    const g = new T.Group();
    let x = 0, enUst = 0;
    for (let i = 0; i < n; i++) {
      const e = DEKOR_TARIF.kavanoz(k, maxH, icler[Math.floor(k.rnd() * icler.length)]);
      if (!e) break;
      if (x + e.w > maxW) { e.grup.traverse((o) => { if (o.isMesh) o.geometry.dispose(); }); break; }
      e.grup.position.x = x + e.w / 2;
      g.add(e.grup);
      x += e.w + 0.006;
      enUst = Math.max(enUst, e.h);
    }
    if (g.children.length < 2) {
      g.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
      return null;
    }
    const w = x - 0.006;
    g.children.forEach((c) => { c.position.x -= w / 2; });
    return { grup: g, w, d: 0.078, h: enUst };
  },

  /** Tek, düz bir dere taşı (kitap yığınlarının üstüne). */
  tasKucuk(k, maxH) {
    if (maxH < 0.02) return null;
    const g = new T.Group();
    const s = new T.SphereGeometry(0.024, 16, 10);
    s.scale(1, 0.48, 0.82);
    s.translate(0, 0.0115, 0);
    g.add(k.mesh(s, k.seramik(k.sec(['#cfc8bd', '#9da3a6', '#e3d0c2', '#7f7a74']), 0.8)));
    return { grup: g, w: 0.05, d: 0.04, h: 0.023 };
  },

  kase(k, maxH) {
    const h = k.ara(0.04, 0.055);
    const r = k.ara(0.055, 0.068);
    if (h + 0.02 > maxH) return null;
    const kalin = 0.004;
    const govde = dekorTorna([
      [0, 0], [r * 0.45, 0], [r * 0.5, h * 0.08], [r * 0.82, h * 0.45], [r, h],
      [r - kalin, h], [r * 0.8 - kalin, h * 0.48], [r * 0.45, h * 0.18], [0, h * 0.16],
    ], 30);
    const g = new T.Group();
    g.add(k.mesh(govde, k.seramik(k.pastel(), 0.4)));
    // Meyve: portakal, limon, yeşil elma.
    if (k.rnd() < 0.7) {
      const n = 2 + Math.floor(k.rnd() * 2);
      for (let i = 0; i < n; i++) {
        const mr = k.ara(0.02, 0.025);
        const meyve = new T.SphereGeometry(mr, 16, 12);
        const a = (i / n) * Math.PI * 2 + k.rnd();
        meyve.translate(Math.cos(a) * r * 0.32, h * 0.2 + mr * 0.9 + (i === n - 1 ? 0.008 : 0), Math.sin(a) * r * 0.32);
        g.add(k.mesh(meyve, k.duz(k.sec(['#f0a65a', '#f2d36b', '#b8cf78', '#e88c6c']), 0.45)));
      }
    }
    return { grup: g, w: r * 2, d: r * 2, h: h + 0.03 };
  },

  /** Biblo: üst üste dengelenmiş taşlar ya da halka heykelcik. */
  figur(k, maxH) {
    const g = new T.Group();
    if (k.rnd() < 0.5) {
      let y = 0, r = k.ara(0.03, 0.036);
      const parcalar = [];
      for (let i = 0; i < 3; i++) {
        const s = new T.SphereGeometry(r, 18, 12);
        s.scale(1, 0.58, 0.9);
        s.translate(k.ara(-0.003, 0.003), y + r * 0.58, 0);
        parcalar.push(s);
        y += r * 1.12;
        r *= 0.74;
      }
      if (y > maxH) return null;
      g.add(k.mesh(dekorBirlestir(parcalar), k.seramik(k.sec(['#cfc8bd', '#b9c4cf', '#d8b4a0', '#f0e6d8']), 0.85)));
      return { grup: g, w: 0.075, d: 0.065, h: y };
    }
    const R = k.ara(0.03, 0.036);
    if (R * 2 + 0.03 > maxH) return null;
    const halka = new T.TorusGeometry(R, R * 0.38, 14, 32);
    halka.translate(0, R + R * 0.38 + 0.012, 0);
    const kaide = new T.CylinderGeometry(0.022, 0.026, 0.012, 24);
    kaide.translate(0, 0.006, 0);
    g.add(k.mesh(dekorBirlestir([halka, kaide]), k.seramik(k.pastel(), 0.5)));
    g.rotation.y = k.ara(-0.5, 0.5);
    return { grup: g, w: (R * 1.38) * 2, d: 0.06, h: R * 2.76 + 0.012 };
  },

  /** Saksıda pilea: yuvarlak yapraklar ince saplarda yukarı-dışarı açılıyor. */
  bitki(k, maxH) {
    const h = Math.min(maxH - 0.01, k.ara(0.13, 0.19));
    if (h < 0.11) return null;
    const pr = k.ara(0.036, 0.043), ph = k.ara(0.06, 0.075);
    const g = new T.Group();
    const saksi = dekorTorna([[0, 0], [pr * 0.78, 0], [pr, ph], [pr - 0.004, ph], [pr * 0.76, 0.006], [0, 0.006]], 26);
    g.add(k.mesh(saksi, k.seramik(k.sec(['#d8b4a0', '#f0e6d8', '#c9d6c3', '#e8c7c1']), 0.75)));
    const toprak = new T.CircleGeometry(pr - 0.004, 20);
    toprak.rotateX(-Math.PI / 2);
    toprak.translate(0, ph - 0.008, 0);
    g.add(k.mesh(toprak, k.duz('#5b4636', 1)));
    const saplar = [], yapraklar = [];
    const n = 7 + Math.floor(k.rnd() * 4);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + k.rnd() * 0.5;
      const egim = k.ara(0.2, 0.75);
      const boy = (h - ph) * k.ara(0.7, 1.0);
      const sap = new T.CylinderGeometry(0.0011, 0.0013, boy, 4);
      sap.translate(0, boy / 2, 0);
      sap.rotateZ(egim);
      sap.rotateY(a);
      sap.translate(0, ph - 0.008, 0);
      saplar.push(sap);
      const yr = k.ara(0.014, 0.022);
      const yaprak = new T.CircleGeometry(yr, 12);
      yaprak.rotateX(-Math.PI / 2 + k.ara(-0.5, 0.2));
      const ux = -Math.sin(egim) * boy, uy = Math.cos(egim) * boy;
      yaprak.translate(ux - yr * 0.6 * Math.sin(egim), uy, 0);
      yaprak.rotateY(a);
      yaprak.translate(0, ph - 0.008, 0);
      yapraklar.push(yaprak);
    }
    g.add(k.mesh(dekorBirlestir(saplar), k.duz('#6d8a58', 0.7)));
    g.add(k.mesh(dekorBirlestir(yapraklar), k.yaprak()));
    const yay = Math.max(pr, (h - ph) * 0.62);
    return { grup: g, w: Math.min(0.15, yay * 2), d: Math.min(0.13, yay * 2), h };
  },

  kaktus(k, maxH) {
    const h = Math.min(maxH - 0.01, k.ara(0.12, 0.16));
    if (h < 0.1) return null;
    const pr = 0.034, ph = 0.055;
    const g = new T.Group();
    const saksi = dekorTorna([[0, 0], [pr * 0.8, 0], [pr, ph], [pr - 0.004, ph], [0, ph - 0.006]], 24);
    g.add(k.mesh(saksi, k.seramik(k.sec(['#f0e6d8', '#d8b4a0']), 0.8)));
    const govde = new T.CapsuleGeometry(0.019, h - ph - 0.02, 6, 12);
    govde.translate(0, ph + (h - ph) / 2 - 0.004, 0);
    const kol = new T.CapsuleGeometry(0.01, 0.025, 4, 8);
    kol.rotateZ(0.5);
    kol.translate(0.024, ph + (h - ph) * 0.45, 0);
    g.add(k.mesh(dekorBirlestir([govde, kol]), k.duz('#7f9a6a', 0.75)));
    return { grup: g, w: pr * 2, d: pr * 2, h };
  },

  mum(k, maxH) {
    const h = k.ara(0.055, 0.095);
    if (h + 0.012 > maxH) return null;
    const r = k.ara(0.026, 0.034);
    const g = new T.Group();
    const govde = new T.CylinderGeometry(r, r, h, 28);
    govde.translate(0, h / 2, 0);
    g.add(k.mesh(govde, k.duz(k.sec(['#f4ede1', '#efdcae', '#e8c7c1', '#f0e6d8']), 0.86)));
    const fitil = new T.CylinderGeometry(0.0008, 0.0008, 0.01, 4);
    fitil.translate(0, h + 0.004, 0);
    g.add(k.mesh(fitil, k.duz('#2b2522', 0.9)));
    return { grup: g, w: r * 2, d: r * 2, h: h + 0.01 };
  },

  /** Kalemlik ve renkli kurşun kalemler. */
  kalemlik(k, maxH) {
    if (maxH < 0.13) return null;
    const r = 0.034, h = 0.09;
    const g = new T.Group();
    const kap = dekorTorna([[0, 0], [r, 0], [r, h], [r - 0.003, h], [r - 0.003, 0.004], [0, 0.004]], 26);
    g.add(k.mesh(kap, k.seramik(k.pastel(), 0.55)));
    const kalemler = [], uclar = [];
    const n = 5 + Math.floor(k.rnd() * 3);
    let enUst = h;
    for (let i = 0; i < n; i++) {
      const boy = Math.min(maxH - 0.012, k.ara(0.14, 0.18));
      const a = k.rnd() * Math.PI * 2, egim = k.ara(0.04, 0.2);
      const kalem = new T.CylinderGeometry(0.0036, 0.0036, boy, 6);
      kalem.translate(0, boy / 2, 0);
      const uc = new T.ConeGeometry(0.0036, 0.012, 6);
      uc.translate(0, boy + 0.006, 0);
      for (const p of [kalem, uc]) {
        p.rotateZ(egim);
        p.rotateY(a);
        p.translate(Math.cos(a) * 0.012, 0.005, Math.sin(a) * 0.012);
      }
      kalemler.push({ geo: kalem, renk: k.sec(DEKOR_PASTEL) });
      uclar.push(uc);
      enUst = Math.max(enUst, Math.cos(egim) * (boy + 0.012) + 0.005);
    }
    // Kalemler iki renk grubunda birleşiyor (çizim çağrısı az kalsın).
    const ikiRenk = [k.sec(DEKOR_KITAP), k.sec(DEKOR_PASTEL)];
    [0, 1].forEach((j) => {
      const parca = kalemler.filter((_, i) => i % 2 === j).map((q) => q.geo);
      if (parca.length) g.add(k.mesh(dekorBirlestir(parca), k.duz(ikiRenk[j], 0.55)));
    });
    g.add(k.mesh(dekorBirlestir(uclar), k.ahsap('#d9b48a')));
    return { grup: g, w: r * 2 + 0.03, d: r * 2 + 0.03, h: enUst };
  },

  /** Halka dizme oyuncağı. */
  halka(k, maxH) {
    if (maxH < 0.15) return null;
    const g = new T.Group();
    const kaide = new T.CylinderGeometry(0.04, 0.042, 0.014, 26);
    kaide.translate(0, 0.007, 0);
    const cubuk = new T.CylinderGeometry(0.0065, 0.0065, 0.11, 10);
    cubuk.translate(0, 0.069, 0);
    const bas = new T.SphereGeometry(0.014, 14, 10);
    bas.translate(0, 0.128, 0);
    g.add(k.mesh(dekorBirlestir([kaide, cubuk, bas]), k.ahsap('#e3c49c')));
    let y = 0.014;
    const renkler = ['#e8c7c1', '#efdcae', '#c9d6c3', '#c7d4e0', '#d9cde4'];
    for (let i = 0; i < 4; i++) {
      const tup = 0.011 - i * 0.001;
      const halka = new T.TorusGeometry(0.03 - i * 0.004, tup, 10, 24);
      halka.rotateX(-Math.PI / 2);
      halka.translate(0, y + tup, 0);
      g.add(k.mesh(halka, k.duz(renkler[i], 0.5)));
      y += tup * 2;
    }
    return { grup: g, w: 0.088, d: 0.088, h: 0.142 };
  },

  /** Ahşap küp bloklar: iki altta, biri üstte. */
  bloklar(k, maxH) {
    const a = 0.036;
    if (a * 2 + 0.005 > maxH) return null;
    const g = new T.Group();
    const yerler = [[-a * 0.55, a / 2, 0], [a * 0.55, a / 2, k.ara(-0.006, 0.006)], [k.ara(-0.006, 0.006), a * 1.5, 0]];
    yerler.forEach(([x, y, z], i) => {
      const b = yuvarlakKutu(a, a, a, 0.004, 1);
      b.rotateY(k.ara(-0.25, 0.25));
      b.translate(x, y, z);
      g.add(k.mesh(b, k.duz(DEKOR_PASTEL[(i * 3 + Math.floor(k.rnd() * 10)) % DEKOR_PASTEL.length], 0.7)));
    });
    return { grup: g, w: a * 2.3, d: a * 1.3, h: a * 2 };
  },

  top(k, maxH) {
    const r = 0.034;
    if (r * 2 > maxH) return null;
    const g = new T.Group();
    const s = new T.SphereGeometry(r, 24, 16);
    s.translate(0, r, 0);
    g.add(k.mesh(s, k.desenli('top', 0.45)));
    g.rotation.y = k.rnd() * Math.PI;
    return { grup: g, w: r * 2, d: r * 2, h: r * 2, top: true };
  },

  /** Oturan oyuncak ayı. */
  ayicik(k, maxH) {
    if (maxH < 0.12) return null;
    const g = new T.Group();
    const p = [];
    const kure = (r, x, y, z, sx = 1, sy = 1, sz = 1) => {
      const s = new T.SphereGeometry(r, 16, 12);
      s.scale(sx, sy, sz);
      s.translate(x, y, z);
      p.push(s);
    };
    kure(0.032, 0, 0.034, 0, 1, 1.12, 0.9);           // gövde
    kure(0.025, 0, 0.088, 0.004);                       // baş
    kure(0.009, -0.018, 0.108, 0); kure(0.009, 0.018, 0.108, 0);   // kulaklar
    kure(0.011, -0.031, 0.046, 0.012, 0.9, 1.4, 0.9);   // kollar
    kure(0.011, 0.031, 0.046, 0.012, 0.9, 1.4, 0.9);
    kure(0.013, -0.017, 0.011, 0.026, 1, 0.85, 1.3);    // bacaklar
    kure(0.013, 0.017, 0.011, 0.026, 1, 0.85, 1.3);
    const renk = k.sec(['#c9a27e', '#e3d0c2', '#d8b4a0']);
    g.add(k.mesh(dekorBirlestir(p), k.kumas(renk)));
    const burun = new T.SphereGeometry(0.0095, 12, 8);
    burun.scale(1, 0.8, 0.8);
    burun.translate(0, 0.083, 0.026);
    g.add(k.mesh(burun, k.kumas('#f0e6d8')));
    const gozler = [];
    for (const sx of [-1, 1]) {
      const goz = new T.SphereGeometry(0.0028, 8, 6);
      goz.translate(sx * 0.0095, 0.094, 0.025);
      gozler.push(goz);
    }
    g.add(k.mesh(dekorBirlestir(gozler), k.duz('#1d1a18', 0.35)));
    g.rotation.y = k.ara(-0.35, 0.35);
    return { grup: g, w: 0.08, d: 0.075, h: 0.118 };
  },

  /** Banyo şişeleri: pompalı, iki boy. */
  sise(k, maxH) {
    if (maxH < 0.15) return null;
    const g = new T.Group();
    let x = 0;
    const n = 1 + Math.floor(k.rnd() * 2);
    for (let i = 0; i < n; i++) {
      const h = k.ara(0.1, Math.min(0.14, maxH - 0.04)), r = k.ara(0.024, 0.03);
      const govde = dekorTorna([[0, 0], [r * 0.9, 0], [r, 0.006], [r, h * 0.82], [r * 0.5, h * 0.95], [r * 0.32, h], [0, h]], 24);
      govde.translate(x + r, 0, 0);
      const pompa = new T.CylinderGeometry(0.006, 0.006, 0.026, 10);
      pompa.translate(x + r, h + 0.013, 0);
      const ag = new T.BoxGeometry(0.022, 0.006, 0.008);
      ag.translate(x + r + 0.008, h + 0.026, 0);
      g.add(k.mesh(govde, k.seramik(k.pastel(), 0.3)));
      g.add(k.mesh(dekorBirlestir([pompa, ag]), k.duz('#f4f1ec', 0.35)));
      x += r * 2 + 0.008;
    }
    g.children.forEach((c) => c.position.x -= (x - 0.008) / 2);
    return { grup: g, w: x, d: 0.06, h: 0.17 };
  },

  /** Katlanmış havlu yığını. */
  havlu(k, maxH, _ic, maxW) {
    const n = 2 + Math.floor(k.rnd() * 2), kat = 0.034;
    if (n * kat > maxH || maxW < 0.15) return null;
    const g = new T.Group();
    for (let i = 0; i < n; i++) {
      const h = yuvarlakKutu(0.14, kat, 0.12, 0.012, 2);
      h.rotateY(k.ara(-0.05, 0.05));
      h.translate(k.ara(-0.004, 0.004), i * kat + kat / 2, 0);
      g.add(k.mesh(h, k.kumas(k.pastel())));
    }
    return { grup: g, w: 0.15, d: 0.125, h: n * kat };
  },

  /** El değirmeni (kahve köşesi). */
  ogutucu(k, maxH) {
    if (maxH < 0.15) return null;
    const g = new T.Group();
    const govde = yuvarlakKutu(0.07, 0.085, 0.07, 0.006, 1);
    govde.translate(0, 0.0425, 0);
    g.add(k.mesh(govde, k.ahsap('#b98c62')));
    const tepe = new T.CylinderGeometry(0.03, 0.034, 0.025, 24);
    tepe.translate(0, 0.0975, 0);
    const mil = new T.CylinderGeometry(0.003, 0.003, 0.02, 6);
    mil.translate(0, 0.12, 0);
    const kol = new T.BoxGeometry(0.06, 0.004, 0.008);
    kol.translate(0.03, 0.13, 0);
    g.add(k.mesh(dekorBirlestir([tepe, mil, kol]), k.metal()));
    const tutamak = new T.SphereGeometry(0.008, 12, 8);
    tutamak.translate(0.058, 0.138, 0);
    g.add(k.mesh(tutamak, k.ahsap('#6e4a2e')));
    g.rotation.y = k.ara(-0.6, 0.2);
    return { grup: g, w: 0.1, d: 0.08, h: 0.146 };
  },
};

/** data: URI → File (sepete dosya olarak eklemek için; fetch(data:) mağaza
    güvenlik politikasına takılabildiği için elle çözülüyor). */
function dataUrlToFile(dataUrl, ad) {
  const [bas, b64] = dataUrl.split(',');
  const tur = /data:([^;]+)/.exec(bas)?.[1] || 'image/jpeg';
  const ikili = atob(b64);
  const dizi = new Uint8Array(ikili.length);
  for (let i = 0; i < ikili.length; i++) dizi[i] = ikili.charCodeAt(i);
  return new File([dizi], ad, { type: tur });
}

/** İki matris gözle ayırt edilemeyecek kadar yakın mı (kamera durdu mu)? */
function matrisYakin(a, b, eps = 1e-6) {
  const x = a.elements, y = b.elements;
  for (let i = 0; i < 16; i++) if (Math.abs(x[i] - y[i]) > eps) return false;
  return true;
}

/** UV'si olmayan geometriye kutu izdüşümlü UV üretir. */
function applyBoxUV(object, unitsPerTile) {
  object.traverse((o) => {
    if (!o.isMesh || o.geometry.attributes.uv) return;
    const pos = o.geometry.attributes.position;
    const nor = o.geometry.attributes.normal;
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      const nx = Math.abs(nor ? nor.getX(i) : 0);
      const ny = Math.abs(nor ? nor.getY(i) : 1);
      const nz = Math.abs(nor ? nor.getZ(i) : 0);
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      let u, v;
      if (nx >= ny && nx >= nz)      { u = z; v = y; }
      else if (ny >= nx && ny >= nz) { u = x; v = z; }
      else                           { u = x; v = y; }
      uv[i * 2] = u / unitsPerTile;
      uv[i * 2 + 1] = v / unitsPerTile;
    }
    o.geometry.setAttribute('uv', new T.BufferAttribute(uv, 2));
  });
}

/* ==========================================================================
   5) KISITLI ORBIT KONTROLÜ
   Müşteri duvarı ±30° yatay, ±18° dikey aralıkta inceleyebilir.
   ========================================================================== */

class LimitedOrbit {
  constructor(camera, dom) {
    this.camera = camera;
    this.dom = dom;
    this.target = new T.Vector3();
    this.enabled = true;

    this.azimuth = 0;
    this.polar = Math.PI / 2;
    this.distance = 4;

    this.minAzimuth = -MAX_AZIMUTH;   // -30°
    this.maxAzimuth = MAX_AZIMUTH;    // +30°
    this.minPolar = Math.PI / 2 - MAX_POLAR_UP;   // ≈ -18°
    this.maxPolar = Math.PI / 2 + MAX_POLAR_DOWN; // ≈ +8°  (aşağıdan bakış sınırlı)
    this.minDistance = 1.4;
    this.maxDistance = 9;

    this._goal = { az: 0, po: Math.PI / 2, di: 4 };
    /* Hedefin de bir "gideceği yeri" var: parmakların arasına yakınlaşırken
       hedef, mesafeyle AYNI yumuşatmayla kayıyor. İkisi aynı oranda
       ilerlediği için parmakların altındaki nokta ekranda yerinde kalıyor. */
    this._goalT = new T.Vector3();
    /** Odayı tam sığdıran mesafe; yakınlık oranı ve kaydırma sınırı buna göre. */
    this.fitDistance = 4;
    this._pointers = new Map();
    this._lastPinch = 0;
    this._lastMid = null;
    this._panning = false;
    this.onChange = null;

    /* Kaydırma sınırları iki uçta tanımlı: oda tam sığmışken (uzak) dar,
       en yakında duvarın tamamı. Arası mesafeye göre açılıyor. Eskiden tek,
       dar bir sınır vardı: yakınlaşınca kenardaki raflara ulaşılamıyordu. */
    this.panUzak = { halfX: 0, minY: 0, maxY: 0 };
    this.panYakin = { halfX: 0, minY: 0, maxY: 0 };
    this.panBase = new T.Vector3();
    this._ray = new T.Raycaster();
    this._duvar = new T.Plane(new T.Vector3(0, 0, 1), 0);
    this._ndc = new T.Vector2();
    this._nokta = new T.Vector3();

    this._bind();
  }

  /** Kaydırma sınırlarını (uzak ve yakın uç) ve merkez noktayı ayarlar. */
  setPanBounds(base, uzak, yakin) {
    this.panBase.copy(base);
    this.panUzak = uzak;
    this.panYakin = yakin || uzak;
    this._kisitla(this.target, this.distance);
    this._kisitla(this._goalT, this._goal.di);
  }

  /** `d` mesafesinde hedefin gidebileceği dikdörtgen. */
  _sinir(d) {
    const f = this.fitDistance;
    const aralik = f - this.minDistance;
    const k = aralik > 1e-6 ? clamp((f - d) / aralik, 0, 1) : 0;
    /* Oda-sığdırma mesafesinin ötesinde sınır merkeze doğru kapanıyor:
       en uzağa çıkan müşteri odayı ortalanmış görüyor, kaydırdığı yerde
       asılı kalmıyor. */
    const disari = this.maxDistance - f;
    const m = disari > 1e-6 ? 1 - clamp((d - f) / disari, 0, 1) : 1;
    const u = this.panUzak, y = this.panYakin, b = this.panBase;
    const halfX = (u.halfX + (y.halfX - u.halfX) * k) * m;
    const minY = u.minY + (y.minY - u.minY) * k;
    const maxY = u.maxY + (y.maxY - u.maxY) * k;
    return {
      minX: b.x - halfX, maxX: b.x + halfX,
      minY: b.y + (minY - b.y) * m,
      maxY: b.y + (maxY - b.y) * m,
    };
  }

  _kisitla(v, d) {
    const s = this._sinir(d);
    v.x = clamp(v.x, s.minX, s.maxX);
    v.y = clamp(v.y, s.minY, s.maxY);
  }

  resetPan() {
    this.target.copy(this.panBase);
    this._goalT.copy(this.panBase);
  }

  /** Ekran pikselini duvar düzleminde metreye çevirip hedefi kaydırır. */
  _pan(dxPx, dyPx) {
    const h = this.dom.clientHeight || 1;
    // Görüş alanının hedef mesafesindeki dikey açıklığı
    const spanY = 2 * this.distance * Math.tan((this.camera.fov * Math.PI) / 360);
    const k = spanY / h;
    // Kaydırma doğrudan: parmağın altındaki duvar parmakla birlikte gitsin.
    const dx = -dxPx * k, dy = dyPx * k;
    this.target.x += dx; this.target.y += dy;
    this._goalT.x += dx; this._goalT.y += dy;
    this._kisitla(this.target, this.distance);
    this._kisitla(this._goalT, this._goal.di);
  }

  /** Ekrandaki noktanın altındaki duvar noktası (z = 0 düzlemi). */
  _duvarNoktasi(clientX, clientY) {
    const r = this.dom.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    this._ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.camera.updateMatrixWorld();
    this._ray.setFromCamera(this._ndc, this.camera);
    return this._ray.ray.intersectPlane(this._duvar, this._nokta);
  }

  /**
   * Ekrandaki bir noktaya doğru yakınlaşır (oran < 1 yaklaşır).
   *
   * Eskiden yakınlaşma hep duvarın ortasına oluyordu; telefonda rafa
   * yaklaşmak için önce kıstırıp sonra iki parmakla kaydırmak gerekiyordu.
   * Artık parmakların (ya da imlecin) altındaki nokta yerinde kalıyor:
   * hedef o noktaya, mesafeyle aynı oranda yaklaşıyor.
   */
  zoomAt(clientX, clientY, oran) {
    const eski = this._goal.di;
    const yeni = clamp(eski * oran, this.minDistance, this.maxDistance);
    if (yeni === eski) return;
    const p = this._duvarNoktasi(clientX, clientY);
    if (p) {
      const k = yeni / eski;
      this._goalT.x = p.x + (this._goalT.x - p.x) * k;
      this._goalT.y = p.y + (this._goalT.y - p.y) * k;
    }
    this._goal.di = yeni;
    this._kisitla(this._goalT, yeni);
  }

  _bind() {
    const dom = this.dom;

    // Sağ tık menüsünü kapat — sağ tık kaydırma için kullanılıyor.
    dom.addEventListener('contextmenu', (e) => e.preventDefault());

    dom.addEventListener('pointerdown', (e) => {
      if (!this.enabled) return;
      // Orta tuş (1) ve sağ tuş (2) kaydırır, sol tuş döndürür.
      const pan = e.button === 1 || e.button === 2 || e.shiftKey;
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, pan });
      try { dom.setPointerCapture(e.pointerId); } catch { /* yoksay */ }
      if (pan) { this._panning = true; dom.classList.add('kz-panning'); }
      else dom.classList.add('kz-grabbing');
    });

    dom.addEventListener('pointermove', (e) => {
      if (!this._pointers.has(e.pointerId)) return;
      const prev = this._pointers.get(e.pointerId);
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, pan: prev.pan });
      if (!this.enabled) return;

      if (this._pointers.size === 1) {
        const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
        if (prev.pan) {
          this._pan(dx, dy);
        } else {
          this._goal.az = clamp(this._goal.az - dx * 0.0052, this.minAzimuth, this.maxAzimuth);
          this._goal.po = clamp(this._goal.po - dy * 0.0040, this.minPolar, this.maxPolar);
        }
      } else if (this._pointers.size === 2) {
        // İki parmak: kıstırma yakınlaştırır, birlikte kayma görüntüyü taşır.
        const [a, b] = [...this._pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        // Parmakların arasına yakınlaş (bkz. zoomAt).
        if (this._lastPinch && dist > 0) this.zoomAt(mid.x, mid.y, this._lastPinch / dist);
        if (this._lastMid) this._pan(mid.x - this._lastMid.x, mid.y - this._lastMid.y);
        this._lastPinch = dist;
        this._lastMid = mid;
      }
    });

    const end = (e) => {
      this._pointers.delete(e.pointerId);
      if (this._pointers.size < 2) { this._lastPinch = 0; this._lastMid = null; }
      if (this._pointers.size === 0) {
        this._panning = false;
        dom.classList.remove('kz-grabbing', 'kz-panning');
      }
    };
    // Bırakma olayını pencereden dinliyoruz: kullanıcı tuvalin dışına sürükleyip
    // bıraktığında işaretçi kayıtta kalırsa sonraki hareketler iki parmak
    // sanılır ve döndürme kalıcı olarak bozulur.
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);

    dom.addEventListener('wheel', (e) => {
      if (!this.enabled) return;
      e.preventDefault();
      const k = Math.exp(clamp(e.deltaY, -120, 120) * 0.0012);
      this.zoomAt(e.clientX, e.clientY, k);   // imlecin altına yakınlaş
    }, { passive: false });
  }

  flyTo(az, po, di, ms = 620) {
    this._goal.az = clamp(az, this.minAzimuth, this.maxAzimuth);
    this._goal.po = clamp(po, this.minPolar, this.maxPolar);
    if (di != null) this._goal.di = clamp(di, this.minDistance, this.maxDistance);
    this._tween = {
      t0: performance.now(), ms,
      from: { az: this.azimuth, po: this.polar, di: this.distance },
      to: { ...this._goal },
    };
  }

  setDistance(d, immediate) {
    this._goal.di = clamp(d, this.minDistance, this.maxDistance);
    if (immediate) {
      this.distance = this._goal.di;
      this._kisitla(this._goalT, this.distance);
      this.target.x = this._goalT.x; this.target.y = this._goalT.y;
    }
  }

  /** Dikey sınırlar değiştiğinde mevcut açıyı yeni aralığa çeker. */
  clampPolar() {
    this._goal.po = clamp(this._goal.po, this.minPolar, this.maxPolar);
    this.polar = clamp(this.polar, this.minPolar, this.maxPolar);
  }

  update() {
    if (this._tween) {
      const k = easeOutCubic(clamp((performance.now() - this._tween.t0) / this._tween.ms, 0, 1));
      this.azimuth = lerp(this._tween.from.az, this._tween.to.az, k);
      this.polar = lerp(this._tween.from.po, this._tween.to.po, k);
      this.distance = lerp(this._tween.from.di, this._tween.to.di, k);
      if (k >= 1) this._tween = null;
    } else {
      // Yumuşatma (damping)
      this.azimuth += (this._goal.az - this.azimuth) * 0.13;
      this.polar += (this._goal.po - this.polar) * 0.13;
      this.distance += (this._goal.di - this.distance) * 0.13;
    }
    /* Hedef mesafeyle aynı oranda ilerliyor. Uzaklaşırken sınır daralıyor;
       gidilecek yer de o sınıra çekiliyor, böylece görüntü odaya kendiliğinden
       ve yumuşakça geri dönüyor. */
    this._kisitla(this._goalT, this._goal.di);
    this.target.x += (this._goalT.x - this.target.x) * 0.13;
    this.target.y += (this._goalT.y - this.target.y) * 0.13;

    const sp = Math.sin(this.polar);
    this.camera.position.set(
      this.target.x + this.distance * sp * Math.sin(this.azimuth),
      this.target.y + this.distance * Math.cos(this.polar),
      this.target.z + this.distance * sp * Math.cos(this.azimuth),
    );
    this.camera.lookAt(this.target);
    this.onChange?.();
  }
}

/* ==========================================================================
   6) ANA UYGULAMA
   ========================================================================== */

class KreazStudio {
  constructor(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.wallW = snap5(clamp(cfg.wall.width, cfg.wall.min, cfg.wall.max));
    this.wallH = snap5(clamp(cfg.wall.height, cfg.wall.minHeight, cfg.wall.maxHeight));
    this.shelves = [];
    this.selected = null;
    this.activeColor = cfg.colors[0].id;
    this.wallColorId = (cfg.wallColors && cfg.wallColors[0]) ? cfg.wallColors[0].id : 'plaster';
    this.lastSay = null;
    this.history = [];
    this.drag = null;
    this.night = !!cfg.night;
    // Işık üç durumlu: gündüz → altın saat → gece. İkisi aynı anda açık olmaz.
    this.altin = false;
    // Raflardaki dekor eşyaları (vazo düğmesi).
    this.dekorAcik = false;
    // Daha önce e-posta verdiyse tekrar sormuyoruz; tasarımı kaydetmeye
    // sessizce devam ediyoruz.
    try { this.email = localStorage.getItem('kz-mail') || null; } catch { this.email = null; }
    this.emailCaptured = !!this.email;
    /* "İstemiyorum" kararı artık YALNIZCA bu oturum için geçerli.
       Eskiden kalıcıydı ve şöyle bir tuzak vardı: e-posta alanı baloncuğun,
       baloncuk da asistanın içinde olduğu için input'a tıklamak asistanın
       "dokununca kapat" dinleyicisine kabarıyor, form o tarayıcıda SONSUZA
       KADAR kapanıyordu. Artık hem kabarma durduruldu (bkz. bindEvents)
       hem de karar kalıcı değil. Eski kurbanların anahtarını temizliyoruz. */
    this.emailDismissed = false;
    try { localStorage.removeItem('kz-mail-dismiss'); } catch { /* yoksay */ }
    this.mailAskIdx = 0;
    this.designId = 'KRZ-' + uid().toUpperCase();

    this.products = cfg.products.map((p) => ({
      ...p,
      priceCents: p.priceCents != null ? p.priceCents : Math.round((p.price || 0) * 100),
    }));
    this.productById = new Map(this.products.map((p) => [p.id, p]));
    this.colorById = new Map(cfg.colors.map((c) => [c.id, c]));
  }

  /* ---------- Shopify varyant / fiyat çözümü ---------- */

  /**
   * (ürün, renk) çiftine karşılık gelen Shopify varyantı.
   * Ürünün tek seçeneği renk olmayabilir (ör. "Karbon Mat / 30 cm"); bu yüzden
   * önce seçenek değerlerinde tam eşleşme, sonra varyant başlığında arama yapılır.
   */
  variantFor(productId, colorId) {
    const direct = this.cfg.variants?.[productId]?.[colorId];
    if (direct) return { id: direct, price: null };

    const list = this.cfg.productVariants?.[productId];
    if (!Array.isArray(list) || !list.length) return null;
    const color = this.colorById.get(colorId);
    if (!color) return null;

    const norm = (s) => (s || '').trim().toLocaleLowerCase('tr');
    const want = norm(color.shopifyOption || color.name);
    if (!want) return null;

    const exact = list.find((v) =>
      (v.options || []).some((o) => norm(o) === want) || norm(v.title) === want);
    if (exact) return exact;

    return list.find((v) => norm(v.title).includes(want)) || null;
  }

  /** Varyant fiyatı varsa onu, yoksa ürünün liste fiyatını kuruş olarak döndürür. */
  priceFor(productId, colorId) {
    const v = this.variantFor(productId, colorId);
    if (v && typeof v.price === 'number') return v.price;
    return this.productById.get(productId).priceCents;
  }

  /**
   * Birim fiyatın müşteriye gösterilecek hali.
   *
   * Müşteri hangi kademedeyse birim fiyat da o kademeye göre yazılır: önce
   * indirimli tutar, hemen sağında üzeri çizili liste fiyatı. Kademe atlayınca
   * bütün kartlar ve liste satırları birlikte düşer — kazanç soyut bir yüzde
   * değil, rafın üzerindeki rakam olarak görünür.
   *
   * @param {number} [rate] Hazır hesaplanmış indirim oranı (tekrar tekrar
   *   `pricing()` çağırmamak için; verilmezse güncel oran kullanılır).
   */
  unitPriceHtml(productId, colorId, rate) {
    const list = this.priceFor(productId, colorId);
    const r = rate ?? this.pricing().discountRate;
    if (!r) return `<span class="kz-price-now">${this.money(list)}</span>`;
    const now = Math.round(list * (1 - r));
    // Oran rozeti: fiyatın neden düştüğünü kartın üstünde söylüyor. Yalnızca
    // katalog kartında görünür (bkz. .kz-price-off), raf listesinde gizli.
    return `<span class="kz-price-now">${this.money(now)}</span>`
      + `<s class="kz-price-was">${this.money(list)}</s>`
      + `<b class="kz-price-off">%${Math.round(r * 100)}</b>`;
  }

  /* ---------- grid yardımcıları ---------- */
  get cols() { return Math.round(this.wallW / 5); }
  get rows() { return Math.round(this.wallH / 5); }

  cellToWorld(col, row, units) {
    return {
      x: -(this.wallW / 100) / 2 + (col + units / 2) * U,
      y: (row + SHELF_ROWS / 2) * U,
    };
  }

  fits(col, row, units, ignoreId) {
    // En alttaki sıralar süpürgeliğin hizası (bkz. ALT_SIRA).
    if (col < 0 || row < ALT_SIRA) return false;
    if (col + units > this.cols || row + SHELF_ROWS > this.rows) return false;
    /* Oda eşyası da dolu hücredir: tabandan `rows` yüksekliğe kadar olan
       bandı kaplar. Böylece rafı yatağın ya da tezgahın üstüne koymaya
       çalışmak, başka bir rafın üstüne koymakla aynı uyarıyı veriyor —
       ayrı bir çarpışma koduna gerek yok. */
    const p = this.prop;
    if (p && p.duvar === 'arka'
        && row < p.rows && col < p.t + p.cols && p.t < col + units) return false;
    for (const s of this.shelves) {
      if (s.id === ignoreId) continue;
      const overlapX = col < s.col + s.units && s.col < col + units;
      const overlapY = row < s.row + SHELF_ROWS && s.row < row + SHELF_ROWS;
      if (overlapX && overlapY) return false;
    }
    return true;
  }

  /** Yeni raf için doğal görünen boş bir yer bulur (son rafın yanına yaslar). */
  /* ---------- Ürün sayfasından gelen hazır tasarım ---------- */

  /**
   * `?kz=` parametresini raf listesine çevirir.
   *
   * Biçim: `renk:ölçü:adet` — virgülle birden fazla kalem yazılabilir.
   *   ?kz=napoli-sun:small:3              → 3 adet 30 cm Napoli Sun
   *   ?kz=napoli-sun:small:2,carbon:large:1  → karışık set
   *
   * Renk hem stüdyo kimliğiyle (`napoli`) hem mağaza adıyla (`napoli-sun`,
   * `Napoli Sun`) yazılabilir; ölçü `small/medium/large` ya da `30/45/60`.
   * Tanınmayan kalem sessizce atlanır: bozuk bir bağlantı stüdyoyu açmasın.
   */
  parsePreset(raw) {
    const norm = (s) => String(s ?? '').trim().toLocaleLowerCase('tr').replace(/[\s_\-]/g, '');
    const colorOf = (t) => {
      const k = norm(t);
      if (!k) return null;
      const c = this.cfg.colors.find((x) => norm(x.id) === k
        || norm(x.name) === k
        || norm(x.shopifyOption) === k);
      return c ? c.id : null;
    };
    const sizeOf = (t) => {
      const k = norm(t);
      if (!k) return null;
      const p = this.products.find((x) => norm(x.id) === k
        || norm(x.name) === k
        || String(x.cm) === k);
      return p ? p.id : null;
    };

    const out = [];
    for (const part of String(raw).split(',')) {
      const bits = part.split(':');
      const colorId = colorOf(bits[0]);
      const productId = sizeOf(bits[1]);
      if (!colorId || !productId) continue;
      out.push({ colorId, productId, count: clamp(parseInt(bits[2], 10) || 1, 1, 40) });
    }
    return out;
  }

  /**
   * Hazır tasarımı duvara kurar.
   *
   * Müşteri ürün sayfasında dizilmiş bir set gördü ve "stüdyoda dene" dedi;
   * duvarda gördüğünün aynısını bulmalı. Raflar bu yüzden BİTİŞİK ve
   * ortalanmış tek bir blok hâlinde yerleşir — aralarında boşluk bırakmak
   * seti dağınık, kurgusuz gösteriyordu.
   *
   * Sütun sayısı `floor(√n)`: 3 → tek sütun, 4 → 2×2, 6 → 2×3, 9 → 3×3.
   * Blok duvara sığmazsa sütun sayısı daraltılır/genişletilir.
   *
   * @returns {number} yerleştirilen raf sayısı
   */
  applyPreset(items) {
    const flat = [];
    for (const it of items) for (let i = 0; i < it.count; i++) flat.push(it);
    if (!flat.length) return 0;

    // Müşterinin önceki tasarımının üstüne yazıyoruz; geri alınabilir olsun.
    this.pushHistory();
    [...this.shelves].forEach((s) => this.removeShelf(s, true));

    const n = flat.length;
    const widest = Math.max(...flat.map((f) => this.productById.get(f.productId).units));

    let cols = Math.max(1, Math.floor(Math.sqrt(n)));
    while (cols > 1 && cols * widest > this.cols) cols--;
    while (cols < n && Math.ceil(n / cols) * SHELF_ROWS > this.rows) cols++;

    const rows = Math.ceil(n / cols);
    const hCells = rows * SHELF_ROWS;
    const col0 = Math.max(0, Math.round((this.cols - cols * widest) / 2));
    const row0 = clamp(
      Math.round(this.rows * 0.54 - hCells / 2), ALT_SIRA, Math.max(ALT_SIRA, this.rows - hCells));

    let placed = 0;
    flat.forEach((f, i) => {
      const p = this.productById.get(f.productId);
      const cx = i % cols;
      const cy = Math.floor(i / cols);
      // Son satır eksik kalabilir; o satırı kendi içinde ortalıyoruz ki blok
      // sağa doğru tek ayaklı kalmasın.
      const inRow = Math.min(cols, n - cy * cols);
      const rowPad = Math.floor(((cols - inRow) * widest) / 2);
      // Grid'de satır numarası yukarı doğru büyür; listeyi üstten doldurmak
      // için satır sırasını ters çeviriyoruz.
      const col = col0 + rowPad + cx * widest + Math.floor((widest - p.units) / 2);
      const row = row0 + (rows - 1 - cy) * SHELF_ROWS;

      const cell = this.fits(col, row, p.units) ? { col, row } : this.findSlot(p.units);
      if (!cell) return;

      const shelf = {
        id: uid(),
        productId: f.productId,
        colorId: f.colorId,
        units: p.units,
        col: cell.col,
        row: cell.row,
        object: this.makeShelfObject(f.productId, f.colorId),
      };
      this.shelfGroup.add(shelf.object);
      this.shelves.push(shelf);
      this.placeObject(shelf);
      placed++;
    });

    if (placed) this.activeColor = flat[0].colorId;
    // Raflar sırayla yerine otursun — hem oda ön ayarları hem ürün
    // sayfasından gelen hazır setler bu yoldan geçiyor.
    this.popIn(this.shelves.map((s) => s.object), 90);
    return placed;
  }

  /**
   * Oda ön ayarını kurar: desen + adet + renk kurgusu.
   *
   * Renk "çok renkli" ise SIRAYA göre değişiyor — aynı hattaki raflar aynı
   * renkte kalıyor, katlar birbirinden ayrışıyor. Rastgele dağıtmak dağınık
   * görünüyordu.
   */
  applyRoomLayout(oda, adet, renkModu) {
    // Duvarın boşaltılması ve geçmiş kaydı çağıranın işi (bkz. applyRoom):
    // eşya bu ikisinden SONRA kurulmalı ki ortaya oturabilsin.
    let konan = 0;
    for (const h of this.odaDuzeni(oda, adet)) {
      const p = this.productById.get(h.urun);
      if (!p) continue;
      const renk = (renkModu === 'cok' && h.sira % 2 === 1)
        ? (oda.renk2 || oda.renk) : oda.renk;
      const cell = this.fits(h.col, h.row, p.units)
        ? { col: h.col, row: h.row } : this.findSlot(p.units);
      if (!cell) continue;

      const shelf = {
        id: uid(), productId: h.urun, colorId: renk, units: p.units,
        col: cell.col, row: cell.row,
        object: this.makeShelfObject(h.urun, renk),
      };
      this.shelfGroup.add(shelf.object);
      this.shelves.push(shelf);
      this.placeObject(shelf);
      konan++;
    }

    this.activeColor = oda.renk;
    // Oda önerisinde adım aralığı biraz daha uzun: müşteri rafların tek tek
    // dizilmesini izliyor, bu an tasarımın "kurulduğu" an.
    this.popIn(this.shelves.map((s) => s.object), 130);
    return konan;
  }

  /**
   * Adres çubuğundaki hazır tasarımı uygular ve parametreyi siler.
   * Parametre kalsaydı sayfa her yenilendiğinde müşterinin o sırada yaptığı
   * tasarımı silip seti yeniden kurardı.
   */
  applyUrlPreset() {
    const m = /[?&]kz=([^&]+)/.exec(location.search);
    if (!m) return 0;

    let placed = 0;
    try {
      placed = this.applyPreset(this.parsePreset(decodeURIComponent(m[1])));
    } catch (err) {
      console.warn('[Kreaz Studio] hazır tasarım okunamadı', err);
    }

    try {
      const url = new URL(location.href);
      url.searchParams.delete('kz');
      history.replaceState(null, '', url);
    } catch { /* eski tarayıcı — adres kalsın, zararsız */ }

    return placed;
  }

  /* ---------- Tasarımı adrese sığdır (paylaşım ve e-posta bunun üstünde) ---------- */

  /**
   * Tasarımı kompakt bir koda çevirir.
   *
   * Sunucu yok: tasarımın tamamı adresin içinde taşınır, yani link süresiz
   * çalışır ve kimsenin bir şey saklaması gerekmez.
   *
   *   1 bayt  sürüm
   *   1 bayt  sütun (wallW/5)
   *   1 bayt  satır (wallH/5)
   *   1 bayt  duvar rengi indeksi | altın saat (bit 6) | gece (bit 7)
   *   1 bayt  raf adedi
   *   3 bayt  her raf: [ürün<<5 | renk<<2], sütun, satır
   *
   * 9 raflık tasarım = 32 bayt → base64url ~43 karakter.
   */
  encodeDesign() {
    const wc = (this.cfg.wallColors || []).findIndex((w) => w.id === this.wallColorId);
    const n = Math.min(this.shelves.length, 255);
    const b = [2, this.cols, this.rows,
      (Math.max(0, wc) & 0x3f) | (this.night ? 0x80 : 0) | (this.altin ? 0x40 : 0), n];

    for (let i = 0; i < n; i++) {
      const s = this.shelves[i];
      const pi = this.products.findIndex((p) => p.id === s.productId);
      const ci = this.cfg.colors.findIndex((c) => c.id === s.colorId);
      b.push(((Math.max(0, pi) & 7) << 5) | ((Math.max(0, ci) & 7) << 2), s.col & 255, s.row & 255);
    }

    /* Sürüm 2: rafların ardından 3 bayt oda bilgisi.
       Paylaşılan linki açan kişi de aynı odayı ve eşyayı görsün — tasarım
       bağlamından kopuk bir duvar olarak gelmesin. Eski (sürüm 1) linkler
       okunmaya devam ediyor. */
    const odaIdx = this.room ? ROOMS.findIndex((o) => o.id === this.room.id) : -1;
    const duvarKodu = !this.prop ? 0 : (this.prop.duvar === 'sol' ? 2 : 1);
    b.push((odaIdx + 1) & 255, duvarKodu, (this.prop?.t || 0) & 255);

    let bin = '';
    for (const v of b) bin += String.fromCharCode(v);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * Kodu duvara geri kurar. Bozuk/eski kod hiçbir şeyi patlatmaz: 0 döner ve
   * müşteri boş stüdyo görür.
   *
   * @returns {number} yerleştirilen raf sayısı
   */
  decodeDesign(code) {
    let bin;
    try {
      bin = atob(String(code).replace(/-/g, '+').replace(/_/g, '/'));
    } catch { return 0; }
    const surum = bin.charCodeAt(0);
    if (bin.length < 5 || (surum !== 1 && surum !== 2)) return 0;

    const B = (i) => bin.charCodeAt(i);
    const cols = B(1), rows = B(2), n = B(4);
    if (bin.length < 5 + n * 3) return 0;

    this.pushHistory();
    [...this.shelves].forEach((s) => this.removeShelf(s, true));

    // Duvar ölçüsü ve rengi önce: raflar bu ızgaraya yerleşecek.
    const w = snap5(clamp(cols * 5, this.cfg.wall.min, this.cfg.wall.max));
    const h = snap5(clamp(rows * 5, this.cfg.wall.minHeight, this.cfg.wall.maxHeight));
    if (w !== this.wallW || h !== this.wallH) {
      this.wallW = w; this.wallH = h;
      if (this.wInput) { this.wInput.value = w; this.hInput.value = h; }
    }
    const wcList = this.cfg.wallColors || [];
    // Duvar rengi 6 bit (eski linklerde 6. bit hiç kullanılmadı, aynen okunuyor).
    const wc = wcList[B(3) & 0x3f];
    if (wc) this.wallColorId = wc.id;
    this.night = !!(B(3) & 0x80);
    this.altin = !this.night && !!(B(3) & 0x40);
    this.syncLightBtn();

    /* Oda bilgisi raflardan ÖNCE kuruluyor: yüzey `buildRoom` içinde
       okunuyor, eşya da ızgarayı işgal ettiği için `fits` ona göre karar
       vermeli. Sürüm 1 linklerde bu baytlar yok, oda boş kalıyor. */
    const odaOfs = 5 + n * 3;
    let oda = null, duvarKodu = 0, propT = 0;
    if (surum === 2 && bin.length >= odaOfs + 3) {
      oda = B(odaOfs) ? ROOMS[B(odaOfs) - 1] : null;
      duvarKodu = B(odaOfs + 1);
      propT = B(odaOfs + 2);
    }
    this.room = oda || null;
    this.surface = oda?.yuzey || null;
    this.buildRoom();

    this.clearProp();
    this.propHidden = !duvarKodu;
    if (oda && duvarKodu) {
      this.buildProp(oda.esya);
      if (this.prop) {
        this.prop.duvar = duvarKodu === 2 ? 'sol' : 'arka';
        this.prop.t = clamp(propT, 0,
          Math.max(0, this.duvarUzunlugu(this.prop.duvar) - this.prop.cols));
        this.placeProp();
      }
    }
    this.syncPropBtn();

    let placed = 0;
    const kay = supurgelikPayi(Array.from({ length: n }, (_, i) => B(5 + i * 3 + 2)));
    for (let i = 0; i < n; i++) {
      const o = 5 + i * 3;
      const p = this.products[(B(o) >> 5) & 7];
      const c = this.cfg.colors[(B(o) >> 2) & 7];
      if (!p || !c) continue;
      const col = B(o + 1), row = B(o + 2) + kay;
      if (!this.fits(col, row, p.units)) continue;
      const shelf = {
        id: uid(), productId: p.id, colorId: c.id, units: p.units, col, row,
        object: this.makeShelfObject(p.id, c.id),
      };
      this.shelfGroup.add(shelf.object);
      this.shelves.push(shelf);
      this.placeObject(shelf);
      placed++;
    }
    if (placed) this.activeColor = this.shelves[this.shelves.length - 1].colorId;
    return placed;
  }

  /** Bu tasarımı açan tam adres. */
  designUrl() {
    const base = this.cfg.studioUrl || (location.origin + location.pathname);
    return `${base}?d=${this.encodeDesign()}`;
  }

  /**
   * Adresteki `?d=` tasarımını kurar ve parametreyi siler.
   * Parametre kalsaydı sayfa her yenilendiğinde müşterinin o sırada yaptığı
   * değişiklikler silinip paylaşılan tasarım geri gelirdi.
   */
  applyUrlDesign() {
    const m = /[?&]d=([^&]+)/.exec(location.search);
    if (!m) return 0;

    let placed = 0;
    try {
      placed = this.decodeDesign(decodeURIComponent(m[1]));
    } catch (err) {
      console.warn('[Kreaz Studio] paylaşılan tasarım okunamadı', err);
    }

    try {
      const url = new URL(location.href);
      url.searchParams.delete('d');
      history.replaceState(null, '', url);
    } catch { /* eski tarayıcı — zararsız */ }

    return placed;
  }

  findSlot(units) {
    const last = this.shelves[this.shelves.length - 1];
    if (last) {
      const tries = [
        [last.col + last.units, last.row],
        [last.col - units, last.row],
        [last.col, last.row + SHELF_ROWS],
        [last.col, last.row - SHELF_ROWS],
        [last.col + last.units, last.row + SHELF_ROWS],
        [last.col - units, last.row - SHELF_ROWS],
      ];
      for (const [c, r] of tries) if (this.fits(c, r, units)) return { col: c, row: r };
    }
    const prefRow = clamp(Math.round(this.rows * 0.52), ALT_SIRA, Math.max(ALT_SIRA, this.rows - SHELF_ROWS));
    const prefCol = Math.floor((this.cols - units) / 2);
    for (let dr = 0; dr < this.rows; dr++) {
      for (const row of dr === 0 ? [prefRow] : [prefRow + dr, prefRow - dr]) {
        if (row < 0 || row + SHELF_ROWS > this.rows) continue;
        for (let dc = 0; dc <= this.cols; dc++) {
          for (const col of dc === 0 ? [prefCol] : [prefCol + dc, prefCol - dc]) {
            if (this.fits(col, row, units)) return { col, row };
          }
        }
      }
    }
    return null;
  }

  /* ======================================================================
     BAŞLATMA
     ====================================================================== */
  /**
   * Tam ekran moduna geçir.
   * Kök öğeyi doğrudan <body> altına taşıyoruz: temanın sayfa sarmalayıcısında
   * transform/filter varsa `position: fixed` o sarmalayıcıya göre hesaplanır ve
   * stüdyo ekranı kaplayamaz. Taşımak bu riski tümüyle ortadan kaldırır.
   */
  goFullscreen() {
    document.documentElement.classList.add('kz-page');
    if (this.root.parentElement !== document.body) document.body.appendChild(this.root);
  }

  /**
   * Logo yükleyicisinin dolma oranı (0–1). Yalnız ileri gidiyor; CSS geçişi
   * yumuşatıyor. Maliyeti tek bir CSS değişkeni.
   */
  yukleme(oran) {
    const b = this.bootEl;
    if (!b) return;
    this._yuklemeOran = Math.max(this._yuklemeOran || 0, oran);
    b.classList.add('kz-boot-js');
    b.style.setProperty('--kz-boot', String(this._yuklemeOran));
  }

  /** Logo tam dolunca yükleyici söner ve DOM'dan çıkar. */
  yuklemeBitti() {
    const b = this.bootEl;
    if (!b) return;
    this.yukleme(1);
    setTimeout(() => b.classList.add('kz-done'), 240);
    setTimeout(() => { b.remove(); if (this.bootEl === b) this.bootEl = null; }, 900);
  }

  async boot() {
    if (this.cfg.fullscreen) this.goFullscreen();
    this.buildDOM();
    this.buildScene();
    this.yukleme(0.62);
    await this.loadModels();
    this.yukleme(0.84);
    this.buildRoom();
    this.restore();
    this.yukleme(0.92);
    // Ürün sayfasından "stüdyoda dene" ile gelindiyse set duvara kurulur.
    // Paylaşılan tam tasarım (?d=) ürün sayfasından gelen hazır setin (?kz=)
    // önüne geçer: biri linke basıp geldiyse tam olarak onu görmeli.
    this.sharedCount = this.applyUrlDesign();
    /* Ziyaretçi paylaşılan bir linkten mi geldi? Tek bayrak; asistanın
       karşılamasını ve WhatsApp'ın ilk sözünü buna göre değiştiriyoruz.
       Ek bir istek ya da hesaplama yok, hız etkisi sıfır. */
    this.fromShare = this.sharedCount > 0;
    this.presetCount = this.sharedCount ? 0 : this.applyUrlPreset();
    this.buildCatalog();
    this.buildColorPalette();
    this.refresh();
    this.startLoop();

    this.yuklemeBitti();
    // Ölçüm: nereden geldi? (Clarity'de "kz_giris" etiketiyle ayrılıyor)
    const giris = this.sharedCount ? 'paylaşılan link'
      : this.presetCount ? 'ürün sayfası' : (this.shelves.length ? 'devam eden tasarım' : 'yeni');
    this.clarityTag('kz_giris', giris);
    this.track('kz_studyo_acildi', { giris });
    if (this.sharedCount) this.track('kz_paylasilan_acildi');
    // Girişte küçük bir kamera hareketi — aracın 3D olduğunu anlatır
    this.orbit.azimuth = -0.52; this.orbit.polar = Math.PI / 2 - 0.22;
    // Hedef mesafe (fitCamera'nın kurduğu): anlık mesafe, tuvalin ilk
    // boyundan kalmış olabiliyor.
    this.orbit.flyTo(ACILIS_AZIMUT, ACILIS_POLAR, this.orbit._goal.di, 1500);

    setTimeout(() => this.say(
      this.sharedCount ? 'shared'
        : this.presetCount ? 'preset'
          : (this.shelves.length ? 'resume' : 'welcome')), 700);
    this.showHint('Raf kartına tıkla, duvara eklensin. Çift tıklama siler.');
    this.armIdleNudge();
    this.armEmailPrompt();
    // Rehber el: giriş kamera hareketi (1,5 sn) bittikten sonra.
    setTimeout(() => this.rehberBasla(), 2300);
  }

  /* ======================================================================
     ARAYÜZ
     ====================================================================== */
  buildDOM() {
    const c = this.cfg;
    this.root.classList.add('kz-root');
    // Sunucunun bastığı logo yükleyicisi silinmesin: dolmaya devam ediyor.
    const bootEl = this.root.querySelector('.kz-boot');
    this.root.innerHTML = `
      <div class="kz-stage">
        <canvas class="kz-canvas"></canvas>

        <div class="kz-stage-ui">
          <a class="kz-back" href="${c.backUrl || '/'}">
            ${ICONS.back}
            ${c.logoUrl ? `<img src="${c.logoUrl}" alt="${c.brandName}">` : ''}
            <span class="kz-back-label">${c.backLabel || 'Mağazaya dön'}</span>
          </a>

          <!-- Üst siyah fiyat çubuğu: tıklayınca tasarım listesi açılır -->
          <div class="kz-pricebar">
            <button class="kz-pill" type="button" aria-expanded="false">
              <span class="kz-pill-total">—</span>
              <span class="kz-pill-count">0 raf</span>
              <span class="kz-pill-save" hidden></span>
              <span class="kz-pill-chev">${ICONS.chevron}</span>
            </button>
            <div class="kz-pricepanel">
              <div class="kz-pricepanel-list"></div>
              <div class="kz-pricepanel-foot">
                <div class="kz-sum-row"><span>Ara toplam</span><span class="kz-sum-sub">—</span></div>
                <div class="kz-sum-row kz-save" hidden><span class="kz-save-label">İndirim</span><span class="kz-sum-disc">—</span></div>
                <div class="kz-sum-row kz-ship"><span>Kargo</span><span class="kz-sum-ship">—</span></div>
                <div class="kz-sum-row kz-sum-grand"><span>Toplam</span><span class="kz-sum-grand-value">—</span></div>
                <button class="kz-panel-clear" type="button">${ICONS.trash}<span>Tasarımı temizle</span></button>
              </div>
            </div>
          </div>

          <!-- Tek araç çubuğu: görünüm + duvar ölçüsü + duvar rengi + araçlar -->
          <!-- Araç çubuğu kapalıyken tek bir yuvarlak düğmedir; dokununca
               aşağı doğru açılır. Sürekli açık durması tasarım alanını
               gereksiz yere kaplıyordu. -->
          <div class="kz-toolbar" role="toolbar" aria-label="Tasarım araçları">
            <button class="kz-toolbar-toggle" type="button" aria-expanded="false" aria-label="Araçlar">
              ${ICONS.tools}
            </button>
            <div class="kz-toolbar-tools">
            <button class="kz-tool" data-view="left" title="Soldan 30°" aria-label="Soldan 30 derece">${ICONS.left}</button>
            <button class="kz-tool kz-on" data-view="front" title="Karşıdan" aria-label="Karşıdan">${ICONS.front}</button>
            <button class="kz-tool" data-view="right" title="Sağdan 30°" aria-label="Sağdan 30 derece">${ICONS.right}</button>

            <span class="kz-tool-sep"></span>

            <!-- Açılır kutular butonun içine konulamaz: iç içe <button> HTML
                 ayrıştırıcısında dış butonu kapatır ve araç çubuğu dağılır. -->
            <div class="kz-tool-wrap" data-pop="wall">
              <button class="kz-tool" type="button" title="Duvar ölçüsü" aria-haspopup="true" aria-expanded="false">
                ${ICONS.ruler}<span class="kz-tool-label kz-wall-readout">240×240</span>
              </button>
              <div class="kz-pop">
                <div class="kz-pop-title">Duvar ölçüsü (cm)</div>
                <div class="kz-dims">
                  <div class="kz-field">
                    <label for="kz-w">Genişlik</label>
                    <div class="kz-stepper">
                      <button type="button" data-dim="w" data-step="-5" aria-label="Genişliği azalt">−</button>
                      <input id="kz-w" type="number" inputmode="numeric" step="5">
                      <button type="button" data-dim="w" data-step="5" aria-label="Genişliği artır">+</button>
                    </div>
                  </div>
                  <div class="kz-field">
                    <label for="kz-h">Yükseklik</label>
                    <div class="kz-stepper">
                      <button type="button" data-dim="h" data-step="-5" aria-label="Yüksekliği azalt">−</button>
                      <input id="kz-h" type="number" inputmode="numeric" step="5">
                      <button type="button" data-dim="h" data-step="5" aria-label="Yüksekliği artır">+</button>
                    </div>
                  </div>
                </div>
                <div class="kz-presets">
                  <button class="kz-preset" type="button" data-wall="180,220">180×220</button>
                  <button class="kz-preset" type="button" data-wall="240,240">240×240</button>
                  <button class="kz-preset" type="button" data-wall="320,260">320×260</button>
                </div>
              </div>
            </div>

            <div class="kz-tool-wrap" data-pop="wallcolor">
              <button class="kz-tool" type="button" title="Duvar rengi" aria-haspopup="true" aria-expanded="false">
                <span class="kz-tool-dot kz-wallcolor-dot"></span>
              </button>
              <div class="kz-pop">
                <div class="kz-pop-title">Duvar rengi</div>
                <div class="kz-wallcolors"></div>
              </div>
            </div>

            <span class="kz-tool-sep"></span>

            <button class="kz-tool" data-act="undo" title="Geri al" aria-label="Geri al" disabled>${ICONS.undo}</button>
            <button class="kz-tool kz-tool-danger" data-act="clear" title="Duvarı temizle" aria-label="Duvarı temizle" disabled>${ICONS.trash}</button>            <button class="kz-tool" data-act="full" title="Tam ekran" aria-label="Tam ekran">${ICONS.expand}</button>
            </div>
          </div>

          <!-- Gece modu araç çubuğunun dışında, kendi düğmesinde: müşterinin
               en çok oynadığı ayar, menü açmadan tek dokunuşta olmalı. -->
          <!-- Üç durumlu ışık: gündüz → altın saat → gece → gündüz. Düğme hep
               BİR SONRAKİ durumu gösteriyor: gündüzken yarım güneş, altın
               saatteyken ay, gecedeyken güneş. -->
          <button class="kz-night-btn" data-act="night" type="button" title="Altın saat" aria-label="Altın saat ışığına geç">
            <span class="kz-night-golden">${ICONS.gunbatimi}</span>
            <span class="kz-night-moon">${ICONS.moon}</span>
            <span class="kz-night-sun">${ICONS.sun}</span>
          </button>

          <!-- Dekor: raflara vazo, kitap, bardak… Oda seçiliyse o odanın eşyaları. -->
          <button class="kz-decor-btn" data-act="decor" type="button"
                  title="Raflara eşya koy" aria-label="Raflara dekor eşyası koy"
                  aria-pressed="false">${ICONS.vazo}</button>

          <!-- Oda eşyaları: yalnızca bir oda seçiliyken görünür. Eşya bağlam
               veriyor ama tasarıma odaklanmak isteyen onu kapatabilmeli. -->
          <button class="kz-prop-btn" data-act="prop" type="button"
                  title="Oda eşyaları" aria-label="Oda eşyalarını göster/gizle"
                  aria-pressed="false">${ICONS.esya}</button>
          <!-- Asistan: tıklanınca son mesajı tekrarlar -->
          <div class="kz-assistant" role="button" tabindex="0" aria-label="Tasarım asistanı — son mesajı tekrarla">
            <div class="kz-assistant-avatar">${ICONS.bot}</div>
            <div class="kz-bubble"></div>
          </div>

          <!-- Fikir sor: asistanın altında, onunla aynı boyutta sabit bir
               yuvarlak. İlk raftan sonra çekmece gibi sola açılıp ne işe
               yaradığını kendi anlatır; simge tek başına anlaşılmıyordu.
               Dokununca doğrudan WhatsApp'a çıkar. -->
          <button class="kz-wa-btn" type="button" hidden
                  aria-label="Tasarımını WhatsApp'ta birine gönder, fikir sor">
            <span class="kz-wa-text"><span></span></span>
            <span class="kz-wa-icon">${ICONS.whatsapp}</span>
          </button>

          <!-- Oda ön ayarları: WhatsApp'ın altında, aynı boyutta. Boş duvar
               yerine "nereye koyacaksın?" sorusuyla başlamak için. -->
          <button class="kz-room-btn" type="button"
                  aria-label="Nereye koyacaksın? Oda seç"
                  aria-haspopup="dialog" aria-expanded="false">
            <span class="kz-wa-text"><span></span></span>
            <span class="kz-wa-icon">${ICONS.oda}</span>
          </button>

          <div class="kz-hint"></div>

          <!-- Ekleme düğmesi rayın üstünde, sağda ve kendi yuvarlağında durur.
               Rayla tek gövde olduğunda ray sıkışıyor, duraklar okunmuyordu. -->
          <button class="kz-drawer-open" type="button" aria-label="Raf ekle">
            ${ICONS.plus}
            <span class="kz-drawer-open-count" hidden></span>
          </button>

          <!-- Alt şerit: yalnızca avantaj rayı. Duraklar eşit aralıklı. -->
          <div class="kz-rail">
            <div class="kz-rail-msg"></div>
            <div class="kz-rail-track">
              <div class="kz-rail-fill" style="width:0%"></div>
              <div class="kz-rail-stops"></div>
            </div>
          </div>

          <div class="kz-obj-tools">
            <button class="kz-obj-tool" data-obj="copy" title="Kopyala" aria-label="Rafı kopyala">${ICONS.copy}</button>
            <button class="kz-obj-tool kz-danger" data-obj="del" title="Sil" aria-label="Rafı sil">${ICONS.trash}</button>
          </div>

        </div>
      </div>

      <div class="kz-scrim"></div>

      <aside class="kz-panel">
        <div class="kz-sheet-grip"></div>
        <div class="kz-panel-scroll">
          <div class="kz-block">
            <div class="kz-block-head">
              <h3>Renk</h3>
              <span class="kz-block-note kz-color-note">Yeni raflar için</span>
            </div>
            <div class="kz-colors"></div>
            <div class="kz-color-active"></div>
          </div>

          <div class="kz-block">
            <div class="kz-block-head">
              <h3>Raflar</h3>
              <span class="kz-block-note">Eklemek için tıkla</span>
            </div>
            <div class="kz-catalog"></div>
          </div>
        </div>

        <div class="kz-summary">
          <div class="kz-sum-total">
            <div class="kz-sum-total-label">Toplam<small>KDV dahil</small></div>
            <div class="kz-sum-total-value">—</div>
          </div>
          <button class="kz-cta" disabled>${ICONS.bag}<span>Tasarımını sepete ekle</span></button>
          <div class="kz-cta-note">Tasarımın sipariş notuna kaydedilir.</div>
        </div>
      </aside>

      <!-- Oda ön ayarları. Kendiliğinden açılmaz; yalnız "?" düğmesiyle
           gelir. Kart seçimi ANINDA uygulanır ve pencere açık kalır, böylece
           müşteri odalar arasında gezinip duvarın dönüşmesini izleyebilir. -->
      <div class="kz-room-scrim" hidden></div>
      <div class="kz-room-modal" role="dialog" aria-modal="true"
           aria-labelledby="kz-room-title" hidden>
        <button class="kz-room-close" type="button" aria-label="Kapat">${ICONS.close}</button>
        <h3 id="kz-room-title">Nereye koyacaksın?</h3>
        <p class="kz-room-sub">Odanı seç, hazır bir düzenle başlayalım.
          Sonrasında her şeyi değiştirebilirsin.</p>
        <div class="kz-room-grid"></div>

        <!-- Öneriyi kişiselleştiren iki segme: her oda için tek bir sabit
             öneri yerine 6 farklı kurgu çıkıyor. -->
        <div class="kz-room-opts">
          <div class="kz-room-opt">
            <label>Tasarım büyüklüğü</label>
            <div class="kz-seg" data-seg="boy">
              ${BOYLAR.map((b, i) => `<button type="button" data-val="${b.id}"
                class="${i === 1 ? 'kz-on' : ''}"><b>${b.ad}</b><small>${b.not}</small></button>`).join('')}
            </div>
          </div>
          <div class="kz-room-opt">
            <label>Renk</label>
            <div class="kz-seg" data-seg="renk">
              ${RENK_MODLARI.map((r, i) => `<button type="button" data-val="${r.id}"
                class="${i === 0 ? 'kz-on' : ''}"><b>${r.ad}</b></button>`).join('')}
            </div>
          </div>
        </div>

        <div class="kz-room-dims">
          <label>Duvarının ölçüsünü biliyor musun?</label>
          <div class="kz-room-dims-row">
            <input type="number" class="kz-room-w" step="5"
                   min="${this.cfg.wall.min}" max="${this.cfg.wall.max}"
                   inputmode="numeric" aria-label="Duvar genişliği (cm)">
            <span>×</span>
            <input type="number" class="kz-room-h" step="5"
                   min="${this.cfg.wall.minHeight}" max="${this.cfg.wall.maxHeight}"
                   inputmode="numeric" aria-label="Duvar yüksekliği (cm)">
            <span class="kz-room-unit">cm</span>
            <button type="button" class="kz-room-apply">Uygula</button>
          </div>
        </div>
        <small class="kz-room-warn">Oda seçince mevcut tasarımın değişir — geri alabilirsin.</small>
      </div>

      <div class="kz-sr" aria-live="polite"></div>
    `;
    // Yükleyici: sunucudan geldiyse aynısı, gelmediyse (yerel önizleme) yenisi.
    if (bootEl) this.root.appendChild(bootEl);
    else this.root.insertAdjacentHTML('beforeend', yukleyiciHTML());
    this.bootEl = this.root.querySelector('.kz-boot');
    this.yukleme(0.5);

    this.stageEl = this.root.querySelector('.kz-stage');
    this.canvas = this.root.querySelector('.kz-canvas');
    this.bubbleEl = this.root.querySelector('.kz-bubble');
    this.assistantEl = this.root.querySelector('.kz-assistant');
    this.objTools = this.root.querySelector('.kz-obj-tools');
    this.hintEl = this.root.querySelector('.kz-hint');
    this.srEl = this.root.querySelector('.kz-sr');
    this.priceBar = this.root.querySelector('.kz-pricebar');

    this.wInput = this.root.querySelector('#kz-w');
    this.hInput = this.root.querySelector('#kz-h');
    this.wInput.value = this.wallW;
    this.hInput.value = this.wallH;
    this.drawerBtn = this.root.querySelector('.kz-drawer-open');

    this.buildRail();
    this.buildWallColors();
    this.buildRooms();
    this.bindUI();
  }

  /**
   * Rayda gösterilen kademeler — tüm merdiven, sıfır noktası dahil.
   * İlk durak "1 raf"tır: müşteri ilk rafı bıraktığında ray hemen yeşile
   * döner, ilerleme duygusu ilk adımda başlar.
   */
  railTiers() {
    return this.cfg.tiers;
  }

  /** Merdivenin en üst kademesi — mesajlarda "maksimum avantaj" için. */
  topTier() {
    return this.cfg.tiers[this.cfg.tiers.length - 1];
  }

  /**
   * Avantaj rayındaki durakları bir kez kurar.
   * Konumlar eşit yüzdelerle veriliyor: duraklar arasındaki mesafe raf
   * adedine değil, kademe sırasına bağlı — böylece "2 raf sonra" ile
   * "10 raf sonra" arası aynı görünüyor ve ray okunur kalıyor.
   */
  buildRail() {
    const stops = this.root.querySelector('.kz-rail-stops');
    stops.innerHTML = '';
    const list = this.railTiers();
    list.forEach((t, i) => {
      const s = el('div', 'kz-stop');
      s.dataset.min = t.min;
      s.style.setProperty('--kz-at', `${(i / Math.max(1, list.length - 1)) * 100}%`);
      s.innerHTML = `<span class="kz-stop-dot"></span><span class="kz-stop-label">${t.short}</span>`;
      stops.appendChild(s);
    });
  }

  buildWallColors() {
    const wrap = this.root.querySelector('.kz-wallcolors');
    wrap.innerHTML = '';
    for (const w of this.cfg.wallColors || []) {
      const b = el('button', 'kz-wallcolor' + (w.id === this.wallColorId ? ' kz-on' : ''));
      b.type = 'button';
      b.dataset.wallcolor = w.id;
      b.title = w.name;
      b.setAttribute('aria-label', `Duvar rengi: ${w.name}`);
      b.style.background = w.hex;
      b.addEventListener('click', (e) => { e.stopPropagation(); this.pickWallColor(w.id); });
      wrap.appendChild(b);
    }
    this.syncWallColorDot();
  }

  syncWallColorDot() {
    const dot = this.root.querySelector('.kz-wallcolor-dot');
    if (dot) dot.style.background = this.wallColorHex();
  }

  syncWallReadout() {
    const r = this.root.querySelector('.kz-wall-readout');
    if (r) r.textContent = `${this.wallW}×${this.wallH}`;
  }

  pickWallColor(id) {
    this.wallColorId = id;
    this.root.querySelectorAll('.kz-wallcolor').forEach((b) => {
      b.classList.toggle('kz-on', b.dataset.wallcolor === id);
    });
    this.syncWallColorDot();
    // Yalnızca duvar malzemesinin rengini değiştirmek yeterli; oda geometrisi aynı.
    const hex = new T.Color(this.wallColorHex());
    this.roomGroup.traverse((o) => {
      if (o.isMesh && Array.isArray(o.material)) {
        o.material.forEach((m) => { if (m.userData?.isWall) m.color.copy(hex); });
      }
    });
    this.invalidate();
    this.persist();
    // Duvar rengi de sessiz — renk seçimiyle aynı sebeple.
  }

  /** Araç çubuğundaki açılır kutuları yönetir (aynı anda yalnızca biri açık). */
  openPop(name) {
    this.root.querySelectorAll('.kz-tool-wrap').forEach((t) => {
      const on = t.dataset.pop === name;
      t.classList.toggle('kz-open', on);
      t.querySelector('.kz-tool')?.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    if (name) this.setPriceOpen(false);
  }

  setPriceOpen(open) {
    this.priceBar.classList.toggle('kz-open', open);
    this.priceBar.querySelector('.kz-pill').setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.openPop(null);
  }

  /**
   * Mobil ürün çekmecesi. Masaüstünde panel zaten sabit olduğu için
   * bu durum yalnızca dar ekranda bir şey ifade eder.
   */
  /**
   * Duvar ölçüsünü uygular. Ölçü alanları ve oda ön ayarları aynı yoldan
   * geçsin diye metot oldu (eskiden bindUI içinde yerel bir kapanıştı).
   *
   * `history` false verilirse geçmişe yazmaz — oda ön ayarı ölçüyü ve rafları
   * TEK bir adımda değiştirdiği için geri al tuşu ikisini birlikte almalı.
   */
  setWallSize(rawW, rawH, { history = true } = {}) {
    const w = snap5(clamp(+rawW || this.wallW, this.cfg.wall.min, this.cfg.wall.max));
    const h = snap5(clamp(+rawH || this.wallH, this.cfg.wall.minHeight, this.cfg.wall.maxHeight));
    this.wInput.value = w; this.hInput.value = h;
    if (w === this.wallW && h === this.wallH) return false;
    if (history) {
      this.pushHistory();
      // Müşterinin girdiği ölçü (oda ön ayarı history:false ile geliyor).
      this.track('kz_duvar_olcusu', { olcu: `${w}×${h}` });
      this.clarityTag('kz_duvar', `${w}×${h} cm`);
    }

    /* Duvar MERKEZDEN büyüyor. Sütun numaraları sol kenardan sayıldığı için,
       genişlik değişince eklenen/çıkan sütunun yarısı kadar kaydırmazsak
       duvardaki her şey sola yapışıp kalıyordu. Yükseklikte kaydırma yok:
       zemin sabit, raflar yerden aynı hizada durmalı. */
    const eskiSutun = this.cols;
    this.wallW = w; this.wallH = h;
    const kayma = Math.round((this.cols - eskiSutun) / 2);
    if (kayma) {
      for (const s of this.shelves) {
        s.col = clamp(s.col + kayma, 0, Math.max(0, this.cols - s.units));
      }
      if (this.prop?.duvar === 'arka') {
        this.prop.t = clamp(this.prop.t + kayma, 0, Math.max(0, this.cols - this.prop.cols));
      }
    }

    this.buildRoom();
    this.reflowProp();
    this.reflowShelves();
    this.refresh();
    return true;
  }

  setDrawer(open) {
    this.root.classList.toggle('kz-drawer', open);
    this.drawerBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) { this.openPop(null); this.setPriceOpen(false); }
  }

  bindUI() {
    const r = this.root;

    /* ---------- duvar ölçüsü ---------- */
    const commitDims = () => this.setWallSize(this.wInput.value, this.hInput.value);
    this.wInput.addEventListener('change', commitDims);
    this.hInput.addEventListener('change', commitDims);
    r.querySelectorAll('[data-dim]').forEach((b) => b.addEventListener('click', (e) => {
      e.stopPropagation();
      const input = b.dataset.dim === 'w' ? this.wInput : this.hInput;
      input.value = +input.value + +b.dataset.step;
      commitDims();
    }));
    r.querySelectorAll('[data-wall]').forEach((b) => b.addEventListener('click', (e) => {
      e.stopPropagation();
      const [w, h] = b.dataset.wall.split(',');
      this.wInput.value = w; this.hInput.value = h;
      commitDims();
    }));
    // Açılır kutu içindeki tıklamalar kutuyu kapatmasın
    r.querySelectorAll('.kz-pop').forEach((p) => p.addEventListener('click', (e) => e.stopPropagation()));

    /* ---------- açılır kutular ---------- */
    r.querySelectorAll('.kz-tool-wrap').forEach((wrap) => {
      wrap.querySelector('.kz-tool').addEventListener('click', () => {
        this.openPop(wrap.classList.contains('kz-open') ? null : wrap.dataset.pop);
      });
    });

    /* ---------- fiyat çubuğu ---------- */
    r.querySelector('.kz-pill').addEventListener('click', () => {
      if (!this.shelves.length) return;
      this.setPriceOpen(!this.priceBar.classList.contains('kz-open'));
    });

    /* ---------- mobil ürün çekmecesi ---------- */
    this.drawerBtn.addEventListener('click', () => {
      this.setDrawer(!this.root.classList.contains('kz-drawer'));
    });
    r.querySelector('.kz-scrim').addEventListener('click', () => this.setDrawer(false));
    r.querySelector('.kz-sheet-grip').addEventListener('click', () => this.setDrawer(false));

    // Dışarı tıklayınca hepsini kapat
    document.addEventListener('pointerdown', (e) => {
      if (!this.root.isConnected) return;
      if (!e.target.closest?.('.kz-toolbar')) this.openPop(null);
      if (!e.target.closest?.('.kz-pricebar')) this.setPriceOpen(false);
    }, true);

    /* ---------- kamera görünümleri ---------- */
    r.querySelectorAll('[data-view]').forEach((b) => b.addEventListener('click', () => {
      r.querySelectorAll('[data-view]').forEach((x) => x.classList.remove('kz-on'));
      b.classList.add('kz-on');
      const az = { left: -MAX_AZIMUTH, front: 0, right: MAX_AZIMUTH }[b.dataset.view];
      this.orbit.resetPan();
      this.orbit.flyTo(az, Math.PI / 2 - 0.05, this.computeFitDistance());
    }));

    /* ---------- araçlar ---------- */
    r.querySelector('[data-act="full"]').addEventListener('click', () => {
      r.classList.toggle('kz-is-fullscreen');
      requestAnimationFrame(() => this.resize());
    });
    /* ---------- araç çubuğu aç/kapa ---------- */
    const toolbar = r.querySelector('.kz-toolbar');
    const toggle = r.querySelector('.kz-toolbar-toggle');
    const setTools = (open) => {
      toolbar.classList.toggle('kz-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) this.openPop(null);
    };
    toggle.addEventListener('click', () => setTools(!toolbar.classList.contains('kz-open')));
    // Bir araç kullanıldıktan sonra çubuk kapansın — ekranı kaplamasın
    toolbar.querySelectorAll('[data-view], [data-act]').forEach((b) => {
      b.addEventListener('click', () => setTimeout(() => setTools(false), 180));
    });
    document.addEventListener('pointerdown', (e) => {
      if (this.root.isConnected && !e.target.closest?.('.kz-toolbar')) setTools(false);
    }, true);

    r.querySelector('[data-act="night"]').addEventListener('click', () => this.cycleLight());
    r.querySelector('[data-act="decor"]').addEventListener('click', () => this.toggleDecor());
    r.querySelector('[data-act="prop"]').addEventListener('click', () => this.toggleProp());

    /* ---------- fikir sor (WhatsApp) ---------- */
    r.querySelector('.kz-wa-btn').addEventListener('click', () => this.shareWhatsApp());

    /* Stüdyodan çıkarken son tasarımı yetiştir (keepalive: sayfa gitse de
       istek tamamlanıyor). Yalnız pagehide yetmiyordu: telefonda uygulamadan
       çıkınca ya da sekme değiştirince çoğu zaman hiç tetiklenmiyor;
       güvenilir olan sekmenin gizlenmesi. Aynı tasarım iki kez yazılmıyor. */
    window.addEventListener('pagehide', () => { this.syncProfile(true, true); this.clarityDesign(true); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') return;
      this.syncProfile(true, true);
      this.clarityDesign(true);
    });

    r.querySelector('[data-act="undo"]').addEventListener('click', () => this.undo());
    const clearAll = () => {
      if (!this.shelves.length) return;
      // Geri alınabilir olsa da tek dokunuşla tüm tasarımı silmek ağır bir
      // sonuç; onay istiyoruz.
      if (!window.confirm(`Duvardaki ${this.shelves.length} raf silinecek. Emin misiniz?`)) return;
      this.pushHistory();
      [...this.shelves].forEach((s) => this.removeShelf(s, true));
      this.setPriceOpen(false);
      this.refresh();
      this.say('cleared');
    };
    r.querySelector('[data-act="clear"]').addEventListener('click', clearAll);
    // Mobilde araç çubuğundaki "temizle" gizli olduğu için buradan da erişilir
    r.querySelector('.kz-panel-clear').addEventListener('click', clearAll);

    r.querySelector('.kz-cta').addEventListener('click', () => this.checkout());

    /* ---------- seçili raf araçları ---------- */
    this.objTools.querySelector('[data-obj="del"]').addEventListener('click', () => {
      if (this.selected) { this.pushHistory(); this.removeShelf(this.selected); this.say('removed'); }
    });
    this.objTools.querySelector('[data-obj="copy"]').addEventListener('click', () => {
      if (this.selected) this.addShelf(this.selected.productId, this.selected.colorId);
    });

    /* ---------- asistan: tıklayınca son mesajı tekrarla ---------- */
    const replay = () => this.replayAssistant();
    /* E-posta formu açıkken baloncuğun HİÇBİR yeri isteği kapatmıyor.
       Form ve onay satırı kendi kabarmalarını zaten kesiyordu ama baloncuğun
       iç boşluğu açıkta kalıyordu: onay yazısının yanına denk gelen bir
       dokunuş asistana ulaşıp mail talebini kapatıyordu. Kapatma artık
       yalnızca avatardan — "asistana dokun" da bunu kastediyordu. */
    this.bubbleEl.addEventListener('click', (e) => {
      if (this.emailBubbleOpen()) e.stopPropagation();
    });
    this.assistantEl.addEventListener('click', replay);
    this.assistantEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); replay(); }
    });

    /* ---------- klavye ---------- */
    window.addEventListener('keydown', (e) => {
      if (!this.root.isConnected) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'Escape') { this.openPop(null); this.setPriceOpen(false); this.setDrawer(false); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); this.undo(); return; }
      if (!this.selected) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault(); this.pushHistory(); this.removeShelf(this.selected); this.say('removed'); return;
      }
      const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[e.key];
      if (step) {
        e.preventDefault();
        const s = this.selected;
        const [dc, dr] = step;
        if (this.fits(s.col + dc, s.row + dr, s.units, s.id)) {
          this.pushHistory();
          s.col += dc; s.row += dr;
          this.placeObject(s);
          this.refresh();
        }
      }
    });

    /* ---------- sahne etkileşimi ---------- */
    this.canvas.addEventListener('pointerdown', (e) => this.onStagePointerDown(e), true);
    this.canvas.addEventListener('dblclick', (e) => this.onDoubleClick(e));
    // Müşteri sahneye dokunduktan sonra ekran boyu değişse de yakınlığı korunuyor.
    const kullanildi = () => { this._kameraKullanildi = true; };
    this.canvas.addEventListener('pointerdown', kullanildi, { passive: true });
    this.canvas.addEventListener('wheel', kullanildi, { passive: true });

    window.addEventListener('pointermove', (e) => this.onDragMove(e));
    window.addEventListener('pointerup', (e) => this.onDragEnd(e));
    window.addEventListener('pointercancel', () => this.cancelDrag());

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.stageEl);
  }

  /* ======================================================================
     SAHNE
     ====================================================================== */
  buildScene() {
    this.scene = new T.Scene();

    this.renderer = new T.WebGLRenderer({
      canvas: this.canvas, antialias: true, alpha: true, powerPreference: 'high-performance',
    });
    /*
     * Dokunmatik cihazlarda piksel oranı 1,5 ile sınırlı (masaüstünde 2).
     *
     * Telefonda artık rafa yakınlaşılabiliyor; yakında duvar ekranın
     * tamamını kaplıyor ve maliyet piksel sayısıyla gidiyor. Ölçüm (Intel
     * HD 530, telefon tuvali 4× MSAA): yakın görünüm 2× oranda 16,5 ms —
     * 60 fps sınırında — 1,5× oranda 8,9 ms; geniş görünüm 6,0 → 3,7 ms.
     * Kenar yumuşatma açık olduğu için fark ancak dokuya çok yakından
     * bakınca seçiliyor; akıcılık her durumda hissediliyor.
     */
    const dokunmatik = !!window.matchMedia?.('(pointer: coarse)').matches;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, dokunmatik ? 1.5 : 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = T.PCFSoftShadowMap;
    /* Gölge haritası yalnızca sahne değiştiğinde yeniden çiziliyor (bkz.
       startLoop / invalidate). Kamerayı döndürmek onu etkilemiyor; her
       karede yeniden çizmek sahneyi ışığın gözünden bir kez daha çizmekti. */
    this.renderer.shadowMap.autoUpdate = false;
    /*
     * Nötr ton eşleme (Khronos PBR Neutral). ACES orta tonları karartıp
     * doygun renkleri soldurup kaydırıyordu: ekrandaki Napoli Sun (RAL 1018,
     * 245/208/51) 229/211/115 çıkıyordu — sarı değil, pastel. Nötr eşlemede
     * 236/194/59. Satılan şey rafın rengi; ekrandaki renk gerçeğine yakın
     * olmalı. Işık renkleri ve dokular buna göre nötrlendi.
     */
    this.renderer.toneMapping = T.NeutralToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = T.SRGBColorSpace;

    this.camera = new T.PerspectiveCamera(34, 1, 0.05, 60);
    this.orbit = new LimitedOrbit(this.camera, this.canvas);
    // Araç çubuğu artık yalnız çizilen karelerde konumlanıyor (startLoop).

    // Ortam haritası (PBR yansımaları)
    const pmrem = new T.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();
    const envSrc = makeStudioEnvTexture();
    this.envMap = pmrem.fromEquirectangular(envSrc).texture;
    this.scene.environment = this.envMap;
    envSrc.dispose();
    // Rafların kendi stüdyo ortamı (bkz. makeShelfEnvTexture)
    const rafSrc = makeShelfEnvTexture();
    this.shelfEnv = pmrem.fromEquirectangular(rafSrc).texture;
    rafSrc.dispose();
    pmrem.dispose();

    // Işıklar — anahtar ışık gölgeyi ve hacmi, dolgu ışığı gölge içini taşır.
    this.key = new T.DirectionalLight(0xfff6ec, 2.6);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.bias = -0.0004;
    this.key.shadow.normalBias = 0.022;
    this.key.shadow.radius = 3;
    this.keyTarget = new T.Object3D();
    this.scene.add(this.key, this.keyTarget);
    this.key.target = this.keyTarget;

    // Dolgu ışığı odanın açık cephesinden geri sekmeyi taklit eder: yan duvarlar
    // pencereye dik olduğu için parlak kalır, arka duvar bu ışık olmadan gri düşer.
    // Hafif serin: sıcak anahtar ışıkla birlikte gölge içleri çamurlaşmıyor.
    this.fill = new T.DirectionalLight(0xf4f6fb, 0.85);
    this.fill.position.set(-3.4, 1.6, 3.0);
    this.scene.add(this.fill);

    // Hemisphere düşük tutulur; ortam aydınlatmasının çoğu env map'ten gelir,
    // aksi halde sahne yıkanmış (kontrastsız) görünür.
    this.hemi = new T.HemisphereLight(0xffffff, 0xcdc6bb, 0.58);
    this.scene.add(this.hemi);

    // Ortak dokular
    this.tex = {
      wall: makeWallTextures(),
      floor: makeFloorTextures(),
      powder: makePowderTextures(),
      grid: makeGridCellTexture(),
      lightPatch: makeLightPatchTexture(),
      glow: makeGlowTexture(),
    };
    const maxAniso = this.renderer.capabilities.getMaxAnisotropy();
    [this.tex.floor.map, this.tex.floor.normalMap, this.tex.floor.roughnessMap,
     this.tex.wall.map, this.tex.wall.normalMap, this.tex.wall.roughnessMap, this.tex.grid]
      .forEach((t) => { t.anisotropy = maxAniso; });

    this.materialCache = new Map();
    this.roomGroup = new T.Group();
    this.shelfGroup = new T.Group();
    /* Oda eşyası kendi grubunda: raf ışınlamasına karışmasın (shelfAt yalnız
       shelfGroup'u tarıyor) ve duvar yeniden kurulduğunda silinmesin. */
    this.propGroup = new T.Group();
    this.scene.add(this.roomGroup, this.propGroup, this.shelfGroup);

    // Sürükleme yardımcıları
    this.ray = new T.Raycaster();
    this.ndc = new T.Vector2();
    this.wallPlane = new T.Plane(new T.Vector3(0, 0, 1), 0);

    this.highlight = new T.Mesh(
      new T.PlaneGeometry(1, 1),
      new T.MeshBasicMaterial({ transparent: true, opacity: 0.32, depthWrite: false, color: 0x2f7d53 }),
    );
    this.highlight.visible = false;
    this.scene.add(this.highlight);

    this.selectionRing = new T.Mesh(new T.PlaneGeometry(1, 1), makeSelectionMaterial());
    this.selectionRing.raycast = () => {};
    this.selectionRing.visible = false;
    this.scene.add(this.selectionRing);

    // Raf malzemesi bu GPU'da doğru renk veriyor mu (bkz. rafMalzemesiSina).
    this.rafMalzemesiSina();

    this._kirli = true;   // ilk kare (ve gölge haritası) mutlaka çizilsin
    this.resize();
  }

  /** Kamerayı duvara oturtan mesafe (oda ölçülerini de bu belirler). */
  computeFitDistance() {
    const W = this.wallW / 100, H = this.wallH / 100;
    const vFov = (this.camera.fov * Math.PI) / 180;
    const aspect = this.camera.aspect || 1.4;
    /* Köşeden bakıldığında kadraja yalnızca tasarım duvarı değil, yan duvarın
       da bir kısmı giriyor. Gereken yatay genişlik bu yüzden duvarın kendisi
       değil, açılı izdüşümü: W·cos(açı) + derinlik·sin(açı). Böylece pencere
       ya da kapı kadrajın dışında kalmıyor — sabit bir "biraz geri çek"
       sayısıyla değil, açının kendisinden türeyen bir ölçüyle. */
    /* Kamera duvarın MERKEZİNE bakıyor, ama oda yalnızca öne doğru uzuyor:
       açılı bakışta bu derinlik tek tarafa biniyor. Simetrik bir kadraj için
       derinliğin katkısını iki kat saymak gerekiyor, yoksa oda bir kenardan
       kesiliyordu (dar ekranda belirgin). */
    const aci = Math.abs(ACILIS_AZIMUT);
    const derinlik = this.roomD || W * 0.8;
    const etkinW = W * Math.cos(aci) + 2 * derinlik * Math.sin(aci);
    /* Odada eşya varken biraz daha geriden bakıyoruz: yatağın/tezgahın
       zeminle buluştuğu yer de kadraja girsin. Eşya sahneye derinlik
       kattığı için raf, duvarda asılı bir nesne olmaktan çıkıp odanın
       parçası gibi okunuyor — bütün mesele bu. */
    /* Yan duvardaki eşya kadrajın kenarında kalıyor; biraz daha geriden
       bakmak onu görünür kılıyor. Arka duvardaki eşya zaten merkezde. */
    const pay = this.prop ? 1.06 : 1.0;
    return Math.max(
      (H * 1.18 * pay) / 2 / Math.tan(vFov / 2),
      (etkinW * 1.16 * pay) / 2 / Math.tan(vFov / 2) / aspect,
    );
  }

  /** Duvar + zemin + yan duvarlar + tavan + süpürgelik. */
  /** Seçili duvar renginin hex değeri. */
  wallColorHex() {
    const list = this.cfg.wallColors || [];
    return (list.find((c) => c.id === this.wallColorId) || list[0] || { hex: '#ffffff' }).hex;
  }

  /**
   * Odayı kurar: kalın duvarlı bir "kesit oda" (IKEA planlayıcısındaki gibi
   * üstü açık kutu). Arka duvar tam olarak tasarım alanıdır — müşteri ölçüyü
   * değiştirdiğinde odanın kendisi görünür biçimde büyür/küçülür.
   */
  buildRoom() {
    const g = this.roomGroup;
    while (g.children.length) {
      const c = g.children.pop();
      c.geometry?.dispose?.();
      // Yalnızca bu yapıya özel üretilmiş dokuları at; this.tex altındaki
      // ortak dokular paylaşıldığı için elden çıkarılmamalı. Duvar ölçüsü
      // düğmeye her basışta yeniden kuruluyor, sızıntı hızla birikirdi.
      for (const m of [].concat(c.material || [])) {
        if (m?.map?.userData?.perBuild) m.map.dispose();
      }
    }

    const W = this.wallW / 100;   // tasarım duvarının genişliği = odanın genişliği
    const H = this.wallH / 100;   // duvar yüksekliği = oda yüksekliği
    const t = 0.10;               // duvar kalınlığı (kesit kenarını görünür kılar)
    // Oda derinliği. Alt sınır duvar genişliğine bağlı: dar bir duvarda sabit
    // 1.7 m derinlik kamerayı yaklaştırıp yan duvarların kadrajın üçte birini
    // yutmasına yol açıyordu.
    const D = clamp(W * 0.80, Math.min(1.7, W * 0.85), 2.9);
    // Oda eşyası yan duvarlara da yaslanabildiği için derinlik dışarıdan da
    // gerekiyor (bkz. placeProp / duvarUzunlugu).
    this.roomD = D;
    const ft = 0.07;              // zemin döşemesi kalınlığı

    // ---------- Malzemeler ----------
    /* Yüzey seti: varsayılan sıva + parke, banyoda fayans. Fayans dokuları
       ilk banyo seçiminde üretilip saklanıyor; hiç banyo seçmeyen ziyaretçi
       bu maliyeti hiç ödemiyor. */
    const fayans = this.surface === 'fayans';          // banyo: duvar + zemin
    const antreZemin = this.surface === 'antre';       // antre: yalnız zemin
    const deck = this.surface === 'balkon';            // balkon: ahşap deck
    if (fayans && !this.tex.tileWall) {
      this.tex.tileWall = makeTileTextures({ adet: 5, taban: [0.95, 0.95, 0.94], derzTon: 0.86 });
      this.tex.tileFloor = makeTileTextures({ adet: 4, taban: [0.44, 0.45, 0.44], derzTon: 0.74, seed: 99 });
    }
    if (antreZemin && !this.tex.tileDark) {
      /* Antre zemini: MAT siyah fayans. Tam siyah değil (0.20) — tam siyahta
         derzler kayboluyor ve zemin delik gibi duruyordu. */
      this.tex.tileDark = makeTileTextures({
        adet: 4, taban: [0.21, 0.21, 0.22], derzTon: 0.62, seed: 777,
      });
    }
    const wTex = fayans ? this.tex.tileWall : this.tex.wall;
    const fTex = fayans ? this.tex.tileFloor : (antreZemin ? this.tex.tileDark : this.tex.floor);
    // Fayans dokusu 5 karo taşıyor; 1,25 m tekrar ≈ 25 cm'lik karo.
    const wRepeat = fayans ? 1.25 : 1.4;
    const fRepeat = (fayans || antreZemin) ? 1.32 : 1.35;

    const wallMat = new T.MeshStandardMaterial({
      color: new T.Color(this.wallColorHex()),
      map: wTex.map,
      normalMap: wTex.normalMap,
      // Kabartma yalnızca ipucu kadar: yüksek değerlerde yalayan ışık altında
      // duvar sıvası betona dönüyor, gölge kenarları kırılıyor.
      // Fayansta derz gerçekten girinti; sıvada yalnızca ipucu kadar kabartma.
      normalScale: new T.Vector2(fayans ? 0.42 : 0.06, fayans ? 0.42 : 0.06),
      roughnessMap: wTex.roughnessMap,
      roughness: 1.0,
      metalness: 0.0,
      envMapIntensity: fayans ? 0.72 : 0.50,
    });
    // Duvar rengi değiştirildiğinde yalnızca bu malzeme güncellenir.
    wallMat.userData.isWall = true;
    [wTex.map, wTex.normalMap, wTex.roughnessMap]
      .forEach((tx) => tx.repeat.set(W / wRepeat, H / wRepeat));

    // Kesit yüzeyleri (duvar kalınlığının göründüğü kenarlar) her zaman beyaz
    const capMat = new T.MeshStandardMaterial({
      color: 0xf2eee7, roughness: 0.82, metalness: 0, envMapIntensity: 0.32,
    });

    const floorMat = new T.MeshStandardMaterial({
      // Balkonda aynı parke dokusu gri-kahve bir deck tonuna boyanıyor:
      // yeni bir doku üretmeden dış mekân hissi veriyor.
      color: deck ? 0xb9ab97 : 0xffffff,
      map: fTex.map,
      normalMap: fTex.normalMap,
      normalScale: new T.Vector2(0.85, 0.85),
      roughnessMap: fTex.roughnessMap,
      roughness: 1.0,
      metalness: 0.0,
      // Mat siyah fayans parlamamalı: antrede yansıma düşük tutuluyor.
      envMapIntensity: fayans ? 0.68 : (antreZemin ? 0.22 : (deck ? 0.30 : 0.5)),
    });
    [fTex.map, fTex.normalMap, fTex.roughnessMap]
      .forEach((tx) => tx.repeat.set(W / fRepeat, D / fRepeat));

    // BoxGeometry yüz sırası: [+x, -x, +y, -y, +z, -z]
    const faces = (inner, index) => {
      const m = [capMat, capMat, capMat, capMat, capMat, capMat];
      m[index] = inner;
      return m;
    };

    // ---------- Arka duvar (tasarım yüzeyi) ----------
    const wall = new T.Mesh(new T.BoxGeometry(W + 2 * t, H, t), faces(wallMat, 4));
    wall.position.set(0, H / 2, -t / 2);
    wall.receiveShadow = true;
    // Oda kabuğu gölge DÜŞÜRMEZ: pencereden gelen ışığın duvarlara takılmaması
    // gerekir, yoksa oda tamamen kendi gölgesinde kalır.
    wall.castShadow = false;
    g.add(wall);
    this.wallMesh = wall;

    // ---------- Yan duvarlar ----------
    // Kendi malzeme kopyalarını alırlar: oda çevrildiğinde kameraya yakın kalan
    // duvar saydamlaşıp rafları göstermeli, arka duvar bundan etkilenmemeli.
    this.sideWalls = [];
    [-1, 1].forEach((sgn) => {
      const inner = wallMat.clone();
      inner.userData.isWall = true;
      const cap = capMat.clone();
      const mats = [cap, cap, cap, cap, cap, cap];
      mats[sgn < 0 ? 0 : 1] = inner;   // iç yüz odaya bakar

      const side = new T.Mesh(new T.BoxGeometry(t, H, D), mats);
      side.position.set(sgn * (W / 2 + t / 2), H / 2, D / 2);
      side.receiveShadow = true;
      side.castShadow = false;
      g.add(side);
      // O duvara ait her şey aynı anda erimeli: süpürgeliği, penceresi ve
      // kasası havada asılı kalırsa duvar yok olmuş gibi değil, bozulmuş
      // gibi görünüyor.
      this.sideWalls.push({ mesh: side, sgn, mats: [inner, cap], parts: [side] });
    });
    const sideOf = (sgn) => this.sideWalls.find((w) => w.sgn === sgn);
    /** Bir nesneyi ilgili yan duvarın erime grubuna katar. */
    const joinSide = (sgn, mesh, mat) => {
      const w = sideOf(sgn);
      if (!w) return;
      w.parts.push(mesh);
      if (mat && !w.mats.includes(mat)) w.mats.push(mat);
    };

    // ---------- Zemin döşemesi ----------
    const floor = new T.Mesh(
      new T.BoxGeometry(W + 2 * t, ft, D + t),
      faces(floorMat, 2),
    );
    floor.position.set(0, -ft / 2, D / 2 - t / 2);
    floor.receiveShadow = true;
    g.add(floor);

    // ---------- Süpürgelik ----------
    const skirtMat = new T.MeshStandardMaterial({
      color: 0xfaf8f5, roughness: 0.38, metalness: 0.02, envMapIntensity: 0.65,
    });
    const skirtH = SUPURGELIK_H, skirtT = 0.016;
    const skirtBack = new T.Mesh(new T.BoxGeometry(W, skirtH, skirtT), skirtMat);
    skirtBack.position.set(0, skirtH / 2, skirtT / 2);
    skirtBack.castShadow = true;
    skirtBack.receiveShadow = true;
    g.add(skirtBack);

    // Yan süpürgelikler kendi duvarlarıyla birlikte erisin diye ayrı malzeme
    // kopyası alır — ortak malzeme kullansalardı arka süpürgelik de solardı.
    [-1, 1].forEach((sgn) => {
      const m = skirtMat.clone();
      const s = new T.Mesh(new T.BoxGeometry(skirtT, skirtH, D - skirtT), m);
      s.position.set(sgn * (W / 2 - skirtT / 2), skirtH / 2, (D + skirtT) / 2);
      s.castShadow = true;
      s.receiveShadow = true;
      g.add(s);
      joinSide(sgn, s, m);
    });

    // ---------- Sağ duvarda pencere ----------
    // Gerçek bir boşluk açmak yerine iç yüze pencere yerleştiriyoruz; dışarısı
    // hiçbir açıdan görünmediği için sonuç aynı, maliyeti ise sıfıra yakın.
    /* Açıklık tipi odaya göre: banyo ve antrede KAPI, mutfakta balkon kapısı
       (zemine inen cam), diğerlerinde pencere. Tek geometri, üç ayar. */
    const aciklik = this.room?.aciklik || 'pencere';
    const zeminden = aciklik !== 'pencere';            // kapı zeminden başlar
    const camli = aciklik !== 'kapi';                  // dolu kapı saydam değil

    const winW = zeminden ? clamp(D * 0.30, 0.62, 0.98) : clamp(D * 0.34, 0.55, 1.15);
    const winH = zeminden ? clamp(H * 0.80, 1.70, 2.30) : clamp(H * 0.42, 0.60, 1.25);
    // Pencere odanın ortasından biraz öne alındı: ışığın zemine düştüğü nokta
    // aksi halde duvarın dibinde kalıyor, leke duvara sıvanmış gibi duruyordu.
    const winZ = clamp(D * 0.52, winW / 2 + 0.25, D - winW / 2 - 0.25);
    const winY = zeminden
      ? winH / 2 + 0.02
      : clamp(H * 0.56, winH / 2 + 0.7, H - winH / 2 - 0.25);
    const winX = W / 2 - 0.012;

    /*
     * Pencereden gelen ışığın YATAY sapması — 1 metre düşüşe karşılık.
     * Hem zemindeki lekeyi hem de anahtar ışığın konumunu bu üretir; ikisi
     * aynı sayıdan türediği için leke, gölgeler ve parlama aynı yöne bakar.
     */
    const LX = -0.72, LZ = -0.30;
    const LL = Math.hypot(LX, LZ);
    const patchX = winX + LX * winY;
    const patchZ = winZ + LZ * winY;

    const glassCv = document.createElement('canvas');
    glassCv.width = 8; glassCv.height = 64;
    const gx = glassCv.getContext('2d');
    /* Dışarısı: üstte soluk gök mavisi, ufka doğru açılan ışık, altta uzak
       zeminin soluk tonu. Bilerek silik — pencere odanın parçası, sahnenin
       konusu değil; bembeyaz bir panel ise duvara yapıştırılmış kâğıt gibi
       duruyordu. */
    const gg = gx.createLinearGradient(0, 0, 0, 64);
    gg.addColorStop(0, '#dce8f2');
    gg.addColorStop(0.58, '#f6f8fa');
    gg.addColorStop(0.72, '#f1f2ee');
    gg.addColorStop(1, '#e6e8df');
    gx.fillStyle = gg;
    gx.fillRect(0, 0, 8, 64);
    const glassTex = new T.CanvasTexture(glassCv);
    glassTex.colorSpace = T.SRGBColorSpace;
    glassTex.userData.perBuild = true;

    /* Dolu kapıda cam yerine mat bir panel var; camlı açıklıklarda dışarısı
       ışıklı bir yüzeyle temsil ediliyor (gerçek bir boşluk açmıyoruz). */
    const glassMat = camli
      ? new T.MeshBasicMaterial({ map: glassTex, toneMapped: false, side: T.DoubleSide })
      : new T.MeshStandardMaterial({
        color: 0xf0ece4, roughness: 0.55, metalness: 0.02, envMapIntensity: 0.5,
        side: T.DoubleSide,
      });
    const glass = new T.Mesh(new T.PlaneGeometry(winW, winH), glassMat);
    glass.rotation.y = -Math.PI / 2;
    glass.position.set(winX, winY, winZ);
    g.add(glass);
    joinSide(1, glass, glassMat);
    // Gece modu camı karartıyor; dolu kapıda böyle bir yüzey yok.
    this.glassMat = camli ? glassMat : null;

    // Kasa + orta kayıt
    const frameMat = new T.MeshStandardMaterial({
      color: 0xfbfaf8, roughness: 0.35, metalness: 0.02, envMapIntensity: 0.8,
    });
    const fw = 0.045, fd = 0.05;
    const addBar = (w, h, y, z) => {
      const bar = new T.Mesh(new T.BoxGeometry(fd, h, w), frameMat);
      bar.position.set(W / 2 - fd / 2, y, z);
      bar.castShadow = false;
      g.add(bar);
      joinSide(1, bar, null);
    };
    addBar(winW + fw * 2, fw, winY + winH / 2 + fw / 2, winZ);   // üst
    if (!zeminden) addBar(winW + fw * 2, fw, winY - winH / 2 - fw / 2, winZ);  // alt
    addBar(fw, winH, winY, winZ - winW / 2 - fw / 2);            // ön dikme
    addBar(fw, winH, winY, winZ + winW / 2 + fw / 2);            // arka dikme
    // Orta kayıt yalnız camlı açıklıkta; dolu kapıda bölüntü olmaz.
    if (camli) addBar(0.022, winH, winY, winZ);

    if (zeminden) {
      // Kapı kolu — kapıyı kapı yapan tek detay.
      const kol = new T.Mesh(new T.BoxGeometry(0.035, 0.035, 0.13), frameMat);
      kol.position.set(W / 2 - 0.06, winY - winH * 0.06, winZ - winW / 2 + 0.11);
      g.add(kol);
      joinSide(1, kol, null);
    } else {
      // Pencere içi denizlik
      const sill = new T.Mesh(new T.BoxGeometry(0.10, 0.025, winW + fw * 2.6), frameMat);
      sill.position.set(W / 2 - 0.05, winY - winH / 2 - fw, winZ);
      sill.castShadow = false;
      sill.receiveShadow = true;
      g.add(sill);
      joinSide(1, sill, null);
    }
    // Kasa ve denizlik ortak malzeme kullanıyor; erime grubuna bir kez katılır.
    sideOf(1).mats.push(frameMat);

    /* ---------- Pencereden düşen ışık lekesi ----------
     * Leke, pencerenin zemine izdüşümüdür: dikey kenar (winH) ışık yönünde
     * uzar, yatay kenar (winW) olduğu gibi kalır. Dokudaki orta kayıt çizgisi
     * düzlemin yerel Y ekseninde olduğu için o ekseni ışık yönüne çeviriyoruz.
     */
    const streak = winH * LL;
    const patchMat = new T.MeshBasicMaterial({
      map: this.tex.lightPatch,
      transparent: true,
      // Zemindeki baskın olay pencere ışığı olmalı; temas gölgesi kısıldığı
      // için leke artık onunla yarışmıyor, ikisi aynı hikâyeyi anlatıyor.
      // Dolu kapıdan ışık girmez; balkon kapısı zemine indiği için daha çok.
      opacity: camli ? (zeminden ? 0.30 : 0.26) : 0,
      depthWrite: false,
      blending: T.AdditiveBlending,
      toneMapped: false,
    });
    /* Düzlem pencereden epey geniş: leke yayıldıkça sönümlendiği için kenarı
       kadraja girmiyor, zeminde sert bir sınır görünmüyor. */
    const patch = new T.Mesh(new T.PlaneGeometry(winW * 2.3, streak * 2.1), patchMat);
    patch.rotation.set(-Math.PI / 2, 0, Math.atan2(-LX, -LZ));
    patch.position.set(patchX, 0.006, patchZ);
    patch.renderOrder = 1;
    g.add(patch);
    this.lightPatchMesh = patch;

    // ---------- Ortam gölgesi örtüsü (arka duvar) ----------
    const wallShade = new T.Mesh(
      new T.PlaneGeometry(W, H),
      new T.MeshBasicMaterial({
        map: Object.assign(makeWallShadeTexture(W, H), { userData: { perBuild: true } }),
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    wallShade.position.set(0, H / 2, 0.0035);
    wallShade.renderOrder = 1;
    g.add(wallShade);

    /* ---------- Gece: tavan armatürünün havuzu ----------
     * Duvarda yukarıdan aşağı sönen bir halka, zeminde ise onun altına düşen
     * bir havuz. İkisi birden "odanın üstünde bir ışık var" hissini kuruyor;
     * ikinci bir gölgeli ışık kaynağına gerek kalmıyor.
     */
    this.nightGlows = [];
    const glowMat = () => new T.MeshBasicMaterial({
      map: this.tex.glow,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: T.AdditiveBlending,
      toneMapped: false,
    });

    const wallGlow = new T.Mesh(new T.PlaneGeometry(W * 1.15, H * 1.05), glowMat());
    wallGlow.position.set(0, H * 0.74, 0.006);
    wallGlow.renderOrder = 2;
    g.add(wallGlow);
    // Toplayıcı parlama, zaten aydınlık duvarın üstüne binince kırmızıyı
    // doyuruyordu; yarıya indirildi (bkz. applyLighting gece kolu).
    this.nightGlows.push({ mesh: wallGlow, max: 0.14 });

    const floorGlow = new T.Mesh(new T.PlaneGeometry(W * 0.95, D * 1.05), glowMat());
    floorGlow.rotation.x = -Math.PI / 2;
    floorGlow.position.set(0, 0.008, D * 0.42);
    floorGlow.renderOrder = 2;
    g.add(floorGlow);
    this.nightGlows.push({ mesh: floorGlow, max: 0.11 });

    /* ---------- Altın saat: pencerenin duvara düşen ışığı ----------
     * Geometrisi güneşin yönüne göre applyLighting'de kuruluyor (bkz.
     * altinLekeGuncelle). Toplayıcı karışım: siyah zemin hiçbir şey eklemiyor,
     * camların olduğu yer duvarı altına boyuyor. */
    this.tex.altinLeke = this.tex.altinLeke || makeGoldenPatchTexture();
    const altinLeke = new T.Mesh(new T.BufferGeometry(), new T.MeshBasicMaterial({
      map: this.tex.altinLeke,
      color: 0xffa54c,
      transparent: true,
      opacity: 0.36,
      depthWrite: false,
      blending: T.AdditiveBlending,
      toneMapped: false,
      side: T.DoubleSide,
    }));
    altinLeke.position.z = 0.0012;      // duvarın hemen önü, rafların arkası
    altinLeke.renderOrder = 2;
    altinLeke.visible = false;
    altinLeke.raycast = () => {};
    g.add(altinLeke);
    this.altinLeke = altinLeke;

    const floorShade = new T.Mesh(
      new T.PlaneGeometry(W, 0.55),
      new T.MeshBasicMaterial({
        map: Object.assign(makeFloorShadeTexture(), { userData: { perBuild: true } }),
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    // Z ekseninde çevirmek şart: gradyanın koyu ucu duvara bakmalı. Ters
    // durduğunda karartma duvardan uzakta kalıyor ve düzlemin bittiği yerde
    // parkede keskin bir çizgi bırakıyor.
    floorShade.rotation.set(-Math.PI / 2, 0, Math.PI);
    floorShade.position.set(0, 0.004, 0.275);
    floorShade.renderOrder = 1;
    g.add(floorShade);

    /* ---------- Oda maketinin altındaki zemin gölgesi ----------
     * Kesit oda boşlukta yüzüyordu. Döşemenin altına yumuşak bir gölge
     * düşünce masaya konmuş bir maket gibi okunuyor. Tek saydam düzlem;
     * tuval saydam olduğu için gölge sayfanın arka planına düşüyor. */
    const zeminGolge = makeRoomGroundShadow(W + 2 * t, D + t);
    const golgeDuz = new T.Mesh(
      new T.PlaneGeometry(zeminGolge.w, zeminGolge.h),
      new T.MeshBasicMaterial({
        map: zeminGolge.tex, transparent: true, depthWrite: false, toneMapped: false,
      }),
    );
    golgeDuz.rotation.x = -Math.PI / 2;
    golgeDuz.position.set(0, -ft - 0.002, D / 2 - t / 2);
    g.add(golgeDuz);

    // ---------- 5 cm grid katmanı ----------
    this.tex.grid.repeat.set(this.cols, this.rows);
    const grid = new T.Mesh(
      new T.PlaneGeometry(W, H),
      new T.MeshBasicMaterial({ map: this.tex.grid, transparent: true, opacity: 0, depthWrite: false }),
    );
    grid.position.set(0, H / 2, 0.0025);
    grid.renderOrder = 2;
    g.add(grid);
    this.gridMesh = grid;

    // ---------- Işıklandırma ----------
    // Anahtar ışık, zemindeki lekeyi üreten vektörün ta kendisidir: pencerenin
    // arkasından gelip lekenin merkezine bakar. Raf gölgeleri de aynı yöne,
    // sola-aşağı düşer.
    this.roomDims = { W, H, D, winY, winZ, winW, winH, camli, patchX, patchZ };
    this.applyLighting();

    // ---------- Kamera ----------
    const targetY = H * 0.5;
    const uzak = {
      halfX: Math.max(0.05, W * 0.28),
      minY: Math.max(0.35, H * 0.28),
      maxY: Math.max(0.4, H * 0.78),
    };
    /* En yakında duvarın her köşesine ulaşılabiliyor. Alt sınır 50 cm:
       daha aşağıda, aşağıdan bakış açısıyla kamera zeminin altına iniyordu. */
    const yakin = {
      halfX: Math.max(0.05, W / 2 - 0.1),
      minY: Math.min(uzak.minY, 0.5),
      maxY: Math.max(uzak.maxY, H - 0.15),
    };
    this.orbit.setPanBounds(new T.Vector3(0, targetY, 0), uzak, yakin);
    this.orbit.resetPan();
    this.fitCamera();

    // Dikey sapmayı kameranın zeminin altına inmeyeceği aralıkta tut
    const dMax = this.orbit.maxDistance;
    const cosLow = clamp((0.45 - targetY) / dMax, -0.95, 0.95);
    const minPolar = Math.PI / 2 - MAX_POLAR_UP;
    const maxPolar = Math.min(Math.PI / 2 + MAX_POLAR_DOWN, Math.acos(cosLow));
    this.orbit.minPolar = minPolar;
    this.orbit.maxPolar = Math.max(minPolar, maxPolar);
    this.orbit.clampPolar();

    this.syncWallReadout();
  }

  /**
   * Oda çevrildiğinde kameraya yakın kalan yan duvar rafların önüne geçiyor.
   * O duvarı açıyla orantılı biçimde saydamlaştırıyoruz. Duvarın süpürgeliği,
   * penceresi, kasası ve denizliği aynı grupta olduğu için birlikte erir —
   * havada asılı kalan bir pencere, duvarın kendisinden daha çok göze batıyordu.
   */
  fadeSideWalls() {
    if (!this.sideWalls) return;
    const az = this.orbit.azimuth;

    for (const w of this.sideWalls) {
      // Kamera bu duvarın tarafına döndükçe t büyür
      const t = clamp(((w.sgn < 0 ? -az : az) / MAX_AZIMUTH - 0.18) / 0.55, 0, 1);
      /* TAM sıfıra iniyor. Eskiden %4 opaklıkta kalıyordu: duvarın kendisi
         görünmez oluyordu ama kesit yüzeyleri (capMat) ile birlikte kenarda
         keskin bir şerit bırakıyordu. Sıfıra inince aşağıdaki görünürlük
         eşiği parçayı tamamen çizim dışı bırakıyor. */
      const opacity = 1 - smoothstep(t);
      const solid = opacity > 0.995;

      for (const mat of w.mats) {
        if (mat.opacity === opacity) continue;
        mat.opacity = opacity;
        // Saydamken derinliğe yazmamalı, yoksa arkasındaki rafları gizler.
        mat.transparent = !solid;
        mat.depthWrite = solid;
        mat.needsUpdate = true;
      }
      // Tamamen sönen parçaları çizmeyi bırak: saydam sıralaması yüzünden
      // kalan hayalet kenarlar böylece hiç oluşmuyor.
      const visible = opacity > 0.02;
      for (const part of w.parts || [w.mesh]) {
        if (part.visible !== visible) part.visible = visible;
        part.castShadow = false;
      }
    }
  }

  /**
   * Gündüz/gece ışıklandırması.
   *
   * Gece için ikinci bir gölgeli ışık eklemiyoruz — gölge haritasının maliyeti
   * ikiye katlanırdı. Bunun yerine aynı anahtar ışık tavana taşınıp sıcak bir
   * tona çevriliyor; raflar tepeden aydınlanıyor, gölgeleri kısalıyor.
   */
  applyLighting() {
    const d = this.roomDims;
    if (!d) return;
    const { W, H, D, winY, winZ, patchX, patchZ } = d;

    if (this.night) {
      /*
       * Armatür tavanda, odanın önünde.
       *
       * Duvara asılı bir rafın duvardaki gölgesinin boyu, ışığın dikey
       * bileşeninin yatay (Z) bileşenine oranıdır: raf duvardan ~14 cm
       * çıktığı için oran 2 olduğunda gölge ~28 cm iner. Işığı tam tepeye
       * koymak bu oranı büyütüyor ve gölgeyi rafın iki katı uzunluğunda bir
       * bloğa çeviriyordu; öne alınca hem gölge kısalıyor hem duvar daha
       * yüzeyden aydınlanıyor.
       */
      /*
       * Pozlama kırmızı kanalı doyurmayacak kadar düşük tutulur.
       *
       * Eskiden duvarın %75–92'sinde kırmızı 255'e kırpılıyordu. Kırmızı
       * sabitlenince sıvanın mikro dokusu artık parlaklığı değil yalnızca
       * yeşil/maviyi oynatabiliyor ve göz bunu YEŞİL BENEK olarak okuyor.
       * Telefonda daha belirgindi: kadraj duvarın yanmış çekirdeğini
       * dolduruyor, üstelik mobil shader'lar daha düşük hassasiyette.
       *
       * Nötr ton eşlemeye geçince ışık rengi biraz açıldı: ACES sıcaklığı
       * bastırıyordu, aynı turuncu nötrde duvarı 207/183/154'e boyuyordu.
       * Bu ayarla duvar 217/198/177 (ACES'teki gece 215/200/181), duvarda
       * kırpılma yok — ölçülen tek 255 sarı rafın kendi pikselleri.
       */
      /* Gölge yapısı: ışık önden ve yukarıdan, eğim 0,8. Eskiden eğim 1,6
         idi — her rafın gölgesi duvarda ~24 cm iniyor, alttaki rafların
         üstüne biniyor, lekeli ve dağınık görünüyordu. Şimdi bütün raflarda
         aynı: tablanın hemen altında 10-12 cm'lik, rafa yapışık bir gölge.
         Işık daha önden geldiği için duvarı daha dik görüyor; şiddet buna
         göre düşürüldü, duvarın parlaklığı eskisiyle aynı. */
      /* Gece modu tavandaki armatürün altında rafların nasıl durduğunu
         gösteriyor: raflarda lamba yansa da oda KISILMIYOR. Lambaların
         ışığı kendi şiddetiyle öne çıkıyor (bkz. lambaIsiklari). */
      this.key.color.setHex(0xffe6c8);
      this.key.intensity = 1.3;
      this.key.position.set(W * 0.02, H * 0.5 + 1.6, 2.0);
      this.keyTarget.position.set(0, H * 0.5, 0);
      // Tek kaynak gece için doğru ama gölgeleri simsiyah bir blok yapıyor;
      // odanın kendi geri sekmesini taklit eden dolgu onları okunur tutuyor.
      this.fill.color.setHex(0xffeada);
      this.fill.intensity = 0.52;
      this.fill.position.set(0, H * 0.92, D + 1.1);
      this.hemi.color.setHex(0xdfd4c6);
      this.hemi.groundColor.setHex(0x3b342c);
      this.hemi.intensity = 0.46;
      this.scene.environmentIntensity = GECE_ORTAM;
      this.renderer.toneMappingExposure = 0.96;
      this.key.shadow.radius = 6;
      // Önden gelen ışıkta büyük normal kayması gölgeyi raftan koparıyordu.
      this.key.shadow.normalBias = 0.008;
    } else if (this.altin) {
      /*
       * Altın saat: batan güneş sağdaki pencereden neredeyse yatay giriyor
       * ve rafların durduğu yere vuruyor. Güneşin hedefi rafların ortası
       * (raf yoksa duvarın ortası); yön pencereden oraya çekilen çizgi.
       * Batan güneş aşağıdan gelemez: en az hafif bir eğimle iniyor.
       *
       * Yan duvarlar gölge düşürmüyor (pencere gerçek bir boşluk değil), bu
       * yüzden pencerenin duvara düşen ışık lekesi ayrı bir saydam yüzey
       * (altinLeke). Rafları altına boyayan ise anahtar ışığın kendisi —
       * sahneye yeni ışık kaynağı eklenmiyor, maliyet gündüzle aynı.
       */
      const hedef = this.altinHedef();
      const kaynak = new T.Vector3(W / 2, winY, winZ);
      const yon = new T.Vector3(hedef.x - W / 2, hedef.y - winY, -winZ);
      yon.y = Math.min(yon.y, -0.09 * Math.hypot(yon.x, yon.z));
      yon.normalize();
      this.altinYon = yon;
      this._altinHedefSon = hedef;
      const duvaraKadar = -winZ / yon.z;
      this.key.color.setHex(0xffa957);
      this.key.intensity = 2.05;
      this.key.position.copy(kaynak).addScaledVector(yon, -3.2);
      this.keyTarget.position.copy(kaynak).addScaledVector(yon, duvaraKadar);
      // Gölge tarafı alacakaranlığın serin morunu alıyor; sıcak güneşle
      // birlikte raflar hacimli, oda ise hafif loş okunuyor.
      this.fill.color.setHex(0xb9b4d8);
      this.fill.intensity = 0.34;
      this.fill.position.set(-W * 0.2, H * 0.6, D + 1.6);
      this.hemi.color.setHex(0xe8cdb6);
      this.hemi.groundColor.setHex(0x4e3f35);
      this.hemi.intensity = 0.32;
      this.scene.environmentIntensity = ALTIN_ORTAM;
      this.renderer.toneMappingExposure = 0.88;
      this.key.shadow.radius = 5;
      this.key.shadow.normalBias = 0.022;
    } else {
      // Anahtar ışık pencerenin arkasından gelip zemindeki lekenin merkezine
      // bakar; leke, gölgeler ve parlama böylece aynı yönü gösterir.
      const k = 2.2;
      this.key.color.setHex(0xfff6ec);
      this.key.intensity = 2.6;
      this.key.position.set(W / 2 + 0.72 * k, winY + k, winZ + 0.30 * k);
      this.keyTarget.position.set(patchX, 0, patchZ);
      this.fill.color.setHex(0xf4f6fb);
      this.fill.intensity = 0.85;
      this.fill.position.set(-W * 0.18, H * 0.70, D + 1.8);
      this.hemi.color.setHex(0xffffff);
      this.hemi.groundColor.setHex(0xcdc6bb);
      this.hemi.intensity = 0.58;
      this.scene.environmentIntensity = 1;
      this.renderer.toneMappingExposure = 1.0;
      this.key.shadow.radius = 3;
      this.key.shadow.normalBias = 0.022;
    }
    /* Rafların kendi ortam haritası var; scene.environmentIntensity ona
       uygulanmıyor. Gece aynı oranda söndürülmezse raflar karanlık odada
       parlıyordu. */
    for (const m of this.materialCache?.values() || []) m.envMapIntensity = this.shelfEnvIntensity();
    if (this._vidaMat) this._vidaMat.envMapIntensity = this.shelfEnvIntensity();

    // Gölge kamerası tüm odayı kapsamalı; kapsamadığı yerde gölge haritası
    // kenar dokusuna sabitlenip zeminde yapay bir çizgi bırakır.
    const radius = 0.5 * Math.hypot(W + 0.2, H, D) + 0.35;
    const cam = this.key.shadow.camera;
    cam.left = -radius; cam.right = radius;
    cam.top = radius; cam.bottom = -radius;
    cam.near = 0.4;
    cam.far = this.key.position.distanceTo(this.keyTarget.position) + radius + 1.5;
    cam.updateProjectionMatrix();
    this.key.shadow.needsUpdate = true;

    // Gece parlamaları ve pencere lekesi
    for (const g of this.nightGlows || []) {
      g.mesh.material.opacity = this.night ? g.max : 0;
      g.mesh.visible = this.night;
    }
    if (this.lightPatchMesh) this.lightPatchMesh.visible = !this.night && !this.altin;
    // Cam: gece lacivert, altın saatte gün batımının turuncusu.
    if (this.glassMat) this.glassMat.color.setHex(this.night ? 0x2b3550 : (this.altin ? 0xffc98c : 0xffffff));
    this.altinLekeGuncelle();
    // Raflardaki masa lambaları akşam yanıyor: gece tam, altın saatte yarım.
    // Işığın kendisi her karede lambaIsiklari() ile güncelleniyor.
    const lambaGucu = this.night ? 1 : (this.altin ? 0.5 : 0);
    const ampul = this._dekorMat?.get('ampul');
    if (ampul) ampul.emissiveIntensity = 2.6 * lambaGucu;
    const astar = this._dekorMat?.get('lambaIc');
    if (astar) astar.emissiveIntensity = 1.15 * lambaGucu;

    // Sıva kabartması gecede tek ışık kaynağıyla abartılı çıkıyor; ortam
    // ışığı düşünce yalnızca normal haritası kalıyor ve duvar betona dönüyor.
    /*
     * Dithering DENENDİ VE ÇIKARILDI: ölçümde bantlanmayı hiç azaltmadı
     * (21 → 21 bant) ama komşu piksel renk farkını 0.45'ten 0.74'e çıkardı —
     * yani tam da gidermeye çalıştığımız beneği ekliyordu. Kırpılmayı
     * bitirince zaten kıracak bant kalmıyor.
     */
    const ns = this.night ? 0.03 : (this.altin ? 0.045 : 0.06);
    this.roomGroup.traverse((o) => {
      for (const m of [].concat(o.material || [])) {
        if (m?.userData?.isWall && m.normalScale) m.normalScale.set(ns, ns);
      }
    });
    this.invalidate();
  }

  /** Raf malzemelerinin ortam ışığı: gündüz tam, gece ve altın saatte odayla aynı oranda. */
  shelfEnvIntensity() {
    return RAF_ENV_OLCEK * (this.night ? GECE_ORTAM : (this.altin ? ALTIN_ORTAM : 1));
  }

  /** Gece modunu açar/kapatır (altın saati kapatır). */
  setNight(on) {
    this.night = !!on;
    if (this.night) this.altin = false;
    this.syncLightBtn();
    this.applyLighting();
    this.showHint(this.night
      ? 'Gece modu açık — raflar tavan ışığının altında.'
      : 'Gündüz moduna dönüldü.');
  }

  /** Altın saati açar/kapatır (geceyi kapatır). */
  setGolden(on) {
    this.altin = !!on;
    if (this.altin) this.night = false;
    this.syncLightBtn();
    this.applyLighting();
    if (this.altin) this.showHint('Altın saat — güneş pencereden rafların üstüne vuruyor.');
  }

  /** Işık düğmesi: gündüz → altın saat → gece → gündüz. */
  cycleLight() {
    if (this.night) {
      this.setNight(false);
    } else if (this.altin) {
      this.setNight(true);
      this.track('kz_gece_modu');
    } else {
      this.setGolden(true);
      this.track('kz_altin_saat');
    }
  }

  /** Düğme ve kök sınıfları ışık durumuna göre. Düğme bir SONRAKİ durumu gösterir. */
  syncLightBtn() {
    this.root.classList.toggle('kz-night', this.night);
    this.root.classList.toggle('kz-golden', this.altin);
    const btn = this.root.querySelector('[data-act="night"]');
    if (!btn) return;
    btn.classList.toggle('kz-on', this.night);
    btn.classList.toggle('kz-golden', this.altin);
    // Gündüz pasif (soluk); altın saat ve gece aktif (canlı) — diğer düğmelerle aynı dil.
    btn.setAttribute('aria-pressed', this.night || this.altin ? 'true' : 'false');
    const sonraki = this.night ? 'Gündüz' : (this.altin ? 'Gece modu' : 'Altın saat');
    btn.title = sonraki;
    btn.setAttribute('aria-label', `${sonraki} ışığına geç`);
  }

  /**
   * Altın saatte güneşin hedefi: arka duvarın ortası, pencerenin biraz
   * altı. SABİT — raflar taşındıkça güneş de yer değiştiriyordu; gerçek
   * güneş rafları izlemez. Leke büyük olduğu için duvarın ortasındaki
   * raflar zaten onun içinde kalıyor.
   */
  altinHedef() {
    const d = this.roomDims;
    const H = d?.H || 2.4;
    return { x: -(d?.W || 3) * 0.04, y: Math.min(H * 0.52, (d?.winY ?? H * 0.56) - 0.05) };
  }

  /**
   * Pencerenin arka duvara düşen altın ışık lekesi. Pencerenin dört köşesi
   * güneş yönünde duvara izdüşürülüyor (paralel ışık → doğrusal dönüşüm,
   * yani düz bir paralelkenar), duvarın sınırlarına kırpılıyor. Leke rafların
   * ARKASINDA (z = 1,2 mm) duruyor; rafları boyayan anahtar ışık.
   */
  altinLekeGuncelle() {
    const m = this.altinLeke;
    const d = this.roomDims;
    if (!m || !d) return;
    const yon = this.altinYon;
    m.visible = !!(this.altin && d.camli && yon && yon.z < -1e-3);
    if (!m.visible) return;
    const { W, H, winY, winZ, winW, winH } = d;
    const kose = [
      [winZ - winW / 2, winY - winH / 2, 0, 0], [winZ + winW / 2, winY - winH / 2, 1, 0],
      [winZ + winW / 2, winY + winH / 2, 1, 1], [winZ - winW / 2, winY + winH / 2, 0, 1],
    ];
    let poli = kose.map(([z, y, u, v]) => {
      const s = -z / yon.z;
      return { x: W / 2 + yon.x * s, y: y + yon.y * s, u, v };
    });
    /* Leke izdüşümün kendisinden büyük: güneş nokta değil, pencereden giren
       ışık odaya yayılarak geniş ve yumuşak bir alan aydınlatıyor. Merkezden
       büyütülüyor; biçim (paralelkenar, iki cam) aynen kalıyor. */
    const BUYUT = 1.6;
    const mx = poli.reduce((t, q) => t + q.x, 0) / 4, my = poli.reduce((t, q) => t + q.y, 0) / 4;
    poli = poli.map((q) => ({ ...q, x: mx + (q.x - mx) * BUYUT, y: my + (q.y - my) * BUYUT }));
    /* Sutherland–Hodgman: duvarın dört kenarına göre kırp. İzdüşüm doğrusal
       olduğu için doku koordinatı da kenar boyunca doğrusal ilerliyor. */
    const kirp = (p, f) => {
      const cikti = [];
      for (let i = 0; i < p.length; i++) {
        const a = p[i], b = p[(i + 1) % p.length];
        const fa = f(a), fb = f(b);
        if (fa >= 0) cikti.push(a);
        if ((fa >= 0) !== (fb >= 0)) {
          const t = fa / (fa - fb);
          cikti.push({
            x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t,
            u: a.u + (b.u - a.u) * t, v: a.v + (b.v - a.v) * t,
          });
        }
      }
      return cikti;
    };
    poli = kirp(poli, (q) => q.x + W / 2);
    poli = kirp(poli, (q) => W / 2 - q.x);
    poli = kirp(poli, (q) => q.y);
    poli = kirp(poli, (q) => H - q.y);
    if (poli.length < 3) { m.visible = false; return; }

    const pos = [], uv = [];
    for (let i = 1; i < poli.length - 1; i++) {
      for (const q of [poli[0], poli[i], poli[i + 1]]) { pos.push(q.x, q.y, 0); uv.push(q.u, q.v); }
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    m.geometry.dispose();
    m.geometry = geo;
  }

  /**
   * Kadrajı odaya oturtur.
   *
   * `koru` verilirse müşterinin yakınlığı korunur (oda-sığdırma mesafesine
   * oranı aynı kalır). Ekran boyu değişince (telefonda adres çubuğu,
   * çekmece) ya da eşya taşınınca yakınlık eskiden sıfırlanıyordu; rafa
   * yaklaşmış müşteri her seferinde yeniden kıstırmak zorunda kalıyordu.
   */
  fitCamera(immediate, koru = false) {
    // Kadraj tasarım alanına göre kurulur; oda geometrisi de aynı mesafeden
    // türetildiği için kamera her açıda oda hacminin içinde kalır.
    const fit = this.computeFitDistance();
    const o = this.orbit;
    const oran = koru && o.fitDistance > 0 ? o._goal.di / o.fitDistance : 1;
    o.fitDistance = fit;
    o.maxDistance = fit * ZOOM_OUT_MAX;
    o.minDistance = this.closestDistance(fit);
    o.setDistance(koru ? fit * oran : this.baslangicMesafesi(fit), immediate);
  }

  /**
   * İlk açılışta (ve oda değişince) kameranın mesafesi.
   *
   * Dikey telefon ekranında odanın tamamını — açılı bakışta yan duvarın
   * derinliğiyle birlikte — sığdırmak kamerayı çok geri çekiyordu: oda
   * ekranın ortasında küçük bir kutu kalıyordu. Dar ekranda TASARIM
   * DUVARININ genişliği esas: duvar ekranı kenardan kenara dolduruyor, oda
   * kenarları dışarıda kalıyor. Geri çekilmek isteyen yine uzaklaşabiliyor.
   */
  baslangicMesafesi(fit) {
    const aspect = this.camera.aspect || 1;
    if (aspect >= 0.9) return fit;
    const tanY = Math.tan((this.camera.fov * Math.PI) / 360);
    const W = this.wallW / 100, H = this.wallH / 100;
    // Açılı bakışta duvarın izdüşümü genişliğinden kısa: 0,86 ile duvar
    // ekranın ~%90'ını kaplıyor (1,0'da yan duvar da sığıyor, duvar %70'te kalıyordu).
    const duvar = Math.max((W * 0.86) / 2 / tanY / aspect, (H * 0.95) / 2 / tanY);
    return clamp(duvar, this.orbit.minDistance, fit);
  }

  /**
   * En yakın bakış mesafesi — ODAYA değil EKRANA göre.
   *
   * Eskiden oda-sığdırma mesafesinin %42'siydi. Dar (dikey) telefon
   * ekranında odayı sığdırmak için kamera ~22 m geri çekildiğinden en yakın
   * bakış 9,3 m'de kalıyordu: 45 cm'lik raf ekranda 64 piksel, parmakla
   * tutup taşımak zordu. Artık en yakında ekranı ~70 cm'lik bir duvar
   * dilimi dolduruyor — hangi ekran olursa olsun raf rahatça tutuluyor.
   */
  closestDistance(fit) {
    const tanY = Math.tan((this.camera.fov * Math.PI) / 360);
    const aspect = this.camera.aspect || 1;
    const genislikten = YAKIN_GENISLIK / (2 * tanY * aspect);
    // Geniş ekranda yükseklik bir rafın iki katının altına inmesin.
    const yukseklikten = YAKIN_YUKSEKLIK / (2 * tanY);
    return clamp(Math.max(genislikten, yukseklikten), 0.8, Math.max(0.8, fit * 0.42));
  }

  resize() {
    if (!this.renderer) return;
    const w = this.stageEl.clientWidth || 1;
    const h = this.stageEl.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    /* Müşterinin yakınlığı korunsun — ama yalnızca müşteri kameraya
       dokunduysa. Telefonda tuval son boyutuna açılıştan sonra oturuyor;
       o ana kadar oran korunursa açılış mesafesi ilk (yanlış) ekran
       boyundan kalıyor, oda uzakta başlıyordu. */
    const koru = !!this._kameraKullanildi;
    this.fitCamera(true, koru);
    if (!koru && this.orbit._tween) this.orbit._tween.to.di = this.orbit._goal.di;
    this.invalidate();
  }

  /**
   * Sahne değişti: sonraki karede yeniden çiz ve gölge haritasını tazele.
   * Kamera hareketi için çağırmaya gerek yok — döngü onu kendisi fark ediyor.
   */
  invalidate() {
    this._kirli = true;
  }

  /*
   * İsteğe bağlı çizim.
   *
   * Eskiden sahne hiçbir şey değişmezken de saniyede 60 kez çiziliyordu:
   * telefonda boşuna ısınma ve pil, ısınınca da tam etkileşim anında
   * takılma. Artık yalnızca kamera oynadığında, sahne değiştiğinde ya da
   * bir animasyon sürerken çiziliyor. Gölge haritası (sahnenin ışığın
   * gözünden ikinci bir çizimi) yalnızca sahne değiştiğinde yenileniyor.
   *
   * Ölçüm (Intel HD 530, 680×768): döndürürken kare başına 5,6 ms → 5,0 ms;
   * dururken 0. Bu pay, eklenen temas gölgelerinin maliyetinin çok üstünde.
   */
  startLoop() {
    const sonKamera = new T.Matrix4();
    let sonCizim = 0;

    /* Güvenlik ağı: her kullanıcı hareketi en az bir kare çizdiriyor.
       Sahneyi değiştiren her yol invalidate() çağırıyor; bu yalnızca
       gözden kaçan bir yolun ekranda bayat kalmasını önlüyor. */
    const uyandir = () => { this._uyanik = performance.now() + 300; };
    const degisti = () => { this.invalidate(); uyandir(); };
    const dinle = { capture: true, passive: true };
    ['pointerdown', 'pointerup', 'pointercancel', 'click', 'keydown', 'input', 'change']
      .forEach((t) => window.addEventListener(t, degisti, dinle));
    // Sürükleme ve kaydırma kamerayı oynatıyor; gölge haritası gerekmiyor.
    window.addEventListener('pointermove', (e) => { if (e.buttons) uyandir(); }, dinle);
    window.addEventListener('wheel', uyandir, dinle);
    document.addEventListener('visibilitychange', () => this.invalidate());
    this.canvas.addEventListener('webglcontextrestored', () => this.invalidate());

    const tick = () => {
      this._raf = requestAnimationFrame(tick);
      const now = performance.now();
      this.orbit.update();

      // Grid opaklığını sürükleme durumuna göre yumuşat
      const goal = this.drag ? 0.5 : 0;
      const m = this.gridMesh.material;
      if (m.opacity !== goal) {
        m.opacity = Math.abs(m.opacity - goal) > 0.002
          ? m.opacity + (goal - m.opacity) * 0.18
          : goal;
        this._kirli = true;
      }
      // Tamamen saydamken hiç çizilmesin: duvar boyu bir katman.
      this.gridMesh.visible = m.opacity > 0.003;

      if (this._popIns) { this.stepPopIns(); this._kirli = true; }
      if (this._dusenler) { this.stepDecor(); this._kirli = true; }
      this.fadeSideWalls();

      this.camera.updateMatrixWorld();
      const kameraOynadi = !matrisYakin(this.camera.matrixWorld, sonKamera);
      // Yarım saniyede bir nabız: gözden kaçan bir değişiklik bayat kalmasın.
      const nabiz = now - sonCizim > 500;
      if (!this._kirli && !kameraOynadi && !nabiz && now > (this._uyanik || 0)) return;

      if (this._kirli || nabiz) this.renderer.shadowMap.needsUpdate = true;
      this._kirli = false;
      sonKamera.copy(this.camera.matrixWorld);
      sonCizim = now;
      this.syncObjTools();
      this.lambaIsiklari();
      this.renderer.render(this.scene, this.camera);
    };
    tick();
  }

  /* ======================================================================
     MODELLER
     ====================================================================== */
  async loadModels() {
    const modelsMod = await import(this.cfg.modelsUrl || new URL('./kreaz-models.js', import.meta.url).href);
    this.templates = new Map();

    for (const p of this.products) {
      let buffer;
      const external = this.cfg.models?.[p.id];
      if (external) {
        buffer = await fetch(external).then((r) => {
          if (!r.ok) throw new Error(`${p.id} modeli yüklenemedi (${r.status})`);
          return r.arrayBuffer();
        });
      } else {
        const b64 = modelsMod.EMBEDDED_MODELS[p.id];
        if (!b64) throw new Error(`"${p.id}" için gömülü model yok.`);
        buffer = modelsMod.decodeModel(b64);
      }

      const raw = glbToObject3D(buffer);
      raw.updateMatrixWorld(true);
      const box = new T.Box3().setFromObject(raw);

      // Gerçek model 30 cm rafta 30.4 cm ölçüsünde. Grid matematiği tam otursun
      // diye modeli mantıksal genişliğe (6/9/12 birim) ölçekliyoruz.
      const target = p.units * U;
      const scale = target / (box.max.x - box.min.x);

      // Orijini: X/Y'de merkez, Z'de duvar yüzeyi (arka montaj plakası z=0'da)
      raw.position.set(
        -(box.max.x + box.min.x) / 2,
        -(box.max.y + box.min.y) / 2,
        -box.min.z,
      );

      const template = new T.Group();
      template.add(raw);
      template.scale.setScalar(scale);
      applyBoxUV(raw, 25); // 25 mm'de bir tekrar → ince toz boya dokusu

      this.templates.set(p.id, {
        template,
        size: {
          w: (box.max.x - box.min.x) * scale,
          h: (box.max.y - box.min.y) * scale,
          d: (box.max.z - box.min.z) * scale,
        },
      });
    }

    // Tüm kartların sabit görseli varsa ikinci bir WebGL bağlamı açmaya gerek
    // yok; eksik olan çıkarsa syncCardImages tuvali o an kurar.
    if (!this.hasPhotos()) this.buildThumbRenderer();
  }

  /** Renk kimliğine göre paylaşılan toz boya malzemesi. */
  materialFor(colorId) {
    if (this.materialCache.has(colorId)) return this.materialCache.get(colorId);
    const c = this.colorById.get(colorId) || this.cfg.colors[0];
    const mat = this.rafMalzemesi(c.hex, c);
    this.materialCache.set(colorId, mat);
    return mat;
  }

  /**
   * Toz boya malzemesi. Hangi yolun kullanılacağı açılıştaki öz-testten
   * geliyor (bkz. rafMalzemesiSina); sağlam cihazda hep tam malzeme.
   */
  rafMalzemesi(hex, c = {}, kip = this._rafKip || RAF_KIPLERI[0]) {
    const o = {
      color: new T.Color(hex),
      roughness: c.roughness ?? 0.5,
      metalness: 0.06,
      // Rafların kendi stüdyo ortamı (bkz. makeShelfEnvTexture).
      envMap: this.shelfEnv,
      envMapIntensity: this.shelfEnvIntensity(),
      side: T.DoubleSide,
      shadowSide: T.DoubleSide,
    };
    if (kip.harita) {
      Object.assign(o, {
        normalMap: this.tex.powder.normalMap,
        normalScale: new T.Vector2(0.22, 0.22),
        roughnessMap: this.tex.powder.roughnessMap,
      });
    }
    const mat = kip.fizik
      ? new T.MeshPhysicalMaterial(Object.assign(o, {
        clearcoat: c.clearcoat ?? 0.28,
        // Daha toplu yansıma: tabla üstlerinde ışık bir çizgi olarak okunuyor.
        clearcoatRoughness: 0.18,
      }))
      : new T.MeshStandardMaterial(o);
    // Masa lambası tablaya, arka plakaya ve yan panellere vuruyor.
    if (kip.lamba) lambaIsigiEkle(mat);
    return mat;
  }

  /**
   * Raf malzemesi öz-testi.
   *
   * Bir Android tablette (Chrome) raflar mor görünüyordu. Ekran görüntüsü
   * ölçülünce rafın kırmızı ve mavi kanalı yer değiştirmişti (Blush Clay
   * plaka 232/134/147 yerine 111/90/157); duvar ise birebir doğruydu. Yani
   * bozulma o GPU'da raf malzemesinin kendi yolunda. Cihaz elimizde
   * olmadığı için açılışta 8×8'lik bir görüntüye pembe bir yüzey çiziliyor
   * ve okunuyor: kırmızı maviden belirgin büyük değilse raflar sırayla
   * daha sade malzemelere geçiyor. Sağlam cihazda tek bir küçük çizim.
   *
   * ?kztani=1 ile açılınca her yolun sonucu ekranın köşesinde yazıyor:
   * cihazdan ekran görüntüsü teşhis için yetiyor.
   */
  rafMalzemesiSina() {
    const tani = /[?&]kztani=/.test(location.search);
    const rt = new T.WebGLRenderTarget(8, 8);
    const sahne = new T.Scene();
    // Kanallar doymasın diye loş: pembe ~200/60/80 okunuyor.
    const isik = new T.DirectionalLight(0xffffff, 0.9);
    isik.position.set(0.3, 0.4, 1);
    sahne.add(isik, new T.AmbientLight(0xffffff, 0.25));
    const kamera = new T.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10);
    kamera.position.z = 2;
    const duz = new T.Mesh(new T.PlaneGeometry(1, 1));
    sahne.add(duz);
    const px = new Uint8Array(8 * 8 * 4);
    const sonuc = [];
    let secilen = null;
    try {
      for (const kip of RAF_KIPLERI) {
        const m = this.rafMalzemesi('#ea899a', {}, kip);
        duz.material = m;
        this.renderer.setRenderTarget(rt);
        this.renderer.render(sahne, kamera);
        this.renderer.readRenderTargetPixels(rt, 0, 0, 8, 8, px);
        m.dispose();
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < 64; i++) { r += px[i * 4]; g += px[i * 4 + 1]; b += px[i * 4 + 2]; }
        const ok = r > b * 1.15;
        sonuc.push({ kip: kip.ad, r: Math.round(r / 64), g: Math.round(g / 64), b: Math.round(b / 64), ok });
        if (ok && !secilen) secilen = kip;
        if (secilen && !tani) break;
      }
    } catch (err) {
      console.warn('[KREAZ] raf malzemesi sınaması', err);
    } finally {
      this.renderer.setRenderTarget(null);
      rt.dispose();
      duz.geometry.dispose();
    }
    this._rafKip = secilen || RAF_KIPLERI[0];
    this._rafSinama = sonuc;
    if (secilen && secilen !== RAF_KIPLERI[0]) {
      this.track('kz_gpu_yedek', { kip: secilen.ad });
      this.clarityTag('kz_gpu', `${secilen.ad} · ${this.gpuAdi()}`);
    }
    if (tani) this.taniGoster();
  }

  /** GPU'nun adı (tarayıcı izin veriyorsa). */
  gpuAdi() {
    try {
      const gl = this.renderer.getContext();
      const e = gl.getExtension('WEBGL_debug_renderer_info');
      return String(e ? gl.getParameter(e.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    } catch { return '?'; }
  }

  /** ?kztani=1 — öz-test sonuçlarını ekranın köşesine yazar (teşhis için). */
  taniGoster() {
    const kutu = document.createElement('pre');
    kutu.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:99999;margin:0;padding:8px 10px;'
      + 'background:rgba(0,0,0,.82);color:#fff;font:11px/1.45 monospace;border-radius:8px;max-width:92vw;white-space:pre-wrap;pointer-events:none';
    const gl = this.renderer.getContext();
    const hp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    kutu.textContent = [
      `GPU: ${this.gpuAdi()}`,
      `highp: ${hp ? hp.precision : '?'} · ${navigator.userAgent.replace(/^.*?\(|\).*$/g, '').slice(0, 60)}`,
      ...this._rafSinama.map((s) => `${s.ok ? '✓' : '✗'} ${s.kip.padEnd(9)} ${s.r}/${s.g}/${s.b}`),
      `seçilen: ${this._rafKip.ad}`,
    ].join('\n');
    document.body.appendChild(kutu);
  }

  /** Ölçüye göre paylaşılan temas gölgesi (doku + düzlem), bir kez üretilir. */
  shadowFor(productId) {
    this._rafGolgeleri = this._rafGolgeleri || new Map();
    let g = this._rafGolgeleri.get(productId);
    if (!g) {
      const p = this.productById.get(productId);
      const d = makeShelfShadowTexture(p.units * U);
      g = {
        geometry: new T.PlaneGeometry(d.gw, d.gh),
        material: new T.MeshBasicMaterial({
          map: d.tex, transparent: true, depthWrite: false, toneMapped: false,
        }),
        yc: d.yc,
      };
      this._rafGolgeleri.set(productId, g);
    }
    return g;
  }

  makeShelfObject(productId, colorId) {
    const { template } = this.templates.get(productId);
    const obj = template.clone(true);
    const mat = this.materialFor(colorId);
    obj.traverse((o) => {
      if (o.isMesh) { o.material = mat; o.castShadow = true; o.receiveShadow = true; }
    });

    /* Temas gölgesi rafın ÇOCUĞU: sürüklenirken rafla birlikte gidiyor,
       duvara otururken rafla birlikte büyüyor, silinince onunla gidiyor.
       Raf şablonu ölçekli (model 30,4 cm → 30 cm); gölge düzlemi metre
       cinsinden kurulduğu için o ölçek geri alınıyor. */
    const g = this.shadowFor(productId);
    const golge = new T.Mesh(g.geometry, g.material);
    const s = obj.scale.x || 1;
    golge.scale.setScalar(1 / s);
    golge.position.set(0, g.yc / s, RAF_GOLGE_Z / s);
    golge.renderOrder = 1;
    golge.userData.kzGolge = true;
    golge.userData.kzEk = true;     // rafın kendi malzemesini almayan ek parça
    golge.raycast = () => {};   // tıklama rafı seçsin; gölgenin altındaki duvarı değil
    obj.add(golge);

    // Montaj vidaları — plakanın ön yüzünde (z = 2 mm). Tıklanınca rafı seçer.
    const vida = new T.Mesh(this.screwFor(productId), this.screwMaterial());
    vida.scale.setScalar(1 / s);
    vida.position.set(0, 0, 0.002 / s);
    vida.userData.kzEk = true;
    obj.add(vida);
    return obj;
  }

  /** Ölçüye göre paylaşılan vida geometrisi. */
  screwFor(productId) {
    this._vidalar = this._vidalar || new Map();
    let g = this._vidalar.get(productId);
    if (!g) {
      g = makeScrewGeometry(this.productById.get(productId).units * U);
      this._vidalar.set(productId, g);
    }
    return g;
  }

  /** Siyah, hafif parlak vida başı. Işığını rafların stüdyo ortamından alıyor. */
  screwMaterial() {
    if (!this._vidaMat) {
      this._vidaMat = new T.MeshStandardMaterial({
        color: 0x141414, roughness: 0.34, metalness: 0.45,
        envMap: this.shelfEnv, envMapIntensity: this.shelfEnvIntensity(),
      });
    }
    return this._vidaMat;
  }

  /* ======================================================================
     KATALOG KÜÇÜK GÖRSELLERİ (canlı 3D render)
     ====================================================================== */
  buildThumbRenderer() {
    const cv = document.createElement('canvas');
    cv.width = 276; cv.height = 186;
    this.thumbRenderer = new T.WebGLRenderer({ canvas: cv, antialias: true, alpha: true });
    this.thumbRenderer.setPixelRatio(1);
    this.thumbRenderer.toneMapping = T.ACESFilmicToneMapping;
    this.thumbRenderer.toneMappingExposure = 1.1;
    this.thumbRenderer.outputColorSpace = T.SRGBColorSpace;

    const pm = new T.PMREMGenerator(this.thumbRenderer);
    const src = makeStudioEnvTexture();
    this.thumbScene = new T.Scene();
    this.thumbScene.environment = pm.fromEquirectangular(src).texture;
    src.dispose(); pm.dispose();

    const l = new T.DirectionalLight(0xffffff, 1.6);
    l.position.set(2, 3, 4);
    this.thumbScene.add(l, new T.HemisphereLight(0xffffff, 0.8));

    this.thumbCam = new T.PerspectiveCamera(26, 276 / 186, 0.05, 20);
    this.thumbMaterials = new Map();
  }

  thumbMaterialFor(colorId) {
    if (this.thumbMaterials.has(colorId)) return this.thumbMaterials.get(colorId);
    const c = this.colorById.get(colorId) || this.cfg.colors[0];
    const m = new T.MeshPhysicalMaterial({
      color: new T.Color(c.hex),
      roughness: c.roughness ?? 0.5,
      metalness: 0.06,
      clearcoat: c.clearcoat ?? 0.28,
      clearcoatRoughness: 0.42,
      envMapIntensity: 1.3,
      side: T.DoubleSide,
    });
    this.thumbMaterials.set(colorId, m);
    return m;
  }

  renderThumbs() {
    if (!this.thumbRenderer) return;
    for (const p of this.products) {
      const target = this.root.querySelector(`.kz-card[data-id="${p.id}"] canvas`);
      if (!target) continue;

      const obj = this.templates.get(p.id).template.clone(true);
      const mat = this.thumbMaterialFor(this.activeColor);
      obj.traverse((o) => { if (o.isMesh) o.material = mat; });
      this.thumbScene.add(obj);

      // Ürünü kadraja oturt: model X/Y'de merkezli, Z'de 0..derinlik aralığında.
      const size = this.templates.get(p.id).size;
      const center = new T.Vector3(0, 0, size.d / 2);
      const radius = Math.hypot(size.w, size.h, size.d) * 0.5;
      const dist = (radius / Math.tan((this.thumbCam.fov * Math.PI) / 360)) * 1.02;
      const dir = new T.Vector3(0.40, 0.30, 0.87).normalize();
      this.thumbCam.position.copy(center).addScaledVector(dir, dist);
      this.thumbCam.lookAt(center);

      this.thumbRenderer.render(this.thumbScene, this.thumbCam);
      this.thumbScene.remove(obj);

      const ctx = target.getContext('2d');
      ctx.clearRect(0, 0, target.width, target.height);
      ctx.drawImage(this.thumbRenderer.domElement, 0, 0, target.width, target.height);
    }
  }

  /* ======================================================================
     PANEL İÇERİĞİ
     ====================================================================== */
  buildCatalog() {
    const wrap = this.root.querySelector('.kz-catalog');
    wrap.innerHTML = '';
    for (const p of this.products) {
      const card = el('div', 'kz-card');
      card.dataset.id = p.id;
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-label', `${p.name} ${p.cm} santimetre raf ekle`);
      // Her kartta hem fotoğraf hem 3D tuval durur; hangisinin görüneceğine
      // syncCardImages karar verir. İkisini birden koymak, o rengin bir
      // ölçüsünde sabit görsel tanımlı değilse yalnızca o kartın render'a
      // düşmesini sağlıyor.
      card.innerHTML = `
        <div class="kz-card-thumb">
          <img alt="" loading="lazy" decoding="async" hidden>
          <canvas width="304" height="192" hidden></canvas>
        </div>
        <div class="kz-card-foot">
          <div class="kz-card-id">
            <div class="kz-card-title">${p.name}</div>
            <div class="kz-card-meta">${p.cm} cm</div>
          </div>
          <div class="kz-card-price">${this.unitPriceHtml(p.id, this.activeColor)}</div>
        </div>
      `;
      // Sürükleme kaldırıldı: tek dokunuş rafı duvara ekler. Sürükleyerek
      // yerleştirmek özellikle mobilde kaydırmayla karışıyordu.
      card.addEventListener('click', () => this.trackShelfAdd(this.addShelf(p.id, this.activeColor), 'tıklama'));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.trackShelfAdd(this.addShelf(p.id, this.activeColor), 'klavye'); }
      });
      wrap.appendChild(card);
    }
    this.syncCardImages();
  }

  /** Stüdyo için sabitlenmiş bir görsel tanımlı mı? */
  hasPhotos() {
    return (this.cfg.colors || []).some((c) => {
      const s = c.studioImages;
      return s && (s.small || s.medium || s.large);
    });
  }

  /**
   * (ölçü, renk) çiftinin katalog görseli.
   *
   * YALNIZCA stüdyo için ayrılmış sabit görseli kullanır — varyant görseline
   * bakmaz. Varyant görselleri mağazanın işi: kutu fotoğrafı, ölçü şeması,
   * kampanya görseli konulabilir ve konulduğu anda stüdyonun katalogu
   * bozuluyordu. Sabit görsel yoksa kart canlı 3D önizlemeye düşer, yani
   * eksik ayar hiçbir zaman kırık bir kart bırakmaz.
   */
  imageFor(productId, colorId) {
    return this.colorById.get(colorId)?.studioImages?.[productId] || null;
  }

  /**
   * Kart görsellerini aktif renge göre günceller.
   * Her kart kendi başına karar verir: sabit görseli varsa fotoğraf, yoksa
   * 3D önizleme. Böylece 18 görselden biri eksik kalsa da yalnızca o kart
   * render'a düşer, katalogun tamamı değil.
   */
  syncCardImages() {
    const color = this.colorById.get(this.activeColor);
    let needThumbs = false;

    for (const p of this.products) {
      const thumb = this.root.querySelector(`.kz-card[data-id="${p.id}"] .kz-card-thumb`);
      if (!thumb) continue;
      const img = thumb.querySelector('img');
      const cv = thumb.querySelector('canvas');
      const src = this.imageFor(p.id, this.activeColor);

      if (src) {
        img.src = src;
        img.alt = `${color?.name || ''} ${p.name} raf ${p.cm} cm`.trim();
        img.hidden = false;
        if (cv) cv.hidden = true;
      } else {
        img.hidden = true;
        if (cv) cv.hidden = false;
        needThumbs = true;
      }
    }

    if (needThumbs) {
      if (!this.thumbRenderer) this.buildThumbRenderer();
      this.renderThumbs();
    }
  }

  buildColorPalette() {
    const wrap = this.root.querySelector('.kz-colors');
    wrap.innerHTML = '';
    for (const c of this.cfg.colors) {
      const b = el('button', 'kz-swatch' + (c.id === this.activeColor ? ' kz-on' : ''));
      b.type = 'button';
      b.dataset.color = c.id;
      b.setAttribute('aria-label', `${c.name}${c.ral ? ' — ' + c.ral : ''}`);
      b.innerHTML = `
        <span class="kz-swatch-chip" style="background:${c.hex}"></span>
        <span class="kz-swatch-name">${c.name}</span>
        ${c.ral ? `<span class="kz-swatch-ral">${c.ral}</span>` : ''}
      `;
      b.addEventListener('click', () => {
        this.pickColor(c.id);
        // Yalnızca MÜŞTERİNİN tıklaması sayılıyor; oda seçimi de renk atıyor.
        this.track('kz_renk_secildi', { renk: c.name });
        this.clarityTag('kz_renk_tiklandi', c.name);
      });
      wrap.appendChild(b);
    }
    this.syncActiveColorName();
  }

  /**
   * Katalog kartlarındaki tutarları tazeler.
   * Hem renk değişince (varyant fiyatı farklı olabilir) hem de kademe atlayınca
   * (indirim oranı değişti) çağrılır.
   */
  syncCardPrices() {
    const rate = this.pricing().discountRate;
    for (const p of this.products) {
      const cell = this.root.querySelector(`.kz-card[data-id="${p.id}"] .kz-card-price`);
      if (cell) cell.innerHTML = this.unitPriceHtml(p.id, this.activeColor, rate);
    }
  }

  /** Seçili rengin adını paletin altında tek satırda gösterir. */
  syncActiveColorName() {
    const box = this.root.querySelector('.kz-color-active');
    const c = this.colorById.get(this.activeColor);
    if (box && c) box.innerHTML = `${c.name}${c.ral ? `<span>${c.ral}</span>` : ''}`;
  }

  pickColor(colorId) {
    this.activeColor = colorId;
    this.root.querySelectorAll('.kz-swatch').forEach((s) => {
      s.classList.toggle('kz-on', s.dataset.color === colorId);
    });

    // Bir raf seçiliyse onun rengini değiştir, değilse yeni rafların rengini ayarla
    if (this.selected) {
      this.pushHistory();
      this.selected.colorId = colorId;
      const mat = this.materialFor(colorId);
      this.selected.object.traverse((o) => { if (o.isMesh && !o.userData.kzEk) o.material = mat; });
      this.refresh();
    }
    this.syncCardImages();
    this.syncActiveColorName();
    this.syncCardPrices();
    // Renk seçimi mesaj üretmiyor: müşteri renkleri denerken baloncuğun
    // her tıkta değişmesi dikkat dağıtıyordu. Değişikliği zaten ekranda
    // görüyor.
  }

  /* ======================================================================
     RAF EKLEME / TAŞIMA / SİLME
     ====================================================================== */
  addShelf(productId, colorId, cell) {
    const p = this.productById.get(productId);
    const slot = cell || this.findSlot(p.units);
    if (!slot) { this.say('nospace'); return null; }

    this.pushHistory();
    const shelf = {
      id: uid(),
      productId,
      colorId: colorId || this.activeColor,
      units: p.units,
      col: slot.col,
      row: slot.row,
      object: this.makeShelfObject(productId, colorId || this.activeColor),
    };
    this.shelfGroup.add(shelf.object);
    this.shelves.push(shelf);
    this.placeObject(shelf);
    // Duvara oturma hareketi — oda ön ayarlarındakiyle aynı.
    this.popIn([shelf.object], 0);
    /* Yeni raf SEÇİLİ gelmiyor (önceki seçim de kalkıyor). Seçili kalınca
       müşterinin bir sonraki raf için tıkladığı renk, az önce eklediği rafı
       boyuyordu. Rafı boyamak ya da taşımak için üstüne dokunması yeterli. */
    this.select(null);
    // Dar ekranda çekmece duvarın üstünü kapatıyor; ekledikten sonra kapat ki
    // müşteri sonucu görsün.
    if (this.isCompact()) this.setDrawer(false);
    this.refresh();
    /* Duvar 1 raftan 2'ye çıktığı an kargo ücretsiz oluyor. Kazanılmış bir
       avantaj olduğu için bir kez duyuruluyor; tavsiyeler kesilmiyor. */
    if (this.shelves.length === 2 && !this._kargoDuyuruldu) {
      this._kargoDuyuruldu = true;
      this.say('kargo');
    } else {
      this.say('added');
    }
    /* İlk raf duvara oturunca rehber el önce rafın taşınabildiğini, sonra
       duvarın çevrilebildiğini gösteriyor — müşteri bu arada başka bir
       şeye dokunmadıysa. */
    const gordu = this._rehberGordu;
    if (this.shelves.length === 1 && this._rehber && (!gordu.tasi || !gordu.gez)) {
      setTimeout(() => {
        if (performance.now() - (this._rehberSonGirdi || 0) < 1200) return;
        this.rehberSira([...(gordu.tasi ? [] : ['tasi']), ...(gordu.gez ? [] : ['gez'])]);
      }, 1500);
    }
    return shelf;
  }

  removeShelf(shelf, silent) {
    this.shelfGroup.remove(shelf.object);
    this.decorTemizle(shelf);   // eşyalar rafla gidiyor; geometrileri bırakılıyor
    /* Geometri ATILMIYOR. Raf, temas gölgesi ve vidalar aynı ölçüdeki bütün
       raflarla ortak geometriyi kullanıyor (şablon klonu). Eskiden silinen
       rafın geometrisi dispose ediliyordu: aynı ölçüdeki diğer rafların da
       ekran kartındaki verisi atılıyor, her oda değişiminde yeniden
       yükleniyordu — bir durumda yeniden yüklenmeyip bütün "medium" raflar
       görünmez kaldı (yalnızca gölgeleri ve vidaları duruyordu). */
    this.shelves = this.shelves.filter((s) => s !== shelf);
    if (this.selected === shelf) this.select(null);
    this.invalidate();
    if (!silent) this.refresh();
  }

  placeObject(shelf) {
    const { x, y } = this.cellToWorld(shelf.col, shelf.row, shelf.units);
    shelf.object.position.set(x, y, 0.0015);
    if (this.selected === shelf) this.syncSelectionRing();
    this.invalidate();
  }

  /** Duvar küçüldüğünde raflar taşarsa içeri çeker, sığmazsa kaldırır. */
  reflowShelves() {
    const dropped = [];
    for (const s of [...this.shelves]) {
      if (this.fits(s.col, s.row, s.units, s.id)) { this.placeObject(s); continue; }
      let moved = false;
      const col = clamp(s.col, 0, this.cols - s.units);
      const row = clamp(s.row, ALT_SIRA, Math.max(ALT_SIRA, this.rows - SHELF_ROWS));
      for (let dr = 0; dr < this.rows && !moved; dr++) {
        for (let dc = 0; dc <= this.cols && !moved; dc++) {
          for (const r of [row - dr, row + dr]) {
            for (const c of [col - dc, col + dc]) {
              if (this.fits(c, r, s.units, s.id)) { s.col = c; s.row = r; moved = true; break; }
            }
            if (moved) break;
          }
        }
      }
      if (moved) this.placeObject(s);
      else { dropped.push(s); this.removeShelf(s, true); }
    }
    if (dropped.length) this.say('dropped', { n: dropped.length });
  }

  select(shelf) {
    this.selected = shelf;
    this.invalidate();   // seçim halkası
    this.root.querySelectorAll('.kz-row').forEach((r) => {
      r.classList.toggle('kz-on', shelf && r.dataset.id === shelf.id);
    });
    this.selectionRing.visible = !!shelf;
    if (shelf) {
      this.activeColor = shelf.colorId;
      this.root.querySelectorAll('.kz-swatch').forEach((s) => {
        s.classList.toggle('kz-on', s.dataset.color === shelf.colorId);
      });
      this.syncSelectionRing();
    }
    this.root.querySelector('.kz-color-note').textContent =
      shelf ? 'Seçili rafı boyar' : 'Yeni raflar için';
    this.objTools.classList.toggle('kz-show', !!shelf);
    this.syncObjTools();
  }

  syncSelectionRing() {
    const s = this.selected;
    if (!s) return;
    const { x, y } = this.cellToWorld(s.col, s.row, s.units);
    const w = s.units * U + 0.05;
    const h = SHELF_ROWS * U + 0.05;
    this.selectionRing.scale.set(w, h, 1);
    this.selectionRing.material.uniforms.uBoyut.value.set(w, h);
    /* Duvarla raf arasında: içindeki hafif ton yalnız rafın çevresinde
       görünüyor, rafın kendisini boyamıyor. */
    this.selectionRing.position.set(x, y, 0.0009);
  }

  /** Seçili rafın üstündeki HTML araç çubuğunu 3D konuma sabitler. */
  syncObjTools() {
    const s = this.selected;
    if (!s || !this.objTools) { return; }
    const { x, y } = this.cellToWorld(s.col, s.row, s.units);
    const v = new T.Vector3(x, y + SHELF_ROWS * U * 0.5 + 0.09, 0.12).project(this.camera);
    const rect = this.stageEl.getBoundingClientRect();
    this.objTools.style.left = `${((v.x + 1) / 2) * rect.width}px`;
    this.objTools.style.top = `${((-v.y + 1) / 2) * rect.height}px`;
  }

  /* ======================================================================
     SÜRÜKLE & BIRAK
     ====================================================================== */
  pointerToWall(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    if (Math.abs(this.ndc.x) > 1.2 || Math.abs(this.ndc.y) > 1.2) return null;

    this.ray.setFromCamera(this.ndc, this.camera);
    const hit = new T.Vector3();
    if (!this.ray.ray.intersectPlane(this.wallPlane, hit)) return null;
    return hit;
  }

  /** Duvar üzerindeki dünya noktasını grid hücresine çevirir. */
  worldToCell(point, units, anchor) {
    const W = this.wallW / 100;
    const colF = (point.x + W / 2) / U - units / 2 - (anchor?.dc || 0);
    const rowF = point.y / U - SHELF_ROWS / 2 - (anchor?.dr || 0);
    return {
      col: clamp(Math.round(colF), 0, Math.max(0, this.cols - units)),
      // Sürüklenen raf süpürgeliğe inmiyor, en alt geçerli sıraya oturuyor.
      row: clamp(Math.round(rowF), ALT_SIRA, Math.max(ALT_SIRA, this.rows - SHELF_ROWS)),
    };
  }

  /* --- katalogdan sürükleyerek yeni raf --- */
  onCardPointerDown(e, product) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const card = e.currentTarget;
    // İşaretçi artık etkin değilse setPointerCapture istisna atar; sürüklemenin
    // buna takılıp hiç başlamaması için yutuyoruz.
    try { card.setPointerCapture(e.pointerId); } catch { /* yoksay */ }
    card.classList.add('kz-dragging');

    this.drag = {
      mode: 'spawn',
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      productId: product.id,
      units: product.units,
      card,
      started: false,
      startX: e.clientX,
      startY: e.clientY,
      ghost: null,
      cell: null,
      valid: false,
    };
  }

  /** Panelin alt sayfa olarak çalıştığı dar ekran modu. */
  isCompact() {
    return this.root.clientWidth <= 900;
  }

  /** İmlecin altındaki rafı döndürür (yoksa null). */
  shelfAt(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, this.camera);

    // Raf az önce eklendiyse dünya matrisi henüz güncellenmemiş olabilir;
    // raycaster matrisi kendisi tazelemez.
    this.shelfGroup.updateMatrixWorld(true);

    const hits = this.ray.intersectObjects(this.shelfGroup.children, true);
    if (!hits.length) return null;

    let node = hits[0].object;
    while (node.parent && node.parent !== this.shelfGroup) node = node.parent;
    return this.shelves.find((s) => s.object === node) || null;
  }

  /* --- duvardaki rafı taşı / seç --- */
  onStagePointerDown(e) {
    if (e.button !== undefined && e.button !== 0) return;

    /* İKİNCİ PARMAK: taşıma iptal, kıstırma başlıyor.
       İlk parmak bir rafa değince kamera kilitleniyordu; raflar ekranı
       kapladığında boş duvar kalmadığı için müşteri hiçbir şekilde geri
       çekilemiyordu. Artık iki parmak her zaman yakınlaştırıp uzaklaştırıyor:
       raf yerine dönüyor, ilk parmak kameraya devrediliyor, bu olay da
       kameranın kendi dinleyicisine geçiyor (durdurulmuyor). */
    const d = this.drag;
    if (d && d.pointerId !== undefined && e.pointerId !== d.pointerId) {
      if (d.mode === 'move' || d.mode === 'prop') {
        const ilk = { id: d.pointerId, x: d.sonX ?? d.startX, y: d.sonY ?? d.startY };
        if (d.mode === 'prop' && d.started) {
          this.prop.duvar = d.origin.duvar; this.prop.t = d.origin.t; this.placeProp();
        }
        this.cancelDrag();
        this.orbit._pointers.set(ilk.id, { x: ilk.x, y: ilk.y, pan: false });
      }
      return;
    }

    const shelf = this.shelfAt(e);
    /* Raf yoksa eşyaya bakıyoruz: eşya duvarda YATAY kayabiliyor. Rafların
       önüne geçmemesi için sıra rafta; ancak raf bulunamazsa eşya sürüklenir. */
    if (!shelf) {
      if (this.prop && this.propAt(e)) {
        e.stopPropagation();
        this.orbit.enabled = false;
        this.select(null);
        this.drag = {
          mode: 'prop',
          pointerId: e.pointerId,
          origin: { duvar: this.prop.duvar, t: this.prop.t },
          /* TUTMA NOKTASI. Eşyayı kolundan tutup biraz oynatınca merkezi
             imlece atlıyordu; hareket artık imlecin başlangıca göre FARKI
             kadar oluyor, yani tutulan yer parmağın altında kalıyor. */
          tutmaT: this.prop.t,
          tutmaNokta: this.pointerToFloor(e),
          started: false,
          startX: e.clientX,
          startY: e.clientY,
        };
        return;
      }
      this.select(null);
      return;
    }

    // Kamerayı kilitle; sürükleme rafı taşısın
    e.stopPropagation();
    this.orbit.enabled = false;
    this.select(shelf);

    const point = this.pointerToWall(e);
    const anchor = point
      ? {
          dc: (point.x + this.wallW / 200) / U - shelf.units / 2 - shelf.col,
          dr: point.y / U - SHELF_ROWS / 2 - shelf.row,
        }
      : { dc: 0, dr: 0 };

    this.drag = {
      mode: 'move',
      pointerId: e.pointerId,
      shelf,
      anchor,
      origin: { col: shelf.col, row: shelf.row },
      started: false,
      startX: e.clientX,
      startY: e.clientY,
      cell: { col: shelf.col, row: shelf.row },
      valid: true,
      pushed: false,
    };
  }

  onDragMove(e) {
    const d = this.drag;
    if (!d || (d.pointerId !== undefined && e.pointerId !== d.pointerId)) return;
    // Son konum: ikinci parmak gelirse bu parmak kameraya devrediliyor.
    d.sonX = e.clientX; d.sonY = e.clientY;

    if (!d.started) {
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.hypot(dx, dy) < 6) return;
      d.started = true;
      // Rafı kendisi taşıdıysa taşıma gösterimine gerek yok.
      if (d.mode === 'move') this.rehberIsaretle('tasi');

      // Mobilde ürünler çekmecededir; sürükleme başlayınca çekmeceyi kapatıp
      // duvarı açığa çıkarıyoruz, yoksa müşteri bıraktığı yeri göremez.
      if (d.mode === 'spawn' && this.isCompact()) this.setDrawer(false);
      if (d.mode === 'spawn') {
        d.ghost = this.makeShelfObject(d.productId, this.activeColor);
        // Havadaki hayalet duvara gölge düşürmesin, henüz vidalanmamış olsun
        // (paylaşılan malzemeleri de aşağıda kopyalanıp atılmasın).
        d.ghost.children.filter((c) => c.userData.kzEk).forEach((c) => d.ghost.remove(c));
        d.ghost.traverse((o) => {
          if (!o.isMesh) return;
          o.material = o.material.clone();
          o.material.transparent = true;
          o.material.opacity = 0.62;
          o.castShadow = false;
        });
        d.ghost.visible = false;
        this.shelfGroup.add(d.ghost);
      }
      this.objTools.classList.remove('kz-show');
    }

    /* Oda eşyası: zeminde imlecin olduğu noktaya en yakın DUVARA yaslanıp
       o duvar boyunca kayar. Konum hep duvar yüzeyinden hesaplandığı için
       eşya sürükleme sırasında duvardan hiç ayrılmıyor. */
    if (d.mode === 'prop') {
      const yer = this.pointerToFloor(e);
      if (!yer || !this.prop) return;
      const tutma = { duvar: d.origin.duvar, t: d.tutmaT, nokta: d.tutmaNokta };
      const hedef = this.propTargetAt(yer, tutma);
      if (hedef && (hedef.duvar !== this.prop.duvar || hedef.t !== this.prop.t)) {
        const duvarDegisti = hedef.duvar !== this.prop.duvar;
        this.prop.duvar = hedef.duvar;
        this.prop.t = hedef.t;
        this.placeProp();
        if (duvarDegisti) {
          /* Duvar değişti: tutma noktasını YENİ duvara göre sıfırlıyoruz,
             yoksa eski duvarın ekseninden hesaplanan fark eşyayı bir daha
             fırlatıyor. */
          d.origin.duvar = hedef.duvar;
          d.tutmaT = hedef.t;
          d.tutmaNokta = yer;
          // Arka duvara geçmek kadrajı değiştiriyor; yumuşak geçiş.
          this.fitCamera(false, true);
        }
      }
      return;
    }

    const point = this.pointerToWall(e);
    const units = d.mode === 'spawn' ? d.units : d.shelf.units;
    const target = d.mode === 'spawn' ? d.ghost : d.shelf.object;

    this.invalidate();   // hayalet, taşınan raf ve yerleşim göstergesi
    if (!point) {
      d.valid = false;
      this.highlight.visible = false;
      if (d.mode === 'spawn' && d.ghost) d.ghost.visible = false;
      return;
    }

    const cell = this.worldToCell(point, units, d.anchor);
    const ignore = d.mode === 'move' ? d.shelf.id : null;
    d.cell = cell;
    d.valid = this.fits(cell.col, cell.row, units, ignore);

    const { x, y } = this.cellToWorld(cell.col, cell.row, units);
    if (target) {
      target.visible = true;
      target.position.set(x, y, d.mode === 'spawn' ? 0.02 : 0.0015);
    }

    // Yerleşim göstergesi: yeşil = uygun, kırmızı = dolu
    this.highlight.visible = true;
    this.highlight.material.color.set(d.valid ? 0x2f7d53 : 0xc2452d);
    this.highlight.geometry.dispose();
    this.highlight.geometry = new T.PlaneGeometry(units * U, SHELF_ROWS * U);
    this.highlight.position.set(x, y, 0.004);

    this.showMeasure(cell, units);
  }

  onDragEnd(e) {
    const d = this.drag;
    if (!d || (d.pointerId !== undefined && e.pointerId !== d.pointerId)) return;

    this.highlight.visible = false;
    this.orbit.enabled = true;
    d.card?.classList.remove('kz-dragging');

    if (d.mode === 'prop') {
      this.drag = null;
      if (d.started) { this.persist(); this.refresh(); }
      return;
    }

    if (!d.started) {
      // Sürükleme değil, tıklama: katalog kartıysa otomatik yerleştir
      this.drag = null;
      if (d.mode === 'spawn') this.trackShelfAdd(this.addShelf(d.productId, this.activeColor), 'tıklama');
      else this.objTools.classList.add('kz-show');
      return;
    }

    if (d.mode === 'spawn') {
      this.shelfGroup.remove(d.ghost);
      d.ghost.traverse((o) => { if (o.isMesh) o.material.dispose(); });
      this.drag = null;
      if (d.valid && d.cell) this.trackShelfAdd(this.addShelf(d.productId, this.activeColor, d.cell), 'sürükleme');
      else if (d.cell) this.say('collision');
      return;
    }

    // Taşıma
    const s = d.shelf;
    if (d.valid && d.cell) {
      const moved = d.cell.col !== d.origin.col || d.cell.row !== d.origin.row;
      if (moved) {
        const { col, row } = d.cell;
        s.col = d.origin.col; s.row = d.origin.row;
        this.pushHistory();
        s.col = col; s.row = row;
        // Taşıma sessiz: sürükleme sırasında her bırakışta konuşmak gereksiz.
      }
    } else {
      s.col = d.origin.col; s.row = d.origin.row;
      this.say('collision');
    }
    this.placeObject(s);
    this.drag = null;
    this.objTools.classList.add('kz-show');
    this.refresh();
  }

  cancelDrag() {
    const d = this.drag;
    if (!d) return;
    if (d.mode === 'spawn' && d.ghost) this.shelfGroup.remove(d.ghost);
    if (d.mode === 'move' && d.started) { d.shelf.col = d.origin.col; d.shelf.row = d.origin.row; this.placeObject(d.shelf); }
    d.card?.classList.remove('kz-dragging');
    this.highlight.visible = false;
    this.orbit.enabled = true;
    this.drag = null;
    this.invalidate();
  }

  onDoubleClick(e) {
    const shelf = this.shelfAt(e);
    if (!shelf) return;
    this.pushHistory();
    this.removeShelf(shelf);
    this.say('removed');
  }

  showMeasure(cell, units) {
    const left = cell.col * 5;
    const bottom = cell.row * 5;
    this.hintEl.textContent = `Soldan ${left} cm · Zeminden ${bottom} cm · ${units * 5} cm raf`;
    this.hintEl.classList.add('kz-show');
    clearTimeout(this._hintT);
    this._hintT = setTimeout(() => this.hintEl.classList.remove('kz-show'), 1600);
  }

  showHint(text) {
    this.hintEl.textContent = text;
    this.hintEl.classList.add('kz-show');
    clearTimeout(this._hintT);
    this._hintT = setTimeout(() => this.hintEl.classList.remove('kz-show'), 5200);
  }

  /* ======================================================================
     GEÇMİŞ (geri al)
     ====================================================================== */
  snapshot() {
    return {
      wallW: this.wallW,
      wallH: this.wallH,
      wallColorId: this.wallColorId,
      // Seçilen oda: yenilemede fayans/eşya kaybolmasın, geri al da odayı alsın.
      roomId: this.room?.id || null,
      propDuvar: this.prop?.duvar || null,
      propT: this.prop?.t ?? null,
      propGizli: !!this.propHidden,
      dekor: !!this.dekorAcik,
      shelves: this.shelves.map((s) => ({
        productId: s.productId, colorId: s.colorId, col: s.col, row: s.row,
      })),
    };
  }

  pushHistory() {
    this.history.push(this.snapshot());
    if (this.history.length > 40) this.history.shift();
  }

  applySnapshot(snap) {
    /* Geri alma rafları sıfırdan kuruyor. Aynı yerde aynı ölçüde kalan
       rafların eşyaları saklanıp yeni rafa geri takılıyor: başka bir
       değişikliği (renk, eşya, mobilya) geri almak, bütün eşyaları yeniden
       düşürmesin. */
    const sakla = new Map();
    for (const s of this.shelves) {
      const d = s.dekor;
      if (!d || d.kapaniyor || !d.ogeler.length) continue;
      d.grup.parent?.remove(d.grup);
      sakla.set(`${s.productId}|${s.col}|${s.row}`, d);
      s.dekor = null;
    }
    [...this.shelves].forEach((s) => this.removeShelf(s, true));
    this.wallW = snap.wallW; this.wallH = snap.wallH;
    if (snap.wallColorId) this.wallColorId = snap.wallColorId;
    this.wInput.value = this.wallW; this.hInput.value = this.wallH;
    this.buildWallColors();
    this.applyRoomScene(snap.roomId, snap, snap.propGizli);
    this.buildRoom();
    for (const s of snap.shelves) {
      const p = this.productById.get(s.productId);
      if (!p) continue;
      const shelf = {
        id: uid(), productId: s.productId, colorId: s.colorId,
        units: p.units, col: s.col, row: s.row,
        object: this.makeShelfObject(s.productId, s.colorId),
      };
      this.shelfGroup.add(shelf.object);
      this.shelves.push(shelf);
      this.placeObject(shelf);
      const anahtar = `${s.productId}|${s.col}|${s.row}`;
      const d = sakla.get(anahtar);
      if (d) { shelf.object.add(d.grup); shelf.dekor = d; sakla.delete(anahtar); }
    }
    // Geri dönmeyen rafların eşyaları bırakılıyor.
    for (const d of sakla.values()) this.decorTemizle({ dekor: d });
    this.select(null);
    this.refresh();
  }

  undo() {
    const snap = this.history.pop();
    if (!snap) return;
    this.applySnapshot(snap);
    this.say('undo');
  }

  /* ======================================================================
     KAYIT
     ====================================================================== */
  persist() {
    try {
      localStorage.setItem(this.cfg.persistKey, JSON.stringify(this.snapshot()));
    } catch { /* özel sekme / kota — sessizce geç */ }
  }

  restore() {
    let snap = null;
    try { snap = JSON.parse(localStorage.getItem(this.cfg.persistKey) || 'null'); } catch { /* yoksay */ }
    if (!snap || !Array.isArray(snap.shelves)) return;

    this.wallW = snap5(clamp(snap.wallW || this.wallW, this.cfg.wall.min, this.cfg.wall.max));
    this.wallH = snap5(clamp(snap.wallH || this.wallH, this.cfg.wall.minHeight, this.cfg.wall.maxHeight));
    if (snap.wallColorId && (this.cfg.wallColors || []).some((w) => w.id === snap.wallColorId)) {
      this.wallColorId = snap.wallColorId;
      this.buildWallColors();
    }
    this.wInput.value = this.wallW; this.hInput.value = this.wallH;
    /* Oda sahnesi duvardan ÖNCE: yüzey bayrağı `buildRoom` içinde okunuyor,
       eşya da raflardan önce durmalı ki `fits` doğru karar versin. */
    this.applyRoomScene(snap.roomId, snap, snap.propGizli);
    this.buildRoom();

    const kay = supurgelikPayi(snap.shelves.map((s) => s.row));
    for (const s of snap.shelves) {
      const p = this.productById.get(s.productId);
      if (!p || !this.colorById.has(s.colorId)) continue;
      const row = s.row + kay;
      if (!this.fits(s.col, row, p.units)) continue;
      const shelf = {
        id: uid(), productId: s.productId, colorId: s.colorId,
        units: p.units, col: s.col, row,
        object: this.makeShelfObject(s.productId, s.colorId),
      };
      this.shelfGroup.add(shelf.object);
      this.shelves.push(shelf);
      this.placeObject(shelf);
    }
    if (this.shelves.length) this.activeColor = this.shelves[this.shelves.length - 1].colorId;
    // Eşyalar açık bırakıldıysa açık gelsin (ilk yenilemede raflara düşüyor).
    if (snap.dekor) {
      this.dekorAcik = true;
      this.root.querySelector('[data-act="decor"]')?.setAttribute('aria-pressed', 'true');
    }
  }

  /* ======================================================================
     FİYAT & AVANTAJ
     ====================================================================== */
  money(cents) {
    const v = cents / 100;
    const f = this.cfg.moneyFormat;
    if (f) {
      const two = v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return f
        .replace(/\{\{\s*amount\s*\}\}/g, two)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(v).toLocaleString('en-US'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
          v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
          Math.round(v).toLocaleString('de-DE'));
    }
    return new Intl.NumberFormat(this.cfg.locale, {
      style: 'currency', currency: this.cfg.currency, maximumFractionDigits: 0,
    }).format(v);
  }

  pricing() {
    const count = this.shelves.length;
    const subtotal = this.shelves.reduce(
      (sum, s) => sum + this.priceFor(s.productId, s.colorId), 0);

    const tiers = this.cfg.tiers;
    let tier = null, next = null;
    for (const t of tiers) {
      if (count >= t.min) tier = t;
      else if (!next) next = t;
    }
    const discountRate = tier?.discount || 0;
    const discount = Math.round(subtotal * discountRate);
    const freeShipping = !!tier?.freeShipping;
    const shipping = count === 0 ? 0 : (freeShipping ? 0 : this.cfg.shippingCents);

    return {
      count, subtotal, discount, discountRate, freeShipping, shipping,
      total: subtotal - discount + shipping,
      tier, next,
    };
  }

  /* ======================================================================
     PANELİ GÜNCELLE
     ====================================================================== */
  refresh() {
    this.invalidate();
    this.clarityDesign();   // tasarım 4 sn durulunca linki oturuma yaz
    // Raflar değiştiyse dekor eşyaları yeni yerlerine düşsün.
    this.syncDecor();
    /* Raf silinip ikinin altına inildiyse açık e-posta formu kapanıyor
       (müşteri yazıyorsa dokunmuyoruz). */
    if (this.shelves.length < MAIL_MIN_RAF && this.emailBubbleOpen() && !this.bubbleLocked()) {
      this.assistantEl?.classList.remove('kz-talking');
      clearTimeout(this._restT);
    }
    const p = this.pricing();
    const r = this.root;

    /* ---------- üst fiyat çubuğu ---------- */
    r.querySelector('.kz-pill-total').textContent = p.count ? this.money(p.total) : '—';
    r.querySelector('.kz-pill-count').textContent = `${p.count} raf`;
    const saveChip = r.querySelector('.kz-pill-save');
    if (p.discount) {
      saveChip.hidden = false;
      saveChip.textContent = `%${Math.round(p.discountRate * 100)} kazanç`;
    } else {
      saveChip.hidden = true;
    }
    r.querySelector('.kz-pill').disabled = p.count === 0;
    if (!p.count) this.setPriceOpen(false);

    /* ---------- tasarım listesi (fiyat çubuğu açılırı) ---------- */
    const list = r.querySelector('.kz-pricepanel-list');
    list.innerHTML = '';
    if (!p.count) {
      list.appendChild(el('div', 'kz-list-empty', 'Henüz raf eklemedin.'));
    } else {
      for (const s of this.shelves) {
        const prod = this.productById.get(s.productId);
        const col = this.colorById.get(s.colorId);
        const row = el('div', 'kz-row' + (this.selected === s ? ' kz-on' : ''));
        row.dataset.id = s.id;
        // Ölçü yazılmıyor: adı zaten ölçüyü söylüyor (Small/Medium/Large) ve
        // satır dar ekranda fiyatla çakışıyordu.
        row.innerHTML = `
          <span class="kz-row-mark" style="color:${col.hex}" title="${col.name}">${ICONS.shelfMark}</span>
          <span class="kz-row-label">${prod.name} <span>${col.name}</span></span>
          <span class="kz-row-price">${this.unitPriceHtml(s.productId, s.colorId, p.discountRate)}</span>
          <button class="kz-row-del" title="Sil" aria-label="${prod.name} rafı sil">${ICONS.trash}</button>
        `;
        row.addEventListener('click', (e) => {
          if (e.target.closest('.kz-row-del')) return;
          this.select(s);
          this.refresh();
        });
        row.querySelector('.kz-row-del').addEventListener('click', (e) => {
          e.stopPropagation();
          this.pushHistory();
          this.removeShelf(s);
          this.say('removed');
        });
        list.appendChild(row);
      }
    }

    r.querySelector('.kz-sum-sub').textContent = p.count ? this.money(p.subtotal) : '—';
    const saveRow = r.querySelector('.kz-save');
    saveRow.hidden = !p.discount;
    if (p.discount) {
      saveRow.querySelector('.kz-save-label').textContent = `İndirim (%${Math.round(p.discountRate * 100)})`;
      r.querySelector('.kz-sum-disc').textContent = `− ${this.money(p.discount)}`;
    }
    const shipRow = r.querySelector('.kz-ship');
    shipRow.classList.toggle('kz-ship-free', p.count > 0 && p.freeShipping);
    r.querySelector('.kz-sum-ship').innerHTML = p.count === 0
      ? '—'
      : (p.freeShipping
          ? `<span class="kz-sum-strike">${this.money(this.cfg.shippingCents)}</span>Ücretsiz`
          : this.money(this.cfg.shippingCents));

    // Listenin en altında ödenecek tutar: indirim ve kargo dahil.
    r.querySelector('.kz-sum-grand-value').textContent = p.count ? this.money(p.total) : '—';

    /* ---------- avantaj rayı ---------- */
    const rail = r.querySelector('.kz-rail');
    rail.classList.toggle('kz-max', !p.next);
    rail.querySelector('.kz-rail-msg').innerHTML = this.tierMessage(p);

    const rt = this.railTiers();
    const stops = [...rail.querySelectorAll('.kz-stop')];
    stops.forEach((s, i) => {
      const reached = p.count >= rt[i].min;
      s.classList.toggle('kz-reached', reached);
      s.classList.toggle('kz-current', reached && p.tier === rt[i]);
    });

    // Dolgu, ulaşılan son durağa kadar uzanır; iki durak arasında oransal
    // ilerler — müşteri "ne kadar kaldı"yı görür.
    let idx = -1;
    for (let i = 0; i < rt.length; i++) if (p.count >= rt[i].min) idx = i;

    let pct = 0;
    if (stops.length > 1 && idx >= 0) {
      if (idx >= rt.length - 1) {
        pct = 100;
      } else {
        const seg = 100 / (rt.length - 1);
        const from = rt[idx].min;
        const to = rt[idx + 1].min;
        pct = seg * (idx + (p.count - from) / Math.max(1, to - from));
      }
    }
    rail.querySelector('.kz-rail-fill').style.width = `${clamp(pct, 0, 100)}%`;

    /* ---------- panel altı ---------- */
    r.querySelector('.kz-sum-total-value').textContent = p.count ? this.money(p.total) : '—';
    const cta = r.querySelector('.kz-cta');
    cta.disabled = p.count === 0;
    cta.querySelector('span').textContent = p.count
      ? `Tasarımını sepete ekle · ${p.count} raf`
      : 'Tasarımını sepete ekle';

    // Kademe değişince katalog tutarları da düşmeli.
    this.syncCardPrices();

    r.querySelector('[data-act="undo"]').disabled = !this.history.length;
    r.querySelector('[data-act="clear"]').disabled = !p.count;

    // Mobil çekmece düğmesindeki adet rozeti
    const badge = r.querySelector('.kz-drawer-open-count');
    if (badge) { badge.hidden = !p.count; badge.textContent = p.count; }
    this.srEl.textContent = `${p.count} raf, toplam ${this.money(p.total)}`;
    this.persist();
    // Her değişiklik sayacı sıfırlar: öneri yalnızca gerçekten durulduğunda gelir.
    this.armIdleNudge();

    this.syncShareButton();
    // E-posta verildiyse tasarımın son hali arka planda Klaviyo'ya yazılır.
    this.syncProfile();

  }

  /**
   * Rayın üstündeki tek satır.
   * Dar ekranda ikinci satıra taşarsa ray şişip görüntüyü bozuyor; cümleler
   * bu yüzden kısa tutulur, CSS de taşmayı kırpar.
   */
  tierMessage(p) {
    if (p.count === 0) return 'İlk rafını ekle, avantajlar başlasın.';
    if (!p.next) {
      const top = Math.round((this.topTier().discount || 0) * 100);
      return `<b>Maksimum avantaj:</b> %${top} + ücretsiz kargo.`;
    }
    const need = p.next.min - p.count;
    const what = p.next.discount
      ? `<b>%${Math.round(p.next.discount * 100)} indirim</b>`
      : '<b>ücretsiz kargo</b>';
    return `${need} raf daha ekle, ${what} kazan.`;
  }

  /* ======================================================================
     TASARIM ASİSTANI
     ====================================================================== */
  say(kind, data = {}) {
    // Müşteri e-posta yazıyorken ya da bir çekmece açıkken susuyoruz:
    // ekranın iki ayrı yerinde aynı anda yazı olmamalı. Dar ekranda asistan
    // baloncuğu ile çekmeceler aynı alanı paylaşıyor, üst üste biniyorlardı.
    if (this.bubbleLocked() || this.waDrawerOpen() || this.roomToastOpen()) return;

    /*
     * Her tuşta konuşma. Uyarılar (çakışma, yer yok) her zaman geçer; geri
     * kalan mesajlar bir bekleme süresine tabidir. Müşteri renk denerken ya
     * da rafı sürüklerken baloncuğun sürekli değişmesi hem dikkat dağıtıyor
     * hem asistanı gevezeleştiriyordu.
     */
    const ACIL = ['collision', 'nospace', 'dropped', 'welcome', 'resume', 'shared',
      'preset', 'kargo', 'oda'];
    if (!ACIL.includes(kind)) {
      if (Date.now() < (this._sayCooldown || 0)) return;
      this._sayCooldown = Date.now() + SAY_COOLDOWN_MS;
      this._sayCount = (this._sayCount || 0) + 1;
    }
    const p = this.pricing();
    const praise = [
      'Süper başlangıç!', 'Harika kombin!', 'Süper seçim!', 'Çok şık duruyor!',
      'Ritmi yakaladın!', 'Bu duvar karakter kazandı!', 'Hizalama kusursuz!',
      'Gözüme çok iyi geldi!',
    ];
    const pick = (a) => a[Math.floor(Math.random() * a.length)];
    const colorName = this.colorById.get(this.activeColor)?.name || 'Bu renk';

    /*
     * Yönlendirme cümlesi artık İNDİRİM YÜZDESİ SÖYLEMİYOR.
     * Müşteri oranı alttaki avantaj rayından zaten takip ediyor; asistanın
     * da aynı şeyi tekrarlaması satış baskısına dönüşüyordu. Yerine
     * duvarın gerçek kompozisyonundan çıkan bir tavsiye veriyoruz — adet
     * tavsiyenin doğal sonucu olarak artıyor.
     */
    const nudge = () => {
      if (p.count === 0) return 'Bir raf ekle, tasarıma başlayalım.';
      return this.idleLine(p)[1];
    };

    const tag = (label, tone) => ({ label, tone });

    let head = '', body = '', chip = null;

    switch (kind) {
      case 'welcome':
        head = 'Merhaba! Ben tasarım asistanın.';
        /* Oda düğmesi burada tanıtılıyor: kendi çekmecesiyle tanıtmak dar
           ekranda bu baloncukla çakışıyordu. */
        body = 'Nereye koyacağını seçmek için soldaki <b>?</b> düğmesine dokun '
          + '— ya da raf kartlarından birine tıkla.';
        chip = tag('İpucu');
        break;
      case 'resume':
        head = 'Tasarımın hazır bekliyordu.';
        body = nudge();
        chip = tag('Devam');
        break;
      case 'added':
        head = pick(praise);
        body = p.count === 1 ? `${colorName} bu duvarda çok iyi duruyor. ${nudge()}` : nudge();
        // Rozet indirim durumunu TEKRAR ETMİYOR: oran alttaki avantaj
        // rayında zaten duruyor, asistanda da görünmesi satış baskısı
        // gibi okunuyordu. Rozet artık mesajın ne olduğunu söylüyor.
        chip = tag('Öneri');
        break;
      case 'moved':
        head = pick(['Şimdi daha dengeli.', 'Güzel hizalama!', 'İşte bu.']);
        body = nudge();
        chip = tag('Yerleşim');
        break;
      case 'removed':
        head = 'Kaldırdım.';
        body = p.count ? nudge() : 'Duvar yeniden bomboş — istersen sıfırdan kurgulayalım.';
        chip = tag('Güncellendi');
        break;
      case 'cleared':
        head = 'Temiz sayfa!';
        body = 'Yeni bir kombin deneyelim mi? Farklı ölçüleri karıştırmak çok iyi duruyor.';
        chip = tag('Sıfırlandı');
        break;
      case 'undo':
        head = 'Geri aldım.';
        body = nudge();
        chip = tag('Geri al');
        break;
      case 'color':
        head = `${data.color || colorName} — güzel renk!`;
        body = this.selected ? 'Seçili rafı boyadım.' : `Şimdi eklediğin raflar ${data.color || colorName} olacak. ` + nudge();
        chip = tag('Renk');
        break;
      case 'wallcolor':
        head = `${data.name || 'Yeni duvar rengi'} denendi.`;
        body = 'Kendi duvarına en yakın tonu seç, raflar gerçekte nasıl duracak gör.';
        chip = tag('Duvar');
        break;
      case 'collision':
        head = 'Raflar üst üste binemez.';
        body = 'Birazcık kaydır, yeşil alan uygun demek.';
        chip = tag('Dikkat', 'warn');
        break;
      case 'nospace':
        head = 'Bu ölçü için boş alan kalmadı.';
        body = 'Duvarı büyütebilir ya da daha küçük bir raf seçebilirsin.';
        chip = tag('Dikkat', 'warn');
        break;
      case 'dropped':
        head = 'Duvar küçüldü.';
        body = `Sığmayan ${data.n} raf kaldırıldı.`;
        chip = tag('Dikkat', 'warn');
        break;
      /* Paylaşılan linkten gelen ziyaretçi. Onu tasarımın SAHİBİ gibi değil,
         fikri sorulan kişi gibi karşılıyoruz — istenen rol bu. */
      case 'shared':
        head = 'KREAZ\'a hoş geldin!';
        body = 'Sanırım biri sana bu tasarımı yolladı. Sen de raf ekle ya da '
          + 'çıkar, yerlerini değiştir — sonra ona geri gönder, fikrini söyle.';
        chip = tag('Sana gönderildi');
        break;
      case 'preset':
        head = 'Seti duvara kurdum.';
        body = `${p.count} raf hazır. İstediğin gibi taşı, renk değiştir, ekle — `
          + 'ya da <b>geri al</b> ile eski tasarımına dön.';
        chip = tag('Hazır set');
        break;
      case 'idle': {
        const [h, b] = this.idleLine(p);
        head = h; body = b;
        chip = tag('Öneri');
        break;
      }
      /* Kazanılmış bir avantajın duyurusu — yüzde yönlendirmesi değil.
         İkinci raf duvara oturduğu anda bir kez söylenir. */
      case 'kargo':
        head = 'İkinci raf da yerleşti — kargo artık bizden.';
        body = nudge();
        chip = tag('Ücretsiz kargo', 'win');
        break;
      case 'oda':
        head = `${data.oda.ad} için bir başlangıç kurdum.`;
        body = `${data.oda.tavsiyeler[0]} Beğenmezsen <b>geri al</b> ile eski `
          + 'tasarımına dönebilirsin.';
        chip = tag(data.oda.ad);
        break;
      case 'olcu':
        head = `Duvarı ${this.wallW}×${this.wallH} cm yaptım.`;
        body = nudge();
        chip = tag('Ölçü');
        break;
      default:
        head = pick(praise);
        body = nudge();
    }

    // Baloncuğa tıklandığında aynısını tekrar gösterebilmek için saklıyoruz.
    this.lastSay = {
      cls: 'kz-bubble' + (chip?.tone ? ` kz-tone-${chip.tone}` : ''),
      html: (chip ? `<span class="kz-bubble-tag">${chip.label}</span>` : '') + `<b>${head}</b> ${body}`,
    };
    this.showBubble(this.lastSay);
  }

  showBubble(msg, holdMs) {
    /* İçerik değişiyor: odaktaki e-posta alanı yok oluyor. focusout her
       tarayıcıda tetiklenmiyor, bayrağı burada elle düşürüyoruz — yoksa
       kilit kalıcı olur ve asistan bir daha konuşmaz. */
    this._mailFocus = false;
    this.bubbleEl.className = msg.cls;
    this.bubbleEl.innerHTML = msg.html;
    this.assistantEl.classList.add('kz-talking');
    clearTimeout(this._sayT);
    // Infinity = kalıcı baloncuk (e-posta dinlenme hali).
    if (holdMs === Infinity) return;
    const hold = holdMs || TIP_HOLD_MS;
    this._sayT = setTimeout(() => {
      this.assistantEl.classList.remove('kz-talking');
      // Sıra WhatsApp'a geçiyorsa e-posta formu onun ardından gelir.
      if (this.waTakeOver()) return;
      // İpucu bitti; baloncuk e-posta formuna dönsün.
      this.armEmailRest(400);
    }, hold);
  }

  /* ======================================================================
     TASARIMI PAYLAŞ / E-POSTA İLE GÖNDER
     ====================================================================== */

  /** Baloncukta e-posta formu açık mı? (üstüne yazıp müşteriyi kesmeyelim) */
  emailBubbleOpen() {
    return !!this.bubbleEl?.querySelector('.kz-mail-form');
  }

  /**
   * E-posta baloncuğu açıkken baloncuk kilitlidir.
   *
   * Boşta kalma önerisi de e-posta istemi de 10. saniyede tetikleniyor;
   * kilit olmadan biri diğerini eziyor ve müşteri yazmaya başladığı alan
   * gözünün önünde kayboluyordu.
   */
  bubbleLocked() {
    // Alan odaktayken kilitli; odaktan çıkınca kısa bir pay kalıyor.
    return !!this._mailFocus || Date.now() < (this._bubbleLockUntil || 0);
  }

  /** Basit bir kalıcı bayrak — "bunu bir kez gösterdik". */
  once(key, set) {
    const k = 'kz-once-' + key;
    try {
      if (set) { localStorage.setItem(k, '1'); return true; }
      return localStorage.getItem(k) === '1';
    } catch { return !!this['_once' + key]; }
  }

  /**
   * E-posta isteği asistanın DİNLENME HALİdir.
   *
   * Zamanlayıcıya bağlı tek seferlik bir istem işe yaramıyordu: müşteri raf
   * ekledikçe asistan başka şeyler söylüyor ve istem araya sıkışıp
   * kayboluyordu; ikinci ziyarette ise hiç görünmüyordu. Artık tersi
   * kuruluyor — ipucu yokken baloncuk e-posta formuna döner ve müşteri
   * adresini verene ya da asistana dokunup kapatana kadar orada durur.
   */
  restEmailBubble() {
    if (!this.emailAvailable() || this.emailCaptured || this.emailDismissed) return;
    /* Duvarda en az iki raf yoksa istemiyoruz: saklanacak bir tasarım yok,
       erken bir istek de itici. O zamana kadar asistan öneri vermeye devam
       ediyor (armIdleNudge); ikinci raf gelince "kargo bizden" mesajının
       ardından form kendiliğinden geliyor. */
    if (this.shelves.length < MAIL_MIN_RAF) return;
    if (this.bubbleLocked()) return;
    // Çekmece açıkken bekle, kapanınca gel — yoksa baloncuk çekmecenin
    // üstüne biner.
    if (this.waDrawerOpen()) { this.armEmailRest(WA_DRAWER_MS); return; }
    if (this.roomToastOpen()) { this.armEmailRest(ROOM_TOAST_MS + DRAWER_SLIDE_MS); return; }

    // Form zaten basılı ama baloncuk gizlenmişse (ipucu ya da çekmece onu
    // kapattıysa) yeniden kurmaya gerek yok, sadece geri göster.
    if (this.emailBubbleOpen()) {
      this.assistantEl.classList.add('kz-talking');
      clearTimeout(this._sayT);
      return;
    }
    this.showEmailBubble();
  }

  /**
   * Klaviyo public key.
   *
   * Önce bölüm ayarı, sonra SAYFADAKİ Klaviyo betiği. Mağazada Klaviyo app
   * embed'i açık olduğu için anahtar zaten sayfaya düşüyor; elle girmeye
   * gerek kalmasın diye oradan okuyoruz.
   */
  klaviyoKey() {
    if (this._klKey !== undefined) return this._klKey;
    let key = this.cfg.klaviyoPublicKey || null;
    if (!key) {
      try {
        for (const s of document.querySelectorAll('script[src*="klaviyo"]')) {
          const m = /company_id=([A-Za-z0-9]+)/.exec(s.src)
            || /\/onsite\/js\/([A-Za-z0-9]+)\//.exec(s.src);
          if (m) { key = m[1]; break; }
        }
      } catch { /* yoksay */ }
    }
    this._klKey = key;
    return key;
  }

  /**
   * Saklayacak yer var mı? Yoksa hiç sormuyoruz.
   * Liste kimliği ŞART DEĞİL: liste yoksa tasarımı bir Klaviyo olayı olarak
   * gönderiyoruz, akış o olayla da tetiklenebiliyor.
   */
  emailAvailable() {
    return !!this.klaviyoKey();
  }

  /**
   * İpucu gösterildikten sonra baloncuğu dinlenme haline döndürür.
   * `holdMs` biter bitmez e-posta formu geri gelir.
   */
  armEmailRest(afterMs) {
    clearTimeout(this._restT);
    if (!this.emailAvailable() || this.emailCaptured || this.emailDismissed) return;
    this._restT = setTimeout(() => {
      if (this.root.isConnected) this.restEmailBubble();
    }, afterMs);
  }

  armEmailPrompt() {
    if (!this.emailAvailable()) return;
    if (this.emailCaptured) return;
    // İlk açılışta da kısa bir nefes payı bırakıyoruz: müşteri önce
    // karşılama mesajını okusun.
    this.armEmailRest(EMAIL_PROMPT_MS);
  }

  showEmailBubble() {
    const note = this.cfg.privacyUrl
      ? ` <a href="${this.cfg.privacyUrl}" target="_blank" rel="noopener">Gizlilik</a>`
      : '';
    // Her kuruluşta sıradaki cümle: aynı istek üst üste tekrarlanmasın,
    // asistan ezber okuyan bir form gibi değil, konuşan biri gibi dursun.
    const ask = MAIL_ASKS[this.mailAskIdx % MAIL_ASKS.length];
    this.mailAskIdx += 1;
    // `lastSay`'e YAZMIYORUZ: asistana tıklayınca form yeniden basılır ama
    // submit dinleyicisi olmadan gelir, yani çalışmayan bir alan kalırdı.
    const msg = {
      cls: 'kz-bubble kz-bubble-form',
      html: '<span class="kz-bubble-tag">Kaydet</span>'
        + `<b>${ask}</b>`
        + '<form class="kz-mail-form" novalidate>'
        + '<input type="email" name="email" inputmode="email" autocomplete="email"'
        + ' placeholder="e-posta adresin" aria-label="E-posta adresin" required>'
        + `<button type="submit" aria-label="Gönder">${ICONS.check}</button>`
        + '</form>'
        + `<small class="kz-mail-note">Kampanya e-postalarını almayı kabul ediyorum.${note}</small>`,
    };
    // Kendiliğinden KAPANMIYOR: bu artık asistanın dinlenme hali. Müşteri
    // adresini verene ya da asistana dokunup kapatana kadar açık durur.
    // Kilit koymuyoruz — ipuçlarının araya girip sonra buraya dönmesi
    // gerekiyor (bkz. armEmailRest).
    this.showBubble(msg, Infinity);
    if (!this._mailPromptTracked) { this.track('kz_mail_prompt'); this._mailPromptTracked = true; }

    const form = this.bubbleEl.querySelector('.kz-mail-form');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitEmail(form.querySelector('input').value);
    });
    /* Form asistanın İÇİNDE duruyor ve asistana dokunmak e-posta isteğini
       kapatıyor. Kabarmayı burada kesmezsek alana tıklamak formu kapatır —
       müşteri adresini yazamaz. Bu hata canlıdaydı; koruma burada. */
    const yut = (e) => e.stopPropagation();
    form?.addEventListener('click', yut);
    form?.addEventListener('keydown', yut);
    form?.addEventListener('pointerdown', yut);
    // Onay/gizlilik satırı da formun dışında ama aynı korumayı hak ediyor.
    this.bubbleEl.querySelector('.kz-mail-note')?.addEventListener('click', yut);
    /* Yazarken baloncuk kapanmasın — ne zamanlayıcı ne de başka bir mesaj.
       Kilit ODAĞA bağlı: eskiden alana bir kez dokunmak 10 dakikalık sabit
       bir kilit kuruyordu ve o süre boyunca hem asistan hem WhatsApp
       tamamen susuyordu. Artık alandan çıkınca kısa bir pay içinde açılıyor. */
    form?.addEventListener('focusin', () => {
      clearTimeout(this._sayT);
      this._mailFocus = true;
    });
    form?.addEventListener('focusout', () => {
      this._mailFocus = false;
      // Müşteri alandan çıktı ama hâlâ yazmaya dönebilir; kısa bir pay.
      this._bubbleLockUntil = Date.now() + 4000;
    });
  }

  /** Girilen e-postayı Klaviyo'ya yollar ve tasarımı profile işler. */
  async submitEmail(raw) {
    const email = String(raw || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) {
      this.bubbleEl.querySelector('.kz-mail-form')?.classList.add('kz-bad');
      return;
    }
    this.email = email;
    this.emailCaptured = true;
    try { localStorage.setItem('kz-mail', email); } catch { /* yoksay */ }

    const form = this.bubbleEl.querySelector('.kz-mail-form');
    form?.classList.remove('kz-bad');
    form?.classList.add('kz-busy');
    this._bubbleLockUntil = 0;   // iş bitti, asistan yeniden konuşabilir
    this._mailFocus = false;

    // Önce görsel (ayar varsa; en çok 8 sn), sonra tek seferde kayıt:
    // ilk e-posta linki ve görseli tetikleyici olaydan okuyor.
    await this.uploadDesignImage();
    const props = this.designProps();
    await this.sendKlaviyoSubscribe(email, props);
    this._klSonOlayKod = this._klSonProfilKod = props.kreaz_tasarim_kodu;

    this.track('kz_mail_captured');
    // E-posta adresi Clarity'ye GİTMİYOR (kişisel veri); yalnızca "bıraktı" bilgisi.
    this.clarityTag('kz_mail', 'evet');
    this.clarity('upgrade', 'kz_mail_captured');
    this.lastSay = {
      cls: 'kz-bubble kz-tone-win',
      html: '<span class="kz-bubble-tag">Kaydedildi</span>'
        + '<b>Tamamdır.</b> Tasarımını değiştirmeye devam et — '
        + 'son halini e-postana göndereceğiz.',
    };
    this.showBubble(this.lastSay);
  }

  /** Klaviyo'ya ortak istek yardımcısı. */
  klaviyoPost(yol, govde) {
    const key = this.klaviyoKey();
    if (!key) return Promise.resolve();
    return fetch(`https://a.klaviyo.com/client/${yol}?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(govde),
    }).catch((err) => console.warn('[Kreaz Studio] Klaviyo', yol, err));
  }

  /**
   * E-postayı ve o anki tasarımı Klaviyo'ya yazar. Üç ayrı kayıt:
   *
   * 1. KİMLİK — tarayıcı bu profile bağlanıyor ve tasarım alanları profile
   *    yazılıyor. Klaviyo'nun sayfa betiği (window.klaviyo) varsa onunla:
   *    Klaviyo tarayıcıdan gelen profil güncellemesini, o tarayıcı profile
   *    bağlıysa kabul ediyor. Eskiden yalnızca ham istemci isteği vardı;
   *    Shopify'dan zaten gelmiş bir müşterinin profilinde tasarım alanları
   *    bu yüzden güncellenmiyordu — "mail kaydoluyor, link kaydolmuyor".
   * 2. OLAY — "Raf Stüdyosu Tasarımı". Olaylar her profilde kesin yazılıyor
   *    ve e-posta akışını bu olay başlatıyor; ilk e-posta linki ve görseli
   *    doğrudan olaydan okuyor.
   * 3. ABONELİK — liste kimliği girilmişse açık pazarlama onayı.
   */
  sendKlaviyoSubscribe(email, props = this.designProps()) {
    const istekler = [
      this.klaviyoIdentify({ email, ...props }),
      this.klaviyoEvent(KLAVIYO_METRIC, email, props),
    ];
    const list = this.cfg.klaviyoListId;
    if (list) istekler.push(this.klaviyoSubscribeList(email, props, list));
    return Promise.allSettled(istekler);
  }

  /** Tasarımı profil zaman çizelgesine olay olarak yazar (keepalive). */
  klaviyoEvent(metrik, email, props) {
    return this.klaviyoPost('events', {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: metrik } } },
          profile: { data: { type: 'profile', attributes: { email, properties: props } } },
        },
      },
    });
  }

  /**
   * Profil alanlarını yazar. Sayfa betiği yüklüyse onun identify'ı (tarayıcı
   * profile bağlanıyor); değilse — ya da sayfa kapanıyorsa — keepalive'lı
   * doğrudan istek.
   */
  klaviyoIdentify({ email, ...ozellik }, kapanis = false) {
    const kl = window.klaviyo;
    if (!kapanis && kl && typeof kl.identify === 'function') {
      try {
        return Promise.resolve(kl.identify({ email, ...ozellik }))
          .catch(() => this.klaviyoProfilePost(email, ozellik));
      } catch { /* aşağıdaki doğrudan isteğe düş */ }
    }
    return this.klaviyoProfilePost(email, ozellik);
  }

  klaviyoProfilePost(email, ozellik) {
    return this.klaviyoPost('profiles', {
      data: { type: 'profile', attributes: { email, properties: ozellik } },
    });
  }

  klaviyoSubscribeList(email, props, list) {
    const key = this.klaviyoKey();
    return fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Raf Stüdyosu',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: props,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      }),
    }).catch((err) => console.warn('[Kreaz Studio] Klaviyo', err));
  }

  /*
   * Shopify'ın `/contact` formuna DOĞRUDAN GÖNDERİM YOK — bilerek.
   *
   * Denendi ve mağaza "Missing CAPTCHA token" ile 400 döndü: Shopify'ın
   * spam koruması açıkken `form_type=customer` gönderimleri, sayfada gerçek
   * bir {% form %} öğesi üzerinden üretilen CAPTCHA jetonunu şart koşuyor.
   * Bir fetch çağrısı bu jetonu üretemez; formu gerçekten göndermek ise
   * sayfayı terk edip 3D tasarımı kaybettirirdi.
   *
   * Bu yüzden e-posta hedefi Klaviyo: istemci uç noktası tam olarak bunun
   * için tasarlanmış (202 döndüğü ölçüldü, CORS açık, CAPTCHA yok) ve
   * müşteriye tasarımını gönderecek olan da zaten Klaviyo. Klaviyo'nun
   * Shopify entegrasyonu aboneleri Shopify Müşteriler'e yazabilir.
   */

  /** Klaviyo profiline yazılan tasarım alanları. */
  designProps() {
    const p = this.pricing();
    const kod = this.encodeDesign();
    const url = this.designUrl();
    let yol = '';
    try { const u = new URL(url); yol = u.pathname + u.search; } catch { /* yoksay */ }
    const props = {
      kreaz_tasarim_url: url,
      /* Mağaza içi yol, URL-kodlu. İndirim e-postasındaki link bununla
         kuruluyor: /discount/KOD?redirect=… kodu sepete işleyip müşteriyi
         doğrudan kendi tasarımına götürüyor (yönlendirme yalnız yol kabul ediyor). */
      kreaz_tasarim_yol: encodeURIComponent(yol),
      // Aynı tasarımı iki kez yazmamak için (ve Klaviyo'da ayırt etmek için)
      kreaz_tasarim_kodu: kod,
      kreaz_raf_adedi: p.count,
      kreaz_tutar: Math.round(p.total / 100),
      // E-postada gösterilecek hali (mağazanın para biçimi; HTML'siz)
      kreaz_tutar_metin: this.money(p.total).replace(/<[^>]*>/g, ''),
      kreaz_raflar: this.layoutString(),
      // Hangi alan için tasarladığı — "mutfak için tasarlayanlar" segmenti
      // kurulabilsin diye. Oda seçmediyse boş gider.
      kreaz_oda: this.room?.ad || '',
      // E-postadaki yedek görsel: tasarımda en çok kullanılan raf/rengin
      // katalog fotoğrafı. Tasarım ekran görüntüsü yoksa bu gösteriliyor.
      kreaz_urun_gorsel: this.dominantImage(),
    };
    // Ekran görüntüsü yalnızca O ANKİ tasarıma aitse yazılıyor; tasarım
    // sonradan değiştiyse eski görsel profilde kalıyor, boşla ezilmiyor.
    if (this._tasarimGorsel && this._gorselKod === kod) props.kreaz_tasarim_gorsel = this._tasarimGorsel;
    return props;
  }

  /** Tasarımda en çok kullanılan ölçü + rengin katalog görseli (tam adres). */
  dominantImage() {
    const sayac = new Map();
    for (const s of this.shelves) {
      const k = `${s.productId}|${s.colorId}`;
      sayac.set(k, (sayac.get(k) || 0) + 1);
    }
    let enIyi = null, n = 0;
    for (const [k, v] of sayac) if (v > n) { enIyi = k; n = v; }
    if (!enIyi) return '';
    const [urun, renk] = enIyi.split('|');
    const url = this.imageFor(urun, renk) || '';
    // Shopify image_url protokolsüz ("//…") döndürüyor; e-postada tam adres şart.
    return url.startsWith('//') ? `https:${url}` : url;
  }

  /**
   * Tasarım değiştikçe Klaviyo profilini günceller.
   *
   * Böylece müşteri e-postasını verdikten SONRA yaptığı her değişiklik de
   * kaydediliyor; akıştaki sonraki e-postalar müşterinin SON tasarımını
   * gösteriyor.
   *
   * `kapanis`: sekme gizleniyor/kapanıyor. O anda profil keepalive'lı
   * doğrudan istekle yazılıyor ve ayrıca "Raf Stüdyosu Son Tasarım" olayı
   * düşülüyor: profil güncellemesi herhangi bir sebeple kabul edilmese bile
   * stüdyodan çıkmadan önceki tasarımın linki profilin zaman çizelgesinde
   * kesin duruyor. Aynı tasarım iki kez yazılmıyor.
   */
  syncProfile(immediate, kapanis = false) {
    if (!this.email || !this.klaviyoKey()) return;
    clearTimeout(this._syncT);
    const go = async () => {
      /* Görsel servisi tanımlıysa, tasarım değiştikçe e-postadaki görsel de
         tazeleniyor (en çok 15 sn'de bir; sayfa kapanırken beklenmiyor).
         Yoksa son yüklenen görsel eski tasarıma ait kalıyor ve mail o
         görseli değil katalog fotoğrafını gösteriyordu. */
      if (!kapanis && this.cfg.designImage?.cloud && this.cfg.designImage?.preset
          && Date.now() - (this._sonGorselYukleme || 0) > 15000) {
        this._sonGorselYukleme = Date.now();
        await this.uploadDesignImage();
      }
      const props = this.designProps();
      const kod = props.kreaz_tasarim_kodu;
      if (kapanis) {
        if (!this.shelves.length || kod === this._klSonOlayKod) return;
        this._klSonOlayKod = this._klSonProfilKod = kod;
        this.klaviyoIdentify({ email: this.email, ...props }, true);
        this.klaviyoEvent(KLAVIYO_METRIC_SON, this.email, props);
        return;
      }
      if (kod === this._klSonProfilKod) return;
      this._klSonProfilKod = kod;
      this.klaviyoIdentify({ email: this.email, ...props });
    };
    if (immediate) go();
    else this._syncT = setTimeout(go, PROFILE_SYNC_MS);
  }

  /**
   * E-posta için tasarım görseli (JPEG, data URI).
   *
   * Raflara odaklı sabit bir açıdan, EKRAN tuvalinde çizilip aynı görev
   * içinde geri alınıyor: tarayıcı arada boyamadığı için müşteri hiçbir şey
   * görmüyor; ton eşleme ve renkler ekrandakiyle birebir aynı (ayrı bir
   * çizim hedefinde ton eşleme uygulanmıyordu).
   */
  designSnapshot(w = 1200, h = 800, kalite = 0.86) {
    if (!this.shelves.length) return null;
    const r = this.renderer, o = this.orbit;

    const kutu = new T.Box3();
    this.shelves.forEach((s) => kutu.expandByObject(s.object));
    const mrk = kutu.getCenter(new T.Vector3());
    const boy = kutu.getSize(new T.Vector3());
    const cam = new T.PerspectiveCamera(this.camera.fov, w / h, 0.05, 60);
    const tanY = Math.tan((cam.fov * Math.PI) / 360);
    // Rafların çevresinde biraz oda kalsın: eşya ve duvar ölçeği versin.
    const bolgeW = Math.max(boy.x * 1.45, 1.5);
    const bolgeH = Math.max(boy.y * 1.8, 1.2);
    mrk.y -= 0.08;                  // hafif aşağı: altındaki eşya da görünsün
    const d = Math.max(bolgeH / 2 / tanY, bolgeW / 2 / tanY / cam.aspect);
    // Kamera odanın içine doğru dönsün; yan duvarın arkasında kalmasın.
    const az = (Math.abs(mrk.x) > 0.05 ? -Math.sign(mrk.x) : Math.sign(ACILIS_AZIMUT)) * 0.26;
    const po = Math.PI / 2 - 0.09;
    cam.position.set(
      mrk.x + d * Math.sin(po) * Math.sin(az),
      mrk.y + d * Math.cos(po),
      d * Math.sin(po) * Math.cos(az),
    );
    cam.lookAt(mrk.x, mrk.y, 0);
    cam.updateMatrixWorld();

    const eskiAz = o.azimuth;
    const eskiOran = r.getPixelRatio();
    const eskiBoy = r.getSize(new T.Vector2());
    const gizle = [this.selectionRing, this.highlight, this.gridMesh].filter((m) => m?.visible);
    let sonuc = null;
    try {
      o.azimuth = az; this.fadeSideWalls();          // yan duvar o açıya göre solsun
      gizle.forEach((m) => { m.visible = false; });  // seçim halkası vb. görselde olmasın
      r.setPixelRatio(1);
      r.setSize(w, h, false);
      r.render(this.scene, cam);
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      const c = cv.getContext('2d');
      const zemin = c.createLinearGradient(0, 0, 0, h);   // tuval saydam: sahne zemini
      zemin.addColorStop(0, '#f8f7f4');
      zemin.addColorStop(1, '#e8e6e1');
      c.fillStyle = zemin;
      c.fillRect(0, 0, w, h);
      c.drawImage(this.canvas, 0, 0, w, h);
      sonuc = cv.toDataURL('image/jpeg', kalite);
    } catch (err) {
      console.warn('[Kreaz Studio] görsel', err);
    } finally {
      r.setPixelRatio(eskiOran);
      r.setSize(eskiBoy.x, eskiBoy.y, false);
      gizle.forEach((m) => { m.visible = true; });
      o.azimuth = eskiAz; this.fadeSideWalls();
      this.invalidate();
    }
    return sonuc;
  }

  /**
   * Tasarım görselini Cloudinary'ye yükler (imzasız ön ayar) ve adresini
   * döndürür. Ayar yoksa ya da 8 sn içinde olmazsa null: e-posta o zaman
   * katalog fotoğrafına düşüyor, müşteri beklemiyor.
   */
  async uploadDesignImage() {
    const c = this.cfg.designImage;
    if (!c?.cloud || !c?.preset) return null;
    const kod = this.encodeDesign();
    if (this._gorselKod === kod && this._tasarimGorsel) return this._tasarimGorsel;
    const veri = this.designSnapshot();
    if (!veri) return null;
    const fd = new FormData();
    fd.append('file', veri);            // Cloudinary data URI kabul ediyor
    fd.append('upload_preset', c.preset);
    const iptal = new AbortController();
    const zaman = setTimeout(() => iptal.abort(), 8000);
    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${encodeURIComponent(c.cloud)}/image/upload`,
        { method: 'POST', body: fd, signal: iptal.signal });
      const j = await res.json();
      if (!j.secure_url) throw new Error(j.error?.message || `HTTP ${res.status}`);
      this._tasarimGorsel = j.secure_url;
      this._gorselKod = kod;
      return j.secure_url;
    } catch (err) {
      console.warn('[Kreaz Studio] tasarım görseli yüklenemedi', err);
      return null;
    } finally {
      clearTimeout(zaman);
    }
  }

  /** Tasarımı WhatsApp'ta paylaş — araya ekran koymadan. */
  shareWhatsApp() {
    this.once('wa', true);
    // Kullanıldı: artık kendini tanıtmasına gerek yok.
    clearTimeout(this._waT);
    this.root.querySelector('.kz-wa-btn')?.classList.remove('kz-open');
    this._waOpenUntil = 0;
    this.track('kz_whatsapp_share');
    this.clarityTag('kz_whatsapp', 'evet');
    this.clarityDesign(true);   // paylaşılan tasarımın kendisi de kayda geçsin
    const msg = (this.cfg.shareMessage || 'Duvarıma böyle bir raf düzeni kurdum — sence nasıl olmuş?');
    const url = `https://wa.me/?text=${encodeURIComponent(msg + '\n\n' + this.designUrl())}`;
    window.open(url, '_blank', 'noopener');
  }

  /**
   * WhatsApp düğmesi ilk raftan sonra görünür ve çekmece gibi açılıp
   * ne işe yaradığını anlatır.
   *
   * Yazılar satış dili kullanmıyor: müşteriye bir şey satmıyoruz, tasarımını
   * birine gösterebileceğini söylüyoruz.
   */
  /** Düğmeyi ilk raftan sonra gösterir ve ilk tanıtımı sıraya koyar. */
  syncShareButton() {
    const b = this.root.querySelector('.kz-wa-btn');
    if (!b) return;
    const show = this.shelves.length > 0;
    if (b.hidden !== !show) b.hidden = !show;
    if (!show || this._waShown1) return;

    /* Paylaşılan bir linkten gelen ziyaretçiye "tasarımını arkadaşına gönder"
       demek anlamsız: tasarım zaten ona gönderilmiş. Tanıtımı atlıyoruz,
       WhatsApp normal sırasında (birkaç ipucu sonra) konuşmaya başlıyor —
       o zamana kadar ziyaretçi tasarımı kendine göre değiştirmiş olur. */
    if (this.fromShare) { this._waShown1 = true; return; }

    /* İlk rafta kendini tanıtır. Asistan o sırada konuşuyorsa araya girmez,
       sözü bitince hemen devralır (bkz. waTakeOver). */
    this._waShown1 = true;
    if (this.assistantEl?.classList.contains('kz-talking')) this._waPending = true;
    else this.openWaDrawer();
  }

  /**
   * Asistan sustuğunda sıranın WhatsApp'a geçip geçmeyeceğine karar verir.
   * Çekmeceyi açtıysa `true` döner; o zaman e-posta formu çekmece
   * kapandıktan sonra gelir.
   *
   * Bu kontrol eskiden yalnızca `syncShareButton()` içindeydi, yani yalnızca
   * TASARIM DEĞİŞTİĞİNDE çalışıyordu. Müşteri duvara dokunmadan ipuçlarını
   * okurken WhatsApp hiç konuşmuyordu — "çok az konuşuyor" şikâyetinin asıl
   * sebebi buydu. Artık asistanın her sözünün sonunda sıra kontrol ediliyor.
   */
  waTakeOver() {
    const b = this.root.querySelector('.kz-wa-btn');
    if (!b || b.hidden) return false;
    if (this._waPending) { this._waPending = false; this.openWaDrawer(); return true; }
    /* Müşteri bir kez paylaştıysa sıklık yarıya iner ama tamamen susmaz;
       eskiden kalıcı olarak susuyordu. */
    const aralik = this.once('wa') ? WA_SPEAK_EVERY * 2 : WA_SPEAK_EVERY;
    if ((this._sayCount || 0) < (this._waLastAt || 0) + aralik) return false;
    this.openWaDrawer();
    return true;
  }

  /**
   * Çekmeceyi açar, sıradaki yazıyı gösterir, kendiliğinden toplar.
   * Asistan konuşuyorsa açmaz — ekranda aynı anda iki yazı olmamalı.
   */
  openWaDrawer() {
    const b = this.root.querySelector('.kz-wa-btn');
    if (!b || b.hidden || this.bubbleLocked()) return;
    /* Oda çekmecesi konuşuyorsa sıraya gir. Oda seçimi ilk rafı da koyduğu
       için ikisi aynı anda açılıyordu; üst üste binmeseler de iki çekmecenin
       birden konuşması kalabalık duruyor. `_waPending` asistan bir sonraki
       kez sustuğunda devralınıyor. */
    if (this.roomToastOpen()) { this._waPending = true; return; }

    const mesajlar = this.cfg.shareHints && this.cfg.shareHints.length
      ? this.cfg.shareHints : SHARE_HINTS;
    this._waIdx = ((this._waIdx ?? -1) + 1) % mesajlar.length;

    b.querySelector('.kz-wa-text span').textContent = mesajlar[this._waIdx];
    // Sayaç burada sıfırlanıyor: sırayı kimin verdiğinden bağımsız olarak
    // bir sonraki konuşma yine WA_SPEAK_EVERY mesaj sonra gelsin.
    this._waLastAt = this._sayCount || 0;
    // Asistan çekmece kapanana kadar sussun.
    this._waOpenUntil = Date.now() + BUBBLE_FADE_MS + WA_DRAWER_MS;
    this.assistantEl?.classList.remove('kz-talking');
    this.track('kz_wa_hint', { yazi: mesajlar[this._waIdx] });

    /* Çekmece, baloncuk tamamen söndükten SONRA açılıyor; yoksa açılırken
       birkaç yüz milisaniye baloncuğun üstüne biniyor. */
    clearTimeout(this._waT);
    this._waT = setTimeout(() => {
      b.classList.add('kz-open');
      this._waT = setTimeout(() => {
        b.classList.remove('kz-open');
        // Çekmece kapandı; asistan dinlenme haline dönebilir.
        this.armEmailRest(DRAWER_SLIDE_MS + 140);
      }, WA_DRAWER_MS);
    }, BUBBLE_FADE_MS);
  }

  /* `placeShareButton()` KALDIRILDI.
     Düğmenin dikey yeri artık CSS'te sabit. Eskiden baloncuğun yüksekliğine
     göre JS'ten hesaplanıyordu ve asistan her konuştuğunda simge yerinden
     oynuyordu. Gerek de yoktu: baloncuk avatarın SAĞINDA açılıyor, düğme ise
     avatarın altındaki sütunda — çakışmıyorlar. Çekmece açıkken asistan
     zaten susturuluyor (bkz. openWaDrawer). */

  /** Çekmece açıkken asistan konuşmaz. */
  waDrawerOpen() {
    return Date.now() < (this._waOpenUntil || 0);
  }

  /* ======================================================================
     ODA ÖN AYARLARI

     Müşteri "45 cm raf" diye düşünmüyor; "antreme bir şey lazım" diye
     düşünüyor. Bu yüzden boş duvar yerine hazır bir oda düzeniyle
     başlayabiliyor: duvar ölçüsü, raf düzeni ve renk tek dokunuşta kuruluyor.

     Yerleştirmeyi ürün sayfası ön ayarlarının motoru yapıyor (`applyPreset`),
     yani raflar burada da bitişik diziliyor — ayrı bir düzen kodu yok.
     ====================================================================== */

  buildRooms() {
    const grid = this.root.querySelector('.kz-room-grid');
    if (!grid) return;
    grid.innerHTML = ROOMS.map((o) => `
      <button type="button" class="kz-room-card" data-room="${o.id}">
        <span class="kz-room-card-mark">${ICONS[o.simge] || ''}</span>
        <span class="kz-room-card-txt">
          <b>${o.ad}</b>
          <small>${o.ipucu}</small>
        </span>
      </button>`).join('');

    grid.querySelectorAll('.kz-room-card').forEach((b) => {
      b.addEventListener('click', () => this.applyRoom(b.dataset.room));
    });

    /* Segmeler. Seçim değişince, bir oda zaten seçiliyse öneri ANINDA
       yenileniyor: müşteri farkı görmek için kartlara geri dönmek zorunda
       kalmıyor. */
    this.roomSecim = { boy: 'dengeli', renk: 'tek' };
    this.root.querySelectorAll('.kz-seg').forEach((seg) => {
      seg.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        seg.querySelectorAll('button').forEach((x) => x.classList.toggle('kz-on', x === b));
        this.roomSecim[seg.dataset.seg] = b.dataset.val;
        if (this.room) this.applyRoom(this.room.id, { pencereyiKapatma: true });
      });
    });

    this.roomBtn = this.root.querySelector('.kz-room-btn');
    this.roomModal = this.root.querySelector('.kz-room-modal');
    this.roomScrim = this.root.querySelector('.kz-room-scrim');
    this.roomW = this.root.querySelector('.kz-room-w');
    this.roomH = this.root.querySelector('.kz-room-h');

    this.roomW.value = this.wallW;
    this.roomH.value = this.wallH;

    this.roomBtn?.addEventListener('click', () => this.setRoomPanel(true));
    this.roomScrim?.addEventListener('click', () => this.setRoomPanel(false));
    this.root.querySelector('.kz-room-close')
      ?.addEventListener('click', () => this.setRoomPanel(false));
    this.root.querySelector('.kz-room-apply')
      ?.addEventListener('click', () => {
        // Ölçüyü tek başına da değiştirebilsin: oda seçmek zorunda değil.
        // Oda kartlarıyla aynı davranış: uygula ve pencereyi kapat ki yeni
        // duvar ölçüsü görünsün.
        const w = this.roomW.value;
        const h = this.roomH.value;
        this.setRoomPanel(false);
        setTimeout(() => {
          if (!this.root.isConnected) return;
          this.setWallSize(w, h);
          this.roomW.value = this.wallW;
          this.roomH.value = this.wallH;
          this.say('olcu');
        }, ROOM_FADE_MS + 40);
      });

    /* Düğme kendini çekmeceyle TANITMIYOR.
       Asistan e-posta formunu kalıcı gösterdiği için baloncuk neredeyse hiç
       kapanmıyor; çekmece de baloncuğun tam altından açıldığından dar ekranda
       yazılar üst üste biniyordu. Bunun yerine düğme ilk kullanıma kadar
       hafifçe nabız atıyor (saf CSS) ve karşılama mesajı ondan söz ediyor. */
    if (!this.once('room')) this.roomBtn?.classList.add('kz-pulse');
  }

  /**
   * Pencereyi açar/kapatır.
   *
   * `display:none` geçiş alamadığı için açılışta önce `hidden` kaldırılıp bir
   * sonraki karede `kz-on` ekleniyor; kapanışta tersi, `hidden` ancak solma
   * bittikten sonra yazılıyor. Kapanma yumuşak olsun ki duvar dönüşümü
   * pencerenin ardından değil, onunla birlikte başlamış gibi dursun.
   */
  setRoomPanel(open) {
    if (!this.roomModal) return;
    clearTimeout(this._roomHideT);
    this.roomBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!open) {
      this.roomModal.classList.remove('kz-on');
      this.roomScrim.classList.remove('kz-on');
      this._roomHideT = setTimeout(() => {
        this.roomModal.hidden = true;
        this.roomScrim.hidden = true;
      }, ROOM_FADE_MS);
      return;
    }

    this.roomModal.hidden = false;
    this.roomScrim.hidden = false;
    requestAnimationFrame(() => {
      this.roomModal.classList.add('kz-on');
      this.roomScrim.classList.add('kz-on');
    });
    this.once('room', true);
    this.roomBtn?.classList.remove('kz-open', 'kz-pulse');
    // Ölçü alanları güncel duvarı göstersin.
    this.roomW.value = this.wallW;
    this.roomH.value = this.wallH;
    this.setDrawer(false);
    this.track('kz_room_panel');
  }

  /**
   * "?" düğmesinin çekmecesinde kısa bir bilgi gösterir.
   *
   * Çekmece asistan baloncuğunun tam altından sola doğru açılıyor; dar
   * ekranda ikisi aynı anda konuşursa yazılar üst üste biniyordu. Bu yüzden
   * çekmece açılırken asistan susuyor ve `roomToastOpen()` açıkken `say()`
   * hiçbir mesaj basmıyor — WhatsApp çekmecesiyle aynı kural.
   */
  roomToast(yazi, sure = ROOM_TOAST_MS) {
    const t = this.roomBtn?.querySelector('.kz-wa-text span');
    if (!t) return;
    t.textContent = yazi;
    /* Oda duyurusu doğrudan bir dokunuşun cevabı: WhatsApp o an konuşuyorsa
       sırayı ona bırakmıyor, çekmecesini kapatıyoruz. */
    if (this.waDrawerOpen()) {
      clearTimeout(this._waT);
      this.root.querySelector('.kz-wa-btn')?.classList.remove('kz-open');
      this._waOpenUntil = 0;
    }
    this._roomToastUntil = Date.now() + BUBBLE_FADE_MS + sure;
    this.assistantEl?.classList.remove('kz-talking');
    // WhatsApp çekmecesiyle aynı kural: baloncuk sönsün, sonra açıl.
    clearTimeout(this._roomToastT);
    this._roomToastT = setTimeout(() => {
      this.roomBtn.classList.add('kz-open');
      this._roomToastT = setTimeout(
        () => this.roomBtn?.classList.remove('kz-open'), sure);
    }, BUBBLE_FADE_MS);
  }

  /** Oda çekmecesi açıkken asistan konuşmaz. */
  roomToastOpen() {
    return Date.now() < (this._roomToastUntil || 0);
  }

  /**
   * Odayı uygular: duvar ölçüsü + hazır düzen + önerilen renk.
   *
   * Tek geçmiş adımı: geri al tuşu odayı bir bütün olarak geri alır. Bu
   * yüzden ölçüyü `history:false` ile yazıp geçmişi `applyPreset`'e
   * bırakıyoruz — o zaten kendi başına `pushHistory()` çağırıyor.
   */
  applyRoom(id, { pencereyiKapatma = false } = {}) {
    const oda = ROOMS.find((o) => o.id === id);
    if (!oda) return;

    /* `this.room` BURADA atanmıyor: geçmiş kaydı (pushHistory) odanın kimliğini
       de saklıyor, bu yüzden kayıt alınana kadar eski oda geçerli kalmalı.
       Yoksa "geri al" eski rafları yeni odanın içine koyuyordu. */
    // Seçim hemen işaretlensin: pencere kapanırken müşteri neye dokunduğunu
    // görmüş olsun.
    this.root.querySelectorAll('.kz-room-card').forEach((c) => {
      c.classList.toggle('kz-on', c.dataset.room === id);
    });

    /* Kart seçiminde pencere ÖNCE kapanır: duvarın dönüşümü asıl gösterilecek
       şey, pencere açık kalsaydı üstünü örterdi. Ama segmeyle öneri
       yenilenirken pencere açık kalıyor — müşteri farkı orada karşılaştırıyor. */
    if (!pencereyiKapatma) this.setRoomPanel(false);
    setTimeout(() => {
      if (!this.root.isConnected) return;


      /* Geçmiş kaydı EN BAŞTA: duvar ölçüsü, yüzey, oda ve raflar değişmeden
         önce. Böylece "geri al" odanın tamamını tek adımda eski haline
         döndürüyor — yeni odanın içinde eski rafları bırakmıyor. */
      this.pushHistory();
      [...this.shelves].forEach((s) => this.removeShelf(s, true));
      const eskiAciklik = this.room?.aciklik || 'pencere';
      this.room = oda;

      /* Yüzey (sıva/parke ↔ fayans) duvarın yeniden kurulmasını gerektiriyor.
         Ölçü de değiştiyse `setWallSize` zaten kuruyor; iki kez kurmayalım
         diye bayrağı önce yazıp gerekirse tek başına tazeliyoruz. */
      const yuzeyDegisti = (this.surface || null) !== (oda.yuzey || null);
      this.surface = oda.yuzey || null;
      const olcuDegisti = this.setWallSize(oda.duvar[0], oda.duvar[1], { history: false });
      /* Açıklık (pencere / kapı / balkon kapısı) da duvarın parçası. Bütün
         odalar aynı ölçüde olduğu için yalnızca ölçüye bakmak yetmiyordu:
         çocuk odasından mutfağa geçince pencere kalıyor, balkon kapısı
         gelmiyordu. */
      const aciklikDegisti = eskiAciklik !== (oda.aciklik || 'pencere');
      if ((yuzeyDegisti || aciklikDegisti) && !olcuDegisti) this.buildRoom();
      this.roomW.value = this.wallW;
      this.roomH.value = this.wallH;

      /* Önceki odanın rafları ÖNCE kalkıyor.
         Eskiden eşya, eski raflar hâlâ duvardayken kuruluyordu; çarpışma
         kontrolü onlara takılınca eşya duvarın ortasına oturamayıp yana
         kayıyordu. Lavabo+ayna gibi yüksek bir eşyada bu her seferinde
         oluyordu. Geçmiş kaydı da burada alınıyor ki "geri al" odanın
         tamamını tek adımda geri alsın. */
      /* Eşya raflardan ÖNCE kuruluyor: kapladığı hücreler çarpışma ızgarasına
         girdiği için hazır düzen kendiliğinden onun etrafına yerleşiyor.
         Yeni oda seçmek eşyaları her zaman geri getirir. */
      this.propHidden = false;
      this.buildProp(oda.esya);
      this.syncPropBtn();
      // Yeni oda = genel bakış: müşterinin önceki yakınlığı burada sıfırlanıyor.
      this.orbit.resetPan();
      this.fitCamera();

      // Düzen: desen + seçilen büyüklük + renk kurgusu.
      const secim = this.roomSecim || { boy: 'dengeli', renk: 'tek' };
      const boy = BOYLAR.find((b) => b.id === secim.boy) || BOYLAR[1];
      const placed = this.applyRoomLayout(oda, boy.adet, secim.renk);

      this.pickColor?.(oda.renk);
      this.refresh();
      this.track('kz_room_pick', { oda: oda.id, raf: placed, boy: boy.id, renk: secim.renk });
      this.clarityTag('kz_oda', oda.ad);
      this.clarityTag('kz_oda_boyu', boy.ad);

      // Segmeyle yenilemede baloncuk konuşmuyor: müşteri pencerede, okumuyor.
      if (pencereyiKapatma) return;

      /* "Oda kuruldu" çekmecesi KALDIRILDI: müşteri zaten odanın kurulduğunu
         görüyor, o cümle asistanın tavsiyesini geciktirmekten başka bir iş
         yapmıyordu. Doğrudan tavsiyeye geçiyoruz — raflar dizilmeye
         başladıktan hemen sonra. */
      clearTimeout(this._odaSayT);
      this._odaSayT = setTimeout(() => {
        if (this.root.isConnected) this.say('oda', { oda });
      }, 420);
    }, pencereyiKapatma ? 0 : ROOM_FADE_MS + 40);
  }

  /* ======================================================================
     ODA EŞYASI

     Duvara yaslı tek bir set. Izgarada yalnızca YATAY kayar; kapladığı
     hücreler raf çarpışmasına katılır (bkz. fits), bu yüzden üstüne raf
     koymaya çalışmak "üst üste raf" ile aynı uyarıyı verir.
     ====================================================================== */

  /** Sahnedeki eşyayı kaldırır ve belleğini bırakır. */
  /**
   * Eşyanın zemin ve duvar temas gölgelerini ekler (bkz.
   * makePropShadowTextures). Eşyanın ÇOCUKLARI: eşya sürüklenince, başka
   * duvara dönünce ya da açılıp kapanırken gölge de onunla birlikte.
   */
  addPropShadows(g) {
    g.updateMatrixWorld(true);   // g henüz sahnede değil: yerel = dünya
    const parcalar = [];
    g.traverse((o) => { if (o.isMesh) parcalar.push(new T.Box3().setFromObject(o)); });
    for (const k of makePropShadowTextures(parcalar)) {
      const mat = new T.MeshBasicMaterial({
        map: k.tex, transparent: true, depthWrite: false, toneMapped: false,
      });
      const mesh = new T.Mesh(new T.PlaneGeometry(k.w, k.h), mat);
      mesh.position.copy(k.konum);
      if (k.zemin) mesh.rotation.x = -Math.PI / 2;
      mesh.renderOrder = 1;
      mesh.userData.kzGolge = true;
      mesh.raycast = () => {};    // eşyayı tutmak için gölgesine değil, kendisine dokunulsun
      g.add(mesh);
      this.propMats.push(mat);    // clearProp dokusuyla birlikte atıyor
    }
  }

  clearProp() {
    if (!this.prop) return;
    // Paylaşılan (model önbelleğinden klonlanmış) geometriler atılmaz.
    this.prop.object.traverse((o) => {
      if (o.isMesh && !o.userData.paylasilan) o.geometry.dispose();
    });
    this.prop.object.parent?.remove(this.prop.object);
    // Temas gölgesi dokuları bu eşyaya özel; ayna dokusu ise ortak, atılmaz.
    this.propMats?.forEach((m) => { if (m.map?.userData?.kzGolge) m.map.dispose(); m.dispose(); });
    this.propMats = null;
    this.prop = null;
    this.fitCamera(false, true);
    this.invalidate();
  }

  /**
   * Odanın eşyasını kurar ve duvarın ortasına yerleştirir.
   * Duvar eşyadan darsa eşya hiç konmuyor — sıkışık bir sahne, boş duvardan
   * daha kötü görünür.
   */
  buildProp(tip) {
    this.clearProp();
    const tarif = PROPS[tip];
    if (!tarif || tarif.en > this.cols) return;

    /* Soluk ve mat: sahnenin yıldızı raflar. Eşya, rafın önüne geçmeden
       ölçeği ve bağlamı veriyor. */
    /* Üç yüzey: gövde (mat ahşap/lake), koyu (derz ve gölgede kalan yerler),
       açık (tezgah, şilte, minder). Üçü de soluk — sahnenin yıldızı raflar.
       Pürüzlülükleri farklı: aynı renkteki düz kutular yerine birbirinden
       ayrışan yüzeyler, sahneyi "kutu yığını" olmaktan çıkarıyor. */
    const govde = new T.MeshStandardMaterial({
      color: 0xcdc6ba, roughness: 0.92, metalness: 0, envMapIntensity: 0.24,
    });
    const koyu = new T.MeshStandardMaterial({
      color: 0x9b9384, roughness: 0.78, metalness: 0, envMapIntensity: 0.30,
    });
    const tas = new T.MeshStandardMaterial({
      color: 0xdedacf, roughness: 0.62, metalness: 0, envMapIntensity: 0.42,
    });
    /* Ayna: metalik ve neredeyse pürüzsüz. Sahnenin ortam haritasını
       yansıtıyor, yani yansıma için ikinci bir kamera/render geçişi yok.
       Tam ayna (roughness 0) cam gibi sert duruyordu; hafif pürüz gerçek
       bir banyo aynası gibi yumuşak bir yansıma veriyor. */
    // Doku bir kez üretiliyor; banyo seçilmezse hiç üretilmiyor.
    this.tex.mirror = this.tex.mirror || makeMirrorTexture();
    const ayna = new T.MeshStandardMaterial({
      map: this.tex.mirror,
      color: 0xffffff, roughness: 0.12, metalness: 0.62, envMapIntensity: 0.9,
    });
    this.propMats = [govde, koyu, tas, ayna];

    /* Kutu sürümü ANINDA kuruluyor: yerleşim ve çarpışma hesabı beklemesin,
       sahne bir an bile boş kalmasın. Gerçek model gelince yerine geçiyor. */
    const g = new T.Group();
    tarif.ciz(g, govde, koyu, tas, ayna);
    this.addPropShadows(g);
    if (tarif.model) this.swapPropModel(tarif, g);

    this.prop = {
      tip, cols: tarif.en, rows: tarif.yukseklik, ad: tarif.ad, object: g,
      duvar: 'arka',
      t: 0,
    };

    // Yaslanacak yer ara: önce arka duvarın ortası, olmazsa sol duvar.
    const yer = this.propSlot();
    if (!yer) {
      // Yer yok: kurduğumuz geometriyi ve malzemeleri bırak.
      g.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
      this.propMats.forEach((mm) => { if (mm.map?.userData?.kzGolge) mm.map.dispose(); mm.dispose(); });
      this.propMats = null;
      this.prop = null;
      return;
    }
    this.prop.duvar = yer.duvar;
    this.prop.t = yer.t;

    this.propGroup.add(g);
    this.placeProp();
    // Kadraj eşyaya göre değişiyor (bkz. computeFitDistance).
    this.fitCamera(false, true);
  }

  /**
   * Eşya modelinin adresi. Mağaza bölüm ayarından ezebilir; yoksa stüdyo
   * JS'inin yanındaki dosya kullanılır (raf modelleriyle aynı desen).
   */
  propModelUrl(ad) {
    const ayar = this.cfg.propModels?.[ad];
    if (ayar) return ayar;
    return new URL(`./kreaz-${ad}.glb`, import.meta.url).href;
  }

  /**
   * Eşya modelini indirir, çözer ve ÖNBELLEĞE alır. Aynı model ikinci kez
   * istendiğinde ağ isteği yapılmıyor, çözümlenmiş geometri klonlanıyor.
   * Hata olursa `null` dönüyor; çağıran kutu sürümüne düşüyor.
   */
  async loadPropModel(ad) {
    this._propModels = this._propModels || new Map();
    if (this._propModels.has(ad)) return this._propModels.get(ad);

    const istek = fetch(this.propModelUrl(ad))
      .then((r) => {
        if (!r.ok) throw new Error(`${ad} modeli yüklenemedi (${r.status})`);
        return r.arrayBuffer();
      })
      .then((buf) => glbToObject3D(buf))
      .catch((err) => {
        console.warn('[Kreaz Studio] eşya modeli', ad, err);
        return null;
      });

    this._propModels.set(ad, istek);
    return istek;
  }

  /**
   * Modeli eşyanın ızgara ayak izine oturtur.
   *
   * Model hangi birimde ve hangi orijinde gelirse gelsin, ölçüsünü BİZ
   * belirliyoruz: genişliği eşyanın tanımlı eni kadar ölçekleniyor (oran
   * korunuyor), tabanı zemine, arkası duvara yaslanıyor. Böylece gönderilen
   * dosyanın ölçeğinin doğru olması gerekmiyor — yalnızca oranları.
   */
  fitPropModel(raw, tarif) {
    const g = new T.Group();
    g.add(raw);

    if (tarif.modelTers) raw.rotation.y = Math.PI;

    raw.updateMatrixWorld(true);
    const kutu0 = new T.Box3().setFromObject(raw);
    const boy0 = kutu0.getSize(new T.Vector3());
    if (!(boy0.x > 0)) return g;

    raw.scale.setScalar((tarif.en * U) / boy0.x);

    // Ölçekten sonra yeniden ölç: yatayda ortala, tabanı zemine, arkası duvara.
    raw.updateMatrixWorld(true);
    const kutu = new T.Box3().setFromObject(raw);
    raw.position.x -= (kutu.min.x + kutu.max.x) / 2;
    raw.position.y -= kutu.min.y;
    raw.position.z -= kutu.min.z;

    raw.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true;
      o.receiveShadow = true;
    });
    return g;
  }

  /**
   * Model gelince kutu sürümünün yerine koyar.
   *
   * Grup nesnesi AYNI kalıyor — konum, dönüş, ızgara kaydı ve sürükleme hep
   * ona bağlı. Yalnızca içi değişiyor. Bu arada müşteri odayı değiştirmişse
   * (grup artık sahnede değilse) sessizce vazgeçiyoruz.
   */
  async swapPropModel(tarif, g) {
    const kok = await this.loadPropModel(tarif.model);
    if (!kok || !this.prop || this.prop.object !== g || !g.parent) return;

    const yerlesmis = this.fitPropModel(kok.clone(true), tarif);
    // Malzeme dosyadan okunmuyor; diğer eşyalarla aynı mat yüzeyi veriyoruz.
    yerlesmis.traverse((o) => {
      if (!o.isMesh) return;
      o.material = this.propMats[0];
      // Geometri önbellekten klonlandı, paylaşılıyor: clearProp atmamalı.
      o.userData.paylasilan = true;
    });

    // Kutu sürümünü at.
    for (const c of [...g.children]) {
      if (c.userData.kzGolge) continue;   // temas gölgeleri kalıyor
      c.traverse?.((o) => { if (o.isMesh) o.geometry.dispose(); });
      g.remove(c);
    }
    g.add(yerlesmis);
    this.invalidate();   // model ağdan geldi; kullanıcı hareketi yok
  }

  /* ======================================================================
     ODA ÖN AYARI DÜZENLERİ

     Her oda bir DESEN'e sahip; desen istenen raf adedine göre ölçekleniyor.
     Böylece 8 oda × 3 büyüklük × 2 renk kurgusu = 48 farklı öneri, 48 ayrı
     tablo yazmadan çıkıyor. Hücreler mutlak ızgara koordinatı döner.
     ====================================================================== */

  /** Rafların başlayacağı satır: eşyanın üstünde, göz hizasına yakın. */
  duzenTabani(satirSayisi) {
    const esyaUstu = (this.prop && this.prop.duvar === 'arka') ? this.prop.rows + 3 : 0;
    const rahat = Math.round(this.rows * 0.42);
    const taban = Math.max(esyaUstu, rahat);
    // Tasarımın tamamı duvara sığsın.
    return clamp(taban, ALT_SIRA, Math.max(ALT_SIRA, this.rows - satirSayisi * SHELF_ROWS));
  }

  /**
   * Odanın desenine göre raf hücreleri üretir.
   * `{ urun, col, row, sira }` döner; `sira` renk atamasında kullanılıyor.
   */
  odaDuzeni(oda, adet) {
    const p = this.productById.get(oda.urun) || this.productById.get('medium');
    const w = p.units;
    const orta = (genislik) => Math.round((this.cols - genislik) / 2);
    const hucreler = [];

    if (oda.desen === 'cevre') {
      /* Mobilyanın ÇEVRESİNE diziliş: raflar eşyanın iki yanında, kat kat
         yükselen sütunlar halinde. Banyoda lavabo+ayna tek parça olduğu için
         üstünde yer kalmıyor; asıl kullanışlı alan yanları. */
      const esya = (this.prop && this.prop.duvar === 'arka') ? this.prop : null;
      const solKenar = esya ? esya.t : Math.round((this.cols - w) / 2) - w;
      const sagKenar = esya ? esya.t + esya.cols : Math.round((this.cols + w) / 2);
      const maxKat = 3;
      const taban = clamp(18, ALT_SIRA, Math.max(ALT_SIRA, this.rows - maxKat * SHELF_ROWS));

      for (let i = 0; i < adet; i++) {
        const sagda = i % 2 === 1;
        const sira = Math.floor(i / 2);
        const kolon = Math.floor(sira / maxKat);   // dışa doğru ikinci sütun
        const kat = sira % maxKat;
        const col = sagda ? sagKenar + kolon * w : solKenar - (kolon + 1) * w;
        hucreler.push({
          urun: oda.urun, sira: kat,
          col: clamp(col, 0, this.cols - w),
          row: taban + kat * SHELF_ROWS,
        });
      }
      return hucreler;
    }

    if (oda.desen === 'izgara') {
      /* Tam bölünen bir dikdörtgen arıyoruz: 6 raf 3×2, 9 raf 3×3 olsun.
         Kareköke en yakın BÖLEN'i seçmek, artık raf bırakan 4+2 gibi
         asimetrik dizilişleri engelliyor. */
      const sigan = Math.max(1, Math.floor(this.cols / w));
      const ideal = Math.sqrt(adet * 1.5);
      let sut = 1;
      for (let d = 1; d <= adet; d++) {
        if (adet % d !== 0 || d > sigan) continue;
        if (Math.abs(d - ideal) < Math.abs(sut - ideal)) sut = d;
      }
      const sat = Math.ceil(adet / sut);
      const taban = this.duzenTabani(sat);
      const col0 = orta(sut * w);
      for (let i = 0; i < adet; i++) {
        const r = Math.floor(i / sut);
        const c = i % sut;
        const buSatir = Math.min(sut, adet - r * sut);
        const kaydir = Math.floor(((sut - buSatir) * w) / 2);
        hucreler.push({
          urun: oda.urun, sira: r,
          col: col0 + kaydir + c * w,
          row: taban + (sat - 1 - r) * SHELF_ROWS,
        });
      }
      return hucreler;
    }

    if (oda.desen === 'sira') {
      // Tek uzun hat; sığmazsa alt sıraya taşar.
      const sut = Math.max(1, Math.min(adet, Math.floor(this.cols / w)));
      const sat = Math.ceil(adet / sut);
      const taban = this.duzenTabani(sat);
      const col0 = orta(sut * w);
      for (let i = 0; i < adet; i++) {
        const r = Math.floor(i / sut);
        hucreler.push({
          urun: oda.urun, sira: r,
          col: col0 + (i % sut) * w,
          row: taban + (sat - 1 - r) * SHELF_ROWS,
        });
      }
      return hucreler;
    }

    if (oda.desen === 'merdiven') {
      // Kademeli diziliş: her raf bir öncekinin bir kademe altında ve yanında.
      const sat = Math.min(adet, Math.max(2, Math.ceil(adet / 2)));
      const taban = this.duzenTabani(sat);
      const toplamEn = Math.ceil(adet / sat) * w + (sat - 1) * Math.round(w / 2);
      let col = orta(Math.min(toplamEn, this.cols));
      for (let i = 0; i < adet; i++) {
        const r = i % sat;
        const k = Math.floor(i / sat);
        hucreler.push({
          urun: oda.urun, sira: r,
          col: clamp(col + k * w + r * Math.round(w / 2), 0, this.cols - w),
          row: taban + (sat - 1 - r) * SHELF_ROWS,
        });
      }
      return hucreler;
    }

    if (oda.desen === 'kuleSira') {
      // Solda kule, üstünden sağa uzanan hat — mutfakta tezgah üstü düzeni.
      const kule = Math.max(2, Math.round(adet * 0.45));
      const hat = adet - kule;
      const sat = Math.max(kule, 1);
      const taban = this.duzenTabani(sat);
      const toplam = w + hat * w;
      const col0 = orta(Math.min(toplam, this.cols));
      for (let i = 0; i < kule; i++) {
        hucreler.push({
          urun: oda.urun, sira: i,
          col: col0,
          row: taban + i * SHELF_ROWS,
        });
      }
      for (let i = 0; i < hat; i++) {
        hucreler.push({
          urun: oda.urun, sira: kule,
          col: clamp(col0 + w + i * w, 0, this.cols - w),
          row: taban + (kule - 1) * SHELF_ROWS,
        });
      }
      return hucreler;
    }

    /* 'braket': başucunun üstünden geçen yatay hat + hattın iki ucundan aşağı
       inen kuleler. Üç boy da aynı üst hattı paylaşıyor; tasarım büyüdükçe
       kompozisyon bozulmuyor, yalnızca uçlardan aşağı uzuyor.

       Kuleler yatağa BİTİŞİK DEĞİL: başlık, eşyanın ızgara ayak izinden
       birkaç santim taşıyor, bu yüzden bitişik kule rafın yan panelini
       yatağın içine sokuyordu. Aralarında en az iki kare (10 cm) var. */
    const esya = (this.prop && this.prop.duvar === 'arka') ? this.prop : null;
    const yatakSol = esya ? esya.t : Math.round((this.cols - 32) / 2);
    const yatakSag = esya ? esya.t + esya.cols : yatakSol + 32;
    const esyaSatir = esya ? esya.rows : 18;

    const BOSLUK = 2;        // yatakla kule arasındaki en az boşluk (kare)
    const KULE_TABAN = 12;   // en alttaki yan raf 60 cm — komidin hizası

    /* Sade yalnızca yatağın üstünde; dengeli 2+2, duvar dolusu 3+3 yanda. */
    const yan = adet <= 3 ? 0 : clamp(Math.round(adet / 3), 1, 3);
    const hat = adet - yan * 2;

    /* Üst hat hem başlığın üstünde kalmalı, hem de kulelerin en altı
       komidin hizasına inebilmeli. */
    const ustSatir = clamp(
      Math.max(esyaSatir + 2, KULE_TABAN + Math.max(0, yan - 1) * SHELF_ROWS),
      ALT_SIRA, Math.max(ALT_SIRA, this.rows - SHELF_ROWS),
    );

    // Başucu hattı yatağın üstünde ortalanıyor.
    const hatSol = yatakSol + Math.round(((yatakSag - yatakSol) - hat * w) / 2);
    for (let i = 0; i < hat; i++) {
      hucreler.push({
        urun: oda.urun, sira: 0,
        col: clamp(hatSol + i * w, 0, this.cols - w),
        row: ustSatir,
      });
    }

    /* Kulelerin en üst rafı hatla AYNI satırda: duvarda kesintisiz bir yatay
       çizgi oluşuyor, uçlar aşağı sarkıyor — yan ve üst birleşik okunuyor. */
    const solKol = clamp(yatakSol - BOSLUK - w, 0, this.cols - w);
    const sagKol = clamp(yatakSag + BOSLUK, 0, this.cols - w);
    for (let i = 0; i < yan; i++) {
      const row = Math.max(0, ustSatir - i * SHELF_ROWS);
      hucreler.push({ urun: oda.urun, sira: i + 1, col: solKol, row });
      hucreler.push({ urun: oda.urun, sira: i + 1, col: sagKol, row });
    }
    return hucreler;
  }

  /** Duvarın ızgara uzunluğu (birim). Arka duvar genişlik, yanlar derinlik. */
  duvarUzunlugu(duvar) {
    if (duvar === 'arka') return this.cols;
    return Math.max(0, Math.floor((this.roomD || 1.7) / U));
  }

  /**
   * Eşyanın yaslanabileceği duvarlar.
   * SAĞ duvarda pencere var: eşya orayı kapatmamalı, o yüzden listede yok.
   */
  propDuvarlari() {
    return ['arka', 'sol'];
  }

  /** Eşyaya yaslanacak bir duvar + konum bulur (arka duvarın ortası öncelikli). */
  propSlot() {
    const p = this.prop;
    if (!p) return null;
    for (const duvar of this.propDuvarlari()) {
      const boy = this.duvarUzunlugu(duvar);
      if (p.cols > boy) continue;
      // Her duvarda ORTADAN başlar, oradan dışa doğru boş yer aranır.
      // Köşeye yaslı başlamak eşyayı kadrajın kenarında sıkışık gösteriyordu.
      const tercih = Math.round((boy - p.cols) / 2);
      for (let d = 0; d <= boy; d++) {
        for (const t of d === 0 ? [tercih] : [tercih + d, tercih - d]) {
          if (this.propFits(duvar, t)) return { duvar, t };
        }
      }
    }
    return null;
  }

  /**
   * Oda sahnesini (yüzey + eşya) kurar. Hem kayıttan dönüşte hem geri al'da
   * kullanılıyor; `applyRoom` ise buna ek olarak rafları ve mesajları da
   * kuruyor. Duvarı BURADA yeniden kurmuyoruz — çağıran zaten kuruyor.
   */
  applyRoomScene(roomId, kayit, gizli) {
    const oda = roomId ? ROOMS.find((o) => o.id === roomId) : null;
    this.room = oda || null;
    this.surface = oda?.yuzey || null;
    this.propHidden = !!gizli;
    if (gizli) { this.clearProp(); this.syncPropBtn(); return; }
    // Oda seçilmemişse de eşya olabilir (varsayılan koltuk).
    this.buildProp(oda?.esya || (kayit?.propDuvar ? VARSAYILAN_ESYA : null));
    if (this.prop && kayit?.propDuvar) {
      this.prop.duvar = kayit.propDuvar;
      this.prop.t = clamp(kayit.propT || 0, 0,
        Math.max(0, this.duvarUzunlugu(kayit.propDuvar) - this.prop.cols));
      this.placeProp();
    }
    this.syncPropBtn();
  }

  /** Eşya düğmesi her zaman görünür; oda seçilmemişse varsayılan eşya gelir. */
  /* ======================================================================
     DEKOR EŞYALARI (vazo düğmesi)

     Eşyalar rafın ÇOCUĞU: raf sürüklenince onunla gidiyor, silinince onunla
     gidiyor, seçim ışını eşyaya değince rafı seçiyor. Raf rengi değişince
     boyanmasınlar diye her parça `kzEk` işaretli.
     ====================================================================== */

  /** Malzeme fabrikası + raf başına sabit rastgele dizi. */
  dekorK(tohum) {
    const rnd = seeded(tohum);
    this.tex.dekor = this.tex.dekor || makeDekorTextures();
    const tx = this.tex.dekor;
    this._dekorMat = this._dekorMat || new Map();
    const ortak = (anahtar, yap) => {
      let m = this._dekorMat.get(anahtar);
      if (!m) { m = yap(); this._dekorMat.set(anahtar, m); }
      return m;
    };
    /* Bütün eşyalar masa lambalarının ışığını alıyor (bkz. lambaIsigiEkle);
       öz-test bu cihazda lamba yolunu kapattıysa eşyalarda da kapalı. */
    const lambaYolu = this._rafKip?.lamba !== false;
    const std = (o) => {
      const m = new T.MeshStandardMaterial(Object.assign({ metalness: 0, envMapIntensity: 0.5 }, o));
      return lambaYolu ? lambaIsigiEkle(m) : m;
    };
    const lambaGucu = this.night ? 1 : (this.altin ? 0.5 : 0);
    const k = {
      rnd,
      ara: (a, b) => a + rnd() * (b - a),
      sec: (dizi) => dizi[Math.floor(rnd() * dizi.length) % dizi.length],
      pastel: () => k.sec(DEKOR_PASTEL),
      kitapRengi: () => k.sec(DEKOR_KITAP),
      mesh: (geo, mat, golgeDusur = true) => {
        const m = new T.Mesh(geo, mat);
        m.castShadow = golgeDusur;
        m.receiveShadow = true;
        m.userData.kzEk = true;
        /* Eşyalar tıklamayı yakalamıyor: rafın önündeki bir vazoya ya da
           kitaba dokunmak, arkadaki rafı/eşyayı (koltuğu) tutmayı
           engelliyordu; müşteri mobilyayı taşırken rafı oynatıyordu. Rafın
           kendisi tıklanabilir kalıyor. */
        m.raycast = () => {};
        return m;
      },
      seramik: (renk, puruz) => ortak(`s${renk}${puruz.toFixed(1)}`, () => std({
        color: new T.Color(renk), map: tx.seramik, roughness: puruz, envMapIntensity: 0.6,
      })),
      duz: (renk, puruz) => ortak(`d${renk}${puruz.toFixed(1)}`, () => std({
        color: new T.Color(renk), roughness: puruz,
      })),
      cam: (renk) => ortak(`c${renk}`, () => std({
        color: new T.Color(renk), transparent: true, opacity: 0.34, roughness: 0.05,
        metalness: 0.1, envMapIntensity: 1.6, depthWrite: false,
      })),
      ahsap: (renk) => ortak(`a${renk}`, () => std({ color: new T.Color(renk), map: tx.ahsap, roughness: 0.68 })),
      kumas: (renk) => ortak(`k${renk}`, () => std({ color: new T.Color(renk), map: tx.kumas, roughness: 0.96, envMapIntensity: 0.35 })),
      metal: () => ortak('metal', () => std({ color: 0x8c8f93, roughness: 0.35, metalness: 0.8, envMapIntensity: 1 })),
      yaprak: () => ortak('yaprak', () => std({ color: 0x7d9c64, roughness: 0.6, side: T.DoubleSide })),
      desenli: (ad, puruz) => ortak(`p${ad}`, () => std({ map: tx[ad], roughness: puruz })),
      // Bütün kitaplar tek malzeme: atlas + köşe rengi (kapak rengi).
      atlas: () => tx.kitap,
      kitapMat: () => ortak('kitap', () => std({ map: tx.kitap.tex, vertexColors: true, roughness: 0.62, envMapIntensity: 0.35 })),
      desen: (ad, puruz) => ortak(`v${ad}`, () => std({ map: tx.desen[ad], roughness: puruz, envMapIntensity: 0.7 })),
      emaye: (renk) => ortak(`e${renk}`, () => std({ color: new T.Color(renk), map: tx.emaye, roughness: 0.28, envMapIntensity: 0.7 })),
      su: () => ortak('su', () => std({
        color: 0xcfe4ec, transparent: true, opacity: 0.26, roughness: 0.02, envMapIntensity: 1.4, depthWrite: false,
      })),
      baski: () => ortak('baski', () => std({ map: tx.baski.tex, roughness: 0.55, envMapIntensity: 0.3 })),
      baskiYerleri: () => tx.baski.yer,
      ekran: () => ortak('ekran', () => std({ color: 0x17191c, roughness: 0.1, metalness: 0.35, envMapIntensity: 1.5 })),
      lambaSapka: (renk) => ortak(`l${renk}`, () => std({ color: new T.Color(renk), roughness: 0.42, side: T.DoubleSide })),
      /* Lambanın ampulü ve şapka astarı ORTAK: akşam tek bir değişkenle
         bütün lambalar birden yanıyor (bkz. applyLighting). */
      ampul: () => ortak('ampul', () => std({
        color: 0xfff3dc, emissive: 0xffd08a, emissiveIntensity: 2.6 * lambaGucu, roughness: 0.3,
      })),
      lambaIc: () => ortak('lambaIc', () => std({
        color: 0xf4eee4, roughness: 0.7, side: T.DoubleSide,
        emissive: 0xffbe78, emissiveIntensity: 1.15 * lambaGucu,
      })),
    };
    return k;
  }

  /**
   * Rafın üstündeki kullanılabilir alan: iç genişlik ve üstte kalan boşluk.
   * Üstteki raf bu rafın iç kısmına sarkıyorsa (kaydırılmış dizilimde yan
   * paneli içeride kalıyorsa) onun panelinin alt ucu, değilse tablasının
   * altı sınır. Duvarın tepesi de sınır.
   */
  dekorAlani(s) {
    const P = RAF_PROFIL;
    const w = s.units * U;
    const x = s.object.position.x;
    const icSol = x - w / 2 + P.panel + 0.006, icSag = x + w / 2 - P.panel - 0.006;
    const ust = s.object.position.y + P.ust;
    let tavan = this.wallH / 100 - 0.02;
    for (const o of this.shelves) {
      if (o === s || o.row <= s.row) continue;
      const ow = o.units * U, ox = o.object.position.x;
      const oSol = ox - ow / 2, oSag = ox + ow / 2;
      if (oSag <= icSol || oSol >= icSag) continue;
      const panelIceride = (oSol > icSol && oSol < icSag) || (oSag > icSol && oSag < icSag);
      const alt = o.object.position.y + (panelIceride ? -P.yariH : P.tabla);
      tavan = Math.min(tavan, alt);
    }
    return { genislik: icSag - icSol, yukseklik: tavan - ust - 0.012 };
  }

  /**
   * Bir rafın eşyalarını kurar.
   *
   * Gerçek raflardaki gibi GRUPLAR halinde: her grupta bir büyük eşya (kitap
   * sırası, vazo, lamba, çerçeve…) ve çoğu zaman önüne ya da yanına konmuş
   * küçük bir eşya (mum, fincan, telefon…). Gruplar arası boşluk geniş, grup
   * içi dar; kalan boşluk kenarlara eşit değil, rastgele dağılıyor.
   *
   * `sayac` bütün duvardaki eşya türlerinin sayısı: her seferinde en az
   * kullanılan tür öne alınıyor, aynı eşya raflarda tekrar tekrar çıkmıyor.
   */
  dekorKur(s, alan, anahtar, sayac = new Map()) {
    if (alan.yukseklik < 0.045 || alan.genislik < 0.12) return null;
    let tohum = 7;
    for (const ch of s.id + (this.room?.id || '')) tohum = (tohum * 31 + ch.charCodeAt(0)) >>> 0;
    const k = this.dekorK(tohum);
    const havuz = DEKOR_HAVUZ[this.room?.id] || DEKOR_HAVUZ.genel;
    const sirala = (liste) => [...new Set(liste)]
      .map((t) => [t, (sayac.get(t) || 0) + k.rnd() * 0.9])
      .sort((a, b) => a[1] - b[1])
      .map((x) => x[0]);
    const at = (e) => e.grup.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
    const DERIN = 0.142;
    /* Ölçüler GEOMETRİDEN: tarifin verdiği genişlik yaprakları, kulpları,
       yaslanan kitabı, eğik çerçeveyi saymıyordu; eşya rafın yan paneline
       ya da üstteki rafa giriyordu. Kutu ortada değilse (kulp tek yanda)
       kaydırma payı da tutuluyor. */
    const kutu = new T.Box3();
    const olc = (e) => {
      // Köşe noktalarından (kesin): eğik eşyada kaba kutu fazla büyüyor.
      kutu.setFromObject(e.grup, true);
      e.w = kutu.max.x - kutu.min.x + 0.002;
      e.d = kutu.max.z - kutu.min.z + 0.002;
      e.h = kutu.max.y;
      e.ox = -(kutu.min.x + kutu.max.x) / 2;
      e.oz = -(kutu.min.z + kutu.max.z) / 2;
      return e;
    };
    const sigmaz = (e, w) => e.w > w || e.d > DERIN || e.h > alan.yukseklik;

    const grupSayisi = s.units <= 6 ? (k.rnd() < 0.55 ? 1 : 2) : (s.units <= 9 ? 2 : (k.rnd() < 0.5 ? 2 : 3));
    const hedef = alan.genislik * k.ara(0.58, 0.82);
    /* Gruplar arası 3–8 cm, BAŞTAN belirleniyor ve yer ayırırken sayılıyor.
       Eskiden 3,5 cm ayrılıp sonra 8 cm'ye kadar boşluk veriliyordu; artan
       pay sağ uçtaki eşyayı yan panelin içine itiyordu. */
    const araliklar = Array.from({ length: grupSayisi }, (_, i) => (i ? k.ara(0.03, 0.08) : 0));
    const gruplar = [];
    const kullanilan = new Set();
    let dolu = 0, bosluk = 0;
    for (let n = 0; n < grupSayisi && dolu < hedef; n++) {
      bosluk += araliklar[n];
      const kalan = alan.genislik - dolu - bosluk;
      if (kalan < 0.06) break;
      let ana = null;
      for (const tur of sirala(havuz.buyuk)) {
        if (kullanilan.has(tur)) continue;
        // Işığı hesaplanabilen lamba sayısı sınırlı; fazlası sönük kalırdı.
        if (tur === 'lamba' && (sayac.get('lamba') || 0) >= LAMBA_EN_COK) continue;
        const e = DEKOR_TARIF[tur]?.(k, alan.yukseklik, undefined, Math.min(kalan, 0.3));
        if (!e) continue;
        if (sigmaz(olc(e), kalan)) { at(e); continue; }
        ana = Object.assign(e, { tur });
        break;
      }
      if (!ana) break;
      kullanilan.add(ana.tur);
      const grup = { ana, yan: null, w: ana.w, onde: false, sag: k.rnd() < 0.6 };
      // Yanına küçük bir eşya: derinlik sığıyorsa ÖNÜNE (yarı bindirmeli).
      if (k.rnd() < 0.62) {
        const yer = kalan - ana.w;
        for (const tur of sirala(havuz.kucuk)) {
          if (kullanilan.has(tur)) continue;
          const e = DEKOR_TARIF[tur]?.(k, Math.min(alan.yukseklik, Math.max(0.05, ana.h * 0.8)), undefined, 0.17);
          if (!e) continue;
          olc(e);
          const onde = ana.d + e.d + 0.012 <= DERIN;
          const ek = onde ? e.w * 0.45 : e.w + 0.008;
          if (ek > yer || sigmaz(e, Infinity)) { at(e); continue; }
          grup.yan = Object.assign(e, { tur });
          grup.onde = onde;
          grup.w = ana.w + ek;
          kullanilan.add(tur);
          break;
        }
      }
      gruplar.push(grup);
      dolu += grup.w;
    }
    if (!gruplar.length) return null;

    // Artan boşluk sola-sağa rastgele paylaşılıyor.
    const toplam = dolu + araliklar.slice(1, gruplar.length).reduce((a, b) => a + b, 0);
    const bos = Math.max(0, alan.genislik - toplam);
    let x = -alan.genislik / 2 + bos * k.ara(0.1, 0.9);

    /* Ölçek ŞABLONDAN: rafın o anki ölçeği değil. Yeni eklenen ya da oda
       değişiminde gelen raf büyüyerek beliriyor (ölçek ~0,01'den başlıyor);
       o anki ölçeğe göre kurulan eşyalar raf büyüyünce 100 kat büyük
       kalıyor, ekranı kaplıyordu. */
    const s0 = this.templates.get(s.productId)?.template.scale.x || 1;
    const g = new T.Group();
    g.userData.kzEk = true;
    g.userData.kzDekor = true;
    g.scale.setScalar(1 / s0);
    g.position.set(0, RAF_PROFIL.ust / s0, 0);
    this._dekorGolgeGeo = this._dekorGolgeGeo || new T.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);

    const ogeler = [];
    const lambalar = [];
    const rafW = s.units * U;
    // cx, dz: eşyanın ölçülen kutusunun ortası (raf koordinatında).
    const koy = (e, cx, dz) => {
      e.grup.position.set(cx + e.ox, 0, dz + e.oz);
      g.add(e.grup);
      /* Lambanın ışığı yalnız kendi rafının içinde: tablanın üstü, arka
         plakanın önü, iki yan panel arası. Alttaki raf, komşu raf ve
         rafın arkasındaki duvar bu kutunun dışında kalıyor. */
      if (e.lamba) {
        lambalar.push({
          obj: e.grup, nokta: e.lamba.nokta, sin: e.lamba.sin, olcek: e.lamba.olcek,
          min: new T.Vector3(-rafW / 2 - 0.002, -0.003, -0.004),
          max: new T.Vector3(rafW / 2 + 0.002, Math.max(alan.yukseklik, e.h) + 0.01, RAF_PROFIL.on + 0.006),
        });
      }
      const gm = new T.MeshBasicMaterial({
        map: this.tex.dekor.golge, transparent: true, depthWrite: false, toneMapped: false, opacity: 0,
      });
      const golge = new T.Mesh(this._dekorGolgeGeo, gm);
      golge.scale.set(e.w * 1.25, 1, e.d * 1.25);
      golge.position.set(cx, 0.0007, dz);
      golge.renderOrder = 1;
      golge.userData.kzEk = true;
      golge.raycast = () => {};
      g.add(golge);
      ogeler.push({ obj: e.grup, golge: gm, x0: cx + e.ox, w: e.w, h: e.h, top: !!e.top, tur: e.tur });
      sayac.set(e.tur, (sayac.get(e.tur) || 0) + 1);
    };
    const duvarda = (e) => e.d / 2 + 0.008;
    const arka = (e) => (e.kitap ? RAF_PROFIL.on - e.d / 2 - 0.006 : duvarda(e));
    const on = (e) => RAF_PROFIL.on - e.d / 2 - 0.008;
    const orta = (e) => clamp(0.074 + k.ara(-0.02, 0.012), e.d / 2 + 0.006, RAF_PROFIL.on - e.d / 2 - 0.004);

    gruplar.forEach((gr, i) => {
      if (i) x += araliklar[i];
      const { ana, yan } = gr;
      if (!yan) {
        koy(ana, x + ana.w / 2, ana.kitap || ana.arkada ? arka(ana) : orta(ana));
      } else if (gr.onde) {
        // Büyük eşya arkada, küçüğü önünde ve yarısı onunla üst üste.
        // Kitaplar da bu durumda arka plakaya itiliyor; önü küçüğe kalıyor.
        const anaX = gr.sag ? x : x + yan.w * 0.45;
        koy(ana, anaX + ana.w / 2, duvarda(ana));
        const yanX = gr.sag ? x + ana.w - yan.w * 0.55 : x;
        koy(yan, yanX + yan.w / 2, on(yan));
      } else {
        const ilk = gr.sag ? ana : yan, ikinci = gr.sag ? yan : ana;
        koy(ilk, x + ilk.w / 2, ilk === ana && (ana.kitap || ana.arkada) ? arka(ana) : orta(ilk));
        const x2 = x + ilk.w + 0.008;
        koy(ikinci, x2 + ikinci.w / 2, ikinci === ana && (ana.kitap || ana.arkada) ? arka(ana) : orta(ikinci));
      }
      x += gr.w;
    });

    g.traverse((o) => { o.userData.kzEk = true; });
    s.object.add(g);
    s.dekor = {
      grup: g, anahtar, ogeler, lambalar,
      turler: gruplar.flatMap((gr) => [gr.ana.tur, gr.yan?.tur].filter(Boolean)),
      // Komşu raflar oynadığında bu eşyaların yerinde kalıp kalamayacağına
      // bakmak için: nerede kuruldu, en uzun eşya ne kadar.
      oda: this.room?.id || '-', col: s.col, row: s.row,
      enYuksek: Math.max(...ogeler.map((o) => o.h)), alanY: alan.yukseklik,
    };
    return s.dekor;
  }

  /** Rafın eşyalarını kaldırır ve geometrilerini bırakır (malzemeler ortak). */
  decorTemizle(s) {
    const d = s.dekor;
    if (!d) return;
    d.grup.parent?.remove(d.grup);
    d.grup.traverse((o) => {
      if (!o.isMesh) return;
      if (o.geometry !== this._dekorGolgeGeo) o.geometry.dispose();
      if (!Array.isArray(o.material) && o.material.map === this.tex.dekor?.golge) o.material.dispose();
    });
    if (this._dusenler) this._dusenler = this._dusenler.filter((a) => !d.ogeler.some((q) => q.obj === a.obj));
    s.dekor = null;
  }

  /** Vazo düğmesi: eşyaları raflara düşür ya da topla. */
  toggleDecor() {
    this.dekorAcik = !this.dekorAcik;
    this.root.querySelector('[data-act="decor"]')?.setAttribute('aria-pressed', this.dekorAcik ? 'true' : 'false');
    if (this.dekorAcik) {
      this.syncDecor();
      this.track('kz_dekor', { oda: this.room?.id || 'genel' });
      if (!this.shelves.length) this.showHint('Raf ekledikçe üstüne eşyalar gelecek.');
    } else {
      const raflar = this.shelves.filter((s) => s.dekor);
      raflar.forEach((s) => { s.dekor.kapaniyor = true; });
      const gruplar = raflar.map((s) => s.dekor.grup);
      this._dusenler = null;
      // Müşteri bu arada tekrar açtıysa yeni gelen eşyalara dokunma.
      this.popOut(gruplar, 0, () => {
        raflar.forEach((s) => { if (s.dekor && gruplar.includes(s.dekor.grup)) this.decorTemizle(s); });
      });
    }
    this.persist();
    this.invalidate();
  }

  /**
   * Eşyaları rafların güncel durumuna uydurur. Her yenilemede çağrılıyor ama
   * yalnız DEĞİŞEN raflar yeniden kuruluyor: anahtar = oda + üstteki boşluk.
   * Yeni kurulan eşyalar 5 cm yukarıdan sırayla düşüyor.
   */
  syncDecor() {
    if (!this.dekorAcik) return;
    const sirali = [...this.shelves].sort((a, b) => (b.row - a.row) || (a.col - b.col));
    let sira = 0;
    const simdi = performance.now();
    const oda = this.room?.id || '-';
    /* Yalnız TAŞINAN rafın eşyaları yeniden düşüyor. Raf yerindeyse ve oda
       aynıysa eşyalar kalıyor — yanına ya da üstüne başka raf gelse bile,
       eşyalar hâlâ sığdığı sürece. Sığmıyorsa (üstüne alçak bir raf
       geldiyse) o rafınkiler yeniden seçiliyor. */
    const alanlar = new Map(sirali.map((s) => [s, this.dekorAlani(s)]));
    const kalir = (s) => {
      const eski = s.dekor, alan = alanlar.get(s);
      if (!eski || eski.kapaniyor || eski.oda !== oda || eski.col !== s.col || eski.row !== s.row) return false;
      return eski.ogeler.length
        ? eski.enYuksek <= alan.yukseklik + 0.003
        : Math.abs((eski.alanY ?? 0) - alan.yukseklik) < 0.02;
    };
    const yenilenecek = sirali.filter((s) => !kalir(s));
    if (!yenilenecek.length) return;
    // Duvarda kalan eşyaların türleri: yeni raflar önce bunlardan AZ olanı alıyor.
    const sayac = new Map();
    for (const s of sirali) {
      if (yenilenecek.includes(s)) continue;
      for (const t of s.dekor?.turler || []) sayac.set(t, (sayac.get(t) || 0) + 1);
    }
    for (const s of yenilenecek) {
      const alan = alanlar.get(s);
      const anahtar = `${oda}|${Math.round(alan.yukseklik * 50)}|${Math.round(alan.genislik * 100)}`;
      this.decorTemizle(s);
      const d = this.dekorKur(s, alan, anahtar, sayac);
      if (!d) {
        s.dekor = { grup: new T.Group(), anahtar, ogeler: [], oda, col: s.col, row: s.row, alanY: alan.yukseklik };
        continue;
      }
      /* Raf henüz büyüyerek beliriyorsa eşyalar onun bitmesini bekliyor:
         önce raf gelir, sonra eşyalar üstüne düşer. */
      const beliris = (this._popIns || []).find((p) => p.obj === s.object);
      const bekle = beliris ? Math.max(0, beliris.bas + beliris.sure - simdi) + 60 : 0;
      for (const o of d.ogeler) {
        const oran = o.h / Math.max(0.03, o.w);
        this._dusenler = this._dusenler || [];
        this._dusenler.push({
          obj: o.obj, golge: o.golge, x0: o.x0, yari: o.w / 2,
          bas: simdi + bekle + Math.min(sira * 65, 1500),
          genlik: (o.top ? 0.12 : clamp(0.018 + oran * 0.03, 0.02, 0.085)) * (Math.random() < 0.5 ? -1 : 1),
        });
        o.obj.visible = false;
        sira++;
      }
    }
    if (sira) this.invalidate();
  }

  /**
   * Düşüş ve sallanma. 5 cm'den serbest düşüş (yavaşlatılmış: gözle
   * görülsün), sonra tabana değen kenarı üzerinde sönümlü bir sallanma.
   * Render döngüsünden çağrılıyor; iş yoksa hemen dönüyor.
   */
  stepDecor() {
    const l = this._dusenler;
    if (!l || !l.length) { this._dusenler = null; return; }
    const now = performance.now();
    const DUS = 0.26, SAL = 0.8, YUKSEK = 0.05;
    for (let i = l.length - 1; i >= 0; i--) {
      const a = l[i];
      const t = (now - a.bas) / 1000;
      if (t < 0) continue;
      a.obj.visible = true;
      if (t < DUS) {
        const q = t / DUS;
        a.obj.position.y = YUKSEK * (1 - q * q);
        a.obj.position.x = a.x0;
        a.obj.rotation.z = 0;
        a.golge.opacity = 0.75 * q * q;
        continue;
      }
      const tt = t - DUS;
      if (tt >= SAL) {
        a.obj.position.set(a.x0, 0, a.obj.position.z);
        a.obj.rotation.z = 0;
        a.golge.opacity = 0.75;
        l.splice(i, 1);
        continue;
      }
      // Eşya tabanının kenarı üzerinde döner: o kenar yerde kalır, diğeri kalkar.
      const aci = a.genlik * Math.exp(-5.5 * tt) * Math.sin(tt * Math.PI * 2 * 2.4);
      const p = aci > 0 ? -a.yari : a.yari;
      a.obj.rotation.z = aci;
      a.obj.position.x = a.x0 + p - p * Math.cos(aci);
      a.obj.position.y = -p * Math.sin(aci);
      a.golge.opacity = 0.75;
    }
    if (!l.length) this._dusenler = null;
  }

  /**
   * Masa lambalarının ışığını malzemelere bildirir: ampulün dünyadaki yeri ve
   * lambanın rafının iç kutusu. Her çizimden önce çağrılıyor — raf
   * sürüklenirken, eşya düşerken ışık onlarla gidiyor. Birkaç lamba için
   * birkaç matris çarpımı; gündüz hiç iş yok.
   */
  lambaIsiklari() {
    const u = LAMBA_U;
    if (!u) return;
    /* Gece oda aydınlık kalıyor (tavan armatürü); lamba ışığı onun
       üstüne çıkacak kadar güçlü. Altın saatte daha sakin. */
    const guc = this.night ? 6 : (this.altin ? 2.2 : 0);
    let n = 0;
    if (guc > 0 && this.dekorAcik) {
      const v = this._lambaV || (this._lambaV = [new T.Vector3(), new T.Vector3(), new T.Vector3()]);
      for (const s of this.shelves) {
        const d = s.dekor;
        if (!d?.lambalar || d.kapaniyor) continue;
        for (const l of d.lambalar) {
          if (n >= LAMBA_EN_COK || !l.obj.visible) continue;
          l.nokta.updateWorldMatrix(true, false);
          v[0].setFromMatrixPosition(l.nokta.matrixWorld);
          v[1].copy(l.min).applyMatrix4(d.grup.matrixWorld);
          v[2].copy(l.max).applyMatrix4(d.grup.matrixWorld);
          u.kzLambaPos.value[n].set(v[0].x, v[0].y, v[0].z, l.sin);
          u.kzLambaMin.value[n].set(Math.min(v[1].x, v[2].x), Math.min(v[1].y, v[2].y), Math.min(v[1].z, v[2].z), guc * l.olcek);
          u.kzLambaMax.value[n].set(Math.max(v[1].x, v[2].x), Math.max(v[1].y, v[2].y), Math.max(v[1].z, v[2].z), 0);
          n++;
        }
      }
    }
    u.kzLambaN.value = n;
  }

  syncPropBtn() {
    const b = this.root.querySelector('.kz-prop-btn');
    if (!b) return;
    b.hidden = false;
    b.setAttribute('aria-pressed', this.prop ? 'true' : 'false');
  }

  /**
   * Eşyaları göster/gizle. Raflarla aynı hareketle gelip gidiyorlar.
   * Gizliyken ızgara da serbest kalıyor: alt banda raf konabiliyor.
   */
  toggleProp() {
    if (this._propAnim) return;
    /* Oda seçilmemişse varsayılan eşya geliyor. Müşteri böylece odaların
       varlığını eşya üzerinden keşfediyor; "?" düğmesi de bir kez bunu
       söylüyor (bkz. odaIpucu). */
    const tip = this.room?.esya || VARSAYILAN_ESYA;
    if (this.prop) {
      const g = this.prop.object;
      this._propAnim = true;
      this.propHidden = true;
      // Müşterinin taşıdığı yeri hatırla: tekrar açınca oraya dönsün.
      this._propSonYer = { duvar: this.prop.duvar, t: this.prop.t };
      this.popOut([g], 0, () => {
        this._propAnim = false;
        this.clearProp();
        this.syncPropBtn();
        this.refresh();
        this.persist();
      });
      this.root.querySelector('.kz-prop-btn')?.setAttribute('aria-pressed', 'false');
      return;
    }
    this.propHidden = false;
    this.buildProp(tip);
    // Kapatmadan önceki yere dön — oraya hâlâ sığıyorsa.
    const son = this._propSonYer;
    if (this.prop && son && this.propFits(son.duvar, son.t)) {
      this.prop.duvar = son.duvar;
      this.prop.t = son.t;
      this.placeProp();
    }
    if (this.prop) this.popIn([this.prop.object], 0);
    this.syncPropBtn();
    this.refresh();
    this.persist();
    // Oda seçilmemişken eşya geldiyse, odaları da tanıtalım.
    if (!this.room && this.prop) this.odaIpucu();
  }

  /**
   * "?" düğmesinden kısa bir yönlendirme. Sırayı bekler: asistan ya da
   * WhatsApp konuşuyorsa araya girmez, onlar susunca çıkar. Oturumda bir kez.
   */
  odaIpucu() {
    if (this._odaIpucuVerildi) return;
    this._odaIpucuVerildi = true;
    const dene = (kalan) => {
      if (!this.root.isConnected) return;
      const mesgul = this.waDrawerOpen() || this.roomToastOpen()
        || this.assistantEl?.classList.contains('kz-talking');
      if (mesgul && kalan > 0) { setTimeout(() => dene(kalan - 1), 900); return; }
      this.roomToast('Buraya dokunup oda değiştirebilirsin', 3400);
    };
    setTimeout(() => dene(14), DRAWER_SLIDE_MS + 200);
  }

  /** İmleci zemin düzlemine (y=0) düşürür — eşya sürüklemek için. */
  pointerToFloor(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, this.camera);
    this._zemin = this._zemin || new T.Plane(new T.Vector3(0, 1, 0), 0);
    const hit = new T.Vector3();
    return this.ray.ray.intersectPlane(this._zemin, hit) ? hit : null;
  }

  /**
   * Zemindeki bir noktaya karşılık gelen duvar + konum.
   *
   * En yakın duvarı seçerken mevcut duvara küçük bir öncelik tanıyoruz
   * (histerezis): köşeye yaklaşınca eşyanın iki duvar arasında titremesini
   * engelliyor.
   */
  propTargetAt(nokta, tutma) {
    const p = this.prop;
    if (!p) return null;
    const yariW = (this.wallW / 100) / 2;

    /* Zemin ışını sığ kamera açısında ufka yaklaştıkça çok uzak noktalara
       düşüyor: küçük bir fare hareketi metrelerce kayma üretiyordu. Odanın
       makul sınırlarının dışına düşen okumaları yok sayıyoruz. */
    const sinir = 1.5;
    if (Math.abs(nokta.x) > yariW + sinir
        || nokta.z < -sinir || nokta.z > (this.roomD || 2) + sinir) return null;
    const uzaklik = {
      sol: nokta.x + yariW,
      arka: nokta.z,
    };
    if (uzaklik[p.duvar] !== undefined) uzaklik[p.duvar] -= 0.30;  // histerezis

    let secilen = null;
    for (const duvar of this.propDuvarlari()) {
      if (p.cols > this.duvarUzunlugu(duvar)) continue;
      if (!secilen || uzaklik[duvar] < uzaklik[secilen]) secilen = duvar;
    }
    if (!secilen) return null;

    /* Duvar boyunca konum: yan duvarlarda derinlik (z), arkada genişlik (x).
       Aynı duvarda kalıyorsak hareket GÖRELİ: imlecin başlangıca göre farkı
       kadar kayıyor. Duvar değiştiğinde göreli referans anlamını yitiriyor,
       orada imleci merkez alıyoruz ve çağıran tutmayı sıfırlıyor. */
    const boy = this.duvarUzunlugu(secilen);
    const eksen = (nk) => (secilen === 'arka' ? nk.x + yariW : nk.z);
    let t;
    if (tutma && tutma.duvar === secilen && tutma.nokta) {
      const fark = (eksen(nokta) - eksen(tutma.nokta)) / U;
      t = Math.round(tutma.t + fark);
    } else {
      t = Math.round(eksen(nokta) / U - p.cols / 2);
    }
    t = clamp(t, 0, boy - p.cols);

    // Arka duvarda raflara çarpıyorsa en yakın boş konuma kay.
    if (secilen === 'arka' && !this.propFits(secilen, t)) {
      let bulunan = null;
      for (let d = 1; d <= boy && bulunan === null; d++) {
        for (const aday of [t + d, t - d]) {
          if (this.propFits(secilen, aday)) { bulunan = aday; break; }
        }
      }
      if (bulunan === null) return null;
      t = bulunan;
    }
    return { duvar: secilen, t };
  }

  /** İmleç eşyanın üstünde mi? (sürükleyerek kaydırmak için) */
  propAt(e) {
    if (!this.prop) return false;
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, this.camera);
    this.propGroup.updateMatrixWorld(true);
    return this.ray.intersectObjects(this.propGroup.children, true).length > 0;
  }

  /**
   * Eşya bu duvarın bu konumuna sığar mı?
   * Yan duvarlarda raf yok, sınır kontrolü yeter. Arka duvar aynı zamanda
   * tasarım yüzeyi olduğu için orada raflarla da çakışmamalı.
   */
  propFits(duvar, t) {
    const p = this.prop;
    if (!p) return false;
    const boy = this.duvarUzunlugu(duvar);
    if (t < 0 || t + p.cols > boy) return false;
    if (duvar !== 'arka') return true;
    for (const s of this.shelves) {
      if (s.row < p.rows && t < s.col + s.units && s.col < t + p.cols) return false;
    }
    return true;
  }

  /**
   * Eşyayı yaslandığı duvara oturtur.
   *
   * Eşya modelleri +z yönüne (odaya doğru) uzuyor. Yan duvarlarda grubu Y
   * ekseninde 90° çevirip AYNI modeli kullanıyoruz; her eşya için ayrı bir
   * "yan duvar" sürümü çizmeye gerek kalmıyor. Konum her zaman duvar
   * yüzeyinden hesaplandığı için eşya sürüklenirken duvardan kopmuyor.
   */
  placeProp() {
    const p = this.prop;
    if (!p) return;
    const yariW = (this.wallW / 100) / 2;
    const orta = (p.t + p.cols / 2) * U;   // duvar boyunca merkez
    const pay = ESYA_DUVAR_PAYI;           // yüzeyden çok küçük bir aralık

    if (p.duvar === 'sol') {
      p.object.position.set(-yariW + pay, 0, orta);
      p.object.rotation.y = Math.PI / 2;
    } else {
      p.object.position.set(-yariW + orta, 0, pay);
      p.object.rotation.y = 0;
    }
    this.invalidate();
  }

  /** Duvar ölçüsü değiştiğinde eşyayı odanın içinde tutar. */
  reflowProp() {
    const p = this.prop;
    if (!p) return;
    if (p.cols > this.duvarUzunlugu(p.duvar)) {
      // Bu duvara artık sığmıyor; başka bir duvar dene.
      const yer = this.propSlot();
      if (!yer) { this.clearProp(); return; }
      p.duvar = yer.duvar; p.t = yer.t;
    } else {
      p.t = clamp(p.t, 0, this.duvarUzunlugu(p.duvar) - p.cols);
    }
    this.placeProp();
  }

  /**
   * Rafları sıfırdan büyüterek duvara oturtur.
   *
   * Tek raf eklerken de (`stagger` 0), oda/ürün ön ayarı kurarken de
   * (raflar ~90 ms arayla) aynı hareket kullanılıyor — duvara bir şey
   * konduğunda her yerde aynı his oluşsun.
   *
   * Hedef ölçek `userData.kzScale`'de saklanıyor: müşteri animasyon
   * bitmeden ikinci rafı eklerse, yarım kalmış ölçeği "hedef" sanıp rafı
   * kalıcı olarak küçük bırakmıyoruz.
   */
  popIn(objects, stagger = 90) {
    this.popScale(objects, stagger, 'in');
  }

  /** Aynı hareketin tersi: küçülerek kaybolur, bitince `bitince` çalışır. */
  popOut(objects, stagger = 0, bitince) {
    this.popScale(objects, stagger, 'out', bitince);
  }

  popScale(objects, stagger, yon, bitince) {
    if (!objects || !objects.length) { bitince?.(); return; }
    const t0 = performance.now();
    // Aynı nesne için bekleyen eski kayıt varsa düşsün.
    this._popIns = (this._popIns || []).filter((a) => !objects.includes(a.obj));
    const son = objects.length - 1;
    objects.forEach((obj, i) => {
      const tam = obj.userData.kzScale || obj.scale.clone();
      obj.userData.kzScale = tam;
      const kucuk = tam.clone().multiplyScalar(0.01);
      const bas0 = yon === 'in' ? kucuk : obj.scale.clone();
      const hedef = yon === 'in' ? tam : kucuk;
      if (yon === 'in') obj.scale.copy(kucuk);
      this._popIns.push({
        obj, bas0, hedef, bas: t0 + i * stagger, sure: 340,
        // Geri çağrı yalnız son nesneye bağlı: hepsi bitince bir kez çalışsın.
        bitince: i === son ? bitince : null,
      });
    });
  }

  /** Render döngüsünden çağrılır; iş yoksa anında geri döner. */
  stepPopIns() {
    const l = this._popIns;
    if (!l || !l.length) return;
    const now = performance.now();
    for (let i = l.length - 1; i >= 0; i--) {
      const a = l[i];
      if (now < a.bas) continue;
      const k = clamp((now - a.bas) / a.sure, 0, 1);
      a.obj.scale.lerpVectors(a.bas0, a.hedef, easeOutCubic(k));
      // Bitenleri listeden düşür ve ölçeği tam hedefe oturt.
      if (k >= 1) { a.obj.scale.copy(a.hedef); l.splice(i, 1); a.bitince?.(); }
    }
    if (!l.length) this._popIns = null;
  }

  /** Ölçüm — GTM/GA varsa olayları oraya basar, yoksa sessizce geçer. */
  track(ad, extra) {
    try {
      (window.dataLayer = window.dataLayer || []).push(
        Object.assign({ event: ad, raf: this.shelves.length }, extra || {}));
    } catch { /* yoksay */ }
    // Müşteri davranışı Clarity'ye de: kayıtlar bu olaylarla filtrelenebiliyor.
    // Asistanın kendi mesajları (ipucu, WhatsApp tanıtımı) gürültü; gitmiyor.
    if (CLARITY_OLAYLARI.has(ad)) this.clarity('event', ad);
  }

  /** Müşterinin kendi eklediği raf (oda ön ayarının dizdikleri sayılmıyor). */
  trackShelfAdd(shelf, yontem) {
    if (!shelf) return;
    const p = this.productById.get(shelf.productId);
    const renk = this.colorById.get(shelf.colorId)?.name;
    const olcu = `${p.name} ${p.cm} cm`;
    this.track('kz_raf_eklendi', { olcu, renk, yontem });
    this.clarityTag('kz_olcu_eklendi', olcu);
    this.clarityTag('kz_renk_eklendi', renk);
  }

  /**
   * Microsoft Clarity köprüsü (temada zaten kurulu). Clarity 3D tuvali
   * kaydedemiyor — kayıtta duvar boş görünüyor. Bu yüzden oturuma
   * ETİKET olarak tasarım linki ve özet bilgiler yazılıyor: kaydı izlerken
   * etiketteki linke tıklayıp müşterinin duvarını aynen açmak mümkün.
   * Clarity sayfada yoksa sessizce geçiyor.
   */
  clarity(...args) {
    try { if (typeof window.clarity === 'function') window.clarity(...args); } catch { /* yoksay */ }
  }

  /** Oturum etiketi (Clarity'de "Custom tags" filtresi). */
  clarityTag(anahtar, deger) {
    if (deger === undefined || deger === null || deger === '') return;
    this.clarity('set', anahtar, String(deger).slice(0, 255));
  }

  /**
   * Tasarımın kendisini Clarity'ye yazar: link + raf adedi + renkler/ölçüler.
   * Her değişiklikte değil, tasarım 4 sn durulunca (ve sekme kapanırken);
   * her ara adım ayrı bir etiket değeri olup listeyi kirletiyordu.
   */
  clarityDesign(hemen) {
    clearTimeout(this._clarityT);
    const yaz = () => {
      if (!this.shelves.length) return;
      const kod = this.encodeDesign();
      if (kod === this._claritySonKod) return;
      this._claritySonKod = kod;
      this.clarityTag('kz_tasarim', this.designUrl());
      this.clarityTag('kz_raf_adedi', this.shelves.length);
      const renkler = [...new Set(this.shelves.map((s) => this.colorById.get(s.colorId)?.name))];
      const olculer = [...new Set(this.shelves.map((s) => this.productById.get(s.productId)?.name))];
      this.clarityTag('kz_renkler', renkler.join(', '));
      this.clarityTag('kz_olculer', olculer.join(', '));
    };
    if (hemen) yaz(); else this._clarityT = setTimeout(yaz, 4000);
  }

  /**
   * Boşta kalma dürtüsü.
   * Müşteri IDLE_MS boyunca hiçbir şey eklemezse asistan bir öneriyle
   * devreye girer. Amaç satış baskısı değil, bir sonraki adımı göstermek.
   *
   * Eskiden üç ipucundan sonra tamamen susuyor, aralık da her turda uzuyordu
   * (10→20→30 sn). Sonuç: yaklaşık bir dakika sonra stüdyo sessizleşiyor,
   * asistan "boş" duruyordu. Artık sabit aralıkla konuşmaya devam ediyor;
   * ısrarcı olmamasını SAY_COOLDOWN_MS ve ipuçlarının kendini tekrar
   * etmemesi sağlıyor.
   */
  armIdleNudge(fresh = true) {
    clearTimeout(this._idleT);
    if (fresh) this._idleN = 0;
    this._idleT = setTimeout(() => {
      if (!this.root.isConnected) return;
      this._idleN = (this._idleN || 0) + 1;
      this.say('idle');
      this.armIdleNudge(false);
    }, IDLE_MS);
  }

  /* ======================================================================
     REHBER EL

     Stüdyoyu ilk açana ne yapabileceğini SÖZSÜZ gösteren soyut bir el:
     - duvar boşsa ekleme düğmesine (masaüstünde ilk raf kartına) dokunur,
     - ilk raf eklenince duvarı sürükleyip çevirir,
     - stüdyo raflı açılırsa çevirir ve yakınlaştırır,
     - müşteri uzun süre hiçbir şey yapmazsa sırayla oda seçiciyi ve
       ışık / eşya / mobilya düğmelerini gösterir.
     Her biri 2–4 sn. Dokunuşa tamamen kapalı; müşteri bir yere dokunduğu,
     kaydırdığı ya da tuşa bastığı an söner. Yalnız transform/opacity
     canlandırılıyor: 3D çizime ve kare süresine etkisi yok.
     ====================================================================== */

  /** Açılıştan sonra çağrılıyor: hangi gösterimin gerektiğine karar verir. */
  rehberBasla() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    this.rehberKur();
    const g = this._rehberGordu;
    if (!this.shelves.length) {
      if (!g.ekle) this.rehberGoster('ekle');
    } else {
      // Raflı duvar: önce rafın taşınabildiği, ardından çevirme ve yakınlaştırma.
      this.rehberSira([...(g.tasi ? [] : ['tasi']), ...(g.gez ? [] : ['gezYakin'])]);
    }
    this.rehberBostaKur();
  }

  /**
   * Gösterimleri art arda oynatır: biri bitince (müşteri bu arada hiçbir
   * şeye dokunmadıysa) kısa bir aradan sonra sıradaki. Gösterilemeyeni
   * (hedefi ekranda yok) atlıyor; bir dokunuş sıranın tamamını iptal ediyor.
   */
  rehberSira(liste) {
    const kalan = [...liste];
    while (kalan.length) {
      if (this.rehberGoster(kalan.shift())) { this._rehberKuyruk = kalan; return true; }
      if (!this.rehberUygun()) return false;
    }
    return false;
  }

  rehberKur() {
    if (this._rehber) return;
    const k = document.createElement('div');
    k.className = 'kz-rehber';
    k.setAttribute('aria-hidden', 'true');
    /* Soyut el: işaret parmağı açık, diğerleri bükük. Önce kalın siyah
       siluet, üstüne aynı parçalar beyaz: birleşik ince bir dış çizgi. */
    const parca = `
      <rect x="9.5" y="3" width="7" height="26" rx="3.5"/>
      <rect x="16.5" y="16" width="6.5" height="14" rx="3.25"/>
      <rect x="23" y="18" width="6.5" height="13" rx="3.25"/>
      <rect x="29.5" y="20.5" width="6" height="11" rx="3"/>
      <rect x="9.5" y="24" width="26" height="20" rx="8"/>
      <rect x="3" y="24" width="7.5" height="13" rx="3.75" transform="rotate(-28 6.75 30.5)"/>`;
    k.innerHTML = `
      <div class="kz-rehber-hayalet"></div>
      <div class="kz-rehber-iz"></div><div class="kz-rehber-iz"></div>
      <div class="kz-rehber-nokta"></div><div class="kz-rehber-nokta"></div><div class="kz-rehber-nokta"></div>
      <div class="kz-rehber-el"><svg viewBox="0 0 40 48">
        <g fill="#111" stroke="#111" stroke-width="3" stroke-linejoin="round">${parca}</g>
        <g fill="#fff">${parca}</g>
        <path d="M16.5 18v7M23 20v6.5M29.5 22.5v5.5" stroke="#111" stroke-width="1.3" stroke-linecap="round"/>
      </svg></div>
      <div class="kz-rehber-not"></div><div class="kz-rehber-not"></div>`;
    this.root.appendChild(k);
    const notlar = k.querySelectorAll('.kz-rehber-not');
    this._rehber = {
      k,
      el: k.querySelector('.kz-rehber-el'),
      svg: k.querySelector('.kz-rehber-el svg'),
      iz: [...k.querySelectorAll('.kz-rehber-iz')],
      nokta: [...k.querySelectorAll('.kz-rehber-nokta')],
      not: notlar[0],
      not2: notlar[1],
      hayalet: k.querySelector('.kz-rehber-hayalet'),
      anim: [],
    };
    // ?rehber=yeniden: gösterimleri baştan izlemek için (deneme).
    if (/[?&]rehber=yeniden/.test(location.search)) { try { localStorage.removeItem('kz-rehber'); } catch { /* yoksay */ } }
    try { this._rehberGordu = JSON.parse(localStorage.getItem('kz-rehber') || '{}') || {}; } catch { this._rehberGordu = {}; }
    this._rehberBostaGosterilen = new Set();
    /* Müşteri bir şey yaptığı an: gösterim söner, boşta sayacı baştan.
       Dinleyiciler pasif ve yakalama aşamasında — hiçbir olayı durdurmuyor. */
    const dur = () => {
      this._rehberSonGirdi = performance.now();
      this._rehberKuyruk = null;
      this.rehberKes();
      this.rehberBostaKur();
    };
    const dinle = { capture: true, passive: true };
    ['pointerdown', 'wheel', 'keydown'].forEach((t) => window.addEventListener(t, dur, dinle));
    // Duvarı kendisi çevirdiyse çevirme gösterimine gerek yok (raf
    // sürüklemek sayılmıyor: o sırada this.drag dolu).
    const ogrendi = (e) => {
      if (!e.buttons || this.drag) return;
      this.rehberIsaretle('gez');
      this.canvas.removeEventListener('pointermove', ogrendi);
    };
    this.canvas.addEventListener('pointermove', ogrendi, { passive: true });
  }

  rehberIsaretle(ad) {
    if (!this._rehberGordu || this._rehberGordu[ad]) return;
    this._rehberGordu[ad] = 1;
    try { localStorage.setItem('kz-rehber', JSON.stringify(this._rehberGordu)); } catch { /* yoksay */ }
  }

  /** Şu an göstermek uygun mu: sekme açık, sürükleme yok, açık menü/çekmece yok. */
  rehberUygun() {
    return !!this._rehber && !this._rehberOynuyor && !document.hidden && !this.drag
      && !this.root.classList.contains('kz-drawer')
      && !this.root.querySelector('[aria-expanded="true"]');
  }

  /** Ekranda görünen bir düğmenin merkezi (görünmüyorsa null). */
  rehberHedef(el) {
    const r = el?.getBoundingClientRect?.();
    if (!r || r.width < 4 || r.height < 4) return null;
    if (r.right < 0 || r.bottom < 0 || r.left > innerWidth || r.top > innerHeight) return null;
    if (el.closest('[hidden]') || getComputedStyle(el).visibility === 'hidden') return null;
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, r };
  }

  /** Bir gösterimi oynatır; uygun değilse ya da hedef yoksa hiçbir şey yapmaz. */
  rehberGoster(tur) {
    if (!this.rehberUygun()) return false;
    const R = this._rehber;
    const sahne = this.canvas.getBoundingClientRect();
    const cx = sahne.left + sahne.width * 0.5, cy = sahne.top + sahne.height * 0.56;
    const dokunmatik = !!window.matchMedia?.('(pointer: coarse)').matches;
    let plan = null;

    if (tur === 'ekle') {
      const h = this.rehberHedef(this.isCompact() ? this.drawerBtn : this.root.querySelector('.kz-card'));
      if (!h) return false;
      plan = this.rehberDokun(h, this.isCompact() ? 'Raf eklemek için dokun' : 'Rafı eklemek için tıkla', 'sol');
      this.rehberIsaretle('ekle');
    } else if (tur === 'gez' || tur === 'gezYakin') {
      plan = this.rehberGez(cx, cy, tur === 'gezYakin', dokunmatik);
      this.rehberIsaretle('gez');
    } else if (tur === 'tasi') {
      const h = this.rehberRaf();
      if (!h) return false;
      plan = this.rehberTasi(h);
      this.rehberIsaretle('tasi');
    } else {
      const BOSTA = {
        oda: ['.kz-room-btn', 'Odanı seç, hazır düzenle başla', 'sag'],
        isik: ['.kz-night-btn', 'Akşam ışığında gör', 'sol'],
        dekor: ['.kz-decor-btn', 'Raflara eşya koy', 'sol'],
        esya: ['.kz-prop-btn', 'Mobilyayı göster ya da gizle', 'sol'],
      }[tur];
      const h = BOSTA && this.rehberHedef(this.root.querySelector(BOSTA[0]));
      if (!h) return false;
      plan = this.rehberDokun(h, BOSTA[1], BOSTA[2], 1);
    }

    // Oynat
    this.rehberKes(true);
    this._rehberOynuyor = true;
    R.k.classList.add('kz-acik');
    for (const [el, kareler, secenek] of plan.adim) {
      /* Yumuşatma HER ARALIĞA ayrı uygulanıyor. Seçeneklere yazılınca
         bütün zaman çizelgesini büküyor, dokunuşlar kayıyordu. */
      const k = secenek?.easing ? kareler.map((x) => Object.assign({ easing: secenek.easing }, x)) : kareler;
      R.anim.push(el.animate(k, { duration: plan.sure, fill: 'both' }));
    }
    clearTimeout(this._rehberT);
    this._rehberT = setTimeout(() => {
      this.rehberKes();
      // Sırada bekleyen varsa (bu arada dokunulmadıysa) kısa bir aradan sonra.
      const sira = this._rehberKuyruk;
      this._rehberKuyruk = null;
      if (sira?.length) setTimeout(() => this.rehberSira(sira), 450);
    }, plan.sure + 60);
    return true;
  }

  /**
   * Taşıma gösterimi için raf: ekranda rahat görünen, merkeze en yakın
   * olanı. Rafın ekrandaki kutusunu (köşelerinin izdüşümü) ve arka
   * plakasının ortasını döndürüyor.
   */
  rehberRaf() {
    const rect = this.canvas.getBoundingClientRect();
    const v = new T.Vector3(), o = new T.Vector3();
    const ekran = (x, y, z) => {
      v.set(x, y, z).project(this.camera);
      return [rect.left + (v.x + 1) / 2 * rect.width, rect.top + (1 - v.y) / 2 * rect.height];
    };
    const P = RAF_PROFIL;
    const mx = rect.left + rect.width / 2, my = rect.top + rect.height / 2;
    let en = null;
    for (const s of this.shelves) {
      s.object.getWorldPosition(o);
      const yw = s.units * U / 2;
      const koseler = [];
      for (const x of [o.x - yw, o.x + yw]) {
        for (const y of [o.y - P.yariH, o.y + P.yariH]) {
          for (const z of [o.z, o.z + P.on]) koseler.push(ekran(x, y, z));
        }
      }
      const xs = koseler.map((p) => p[0]), ys = koseler.map((p) => p[1]);
      const kutu = { l: Math.min(...xs), t: Math.min(...ys), r: Math.max(...xs), b: Math.max(...ys) };
      const [x, y] = ekran(o.x, o.y, o.z + 0.004);
      // Çok küçük ya da kenarda (düğmelerin, rayın altında) kalanı geç.
      if (kutu.r - kutu.l < 28 || x < rect.left + 60 || x > rect.right - 60
          || y < rect.top + 100 || y > rect.bottom - 140) continue;
      const uzak = Math.hypot(x - mx, y - my);
      if (!en || uzak < en.uzak) en = { x, y, kutu, uzak, rect };
    }
    return en;
  }

  /** Gösterimi bitirir (müşteri dokunduysa hemen söndürür). */
  rehberKes(sessiz) {
    const R = this._rehber;
    if (!R) return;
    clearTimeout(this._rehberT);
    if (!sessiz && this._rehberOynuyor) R.k.classList.remove('kz-acik');
    const animler = R.anim.splice(0);
    const bitir = () => animler.forEach((a) => a.cancel());
    if (sessiz) bitir(); else setTimeout(bitir, 170);   // önce sönsün, sonra dursun
    this._rehberOynuyor = false;
  }

  /* ---------- hareket tarifleri ----------
     Her tarif { sure, adim: [[öğe, kareler, seçenek]] } döner; bütün
     öğeler aynı süreyle oynuyor. Kare zamanları milisaniye, o(ms) onları
     toplam süreye oranlıyor. */

  /** Yazı balonu: [bas, bit] aralığında görünür. */
  rehberNot(el, metin, x, y, yon, bas, bit, sure) {
    el.textContent = metin;
    const kay = yon === 'sol' ? 'translate(-100%, -50%)' : yon === 'alt' ? 'translate(-50%, 0)' : 'translate(0, -50%)';
    const t = `translate(${x}px, ${y}px) ${kay}`;
    const o = (ms) => Math.min(1, Math.max(0, ms / sure));
    return [el, [
      { transform: t, opacity: 0, offset: 0 },
      { transform: t, opacity: 0, offset: o(bas + 300) },
      { transform: t, opacity: 1, offset: o(bas + 550) },
      { transform: t, opacity: 1, offset: o(bit - 320) },
      { transform: t, opacity: 0, offset: o(bit) },
      { transform: t, opacity: 0, offset: 1 },
    ]];
  }

  /** Basılan yerde açılıp sönen halka. */
  rehberHalka(el, x, y, an, sure) {
    const o = (ms) => Math.min(1, Math.max(0, ms / sure));
    const t = (s) => `translate(${x}px, ${y}px) scale(${s})`;
    return [el, [
      { transform: t(0.4), opacity: 0, offset: 0 },
      { transform: t(0.4), opacity: 0, offset: o(an - 10) },
      { transform: t(0.5), opacity: 0.95, offset: o(an) },
      { transform: t(1.5), opacity: 0, offset: o(an + 620) },
      { transform: t(1.5), opacity: 0, offset: 1 },
    ]];
  }

  /** Düğmeye uzanıp dokunma (tekrar: kaç kez). */
  rehberDokun(h, metin, yon, tekrar = 2) {
    const R = this._rehber;
    const { x, y } = h;
    const gelX = x + (yon === 'sol' ? -70 : 70), gelY = y - 64;
    const varis = 850, aralik = 650;
    const basmalar = Array.from({ length: tekrar }, (_, i) => varis + 150 + i * aralik);
    const sure = basmalar[basmalar.length - 1] + 900;
    const o = (ms) => Math.min(1, ms / sure);
    const el = (px, py) => `translate(${px - 13}px, ${py - 3}px)`;
    const adim = [
      [R.el, [
        { transform: el(gelX, gelY), opacity: 0, offset: 0 },
        { transform: el(gelX + (x - gelX) * 0.3, gelY + (y - gelY) * 0.3), opacity: 1, offset: o(260) },
        { transform: el(x, y), opacity: 1, offset: o(varis) },
        { transform: el(x, y), opacity: 1, offset: o(sure - 320) },
        { transform: el(x + 6, y + 10), opacity: 0, offset: 1 },
      ], { easing: 'cubic-bezier(.3,.7,.3,1)' }],
    ];
    // Basış: parmak hafifçe çöküyor, altında halka açılıyor.
    const bas = [{ transform: 'scale(1)', offset: 0 }];
    for (const b of basmalar) {
      bas.push({ transform: 'scale(1)', offset: o(b - 90) },
        { transform: 'scale(.84)', offset: o(b) },
        { transform: 'scale(1)', offset: o(b + 170) });
    }
    bas.push({ transform: 'scale(1)', offset: 1 });
    adim.push([R.svg, bas]);
    basmalar.slice(0, 2).forEach((b, i) => adim.push(this.rehberHalka(R.iz[i], x, y, b, sure)));
    const notX = yon === 'sol' ? h.r.left - 14 : h.r.right + 14;
    adim.push(this.rehberNot(R.not, metin, notX, y, yon, 0, sure, sure));
    return { sure, adim };
  }

  /**
   * Rafı taşıma: parmak rafın plakasına basıyor; rafın çerçevesi (hayalet)
   * parmakla birlikte boş tarafa kayıyor, bırakınca sönüyor.
   */
  rehberTasi(h) {
    const R = this._rehber;
    const { x, y, kutu, rect } = h;
    const dx = x < rect.left + rect.width / 2 ? 90 : -90;
    const dy = y > rect.top + rect.height * 0.5 ? -46 : 46;
    const bas = 650, son = 1650, sure = 2550;
    const o = (ms) => Math.min(1, ms / sure);
    const el = (px, py) => `translate(${px - 13}px, ${py - 3}px)`;
    const g = R.hayalet;
    g.style.width = `${Math.round(kutu.r - kutu.l)}px`;
    g.style.height = `${Math.round(kutu.b - kutu.t)}px`;
    const ht = (ox, oy, s = 1) => `translate(${kutu.l + ox}px, ${kutu.t + oy}px) scale(${s})`;
    const notY = Math.min(Math.max(kutu.b, kutu.b + dy) + 16, rect.bottom - 150);
    const adim = [
      [g, [
        { transform: ht(0, 0, 0.98), opacity: 0, offset: 0 },
        { transform: ht(0, 0, 0.98), opacity: 0, offset: o(bas - 10) },
        { transform: ht(0, 0, 1.03), opacity: 1, offset: o(bas + 140) },
        { transform: ht(dx, dy, 1.03), opacity: 1, offset: o(son) },
        { transform: ht(dx, dy, 1), opacity: 0.9, offset: o(son + 160) },
        { transform: ht(dx, dy, 1), opacity: 0, offset: o(son + 600) },
        { transform: ht(dx, dy, 1), opacity: 0, offset: 1 },
      ], { easing: 'ease-in-out' }],
      [R.el, [
        { transform: el(x + 40, y + 46), opacity: 0, offset: 0 },
        { transform: el(x, y), opacity: 1, offset: o(450) },
        { transform: el(x, y), opacity: 1, offset: o(bas + 140) },
        { transform: el(x + dx, y + dy), opacity: 1, offset: o(son) },
        { transform: el(x + dx, y + dy), opacity: 1, offset: o(son + 300) },
        { transform: el(x + dx + 8, y + dy + 12), opacity: 0, offset: o(sure - 80) },
        { transform: el(x + dx + 8, y + dy + 12), opacity: 0, offset: 1 },
      ], { easing: 'ease-in-out' }],
      [R.svg, [
        { transform: 'scale(1)', offset: 0 },
        { transform: 'scale(1)', offset: o(bas - 90) },
        { transform: 'scale(.86)', offset: o(bas) },
        { transform: 'scale(.86)', offset: o(son) },
        { transform: 'scale(1)', offset: o(son + 160) },
        { transform: 'scale(1)', offset: 1 },
      ]],
      this.rehberHalka(R.iz[0], x, y, bas, sure),
      this.rehberNot(R.not, 'Rafı tutup sürükle, yerini değiştir', (kutu.l + kutu.r) / 2 + dx / 2, notY, 'alt', 0, sure, sure),
    ];
    return { sure, adim };
  }

  /**
   * Duvarı sürükleyip çevirme; yakin ise ardından yakınlaştırma:
   * dokunmatikte iki parmak açılıyor, masaüstünde el tekerleği çeviriyor.
   */
  rehberGez(cx, cy, yakin, dokunmatik) {
    const R = this._rehber;
    const bas = 600, son = 1650, cevirSon = 2250, Z = 2450;
    const sure = yakin ? Z + 1700 : cevirSon;
    const o = (ms) => Math.min(1, ms / sure);
    const x0 = cx + 95, x1 = cx - 95;
    const el = (px, py) => `translate(${px - 13}px, ${py - 3}px)`;
    const nk = (px, py, s = 1) => `translate(${px}px, ${py}px) scale(${s})`;

    const elKare = [
      { transform: el(x0 + 30, cy + 30), opacity: 0, offset: 0 },
      { transform: el(x0, cy), opacity: 1, offset: o(420) },
      { transform: el(x0, cy), opacity: 1, offset: o(bas) },
      { transform: el(cx, cy - 8), opacity: 1, offset: o((bas + son) / 2) },
      { transform: el(x1, cy), opacity: 1, offset: o(son) },
      { transform: el(x1, cy), opacity: 1, offset: o(son + 250) },
      { transform: el(x1 - 10, cy + 14), opacity: 0, offset: o(cevirSon) },
    ];
    if (yakin && !dokunmatik) {
      // Tekerlek: el ortada iki kez kısa aşağı-yukarı.
      elKare.push(
        { transform: el(cx + 24, cy + 26), opacity: 0, offset: o(Z) },
        { transform: el(cx, cy), opacity: 1, offset: o(Z + 260) },
        { transform: el(cx, cy - 7), opacity: 1, offset: o(Z + 520) },
        { transform: el(cx, cy + 6), opacity: 1, offset: o(Z + 780) },
        { transform: el(cx, cy - 7), opacity: 1, offset: o(Z + 1040) },
        { transform: el(cx, cy + 6), opacity: 1, offset: o(Z + 1300) },
        { transform: el(cx + 8, cy + 16), opacity: 0, offset: o(Z + 1600) },
      );
    }
    const sonKare = elKare[elKare.length - 1];
    if (sonKare.offset < 1) elKare.push(Object.assign({}, sonKare, { offset: 1 }));

    const adim = [
      [R.el, elKare, { easing: 'ease-in-out' }],
      [R.svg, [
        { transform: 'scale(1)', offset: 0 },
        { transform: 'scale(1)', offset: o(bas - 90) },
        { transform: 'scale(.86)', offset: o(bas) },
        { transform: 'scale(.86)', offset: o(son) },
        { transform: 'scale(1)', offset: o(son + 160) },
        { transform: 'scale(1)', offset: 1 },
      ]],
      // Parmak ucunun izi sürüklerken parmağı takip ediyor.
      [R.nokta[0], [
        { transform: nk(x0, cy, 0.6), opacity: 0, offset: 0 },
        { transform: nk(x0, cy, 0.6), opacity: 0, offset: o(bas - 10) },
        { transform: nk(x0, cy), opacity: 0.9, offset: o(bas + 80) },
        { transform: nk(cx, cy - 8), opacity: 0.9, offset: o((bas + son) / 2) },
        { transform: nk(x1, cy), opacity: 0.9, offset: o(son) },
        { transform: nk(x1, cy, 0.6), opacity: 0, offset: o(son + 200) },
        { transform: nk(x1, cy, 0.6), opacity: 0, offset: 1 },
      ], { easing: 'ease-in-out' }],
      this.rehberHalka(R.iz[0], x0, cy, bas, sure),
      this.rehberNot(R.not, 'Sürükle, odayı çevir', cx, cy + 46, 'alt', 0, cevirSon, sure),
    ];

    if (yakin && dokunmatik) {
      // İki parmak: iki iz merkezden çapraz dışa açılıyor.
      [[-1, 1], [1, -1]].forEach(([sx, sy], i) => {
        const t = (dx, dy, s = 1) => nk(cx + sx * dx, cy + sy * dy, s);
        adim.push([R.nokta[i + 1], [
          { transform: t(16, 12, 0.6), opacity: 0, offset: 0 },
          { transform: t(16, 12, 0.6), opacity: 0, offset: o(Z) },
          { transform: t(16, 12), opacity: 0.95, offset: o(Z + 220) },
          { transform: t(74, 52), opacity: 0.95, offset: o(Z + 1150) },
          { transform: t(80, 56, 0.6), opacity: 0, offset: o(Z + 1450) },
          { transform: t(80, 56, 0.6), opacity: 0, offset: 1 },
        ], { easing: 'ease-in-out' }]);
      });
    }
    if (yakin) {
      adim.push(this.rehberNot(R.not2, dokunmatik ? 'İki parmakla yakınlaştır' : 'Tekerlekle yakınlaştır',
        cx, cy + (dokunmatik ? 86 : 46), 'alt', Z - 200, sure, sure));
    }
    return { sure, adim };
  }

  /**
   * Uzun boşluk: müşteri REHBER_BOSTA_MS boyunca hiçbir şeye dokunmazsa
   * el sırayla bir sonraki adımı gösteriyor. Her biri oturumda bir kez.
   */
  rehberBostaKur() {
    if (!this._rehber) return;
    clearTimeout(this._rehberBostaT);
    this._rehberBostaT = setTimeout(() => {
      if (!this.root.isConnected) return;
      const sira = this.shelves.length
        ? [...(this._rehberGordu.tasi ? [] : ['tasi']), ...(this._rehberGordu.gez ? [] : ['gez']),
          'oda', 'isik', 'dekor', 'esya']
        : ['ekle', 'oda', 'isik', 'dekor', 'esya'];
      const goster = this._rehberBostaGosterilen;
      for (const tur of sira) {
        if (goster.has(tur)) continue;
        if (this.rehberGoster(tur)) { goster.add(tur); break; }
        // Uygun değilse (menü açık vb.) bu turu sonraya bırak.
        if (!this.rehberUygun()) break;
        goster.add(tur);   // hedefi görünmüyor: atla
      }
      if (sira.some((t) => !goster.has(t))) this.rehberBostaKur();
    }, REHBER_BOSTA_MS);
  }

  /** Duvarda henüz kullanılmamış bir renk — varsa öneri için. */
  unusedColor() {
    const used = new Set(this.shelves.map((s) => s.colorId));
    used.add(this.activeColor);
    const rest = this.cfg.colors.filter((c) => !used.has(c.id));
    if (!rest.length) return null;
    return rest[Math.floor(Math.random() * rest.length)];
  }

  /**
   * Duvarın o anki kompozisyonunu ölçer.
   *
   * Öneriler buradan çıkıyor: tahmin ya da genel geçer laf değil, müşterinin
   * ekranda gördüğü düzenin gerçek ölçüsü.
   */
  composition() {
    const S = this.shelves;
    if (!S.length) return null;

    const minC = Math.min(...S.map((s) => s.col));
    const maxC = Math.max(...S.map((s) => s.col + s.units));
    const minR = Math.min(...S.map((s) => s.row));
    const maxR = Math.max(...S.map((s) => s.row + SHELF_ROWS));

    // Ağırlık merkezi: raf genişliğine göre ağırlıklandırılmış orta nokta.
    let agirlik = 0, toplam = 0;
    for (const s of S) { agirlik += (s.col + s.units / 2) * s.units; toplam += s.units; }
    const merkez = agirlik / toplam;

    // Bloğun içindeki en geniş yatay boşluk (aynı satır bandında).
    const satirlar = new Map();
    for (const s of S) {
      if (!satirlar.has(s.row)) satirlar.set(s.row, []);
      satirlar.get(s.row).push(s);
    }
    let enGenisBosluk = 0, bosSatir = null;
    for (const [row, list] of satirlar) {
      const sirali = [...list].sort((a, b) => a.col - b.col);
      for (let i = 1; i < sirali.length; i++) {
        const g = sirali[i].col - (sirali[i - 1].col + sirali[i - 1].units);
        if (g > enGenisBosluk) { enGenisBosluk = g; bosSatir = row; }
      }
    }

    return {
      adet: S.length,
      minC, maxC, minR, maxR,
      genislik: maxC - minC,
      yukseklik: maxR - minR,
      merkez,
      duvarMerkezi: this.cols / 2,
      kayma: merkez - this.cols / 2,          // + sağa, − sola
      satirSayisi: satirlar.size,
      enGenisBosluk, bosSatir,
      olculer: new Set(S.map((s) => s.productId)).size,
      renkler: new Set(S.map((s) => s.colorId)).size,
      sagBosluk: this.cols - maxC,
      solBosluk: minC,
    };
  }

  /** Verilen boşluğa sığan en büyük rafın adı (örn. "30'luk"). */
  boslugaSigan(birim) {
    const uygun = [...this.products].filter((p) => p.units <= birim)
      .sort((a, b) => b.units - a.units)[0];
    return uygun ? { ad: `${uygun.cm}'lik`, id: uygun.id } : null;
  }

  /**
   * Kompozisyon temelli ipucu.
   *
   * Kural: indirim yüzdesinden HİÇ bahsetmiyoruz — müşteri onu alttaki
   * avantaj rayından zaten takip ediyor, asistanın da aynı şeyi söylemesi
   * satış baskısı gibi duruyor. Bunun yerine gerçek bir yerleşim tavsiyesi
   * veriyoruz; adet zaten tavsiyenin doğal sonucu olarak artıyor.
   */
  idleLine(p) {
    if (!p.count) {
      return [
        'Duvarın seni bekliyor.',
        'Aşağıdaki kartlardan birine tıkla — raf duvara kendi yerini bulsun.',
      ];
    }

    const opts = this.tipPool(p);

    // Son iki ipucunu tekrarlama — asistan kendini tekrar ediyor gibi durmasın.
    this._tipGecmis = this._tipGecmis || [];
    const taze = opts.filter((o) => !this._tipGecmis.includes(o[0]));
    const havuz = taze.length ? taze : opts;
    const pick = havuz[Math.floor(Math.random() * havuz.length)];

    this._tipGecmis.push(pick[0]);
    if (this._tipGecmis.length > 4) this._tipGecmis.shift();
    this.track('kz_tip_shown', { ipucu: pick[0] });
    return pick;
  }

  /**
   * İpucu havuzu — hepsi duvarın o anki durumuna bakar.
   *
   * Altı aile var: yerleşim, ölçü, renk, oda, kullanım ve duvar rengi.
   * Her biri yalnızca gerçekten geçerliyse havuza giriyor; böylece asistan
   * duvarda olmayan bir şeyden bahsetmiyor.
   */
  tipPool(p) {
    const k = this.composition();
    const o = [];
    if (!k) return [['Duvarın seni bekliyor.', 'Aşağıdaki kartlardan birine tıkla.']];

    const duvarAdi = (this.cfg.wallColors || []).find((w) => w.id === this.wallColorId)?.name;

    /* ---------- Oda bağlamı ----------
       Müşteri bir oda seçtiyse asistan o odanın eşyaları üzerinden konuşur:
       mutfakta baharatlık, kahve köşesinde öğütücü, antrede anahtar. Genel
       tasarım tavsiyesi yerine gerçek bir danışman gibi duruyor. */
    if (this.room) {
      for (const c of this.room.tavsiyeler) o.push([`${this.room.ad}:`, c]);
    }

    /* ---------- Yerleşim ---------- */
    if (Math.abs(k.kayma) > this.cols * 0.08) {
      const saga = k.kayma < 0;
      const sigan = this.boslugaSigan(saga ? k.sagBosluk : k.solBosluk);
      if (sigan) {
        o.push([`Düzen ${saga ? 'sola' : 'sağa'} toplanmış.`,
          `${saga ? 'Sağ' : 'Sol'} tarafta yer var — <b>${sigan.ad}</b> bir raf dengeler.`]);
      }
    }
    if (k.enGenisBosluk >= 6) {
      const sigan = this.boslugaSigan(k.enGenisBosluk);
      if (sigan) {
        o.push(['Sıranın ortasında boşluk var.',
          `Tam oraya <b>${sigan.ad}</b> bir raf oturuyor.`]);
      }
    }
    if (k.satirSayisi === 1 && k.adet >= 2) {
      o.push(['Hepsi tek hizada.',
        'Bir rafı bir sıra aşağı alıp yarım boy kaydır — merdiven düzeni derinlik veriyor.']);
      o.push(['Simetri mi, ritim mi?',
        'Üstte iki, altta bir raf — üçgen yerleşim gözü yukarı çekiyor.']);
    }
    if (k.satirSayisi >= 2 && k.adet >= 3) {
      o.push(['Katlar oluşmuş.',
        'Alt sırayı biraz uzatmak kompozisyona sağlam bir taban veriyor.']);
    }
    if (k.yukseklik >= 12 && k.adet >= 3) {
      o.push(['Dikey bir kule kurmuşsun.',
        'Yanına tek bir raf eklemek kuleyi duvara bağlıyor, havada kalmıyor.']);
    }

    /* ---------- Ölçü ---------- */
    if (k.olculer === 1 && k.adet >= 2) {
      const m = this.productById.get(this.shelves[0].productId);
      const d = this.products.find((x) => x.id !== m.id);
      if (d) {
        o.push([`Hepsi ${m.cm} cm.`,
          `Araya bir <b>${d.cm}'lik</b> girince diziliş tekdüze olmaktan çıkıyor.`]);
      }
    }
    if (k.olculer >= 2) {
      o.push(['Ölçüleri karıştırmışsın.',
        'En uzun rafı en alta almak düzeni oturtuyor — ağırlık aşağıda iyi durur.']);
    }
    if (k.adet === 1) {
      o.push(['Tek raf da güzel.',
        'Yanına ikinci bir raf koyduğunda duvar "kompozisyon" olmaya başlıyor.']);
    }

    /* ---------- Renk ---------- */
    const c = this.unusedColor();
    if (c && k.renkler === 1 && k.adet >= 3) {
      o.push(['Tek aksan dener misin?',
        `Raflardan birini <b>${c.name}</b> yapmak bütün düzeni toparlıyor.`]);
    }
    if (k.renkler >= 3) {
      o.push(['Renk sayısı arttı.',
        'Üç tondan fazlası dağıtabiliyor; birini tekrar edip diğerini aksan bırak.']);
    }
    if (c && k.renkler === 2) {
      o.push(['İki ton iyi gidiyor.',
        `Üçüncü ton isteyeceksen <b>${c.name}</b> en az riskli olanı.`]);
    }

    /* ---------- Duvar / oda ---------- */
    if (duvarAdi) {
      o.push([`Duvarın ${duvarAdi}.`,
        'Kendi duvarının tonunu üstteki araç çubuğundan seçersen raflar gerçekte nasıl duracak görürsün.']);
    }
    if (this.night) {
      o.push(['Gece modundasın.',
        'Açık renk raflar gece daha çok öne çıkıyor; koyu tonlar duvara gömülüyor.']);
    } else {
      o.push(['Işık sağdaki pencereden geliyor.',
        'Rafları sola doğru yaydıkça gölgeler uzuyor, derinlik artıyor.']);
    }

    /* ---------- Kullanım alanı ---------- */
    if (k.adet >= 2) {
      o.push(['Ne koyacağını düşündün mü?',
        'Kitap için 30, bitki ve çerçeve için 45, vinil ve kutu için 60 rahat ediyor.']);
      o.push(['Çalışma duvarı mı, salon mu?',
        'Salonda raflar göz hizasının biraz üstünde, çalışma masasının üstünde ise 40-50 cm yukarıda iyi duruyor.']);
    }
    if (k.adet >= 4) {
      o.push(['Bu düzen koridorda da çalışır.',
        'Dar alanlarda 30\'luklardan kurulu iki sıra, derinlik kaybetmeden yer açıyor.']);
    }
    if (k.genislik < this.cols * 0.45 && k.adet >= 2) {
      o.push(['Duvarda epey boşluk kalıyor.',
        'Diziliği yanlara doğru genişletmek kompozisyonu duvara oturtur.']);
    }

    for (const line of this.cfg.socialProof || []) o.push(['Bugün stüdyoda', line]);

    if (!o.length) {
      o.push(['Kompozisyon oturdu.',
        'Bir rafı seçip yerini değiştir — küçük kaydırmalar düzeni epey değiştiriyor.']);
    }
    return o;
  }

  /**
   * Asistana dokunma.
   *
   * E-posta isteği beklemedeyse dokunmak onu kapatır — müşteri "istemiyorum"
   * demiş olur. İpuçları buna rağmen gelmeye devam eder. Karar bu oturumla
   * sınırlı: müşteri yarın geldiğinde form yine sorar.
   *
   * DİKKAT: e-posta alanı ve gönder düğmesi bu dinleyiciye ULAŞMAZ; onlar
   * kabarmayı `bindEvents()` içinde durduruyor. Yoksa alana tıklamak formu
   * kapatırdı — bir süre bu hata canlıdaydı.
   */
  replayAssistant() {
    // E-posta isteği hâlâ beklemedeyse dokunmak onu kapatır — o an ekranda
    // form değil bir ipucu olsa bile. Müşterinin niyeti "bunu kapat";
    // baloncukta ne yazdığına bakıp niyeti görmezden gelmek yanlış olurdu.
    if (this.emailAvailable() && !this.emailCaptured && !this.emailDismissed) {
      this.emailDismissed = true;
      clearTimeout(this._restT);
      this.assistantEl.classList.remove('kz-talking');
      this.track('kz_mail_dismissed');
      return;
    }
    if (!this.lastSay) { this.say(this.shelves.length ? 'resume' : 'welcome'); return; }
    // Aynı mesajı tekrar "konuşuyor" göstermek için animasyonu sıfırla.
    this.assistantEl.classList.remove('kz-talking');
    void this.assistantEl.offsetWidth;
    this.showBubble(this.lastSay);
  }

  /* ======================================================================
     SEPET / SATIN ALMA
     ====================================================================== */

  /** Tasarımı sipariş notuna yazılacak kısa metne çevirir. */
  layoutString() {
    return this.shelves
      .map((s) => `${s.productId[0].toUpperCase()}${this.productById.get(s.productId).cm}` +
                  `/${s.colorId}/${s.col * 5}x${s.row * 5}`)
      .join(' | ');
  }

  /** (ürün, renk) kırılımında miktar tablosu. */
  cartLines() {
    const map = new Map();
    for (const s of this.shelves) {
      const key = `${s.productId}|${s.colorId}`;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].map(([key, qty]) => {
      const [productId, colorId] = key.split('|');
      return {
        productId, colorId, quantity: qty,
        product: this.productById.get(productId),
        color: this.colorById.get(colorId),
        variantId: this.variantFor(productId, colorId)?.id ?? null,
      };
    });
  }

  async checkout() {
    const p = this.pricing();
    if (!p.count) return;

    const lines = this.cartLines();
    const cta = this.root.querySelector('.kz-cta');

    // Shopify varyantları tanımlı değilse (demo modu) özeti göster
    const missing = lines.filter((l) => !l.variantId);
    if (missing.length) {
      const summary = lines
        .map((l) => `• ${l.quantity} × ${l.product.name} (${l.product.cm} cm) — ${l.color.name}`)
        .join('\n');
      alert(
        `KREAZ STUDIO — Tasarım Özeti (${this.designId})\n\n${summary}\n\n` +
        `Ara toplam: ${this.money(p.subtotal)}\n` +
        `İndirim: ${p.discount ? '−' + this.money(p.discount) : '—'}\n` +
        `Kargo: ${p.freeShipping ? 'Ücretsiz' : this.money(this.cfg.shippingCents)}\n` +
        `TOPLAM: ${this.money(p.total)}\n\n` +
        `Yerleşim: ${this.layoutString()}\n\n` +
        `(Demo modu — Shopify varyant kimlikleri tanımlanınca doğrudan sepete eklenir.)`,
      );
      return;
    }

    cta.classList.add('kz-busy');
    cta.querySelector('span').textContent = 'Sepete ekleniyor…';

    // Ölçüm: sepete ekleyen oturum. Clarity bu oturumun kaydını önceliklendiriyor.
    this.clarityDesign(true);
    this.clarityTag('kz_sepet', 'evet');
    this.clarity('upgrade', 'kz_sepete_eklendi');
    this.track('kz_sepete_eklendi', { raf: p.count, tutar: Math.round(p.total / 100) });

    try {
      const ozet = `${p.count} raf${this.room ? ` · ${this.room.ad}` : ''}`;
      const link = this.designUrl();
      const items = lines.map((l, i) => ({
        id: l.variantId,
        quantity: l.quantity,
        properties: {
          'Renk': l.color.name,
          'Ölçü': `${l.product.name} (${l.product.cm} cm)`,
          /* Tasarım bilgisi yalnız İLK satırda — her satırda tekrarlanınca
             sepet kalabalıklaşıyor. Ödeme sayfasında kısa özet görünüyor;
             uzun link ve görsel alt çizgiyle gizli (sepette tema onları
             küçük resim ve "Tasarımı 3D aç" linki olarak gösteriyor). */
          ...(i === 0 ? { 'Tasarım': ozet, '_Tasarım linki': link } : {}),
          '_kreaz_design': this.designId,
        },
      }));

      /* İlk satır tasarım görseliyle birlikte, çok parçalı istekle gidiyor:
         Shopify dosyayı kendi CDN'ine yükleyip adresini satıra yazıyor —
         ayrı bir görsel sunucusu gerekmiyor. Görsel alınamaz ya da yükleme
         reddedilirse satır görselsiz eklenir; sepete ekleme hiç engellenmez. */
      const sepeteEkle = async (satirlar) => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: satirlar }),
        });
        if (!res.ok) throw new Error((await res.json())?.description || 'Sepete eklenemedi.');
      };

      /* Tasarım satırı EN SONA ekleniyor: Shopify en son ekleneni sepetin
         başına koyuyor; görsel ve link en üstte görünsün. */
      let gorselUrl = null;
      const tasarimSatiri = items.shift();
      if (items.length) await sepeteEkle(items);

      const veri = this.designSnapshot(1000, 667, 0.85);
      let eklendi = false;
      if (veri) {
        const fd = new FormData();
        fd.append('items[0][id]', tasarimSatiri.id);
        fd.append('items[0][quantity]', tasarimSatiri.quantity);
        for (const [k, v] of Object.entries(tasarimSatiri.properties)) {
          fd.append(`items[0][properties][${k}]`, v);
        }
        fd.append('items[0][properties][_Tasarım görseli]',
          dataUrlToFile(veri, `kreaz-tasarim-${this.designId}.jpg`));
        const r1 = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        if (r1.ok) {
          eklendi = true;
          const j = await r1.json().catch(() => null);
          gorselUrl = j?.items?.[0]?.properties?.['_Tasarım görseli'] || null;
        }
      }
      if (!eklendi) await sepeteEkle([tasarimSatiri]);   // görselsiz, ama eksiksiz

      /* Tasarımın tamamı siparişin "Ek ayrıntılar" kutusunda — satıcı için.
         Sepet özellikleri ödemeye ve siparişe olduğu gibi taşınıyor; sıra
         korunduğu için satıcının ilk göreceği link en başta. */
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Tasarım Linki': link,
            'Tasarım Görseli': gorselUrl || '',
            'Kreaz Tasarım No': this.designId,
            'Oda': this.room?.ad || '—',
            'Raf Sayısı': String(p.count),
            'Duvar Ölçüsü': `${this.wallW}×${this.wallH} cm`,
            'Yerleşim': this.layoutString(),
          },
        }),
      }).catch(() => { /* not kritik değil */ });

      /* E-postasını bırakmış müşterinin profiline de gerçek tasarım görseli
         yazılıyor: akıştaki sonraki e-postalar katalog fotoğrafı yerine
         müşterinin kendi duvarını gösteriyor. Sayfa değişirken gideceği için
         keepalive'lı doğrudan istek. */
      if (gorselUrl && this.email && this.klaviyoKey()) {
        this._tasarimGorsel = gorselUrl;
        this._gorselKod = this.encodeDesign();
        this.klaviyoIdentify({ email: this.email, ...this.designProps() }, true);
      }

      // Kademeye bağlı indirim kodu varsa uygula, yoksa doğrudan ilerle
      const dest = this.cfg.afterAdd === 'cart' ? this.cfg.cartUrl : this.cfg.checkoutUrl;
      const code = p.tier?.code;
      window.location.href = code
        ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(dest)}`
        : dest;
    } catch (err) {
      cta.classList.remove('kz-busy');
      cta.querySelector('span').textContent = `Tasarımını sepete ekle · ${p.count} raf`;
      this.bubbleEl.className = 'kz-bubble kz-tone-warn';
      this.bubbleEl.innerHTML =
        `<span class="kz-bubble-tag">Hata</span><b>Sepete eklenemedi.</b> ${err.message}`;
      this.assistantEl.classList.add('kz-talking');
      console.error('[Kreaz Studio]', err);
    }
  }
}

/* ==========================================================================
   7) GİRİŞ NOKTASI
   ========================================================================== */

function deepMerge(base, extra) {
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const [k, v] of Object.entries(extra || {})) {
    if (v && typeof v === 'object' && !Array.isArray(v) && base[k] && typeof base[k] === 'object' && !Array.isArray(base[k])) {
      out[k] = deepMerge(base[k], v);
    } else if (v !== undefined && v !== null) {
      out[k] = v;
    }
  }
  return out;
}

export async function mount(target, userConfig = {}) {
  const root = typeof target === 'string' ? document.querySelector(target) : target;
  if (!root) throw new Error('[Kreaz Studio] Bağlanacak öğe bulunamadı.');

  const cfg = deepMerge(DEFAULT_CONFIG, userConfig);
  T = await import(cfg.threeUrl);

  const app = new KreazStudio(root, cfg);
  // Tema geliştiricisi için konsoldan erişim: window.__kreaz
  if (typeof window !== 'undefined') window.__kreaz = app;
  try {
    await app.boot();
  } catch (err) {
    console.error('[Kreaz Studio]', err);
    root.innerHTML =
      `<div style="padding:40px;font:15px/1.6 system-ui;color:#7a2f1f;text-align:center">
         Tasarım stüdyosu yüklenemedi.<br>
         <small style="color:#928b80">${err.message}</small>
       </div>`;
  }
  return app;
}

// Sayfada KREAZ_STUDIO_CONFIG varsa otomatik bağlan.
if (typeof window !== 'undefined') {
  const auto = () => {
    const node = document.querySelector('[data-kreaz-studio]');
    if (node && !node.dataset.kreazMounted) {
      node.dataset.kreazMounted = '1';
      const inline = node.dataset.kreazConfig ? JSON.parse(node.dataset.kreazConfig) : {};
      mount(node, deepMerge(window.KREAZ_STUDIO_CONFIG || {}, inline));
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auto);
  else auto();
}

export default { mount };
